// =============================================================================
// Rolled — unified site app: BACK-OF-HOUSE (kitchen) module. Poured in from the
// live Manchester Kitchen app (Phase 1). Site values come from config; behaviour
// is migrated AS-IS. Mounted by src/main.jsx only when the active site is BOH.
// =============================================================================
import React from "react";
import * as ReactDOM from "react-dom";
import exifr from "exifr";
import { getActiveSite, getSite } from "./config/sites.js";
import { confirmedWrite } from "./lib/confirmedWrite.js";
import * as stockSync from "./lib/stockSync.js"; // Phase 2 dual-run shadow (best-effort)
import * as pinAuth from "./lib/auth.js"; // A1: server-side PIN verify, OFF everywhere (closes the downloaded-PIN-list leak one shop at a time)
import * as rosterFree from "./lib/rosterFree.js"; // BF: pure helpers so a gated kiosk never DECIDES from the downloaded roster (role/clock-out compare)
import { KioskBindPanel } from "./lib/kioskBindPanel.jsx"; // CB: admin-only "which shop is this tablet?" (gated OFF — KIOSK_BIND_HOSTS is empty, so this renders nothing anywhere)
import * as clockPinCheck from "./lib/clockPinCheck.js"; // BT: pure decisions for the gated clock-scrim PIN check ("Checking…", stale-answer guard, 8 s timeout) — the OFF path never runs a check
import * as stockReader from "./lib/stockReader.js"; // Phase 3 read-flip (gated OFF until STOCK_READ_HOSTS lists this host)
import * as complianceSync from "./lib/complianceSync.js"; // Phase 3 dual-run shadow (compliance + HACCP)
import * as complianceReader from "./lib/complianceReader.js"; // Lane AK: compliance/HACCP read-flip front end (gated OFF until COMPLIANCE_READ_HOSTS lists this host)
import * as wastageSync from "./lib/wastageSync.js"; // Phase 3 dual-run shadow (kitchen wastage -> wastage_events, best-effort)
import * as deliveriesSync from "./lib/deliveriesSync.js"; // Option A: inbound delivery photo -> private Supabase bucket (best-effort)
import { fetchEmergencyContacts } from "./lib/emergencyContacts.js"; // Phase 3: emergency contacts read from Supabase (was the Apps Script feed)
import { fetchTileOverrides } from "./lib/tileOverrides.js"; // Phase 3: tile config read from Supabase (was the Apps Script feed)
import { fetchStaffCerts } from "./lib/staffCerts.js"; // Phase 3: staff certs read from Supabase (was the Apps Script getStaffCerts POST)
import * as stockCatalogSrc from "./lib/stockCatalog.js"; // Phase 3: the stock item list + photos from Supabase (gated OFF until CATALOG_READ_HOSTS lists this host)
import * as presence from "./lib/presence.js"; // S22: shadow-record every PIN tap so we know who was in the shop (write-only, nothing reads it)
import * as notificationsSync from "./lib/notificationsSync.js"; // S28: a running-low / out-of-stock tap raises a real reorder alert (queues only — nothing sends)
import * as eodInsights from "./lib/eodInsights.js"; // Previous-Week EOD Insights read from Supabase (gated by EOD_INSIGHTS_READ_HOSTS; falls back to the Google feed on any failure)
import * as forecastReader from "./lib/forecastReader.js"; // Lane BG: forecast ENGINE surface read from Supabase (gated OFF by FORECAST_READ_HOSTS; falls back to the Google ?action=forecast on any failure/stale bench). Manchester V2 / tc only — Meadowhall v4.5 out of scope.

const SITE = getActiveSite();
const BACKEND = SITE.backend;
void confirmedWrite; // wired into BOH writes in task #6

const {useState,useEffect,useCallback,useRef,useMemo} = React;

const BUILD_VERSION = (typeof window !== "undefined" && window.BUILD_VERSION) || "unknown";

function useAutoUpdater(){
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      try {
        const url = window.location.pathname + "?_v=" + Date.now();
        const r = await fetch(url, {cache: "no-store"});
        if (!r.ok || cancelled) return;
        const html = await r.text();
        const m = html.match(/window\.BUILD_VERSION\s*=\s*"([^"]+)"/);
        if (m && m[1] !== BUILD_VERSION && !cancelled) setUpdateAvailable(true);
      } catch (e) {}
    };
    const firstCheck = setTimeout(check, 15000);
    const interval = setInterval(check, 120000);
    return () => { cancelled = true; clearTimeout(firstCheck); clearInterval(interval); };
  }, []);
  return updateAvailable;
}

function useVersionPillDOM(updateAvailable){
  useEffect(() => {
    const pill = document.createElement("div");
    pill.id = "__rolled-version-pill";
    pill.style.cssText = "position:fixed;bottom:6px;right:10px;z-index:9998;font-family:monospace;font-size:10px;color:rgba(254,250,240,0.3);letter-spacing:0.02em;pointer-events:none;user-select:none";
    pill.textContent = "v" + BUILD_VERSION;
    document.body.appendChild(pill);
    return () => { try { document.body.removeChild(pill); } catch(e) {} };
  }, []);
  useEffect(() => {
    if (!updateAvailable) return;
    // Full-screen non-dismissible modal (2026-04-21): the old corner banner
    // let staff ignore new builds indefinitely on the same tablet session.
    // This version blocks all page interaction and exposes only one action:
    // Refresh. Escape is swallowed so the OS can't close it.
    const backdrop = document.createElement("div");
    backdrop.id = "__rolled-update-modal";
    backdrop.style.cssText = "position:fixed;inset:0;background:rgba(0,20,15,0.85);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:24px;z-index:99999;animation:__rolledFade 0.25s ease-out";
    backdrop.innerHTML = '<style>@keyframes __rolledFade{from{opacity:0}to{opacity:1}}</style><div role="alertdialog" aria-modal="true" aria-labelledby="__rolled-update-title" style="background:#00392C;color:#FEFAF0;padding:40px 36px;border-radius:20px;max-width:420px;width:100%;text-align:center;box-shadow:0 24px 64px rgba(0,0,0,0.45);border:1px solid rgba(212,251,111,0.15)"><div style="font-size:48px;margin-bottom:8px" aria-hidden="true">\u2728</div><div id="__rolled-update-title" style="font-family:gelica,Georgia,serif;font-size:26px;font-weight:700;color:#D4FB6F;letter-spacing:-0.02em;margin-bottom:10px">New version ready</div><div style="font-size:15px;color:rgba(254,250,240,0.75);line-height:1.5;margin-bottom:28px">A newer build of this app is live. Tap refresh to load it.</div><button id="__rolled-update-refresh" style="background:#D4FB6F;color:#00392C;border:none;padding:18px 28px;border-radius:12px;font-family:Manrope,sans-serif;font-weight:800;font-size:17px;letter-spacing:0.02em;cursor:pointer;width:100%;box-shadow:0 4px 16px rgba(212,251,111,0.25);min-height:56px">\u21BB Refresh now</button></div>';
    document.body.appendChild(backdrop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const btn = backdrop.querySelector("#__rolled-update-refresh");
    const reload = () => window.location.reload();
    btn.addEventListener("click", reload);
    const swallowKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener("keydown", swallowKey, true);
    setTimeout(() => { try { btn.focus(); } catch(e) {} }, 50);
    return () => {
      btn.removeEventListener("click", reload);
      window.removeEventListener("keydown", swallowKey, true);
      document.body.style.overflow = prevOverflow;
      try { document.body.removeChild(backdrop); } catch(e) {}
    };
  }, [updateAvailable]);
}

// Orientation hook — detects portrait vs landscape for responsive layout
function useOrientation(){
  const [isPortrait,setIsPortrait]=useState(window.innerHeight>window.innerWidth);

  useEffect(()=>{
    const h=()=>setIsPortrait(window.innerHeight>window.innerWidth);
    window.addEventListener('resize',h);window.addEventListener('orientationchange',h);
    return()=>{window.removeEventListener('resize',h);window.removeEventListener('orientationchange',h)};
  },[]);
  return isPortrait;
}

// Mobile detection hook — hides sidebar, adjusts padding on small screens
function useIsMobile(){
  const [isMobile,setIsMobile]=useState(window.innerWidth<=600);
  useEffect(()=>{
    const h=()=>setIsMobile(window.innerWidth<=600);
    window.addEventListener('resize',h);
    return()=>window.removeEventListener('resize',h);
  },[]);
  return isMobile;
}

// ===========================================
// CONFIG — MANCHESTER KITCHEN (multi-site aggregator)
// ===========================================
// [poured in: SITE now from config/sites.js]
// Arndale removed 2026-08-17: site closed permanently 16/08/2026. This map is the
// single source for the Daily Forecast grid, the Stock Requests grid and SITE_KEYS
// (all derived via Object.keys/entries(SITES)), so dropping the key here retires
// every forward-looking kitchen fan-out at once. The arndale entries in
// SITE_LABEL_SHORT / SITE_LABEL_FULL below are deliberately KEPT so any historical
// row that still carries site="arndale" renders with a proper name, not a raw key.
const SITES = {tc:{id:"a1000000-0000-4000-8000-000000000001",name:"Trafford Centre",short:"TC",key:"tc"}};
// [poured in: BACKEND now from config/sites.js]

// FOH Clock-In gate. Per JJ 2026-04-29: feature is scoped to Manchester
// Kitchen only — all other sites stay disabled. Kitchen's SITE.key is "mcr"
// (legacy) so this evaluates TRUE here and the banner + scrim run live;
// keeping the gate site-aware means the same source can be lifted into
// another worktree without re-introducing the original contamination on
// TC/Arndale/Meadowhall.
// 2026-04-30: kill switch flipped to false. Decision rationale (JJ):
// Bizimply is the source of truth for payroll. Staff already clock in there
// via Bizimply's Manager / Employee / Timestation apps. Our kiosk Clock In
// tile was duplicating effort, requiring a token-refresh dance that doesn't
// actually work (PATCH /api/sessions/reauthorize always 422s for ApiKey-class
// JWTs — verified 12-shape black-box test 2026-04-30).
//
// New presence model: per-action PIN entry IS proof of presence. Someone
// physically tapped this PIN on the kiosk to perform an action; no separate
// clock-in required. The admin "On Shift Now" widget will derive presence
// from Activity Log (PINs that fired any per-action POST in the last 4 hours).
//
// Keeping the constant + the conditional renders so a future re-enable is a
// one-line flip if the operational model changes. Backend cleanup follows
// once clasp's RAPT is back (see TOMORROW_MORNING_CHECKLIST.md).
const CLOCK_IN_ENABLED = false;
// Backend siteKey for clock-in actions. The Manchester Kitchen frontend's
// SITE.key is "mcr" (legacy) but the backend's SITE_TO_BIZIMPLY_STORE_ +
// StaffShifts schema use "kitchen". This constant routes the clock-in
// POSTs correctly without forcing a SITE.key rename across every other
// kitchen-frontend backend call (which would have cascade effects).
const CLOCK_IN_SITE_KEY = "kitchen";

// Coarse device classifier (forensic only) — used on PIN attempts so the
// admin Activity Log can show where a failed PIN came from.
function getDeviceInfo(){
  if (typeof navigator === "undefined") return {device:"unknown", ua:""};
  const ua = String(navigator.userAgent || "");
  const w = (typeof window !== "undefined" && window.innerWidth) || 0;
  const isiPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
  const isPhone = /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/.test(ua);
  let device = "desktop";
  if (isiPad || isAndroidTablet) device = "tablet";
  else if (isPhone || (w && w < 600)) device = "mobile";
  else if (w && w >= 600 && w < 1100 && /Mobi|Tablet|Touch/i.test(ua)) device = "tablet";
  return {device, ua};
}
// No hard-coded PINs (removed 2026-06-21, Phase 0 security). Staff roster is fetched from the
// Admin Staff Directory on mount (see useEffect in App), then cached in localStorage. If the
// backend is unreachable AND there's no cache, login fails closed (empty roster) rather than
// exposing emergency PINs in the public bundle. A proper offline emergency login is a Phase 2 task.
const STAFF_PINS_FALLBACK = {};
// Mirror of pinsForLogin's `staff` map (PIN → {name, role, homeSite}). Used by
// makeCmpPinPrompt to pre-check requiredRoles client-side; backend re-checks
// every action via canPerformAction so this is a UX optimisation, not a gate.
const STAFF_BY_PIN_FALLBACK = {};
const B = {nori:"#00392C",wasabi:"#D4FB6F",kewpie:"#FEFAF0",rice:"#FFFFFF",coral:"#FF7A5C",sunshine:"#F1C544",eucalyptus:"#6FAF8F"};

// Kitchen ingredient list for wastage tracking (v1 hardcoded — admin-editable later)
const INGREDIENTS = {
  "Rice & Nori":["Sushi Rice (kg)","Nori Sheets","Sesame Seeds"],
  "Proteins":["Salmon (kg)","Cooked Chicken (kg)","Cooked Prawns (kg)","Tuna (kg)","Shrimp Tempura (pcs)","Eggplant Tempura (pcs)","Tofu (kg)"],
  "Vegetables & Fillings":["Avocado","Cucumber","Spring Onion","Edamame (kg)","Pickled Ginger (kg)","Corn Kernels (kg)"],
  "Sauces":["Red Hot Mayo","Katsu Mayo","Wasabi Mayo","Lemon Pepper Mayo","Herby Mayo","Teriyaki Sauce","Soy Sauce","Spicy Mayo"],
  "Miso & Sides":["Miso Paste (kg)","Tofu for Miso (kg)","Seaweed for Miso","Spring Onion for Miso"],
};

// Stock items list (same as FOH — used for kitchen stock receiver display)
const STOCK_ITEMS = {
  "Sushi Consumables":["Dip Pots","Soy Sauce Fish","Gluten Free Soy Sauce","Wasabi","Ginger","Spoons","Spring Onion for Miso","Tofu for Miso","Seaweed for Miso","Miso Cups","Miso Lids"],
  "Stickers & Labels":["Red Hot Mayo Stickers","Katsu Mayo Stickers","Wasabi Mayo Stickers","Lemon Pepper Mayo Stickers","Herby Mayo Stickers","Soy Sauce Stickers","Rolled Stickers"],
  "Sauces (Bottles)":["Red Hot Mayo (Bottle)","Katsu Mayo (Bottle)","Wasabi Mayo (Bottle)","Teriyaki Sauce (Bottle)","Lemon Pepper Mayo (Bottle)","Herby Mayo (Bottle)"],
  "Packaging":["Rolled Carrier Bags","White Bags","Rolled Boxes","M Black Gloves","XL Black Gloves","Napkins"],
  "Drinks":["Coke Regular","Coke Zero","Lipton Peach Ice Tea","Water","Karma Lemony Lemonade","Matcha Cans","Matcha Lids"],
  "Hip-Pop Kombucha":["Tropical Peach Hip-Pop","Apple & Elderflower Hip-Pop","Berries & Cherries Hip-Pop","Ginger & Tumeric Hip-Pop","Pink Grapefruit Hip-Pop"],
  "Snacks":["Allens Killer Pythons","Sweet & Sour Allens Killer Pythons","Allens Party Mix","Burger Rings","Caramello Koala","Cheezels","Cherry Ripe","Chomp","Clinkers","Marvellous Creations Dairy Milk","Furry Friends Dairy Milk","KitKat Chunky Milo","Mint Patties","Shapes (BBQ)","Shapes (Pizza)","Tim Tams","Violet Crumble","Zappos"],
  "Marketing & Print":["Club Cards","Box QR Review Codes","New Menu Card","Menu Flyers"],
  "Cleaning & Office":["Bin Bags","Glass Spray","Anti-Bacterial Spray","Blue Roll","Notepads","Staples","Pens","Band-Aids","Paracetamol"],
};

// Emergency Contacts are now admin-managed via the Command Centre. The
// FOH/kitchen tile fetches them from the backend on mount (per-site) and
// caches in localStorage for 5 min so the tile paints instantly. See
// admin tile "Emergency Contacts" in src/admin.jsx for the editor.
const EMERGENCY_CONTACTS_CACHE_KEY = "emergencyContactsCache::kitchen";
const EMERGENCY_CONTACTS_TTL_MS = 5 * 60 * 1000;

// Tile-level overrides (keyed by tool.id) — admin Tile Editor writes
// here. Applied at render time so admin renames + state flips flow to
// tablets without a deploy. Mirrors Trafford's pattern. State priority:
// explicit `state` field wins; otherwise legacy `comingSoon` boolean.
function applyTileOverride(tool, tileOverrides) {
  if (!tool || !tileOverrides) return tool;
  const o = tileOverrides[tool.id];
  if (!o) return tool;
  const out = {...tool};
  if (o.label && String(o.label).trim()) out.label = String(o.label);
  if (o.desc  && String(o.desc).trim())  out.desc  = String(o.desc);
  if (o.icon  && String(o.icon).trim())  out.icon  = String(o.icon);
  if (o.state === "live" || o.state === "coming_soon" || o.state === "hidden") {
    out.state = o.state;
    out.on = (o.state === "live"); // keep legacy `on` boolean consistent
  } else if (o.comingSoon === true) {
    out.state = "coming_soon";
    out.on = false;
  }
  if (o.tag) out.tag = o.tag;
  return out;
}

const TOOLS = [
  {id:"forecast",label:"Production Forecast",desc:"Per-SKU targets for each site",icon:"📊",on:true},
  // Previous Week Insights (Phase A, 2026-05-06). Read-only EOD view —
  // yesterday + last 7 days for whichever site is selected. Hidden by
  // default (on:false); flip live via CC Tile Editor (state:"live") when ready.
  {id:"insights",label:"Previous Week Insights",desc:"Yesterday + last 7 days · wastage, TGTG, sell-outs",icon:"📈",on:false},
  // Compliance Module (Phase 1, 2026-04-29). LIVE on Kitchen first per JJ — other sites stay
  // Coming Soon until kitchen rollout proves the flow. Cleaning Schedule + Pest Daily +
  // Corrective Actions ship in this session; HACCP/Probe/Fish/DTR/Emergency/Declaration/FSMS
  // come in next sessions.
  {id:"compliance",fn:"complianceLog",label:"Compliance",desc:"HACCP · Cleaning · Pest · Probe · Fish · Emergency · Declaration · Corrective",icon:"🛡️",on:true},
  {id:"wastage",label:"Wastage Tracking",desc:"Log ingredient waste & costs",icon:"🗑️",on:false},
  {id:"stockreq",label:"Stock Requests",desc:"View & fulfil FOH requests",icon:"🚚",on:false},
  {id:"contacts",label:"Emergency Contacts",desc:"Key numbers & emergency info",icon:"📞",on:true},
  {id:"checklist",label:"Production Checklist",desc:"BOH tick-off against targets",icon:"✅",on:false},
  {id:"prep",label:"Prep Sheets",desc:"Daily prep list from forecast",icon:"📋",on:false},
  {id:"foodspecs",label:"Food Specs",desc:"Ingredients, portions & photos",icon:"🍣",on:false},
  {id:"stocktake",label:"Stocktake",desc:"Kitchen stock count",icon:"📦",on:false},
  {id:"stockloc",label:"Stock Locations",desc:"Storage codes per item",icon:"📍",on:false},
  {id:"lowstock",label:"Low Stock Ordering",desc:"Request reorders",icon:"📩",on:false},
  {id:"intake",label:"Stock Intake Log",desc:"Log received deliveries",icon:"📥",on:false},
  // 2026-05-04: Inbound Deliveries — staff confirms a delivery with a photo
  // and either picks an item from the running list (matches the in-flight
  // stockOrdered/running-low record) or types a custom item name for
  // one-off arrivals (e.g. slushie machine, marketing signage). Same
  // photo-required flow as the FOH Confirm Delivery modal.
  {id:"inbound",label:"Inbound Deliveries",desc:"Log + photo-confirm what just arrived",icon:"🚛",on:true},
  {id:"driver",label:"Driver Departure Log",desc:"Departure time logging",icon:"📱",on:false},
  {id:"incident",label:"Incident Report",desc:"Log issues, accidents & equipment",icon:"🚨",on:false},
  {id:"guides",label:"Guides / Handbook",desc:"Staff guides and training",icon:"📖",on:false},
];

// Logo as base64 image
const LOGO_B64 = "data:image/svg+xml;base64,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";
function RolledLogo({height}) {
  height = height || 48;
  return React.createElement("img", {src: LOGO_B64, style: {height: height, width: "auto", display: "block"}, alt: "Rolled"});
}

// ===========================================
// APP DATA — persists across logins
// ===========================================
const appData = {wastageLog:[],forecastConfirmed:{},stockLocalMarks:{}};
const LS_KEY = "rolled.kitchen.appData";
// UK-local day key so the rollover boundary lines up with shop closing (00:00
// London time), not 00:00 UTC (which is 01:00 BST and falls during early-AM
// closing routines). Intl.DateTimeFormat with timeZone:Europe/London handles
// BST/GMT automatically. en-CA gives YYYY-MM-DD natively.
function todayKey(){
  try{ return new Intl.DateTimeFormat("en-CA",{timeZone:"Europe/London"}).format(new Date()); }
  catch(e){ return new Date().toISOString().slice(0,10); }
}
(function hydrateAppData(){
  try{
    const raw = localStorage.getItem(LS_KEY);
    if(!raw) return;
    const parsed = JSON.parse(raw);
    // A 2026-05-15 one-shot migration used to sit here. It dropped anything
    // written by a build that didn't carry a "day-rollover-watchdog" marker in
    // BUILD_VERSION — except NO BUILD_VERSION HAS EVER CONTAINED THAT STRING.
    // All 89 values ever set were checked; not one matches. persistAppData
    // writes `build: BUILD_VERSION`, so the test was false on every load and
    // this tablet has been deleting its own saved work every time the page
    // opened, for three months.
    //
    // What that cost: a refresh, a crash or a tab restore part-way through the
    // end-of-day count wiped the per-SKU waste, the TGTG numbers and the
    // sell-out times, and the screen came back to zeros. Staff re-typed them.
    // The one-shot has therefore already run, on every device, hundreds of
    // times — there cannot be a pre-2026-05-15 payload left anywhere — so it is
    // removed rather than repaired.
    //
    // The day-rollover case it was written for is still covered by the date
    // check on the next line, which is the check that actually works.
    if(!parsed || parsed.date !== todayKey()){ localStorage.removeItem(LS_KEY); return; }
    const d = parsed.data || {};
    if(Array.isArray(d.wastageLog)) appData.wastageLog = d.wastageLog;
    if(d.forecastConfirmed) appData.forecastConfirmed = d.forecastConfirmed;
    if(d.stockLocalMarks) appData.stockLocalMarks = d.stockLocalMarks;
  }catch(e){}
})();
function persistAppData(){
  try{
    localStorage.setItem(LS_KEY, JSON.stringify({date: todayKey(), build: BUILD_VERSION, data: {
      wastageLog: appData.wastageLog,
      forecastConfirmed: appData.forecastConfirmed,
      stockLocalMarks: appData.stockLocalMarks,
    }}));
  }catch(e){}
}

// ===========================================
// MAIN APP
// ===========================================
// ===========================================

// Helper: is `staffName` in the current on-shift roster? Used by the
// per-action PIN modal to gate every action behind a clock-in. Backend
// backstops the same rule with a NOT_CLOCKED_IN response if the cache
// is stale.
function staffIsOnShift(staffName, staffOnShift) {
  if (!staffName || !Array.isArray(staffOnShift)) return false;
  const target = String(staffName).trim().toLowerCase();
  return staffOnShift.some(function(s){
    return s && String(s.name || "").trim().toLowerCase() === target;
  });
}

// UUID v4 — minted on clock-in, reused server-side on clock-out (Bizimply V1
// rejects fresh UUIDs on clock_out per backend handler comment, so we keep
// the original UUID in the row's RawJson).
function uuid4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c){
    var r = Math.random()*16|0, v = c==="x" ? r : (r&0x3|0x8);
    return v.toString(16);
  });
}

// Tablet-only gate for the Clock-In flow. iPads + Android tablets pass.
// Phones + desktops fail. Local preview hosts (localhost / LAN / 127.x) also
// pass so QA + dev work without a real iPad — production hostnames respect
// getDeviceInfo() strictly.
function isTabletKioskHost() {
  try {
    var host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
    if (/^(localhost|127\.0\.0\.1)$/.test(host)) return true;
    if (/^(192\.168\.|10\.0\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(host)) return true;
    return getDeviceInfo().device === "tablet";
  } catch (e) { return false; }
}

// Camera hook — wraps getUserMedia + cleanup. Stops every track when active
// flips false or component unmounts (memory-leak fix). Capture returns a
// JPEG dataURL at q=0.7, target width 720. If the device blocks camera or
// the page is on plain HTTP (Safari blocks getUserMedia outside HTTPS +
// localhost), error stays populated and capture returns null — caller
// should fall back to a Skip button.
function useClockInCamera(active) {
  var videoRef = React.useRef(null);
  var streamRef = React.useRef(null);
  var [error, setError] = React.useState("");

  React.useEffect(function(){
    if (!active) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function(t){ t.stop(); });
        streamRef.current = null;
      }
      setError("");
      return;
    }
    var cancelled = false;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Camera not available on this device");
      return function(){ cancelled = true; };
    }
    navigator.mediaDevices.getUserMedia({video: {facingMode: "user"}, audio: false})
      .then(function(stream){
        if (cancelled) { stream.getTracks().forEach(function(t){ t.stop(); }); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          var p = videoRef.current.play();
          if (p && p.catch) p.catch(function(){});
        }
      })
      .catch(function(e){
        if (cancelled) return;
        // Surface a brief reason so the Skip-photo button has context.
        var msg = (e && e.name === "NotAllowedError") ? "Camera blocked — tap Skip" :
                  (e && e.name === "NotFoundError")   ? "No camera on this tablet" :
                  (e && e.message) || "Camera unavailable";
        setError(msg);
      });
    return function(){
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(function(t){ t.stop(); });
        streamRef.current = null;
      }
    };
  }, [active]);

  var capture = React.useCallback(function(){
    var v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    var targetW = 720;
    var w = Math.min(targetW, v.videoWidth);
    var h = Math.round(w * (v.videoHeight / v.videoWidth));
    var c = document.createElement("canvas");
    c.width = w; c.height = h;
    var ctx = c.getContext("2d");
    // Mirror the canvas to match the mirrored <video> preview (`transform:
    // scaleX(-1)` is preview-only — without this, the captured JPEG would
    // be the un-mirrored front-camera frame, so the audit photo wouldn't
    // match what the staffer saw on screen mid-capture). Code-review fix.
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, w, h);
    return c.toDataURL("image/jpeg", 0.7);
  }, []);

  return {videoRef: videoRef, error: error, capture: capture};
}

// Roster banner — empty ("No one is clocked in" + Clock in CTA) vs live
// ("3 on shift: Sharon · Muhammad · Jo" + Clock out CTA). Sits at the top
// of the home view, above the tile grid. Tablet-only — caller gates the
// device-class check.
function ClockInRosterBanner({ staffOnShift, onClockIn, onClockOut }) {
  const live = staffOnShift && staffOnShift.length > 0;
  const names = (staffOnShift || []).map(function(s){ return s.name; }).filter(Boolean);
  const baseStyle = {
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:14,
    width:"100%", maxWidth:960, margin:"0 auto 18px", padding:"18px 22px",
    borderRadius:18, minHeight:108,
    fontFamily:"Manrope,sans-serif", textAlign:"left", boxSizing:"border-box",
    flexWrap:"wrap"
  };
  const liveStyle = {
    ...baseStyle,
    background:"linear-gradient(135deg,#0f6849 0%,#185a44 100%)",
    color:B.rice, border:"none", boxShadow:"0 16px 32px -16px rgba(0,57,44,0.5)"
  };
  // Empty state matches the signed-off mockup: white Rice card on the
  // dark Nori home, Nori dashed border, Nori CTA pill with Wasabi text.
  const emptyStyle = {
    ...baseStyle,
    background:B.rice,
    border:"1px dashed rgba(0,57,44,0.25)",
    color:B.nori,
    cursor:"pointer"
  };
  const eyebrow = (col)=>({fontSize:"0.6rem", letterSpacing:"0.2em", textTransform:"uppercase", fontWeight:700, color:col, display:"flex", alignItems:"center", gap:6});
  const title = {fontFamily:"gelica,serif", fontSize:"1.4rem", fontWeight:700, lineHeight:1.1, marginTop:6};
  const rosterLine = (col)=>({fontSize:"0.82rem", fontWeight:500, lineHeight:1.35, color:col, marginTop:6, overflow:"hidden", textOverflow:"ellipsis"});
  // Buttons share padding/shape but differ in fill so a quick glance can't
  // mistake one for the other. Wasabi=Clock in (primary positive action),
  // Rice=Clock out (secondary, distinct fill so taps can't go wrong on a
  // tablet kiosk).
  const ctaBase = {padding:"14px 22px", borderRadius:999, fontWeight:700, fontSize:"0.85rem", minWidth:130, textAlign:"center", border:"none", fontFamily:"Manrope,sans-serif", cursor:"pointer", flexShrink:0};
  const ctaIn   = {...ctaBase, background:B.wasabi, color:B.nori};
  const ctaOut  = {...ctaBase, background:B.rice,   color:B.nori};
  const ctaEmpty= {...ctaBase, background:B.nori,   color:B.wasabi};

  if (live) {
    // Whole card is NOT a button (would nest buttons illegally). Each pill is
    // its own real button; the eyebrow+title+roster on the left is purely
    // informational.
    return (
      <div style={liveStyle}>
        <div style={{flex:"1 1 200px", minWidth:0}}>
          <div style={eyebrow("rgba(212,251,111,0.85)")}>
            <span style={{display:"inline-block", width:7, height:7, borderRadius:"50%", background:B.wasabi}}></span>
            On shift
          </div>
          <div style={title}>{names.length} on shift</div>
          <div style={rosterLine("rgba(255,255,255,0.78)")}>{names.join(" · ")}</div>
        </div>
        <div style={{display:"flex", gap:10, flexShrink:0, flexWrap:"wrap", justifyContent:"flex-end"}}>
          <button onClick={onClockIn}  style={ctaIn}  aria-label="Start clock-in flow">Clock in</button>
          <button onClick={onClockOut} style={ctaOut} aria-label="Open clock-out picker">Clock out</button>
        </div>
      </div>
    );
  }
  return (
    <button style={emptyStyle} onClick={onClockIn} aria-label="Start clock-in flow">
      <div style={{flex:1, minWidth:0}}>
        <div style={eyebrow("rgba(0,57,44,0.55)")}>Clock in</div>
        <div style={title}>No one is clocked in</div>
        <div style={rosterLine("rgba(0,57,44,0.55)")}>Tap below when you start your shift</div>
      </div>
      <span style={ctaEmpty}>Clock in</span>
    </button>
  );
}

// Numpad block reused inside the dark scrim — visual match for the mockup's
// landscape PIN step. Auto-fires onComplete when the 4th digit lands so the
// caller can advance to the next step without a Submit button.
function ScrimNumpad({ pin, setPin, busy, onComplete }) {
  const handle = (d)=>{
    if (busy) return;
    setPin(p => {
      if (p.length >= 4) return p;
      const next = p + d;
      if (next.length === 4 && onComplete) setTimeout(()=>onComplete(next), 60);
      return next;
    });
  };
  const clear = ()=>{ if (!busy) setPin(""); };
  const back = ()=>{ if (!busy) setPin(p => p.slice(0,-1)); };
  const numStyle = {
    height:62, borderRadius:14,
    background:"rgba(255,255,255,0.08)",
    border:"1px solid rgba(255,255,255,0.12)",
    color: B.rice, fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700,
    cursor: busy ? "default" : "pointer"
  };
  const muted = {...numStyle, background:"transparent", border:"1px solid transparent", color:"rgba(255,255,255,0.6)", fontSize:"0.82rem", fontFamily:"Manrope,sans-serif", fontWeight:600};
  return (
    <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, width:"100%", maxWidth:340}}>
      {["1","2","3","4","5","6","7","8","9"].map(d=>(
        <button key={d} style={numStyle} onClick={()=>handle(d)} disabled={busy}>{d}</button>
      ))}
      <button style={muted} onClick={clear} disabled={busy}>Clear</button>
      <button style={numStyle} onClick={()=>handle("0")} disabled={busy}>0</button>
      <button style={muted} onClick={back} disabled={busy}>←</button>
    </div>
  );
}

// PIN dots row — mirrors the existing login-screen visual but tuned for the
// dark scrim background.
function ScrimPinDots({ count }) {
  return (
    <div style={{display:"flex", gap:14, marginBottom:12}}>
      {[0,1,2,3].map(i=>(
        <div key={i} style={{
          width:14, height:14, borderRadius:"50%",
          background: count>i ? B.wasabi : "rgba(255,255,255,0.18)",
          boxShadow: count>i ? "0 0 0 3px rgba(212,251,111,0.18)" : "none",
          transition:"all 0.15s ease"
        }}/>
      ))}
    </div>
  );
}

// Full-screen scrim used for both clock-in (PIN → Camera → Success) and
// clock-out (Picker → PIN → Success). Dark Nori background, Wasabi accents,
// matches the v3 mockup. Closes via the ✕ in the top-right (calls onClose).
function ClockScrim({ children, headerLabel, onClose }) {
  // Portal to document.body so the fixed-position scrim escapes the home
  // view's transformed ancestor (S.homeRoot has `animation: viewIn` which
  // applies a transform — that creates a new containing block, causing the
  // scrim to size to the home content (~1845px) instead of the viewport.
  // Portaling ensures the scrim sits at the body level where `position:fixed`
  // is correctly bound to the actual viewport.
  return ReactDOM.createPortal(
    <div style={{
      position:"fixed", inset:0, background:B.nori, color:B.rice, zIndex:600,
      display:"flex", flexDirection:"column",
      fontFamily:"Manrope,sans-serif"
    }}>
      <div style={{
        flexShrink:0,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.08)"
      }}>
        <div style={{fontSize:"0.6rem", letterSpacing:"0.2em", textTransform:"uppercase", color:B.wasabi, fontWeight:700}}>
          {headerLabel}
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          background:"transparent", border:"none", color:B.rice, fontSize:"1.4rem", cursor:"pointer", lineHeight:1, padding:"4px 8px"
        }}>✕</button>
      </div>
      <div style={{flex:1, minHeight:0, display:"flex", flexDirection:"column"}}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// Camera step — mounts the useClockInCamera hook so getUserMedia starts /
// stops with the step's lifecycle (avoids leaving the camera light on if
// the user backs out of the flow). Shows a live <video> with a Wasabi-edge
// guide and a shutter button. Skip-photo button kicks in if the camera
// errored — keeps the flow shippable on plain HTTP local previews where
// Safari blocks getUserMedia.
function ClockCameraStep({ staffName, busy, onCapture, onSkip }) {
  const cam = useClockInCamera(true);
  const [flash, setFlash] = useState(false);
  const tryCapture = ()=>{
    if (busy) return;
    // Visual feedback: white flash overlay so the staffer sees a clear
    // "shutter clicked" cue. Yield two animation frames so React mounts
    // the flash overlay AND the browser paints it before we run the
    // synchronous canvas grab — without the RAFs the flash either doesn't
    // render at all or paints for a single frame.
    setFlash(true);
    requestAnimationFrame(()=>requestAnimationFrame(()=>{
      const dataUrl = cam.capture();
      setTimeout(()=>setFlash(false), 220);
      if (!dataUrl) { onCapture(""); return; }
      // Strip the "data:image/jpeg;base64," prefix — backend expects the raw
      // base64 only (and wants a tight payload anyway).
      const idx = dataUrl.indexOf("base64,");
      const base64 = idx >= 0 ? dataUrl.slice(idx + 7) : dataUrl;
      onCapture(base64);
    }));
  };
  return (
    <>
      <style>{`
        @keyframes clockin-flash {
          0%   { opacity: 0; }
          12%  { opacity: 0.95; }
          100% { opacity: 0; }
        }
      `}</style>
      <div style={{flex:1, padding:"14px 18px 8px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", overflow:"hidden", minHeight:0, gap:14}}>
        <div style={{position:"relative", width:"100%", maxWidth:360, aspectRatio:"1/1", borderRadius:18, overflow:"hidden", background:"#0e1d18", flexShrink:0}}>
          <video ref={cam.videoRef} playsInline muted autoPlay style={{
            position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover",
            transform:"scaleX(-1)" /* mirror so it feels like a selfie */
          }}/>
          <div style={{
            position:"absolute", inset:"8%",
            border:"2px dashed rgba(212,251,111,0.5)", borderRadius:24, pointerEvents:"none"
          }}/>
          <div style={{
            position:"absolute", top:18, left:0, right:0, textAlign:"center",
            fontSize:"0.78rem", fontWeight:600, color:B.wasabi, letterSpacing:"0.05em",
            textShadow:"0 1px 2px rgba(0,0,0,0.4)"
          }}>
            {cam.error ? cam.error : "Frame your face inside the box"}
          </div>
          <div style={{
            position:"absolute", left:14, bottom:14,
            background:"rgba(0,57,44,0.6)", border:"1px solid rgba(212,251,111,0.4)",
            color:B.rice, padding:"6px 12px", borderRadius:999, fontSize:"0.75rem", fontWeight:600
          }}>
            Capturing for <b style={{color:B.wasabi}}>{staffName}</b>
          </div>
          {/* Capture flash — white overlay, fades 220ms */}
          {flash && (
            <div style={{
              position:"absolute", inset:0, background:B.rice, pointerEvents:"none",
              animation:"clockin-flash 220ms ease-out forwards"
            }}/>
          )}
        </div>
        <div style={{textAlign:"center", fontSize:"0.78rem", color:"rgba(255,255,255,0.78)", maxWidth:360}}>
          {busy ? "Sending..." : "Look at the camera, then tap the shutter."}
        </div>
      </div>
      <div style={{padding:"10px 18px 22px", display:"flex", flexDirection:"column", gap:10, alignItems:"center", flexShrink:0}}>
        <button onClick={tryCapture} disabled={busy} aria-label="Capture photo and clock in" style={{
          width:80, height:80, borderRadius:"50%",
          border:"4px solid "+B.wasabi, background:"rgba(255,255,255,0.08)",
          display:"grid", placeItems:"center", cursor: busy?"default":"pointer", padding:0,
          transition:"transform 80ms ease",
          ...(busy ? {opacity:0.5} : {})
        }}
        onTouchStart={e=>{ e.currentTarget.style.transform="scale(0.94)"; }}
        onTouchEnd={e=>{ e.currentTarget.style.transform="scale(1)"; }}>
          <span style={{width:54, height:54, borderRadius:"50%", background:B.wasabi, display:"block"}}></span>
        </button>
        {cam.error && (
          <button onClick={onSkip} disabled={busy} style={{
            background:"transparent", border:"1px solid rgba(255,255,255,0.18)",
            color:"rgba(255,255,255,0.7)", padding:"8px 16px", borderRadius:999,
            fontSize:"0.78rem", fontFamily:"Manrope,sans-serif", cursor: busy?"default":"pointer"
          }}>Skip photo &amp; clock in</button>
        )}
      </div>
    </>
  );
}

// Clock-out picker — list of staff currently on shift. Tap a row to nominate
// who's clocking out; the next step asks for that staff's PIN. Mirrors the
// mockup's ClockOutPicker layout (avatar + name + on-since chip).
function ClockOutPickerStep({ staffOnShift, onPick }) {
  const initials = (n)=>{
    const parts = String(n||"").trim().split(/\s+/);
    return ((parts[0]||"")[0] + ((parts[1]||"")[0]||"")).toUpperCase();
  };
  const fmt = (iso)=>{
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"}); } catch(e) { return "—"; }
  };
  return (
    <div style={{flex:1, overflowY:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", alignItems:"center", gap:14}}>
      <div style={{fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700, color:B.rice, textAlign:"center"}}>Who&apos;s clocking out?</div>
      <div style={{fontSize:"0.82rem", color:"rgba(255,255,255,0.7)", textAlign:"center", maxWidth:380, marginBottom:6}}>
        Tap your name. We&apos;ll ask for your PIN to confirm.
      </div>
      <div style={{display:"flex", flexDirection:"column", gap:10, width:"100%", maxWidth:420}}>
        {(staffOnShift || []).map((s, i) => (
          <button key={(s.name||"")+i} onClick={()=>onPick(s)} style={{
            display:"flex", alignItems:"center", gap:14,
            background:"rgba(255,255,255,0.06)",
            border:"1px solid rgba(255,255,255,0.12)",
            borderRadius:14, padding:"14px 16px", color:B.rice, textAlign:"left",
            cursor:"pointer", fontFamily:"Manrope,sans-serif", width:"100%"
          }}>
            <div style={{width:42, height:42, borderRadius:"50%", background:B.wasabi, color:B.nori, display:"grid", placeItems:"center", fontFamily:"gelica,serif", fontWeight:700, fontSize:"0.95rem", flexShrink:0}}>
              {initials(s.name)}
            </div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontWeight:600, fontSize:"0.95rem", lineHeight:1.2}}>{s.name || "Unknown"}</div>
              <div style={{fontSize:"0.7rem", color:"rgba(255,255,255,0.6)", marginTop:2}}>On since {fmt(s.clockedInAt)}</div>
            </div>
            <div style={{fontSize:"0.82rem", fontWeight:700, color:B.wasabi, flexShrink:0}}>Clock out →</div>
          </button>
        ))}
        {(!staffOnShift || staffOnShift.length === 0) && (
          <div style={{textAlign:"center", color:"rgba(255,255,255,0.55)", fontSize:"0.82rem", padding:24}}>
            No one&apos;s currently on shift here.
          </div>
        )}
      </div>
    </div>
  );
}

function ClockSuccessStep({ name, time, mode, onDone }) {
  const greeting = mode === "out" ? "See you next time" : "G'day";
  const farewell = mode === "out" ? "Thanks for the shift." : "Have a ripper shift.";
  // Stays on screen until the staffer taps Done (or the ✕ in the header) —
  // no auto-close. Earlier versions auto-dismissed after 2.5s, which was too
  // fast on a tablet held at arm's length and left no clear "I'm done"
  // affordance.
  return (
    <>
      <style>{`
        @keyframes clockin-tick-pop {
          0%   { transform: scale(0.4); opacity: 0; }
          55%  { transform: scale(1.1); opacity: 1; }
          80%  { transform: scale(0.96); }
          100% { transform: scale(1); }
        }
        @keyframes clockin-tick-stroke {
          0%   { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes clockin-tick-glow {
          0%, 100% { box-shadow: 0 0 0 12px rgba(212,251,111,0.18), 0 12px 24px -8px rgba(212,251,111,0.4); }
          50%      { box-shadow: 0 0 0 18px rgba(212,251,111,0.28), 0 14px 28px -6px rgba(212,251,111,0.55); }
        }
        @keyframes clockin-success-fade {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{
        flex:1, display:"flex", flexDirection:"column", alignItems:"center",
        justifyContent:"center", textAlign:"center", padding:"24px 24px 8px", gap:18,
        animation:"clockin-success-fade 0.45s ease-out both"
      }}>
        <div style={{
          width:104, height:104, borderRadius:"50%", background:B.wasabi,
          display:"grid", placeItems:"center",
          animation:"clockin-tick-pop 0.55s cubic-bezier(.34,1.56,.64,1) both, clockin-tick-glow 2.4s ease-in-out 0.6s infinite"
        }}>
          <svg width="60" height="60" viewBox="0 0 64 64" aria-hidden="true">
            <path d="M14 33 L27 46 L50 21" style={{
              fill:"none", stroke:B.nori, strokeWidth:6, strokeLinecap:"round", strokeLinejoin:"round",
              strokeDasharray:60, strokeDashoffset:60,
              animation:"clockin-tick-stroke 0.4s cubic-bezier(.5,0,.5,1) 0.35s forwards"
            }}/>
          </svg>
        </div>
        <div>
          <h2 style={{fontFamily:"gelica,serif", fontSize:"2.2rem", margin:"6px 0 0", lineHeight:1.05, color:B.rice}}>{greeting}, {name}</h2>
          <div style={{fontFamily:"gelica,serif", fontSize:"3rem", fontWeight:700, color:B.wasabi, letterSpacing:"-0.02em", lineHeight:1, marginTop:8}}>{time}</div>
          <div style={{fontSize:"0.95rem", color:"rgba(255,255,255,0.78)", marginTop:10}}>{farewell}</div>
        </div>
      </div>
      <div style={{padding:"8px 24px 24px", display:"flex", justifyContent:"center", flexShrink:0}}>
        <button onClick={onDone} style={{
          padding:"14px 32px", borderRadius:999, border:"none",
          background:B.wasabi, color:B.nori, fontWeight:700, fontSize:"0.95rem",
          fontFamily:"Manrope,sans-serif", cursor:"pointer", letterSpacing:"0.02em",
          minWidth:160
        }}>Done</button>
      </div>
    </>
  );
}

// Per-action prompt-to-clock-in (2026-04-29). Mounted at the App level when
// a staffer enters their PIN for any per-action gate but isn't currently
// on shift. The CTA closes the prompt and opens the existing clock-in scrim
// — the staffer just retries their action after clocking in. Portaled to
// document.body so the home view's transformed ancestor doesn't squish it.
function NotClockedInPrompt({ staffName, onClockIn, onDismiss }) {
  return ReactDOM.createPortal(
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,57,44,0.72)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:24, zIndex:650, fontFamily:"Manrope,sans-serif"
    }} onClick={onDismiss}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:B.rice, color:B.nori, borderRadius:18, padding:"28px 24px 22px",
        maxWidth:420, width:"100%", boxShadow:"0 24px 48px -12px rgba(0,57,44,0.6)",
        textAlign:"center"
      }}>
        <div style={{fontSize:"2.5rem", marginBottom:6}}>⏰</div>
        <h2 style={{fontFamily:"gelica,serif", fontSize:"1.5rem", margin:"0 0 8px", color:B.nori, lineHeight:1.15}}>
          {staffName ? "G'day, " + staffName + " — clock in first?" : "Clock in first?"}
        </h2>
        <p style={{fontSize:"0.9rem", color:"rgba(0,57,44,0.65)", margin:"0 0 20px", lineHeight:1.45}}>
          You need to be clocked in to use this. It only takes 5 seconds — tap below to start your shift.
        </p>
        <div style={{display:"flex", gap:10, flexDirection:"column"}}>
          <button onClick={onClockIn} style={{
            background:B.wasabi, color:B.nori, border:"none", borderRadius:999,
            padding:"14px 22px", fontWeight:700, fontSize:"0.95rem",
            fontFamily:"Manrope,sans-serif", cursor:"pointer"
          }}>Clock in now</button>
          <button onClick={onDismiss} style={{
            background:"transparent", color:"rgba(0,57,44,0.55)", border:"none",
            borderRadius:999, padding:"10px 22px", fontWeight:600, fontSize:"0.85rem",
            fontFamily:"Manrope,sans-serif", cursor:"pointer"
          }}>Not now</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Step-router for the Clock-In scrim. Mounted only while a flow is active —
// the camera hook (inside ClockCameraStep) starts and stops with this
// component's lifecycle, so backing out of the flow always frees the camera.
// All flow state lives in clockFlow on the App; this component just reads
// + dispatches.
function ClockFlowScrimRenderer({ clockFlow, setClockFlow, submitClockIn, submitClockOut, closeClock, staffOnShift, staffPins }) {
  const headerLabel =
    clockFlow.mode === "in"  && clockFlow.step === "pin"      ? "Step 1 of 2 · PIN" :
    clockFlow.mode === "in"  && clockFlow.step === "camera"   ? "Step 2 of 2 · Photo" :
    clockFlow.mode === "in"  && clockFlow.step === "success"  ? "Done" :
    clockFlow.mode === "out" && clockFlow.step === "picker"   ? "Clock out" :
    clockFlow.mode === "out" && clockFlow.step === "pin"      ? "Step 1 of 2 · Confirm PIN" :
    clockFlow.mode === "out" && clockFlow.step === "camera"   ? "Step 2 of 2 · Photo" :
    clockFlow.mode === "out" && clockFlow.step === "success"  ? "Done" : "";

  // A1 leak-close: resolve the PIN's owner from the SERVER (verify-only, so it
  // never unlocks) when server PIN verify is on, instead of the downloaded
  // roster. Same opaque messages either way. Inert until a shop is flipped.
  //
  // Lane BT: on a gated host the server is asked ONCE, up front in onPinComplete
  // (checkPinOnServer), with the three guards the login numpad already has — a
  // neutral "Checking…" state, an attempt counter so an answer that lands after
  // the person retyped or closed the scrim is dropped, and an 8 s timeout that
  // hands the numpad back with the existing "couldn't reach" message. The OFF
  // path (every shop today) is this roster lookup, byte for byte as before.
  const resolvePinName = async (pin) => (staffPins && staffPins[pin]) || null;
  const pinAttemptRef = useRef(0);
  useEffect(() => () => { pinAttemptRef.current++; }, []);   // scrim closed (unmount): any in-flight check is now stale
  const checkPinOnServer = async (pin) => {
    const attempt = ++pinAttemptRef.current;
    setClockFlow(f => ({...f, checking: true, err: ""}));
    let srv = null;
    try { srv = await pinAuth.verifyPin(pin, { timeoutMs: clockPinCheck.CLOCK_PIN_VERIFY_TIMEOUT_MS }); } catch (e) { srv = null; }
    const out = clockPinCheck.clockPinOutcome(srv, attempt, pinAttemptRef.current);
    if (!out.stale) setClockFlow(f => ({...f, checking: false}));
    return out;
  };
  const onPinComplete = async (pin) => {
    const srv = pinAuth.serverPinVerifyEnabled() ? await checkPinOnServer(pin) : null;
    if (srv && srv.stale) return;                                   // a newer attempt (or a closed scrim) owns the screen — touch nothing
    if (srv && srv.unreachable) {                                   // offline / timed out: say so and hand the numpad back
      setClockFlow(f => ({...f, pin: "", err: srv.error}));
      return;
    }
    const whoIs = async (p) => (srv ? srv.name : resolvePinName(p));   // gated: the server's answer · OFF: the roster, as before
    if (clockFlow.mode === "in") {
      // Reject unknown PINs at the boundary so the camera step never opens
      // with an empty staffName / a PIN that would only fail on submit.
      const name = await whoIs(pin);
      if (!name) {
        setClockFlow(f => ({...f, pin: "", err: "PIN not recognised"}));
        return;
      }
      // Thread the verified name so the camera step + submit never re-read the
      // roster for it (row C). OFF path stores staffPins[pin]; the camera step
      // ignores it there, so behaviour is unchanged.
      setClockFlow(f => ({...f, pin: pin, step: "camera", err: "", pinName: name}));
    } else if (clockFlow.mode === "out") {
      // Clock-out PIN privacy (memory pin_privacy.md). BOTH error paths
      // surface the SAME opaque "Wrong PIN" message:
      //   - PIN not in directory at all
      //   - PIN belongs to a different staffer than the tapped target
      // Distinct messages would let a probing staffer distinguish
      // "this PIN doesn't exist" from "this PIN exists but for someone
      // else" — that's still a directory-membership leak. Identical
      // message either way; the user just tries again.
      const localName = await whoIs(pin);
      const target = clockFlow.target;
      if (rosterFree.clockOutRejected(localName, target && target.name)) {
        setClockFlow(f => ({...f, pin: "", err: rosterFree.WRONG_PIN_MESSAGE}));
        return;
      }
      // PIN matches the nominated target. Advance to camera step (clock-out
      // photo for audit symmetry with clock-in). Thread the verified name (row C).
      setClockFlow(f => ({...f, pin: pin, step: "camera", err: "", pinName: localName}));
    }
  };
  // Camera step routes to clock-in OR clock-out submit based on flow mode.
  const onCapture = (photoBase64) => {
    if (clockFlow.mode === "in") submitClockIn(clockFlow.pin, photoBase64, clockFlow.pinName);
    else submitClockOut(clockFlow.pin, clockFlow.target && clockFlow.target.name, photoBase64, clockFlow.pinName);
  };
  const onSkip = () => {
    if (clockFlow.mode === "in") submitClockIn(clockFlow.pin, "", clockFlow.pinName);
    else submitClockOut(clockFlow.pin, clockFlow.target && clockFlow.target.name, "", clockFlow.pinName);
  };
  const onPickerPick = (s) => setClockFlow(f => ({...f, step:"pin", target: s, pin:"", err:""}));
  const setScrimPin = (updater) => setClockFlow(f => ({
    ...f,
    pin: typeof updater === "function" ? updater(f.pin) : updater,
    err: ""
  }));

  return (
    <ClockScrim headerLabel={headerLabel} onClose={closeClock}>
      {clockFlow.step === "pin" && (
        <div style={{flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:18, padding:"14px 18px"}}>
          {clockFlow.mode === "out" && clockFlow.target && (
            <div style={{textAlign:"center", marginBottom:6}}>
              <div style={{fontSize:"0.65rem", letterSpacing:"0.2em", textTransform:"uppercase", color:"rgba(212,251,111,0.6)", fontWeight:700}}>Clocking out</div>
              <div style={{fontFamily:"gelica,serif", fontSize:"1.4rem", fontWeight:700, color:B.rice, marginTop:4}}>{clockFlow.target.name}</div>
            </div>
          )}
          <ScrimPinDots count={clockFlow.pin.length}/>
          {clockPinCheck.showChecking(clockFlow) && (
            <div style={{color:B.rice, opacity:0.6, fontSize:"0.78rem", fontWeight:600, textAlign:"center", maxWidth:340}}>
              Checking…
            </div>
          )}
          {clockFlow.err && (
            <div style={{color:B.coral, fontSize:"0.78rem", fontWeight:600, textAlign:"center", maxWidth:340}}>
              {clockFlow.err}
            </div>
          )}
          <ScrimNumpad
            pin={clockFlow.pin}
            setPin={setScrimPin}
            busy={clockPinCheck.numpadBusy(clockFlow)}
            onComplete={onPinComplete}
          />
        </div>
      )}
      {clockFlow.step === "camera" && (
        <ClockCameraStep
          staffName={
            clockFlow.mode === "in"
              ? (pinAuth.serverPinVerifyEnabled() ? (clockFlow.pinName || "") : ((staffPins && staffPins[clockFlow.pin]) || ""))
              : (clockFlow.target && clockFlow.target.name) || ""
          }
          busy={clockFlow.busy}
          onCapture={onCapture}
          onSkip={onSkip}
        />
      )}
      {clockFlow.mode === "out" && clockFlow.step === "picker" && (
        <ClockOutPickerStep staffOnShift={staffOnShift} onPick={onPickerPick}/>
      )}
      {clockFlow.step === "success" && (
        <ClockSuccessStep
          name={clockFlow.successName}
          time={clockFlow.successTime}
          mode={clockFlow.mode}
          onDone={closeClock}
        />
      )}
    </ClockScrim>
  );
}

function App() {
  const __updateAvailable = useAutoUpdater();
  useVersionPillDOM(__updateAvailable);
  const [view,setView] = useState(()=>{try{return sessionStorage.getItem("rolled.kitchen.view")||"login"}catch(e){return "login"}});
  const [pin,setPin] = useState("");
  const [staff,setStaff] = useState(()=>{try{return sessionStorage.getItem("rolled.kitchen.staff")||""}catch(e){return ""}});
  // Logged-in user's PIN — kept only in memory (NEVER persisted). Used to look
  // up the current role in staffByPin for the admin clock-in bypass; without
  // this we'd have to reverse-search by name. Cleared on logout.
  const [loggedInPin,setLoggedInPin] = useState("");
  // Row D (Lane BF): the logged-in user's role, captured from the server login on
  // a gated host so the admin check never has to read the downloaded roster. Empty
  // (and unused) on the OFF path, where isAdminLoggedIn reads staffByPin as before.
  const [loggedInRole,setLoggedInRole] = useState("");
  const [err,setErr] = useState("");
  const [shake,setShake] = useState(false);
  const [flash,setFlash] = useState(false);
  const [now,setNow] = useState(new Date());
  // Staff PIN roster — fetched live from the Admin Staff Directory.
  // Cached in localStorage on first success so offline tablets keep the full roster mid-shift.
  // Falls back to STAFF_PINS_FALLBACK (JJ + Adam only) as last resort.
  const [staffPins,setStaffPins] = useState(()=>{
    try{const c=localStorage.getItem("staffPinsCache");if(c){const p=JSON.parse(c);if(p&&Object.keys(p).length>0)return p}}catch(e){}
    return STAFF_PINS_FALLBACK;
  });
  // Per-PIN {name, role, homeSite} — populated from the same pinsForLogin
  // response. Compliance forms use this to pre-check requiredRoles client-side
  // before opening the PIN modal (mirror of Trafford 1216-1219).
  const [staffByPin,setStaffByPin] = useState(()=>{
    try{const c=localStorage.getItem("staffByPinCache");if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return STAFF_BY_PIN_FALLBACK;
  });
  // Tile overrides — admin Tile Editor writes per-site overrides keyed by
  // tool.id. Cache-first hydration so the home grid paints with the most
  // recent admin state instantly; live fetch refreshes in the background.
  const [tileOverrides,setTileOverrides] = useState(()=>{
    try{const c=localStorage.getItem("tileOverridesCache");if(c){const p=JSON.parse(c);if(p&&typeof p==="object")return p}}catch(e){}
    return null;
  });
  useEffect(()=>{
    fetch(BACKEND+"?action=pinsForLogin")
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d&&d.pins&&Object.keys(d.pins).length>0){
          setStaffPins(d.pins);
          try{localStorage.setItem("staffPinsCache",JSON.stringify(d.pins))}catch(e){}
        }
        if(d&&d.staff&&typeof d.staff==="object"&&Object.keys(d.staff).length>0){
          setStaffByPin(d.staff);
          try{localStorage.setItem("staffByPinCache",JSON.stringify(d.staff))}catch(e){}
        }
      })
      .catch(()=>{/* keep current state — cache (from useState init) or fallback */});
    // Tile overrides now come from Supabase (was ?action=tileOverrides on Apps Script).
    // NB the kitchen's SITE.key is "mcr" but the Command Centre saves the kitchen under
    // "kitchen"; the old backend translated that server-side, so fetchTileOverrides()
    // does the same. Miss it and the kitchen silently loses all 22 of its tile settings.
    // See lib/tileOverrides.js — the '*'-plus-site merge is a whole-row replace. Semantics
    // pinned by lib/tileOverrides.test.mjs; per-site parity with the live feed is checked by
    // supabase/_tiles_parity_check.mjs (which asks as 'mcr', exactly as this app does).
    fetchTileOverrides(SITE.key)
      .then(m=>{
        if(m && typeof m==="object"){
          setTileOverrides(m);
          try{localStorage.setItem("tileOverridesCache",JSON.stringify(m))}catch(e){}
        }
      })
      .catch(()=>{/* keep cache */});
  },[]);
  // Stock catalog — the kitchen receiver uses this to enrich each request
  // card with the item's image + per-site default quantity. Fetched live from
  // the admin-managed Stock Catalog; cached in localStorage for offline loads.
  const [stockCatalog,setStockCatalog] = useState(()=>{
    try{const c=localStorage.getItem("stockCatalogCache");if(c){const a=JSON.parse(c);if(Array.isArray(a))return a}}catch(e){}
    return [];
  });
  // Which source served the last catalog read ('supabase' green / 'sheet' amber).
  // Only shown on a flipped kitchen — everywhere else it's always the sheet.
  const [catalogSource,setCatalogSource] = useState(null);
  useEffect(()=>{
    // CATALOG READ FLIP (gated OFF by default — stockCatalog.CATALOG_READ_HOSTS is
    // empty): on a flipped kitchen the item list + photos come from Supabase, with
    // an automatic fall back to this same sheet feed on any problem. Everywhere
    // else this is byte-for-byte the old sheet fetch.
    stockCatalogSrc.fetchStockCatalog({ sheetUrl: BACKEND+"?action=stockCatalog" })
      .then(d=>{
        // An empty or malformed answer never replaces a list we already have
        // (stockCatalog.shouldReplaceCatalog). This kitchen is the ONE screen
        // already reading Supabase in production, so it is the one that most
        // needs the guard: on a refusal it keeps the cached list and still sets
        // the source, leaving the amber "Fallback" badge showing.
        if(!stockCatalogSrc.shouldReplaceCatalog(stockCatalog,d)){
          // Warn on every refusal, not just the well-formed-empty one — an
          // unrecognised answer shape is a real signal too and was silently
          // dropped before (review of #372, finding 2).
          try{console.warn("[catalog] kept the cached list — the catalogue answer was "+(d&&Array.isArray(d.items)?"empty":"missing or malformed"),{source:(d&&d._source)||null,kept:stockCatalog.length})}catch(e){}
          if(d&&Array.isArray(d.items)){
            setCatalogSource(d._source||null);
          }
          return;
        }
        setStockCatalog(d.items);
        setCatalogSource(d._source||null);
        try{localStorage.setItem("stockCatalogCache",JSON.stringify(d.items))}catch(e){}
      })
      .catch(()=>{/* keep current state */});
  },[]);
  const [wastageLog,setWastageLog] = useState(appData.wastageLog);
  const [wastageDirty,setWastageDirty] = useState(false);
  const [backConfirm,setBackConfirm] = useState(false);
  const [pendingNavDest,setPendingNavDest] = useState("home");
  const [sideOpen,setSideOpen] = useState(()=>{try{const v=localStorage.getItem("rolled.kitchen.sideOpen");return v===null?false:v==="1"}catch(e){return false}});

  // Persist login state to sessionStorage so refresh doesn't log out
  useEffect(()=>{try{sessionStorage.setItem("rolled.kitchen.view",view);sessionStorage.setItem("rolled.kitchen.staff",staff)}catch(e){}},[view,staff]);
  // If we restored a logged-in state but staff is empty (corrupt), fall back to login
  useEffect(()=>{if(view!=="login"&&!staff){setView("login")}},[]);

  // Responsive orientation + mobile detection
  const isPortrait = useOrientation();
  const isMobile = useIsMobile();

  // FOH Clock-In flow state. Manchester Kitchen is the pilot — banner +
  // scrim + poller all run live here. staffOnShift hydrates from localStorage
  // so the banner paints from cache before the first poll resolves.
  const isTabletKiosk = isTabletKioskHost();
  const [staffOnShift,setStaffOnShift] = useState(()=>{
    try {
      const c = localStorage.getItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY);
      if (c) {
        const p = JSON.parse(c);
        if (p && Array.isArray(p.shifts)) return p.shifts;
      }
    } catch(e) {}
    return [];
  });
  const [clockFlow,setClockFlow] = useState({
    mode:null, step:null, pin:"", photo:null, target:null,
    busy:false, err:"", successName:"", successTime:""
  });
  // Holds the success-auto-close setTimeout id so a manual ✕ tap (or a
  // re-opened flow before the timer fires) can clear it.
  const clockCloseTimerRef = useRef(null);
  // Per-action prompt-to-clock-in (2026-04-29).
  const [notClockedInPrompt, setNotClockedInPrompt] = useState(null);

  // PIN modal — declared before effects that reference it
  const [pinModal,setPinModal] = useState(false);
  const [pmVal,setPmVal] = useState("");
  const [pmErr,setPmErr] = useState("");
  const [pmLoad,setPmLoad] = useState(false);
  const pmCb = useRef(null);
  const pmCancelCb = useRef(null);

  // Lock body scroll when any modal is open
  useEffect(()=>{
    if(pinModal||backConfirm){document.body.classList.add("modal-open")}
    else{document.body.classList.remove("modal-open")}
    return()=>document.body.classList.remove("modal-open");
  },[pinModal,backConfirm]);

  // Mobile hardware back button: navigate within the app (tool → home),
  // don't close the whole tab. Wires history.pushState/popstate so the
  // Android/iOS back gesture goes home instead of exiting. Pending changes
  // still route through BackConfirmModal so unsubmitted work isn't lost.
  const navStateRef = useRef({view, wastageDirty, backConfirm});
  navStateRef.current = {view, wastageDirty, backConfirm};
  useEffect(()=>{
    try{
      const v0 = navStateRef.current.view;
      if(v0==="login"||v0==="home"){
        window.history.replaceState({rolledNav:v0},"");
      } else {
        window.history.replaceState({rolledNav:"home"},"");
        window.history.pushState({rolledNav:v0},"");
      }
    }catch(e){}
  },[]);
  useEffect(()=>{
    try{
      const cur = window.history.state;
      if(view==="login"||view==="home"){
        if(!cur||cur.rolledNav!==view){
          window.history.replaceState({rolledNav:view},"");
        }
      } else if(!cur||cur.rolledNav!==view){
        const prevWasTool = cur && cur.rolledNav && cur.rolledNav!=="home" && cur.rolledNav!=="login";
        if(prevWasTool){
          window.history.replaceState({rolledNav:view},"");
        } else {
          window.history.pushState({rolledNav:view},"");
        }
      }
    }catch(e){}
  },[view]);
  useEffect(()=>{
    const onPop = ()=>{
      try{
        const {view:v,wastageDirty:wd,backConfirm:bc} = navStateRef.current;
        if(bc){
          setBackConfirm(false);
          if(v!=="login"&&v!=="home"){
            window.history.pushState({rolledNav:v},"");
          }
          return;
        }
        if(v==="login"||v==="home") return;
        const pending = (v==="wastage"&&wd);
        if(pending){
          window.history.pushState({rolledNav:v},"");
          setPendingNavDest("home");
          setBackConfirm(true);
        } else {
          setView("home");
        }
      }catch(err){}
    };
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);

  // Sync state back to appData + localStorage so it survives logout, refresh, tab close mid-day
  useEffect(()=>{appData.wastageLog=wastageLog;persistAppData()},[wastageLog]);

  // Day-rollover watchdog. Kitchen tablets stay running across midnight,
  // so React state would otherwise hold yesterday's wastage log, forecast
  // confirmation, and local stock marks into a new day (FOH-side variant
  // bit Sophie @ Trafford 2026-05-15). Polls every 60s and listens for
  // `visibilitychange` so wake-from-sleep catches an overnight rollover.
  // On rollover: wipe all day-scoped state, clear localStorage, bump
  // dayKey so child components remount cleanly via key={dayKey}.
  const [dayKey,setDayKey] = useState(()=>todayKey());
  useEffect(()=>{
    const check = ()=>{
      const cur = todayKey();
      if (cur === dayKey) return;
      setWastageLog([]);
      setStaffOnShift([]);
      appData.wastageLog = [];
      appData.forecastConfirmed = {};
      appData.stockLocalMarks = {};
      // Wipe day-scoped localStorage caches outside the main LS_KEY blob —
      // otherwise yesterday's on-shift roster (gates clock-in actions)
      // carries over and blocks staff from clocking in next morning.
      try { localStorage.removeItem(LS_KEY); } catch(e) {}
      try { localStorage.removeItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY); } catch(e) {}
      setDayKey(cur);
    };
    check();
    const id = setInterval(check, 60000);
    const onVis = ()=>{ if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return ()=>{ clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  },[dayKey]);
  // Persist sidebar preference across sessions
  useEffect(()=>{try{localStorage.setItem("rolled.kitchen.sideOpen",sideOpen?"1":"0")}catch(e){}},[sideOpen]);

  useEffect(()=>{const t=setInterval(()=>setNow(new Date()),30000);return()=>clearInterval(t)},[]);

  const digit = useCallback(d=>{
    setErr("");
    if(pin.length<4){const next=pin+d;setPin(next);
      if(next.length===4){setTimeout(async ()=>{
        // A1 — same two ways of answering "who is this?" as the FOH till.
        // Gate OFF everywhere today, so this is byte-for-byte the old behaviour.
        // Gate ON: the server is asked about this ONE PIN and the downloaded
        // roster is not consulted for the decision. The roster download itself
        // stays — JJ's scanner still reads it. See PIN_LEAK_STATUS.md.
        let __srvName = "", __srvRole = "";
        if (pinAuth.serverPinVerifyEnabled()) {
          let __srv = null;
          try { __srv = await pinAuth.loginWithPin(next); } catch (e) { __srv = null; }
          if (__srv && __srv.ok) { __srvName = __srv.staffName || ""; __srvRole = __srv.role || ""; }
        }
        const __who = pinAuth.serverPinVerifyEnabled() ? __srvName : staffPins[next];
        if(__who){  /* gate off: validated locally against fetched directory · gate on: validated by the server */
          // A1: when the server decided, seed this staffer's role so the admin
          // checks (isAdminLoggedIn, the per-action admin bypass) keep working
          // without the downloaded roster.
          if (pinAuth.serverPinVerifyEnabled()) { setStaffByPin(prev=>({...prev,[next]:{role:__srvRole}})); setLoggedInRole(__srvRole); }
          setStaff(__who);setLoggedInPin(next);setFlash(true);
          // Fire-and-forget login-success log so admin dashboard can see sign-ins.
          try{const __di=getDeviceInfo();fetch(BACKEND,{method:"POST",mode:"no-cors",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"loginEvent",site:SITE.key,pin:next,staffName:__who,device:__di.device,ua:__di.ua})})}catch(e){}
          // S22 presence: logins are recorded too, tagged 'login' so the daily
          // view can leave them out — attribution counts action taps only, same
          // as the sheet does today.
          presence.recordTap({ siteKey: SITE.key, pin: next, kind: "login", source: "login" });
          setTimeout(()=>{setView("home");setPin("");setFlash(false)},1000);
        }
        else{setErr("PIN not recognised");setShake(true);setTimeout(()=>{setShake(false);setPin("")},500)}
      },60)}
    }
  },[pin]);
  const del = useCallback(()=>{setPin(p=>p.slice(0,-1));setErr("")},[]);
  const logout = ()=>{setView("login");setStaff("");setLoggedInPin("");setLoggedInRole("");try{sessionStorage.removeItem("rolled.kitchen.view");sessionStorage.removeItem("rolled.kitchen.staff")}catch(e){}};
  // Admin role check for the current logged-in user.
  // Row D (Lane BF): on a server-PIN-verify host the role comes from the login
  // result (loggedInRole, captured at sign-in), NOT the downloaded roster. OFF
  // path is byte-identical — it reads staffByPin (hydrated from pinsForLogin), the
  // dynamic lookup that lets a role change via the admin Staff Management tile flow
  // through. `admin` is compared case-insensitively either way (the Admin/admin
  // vocabulary trap, 19 Aug sweep).
  const isAdminLoggedIn = pinAuth.serverPinVerifyEnabled()
    ? rosterFree.isAdminRole(loggedInRole)
    : !!(loggedInPin && staffByPin && staffByPin[loggedInPin] && rosterFree.isAdminRole(staffByPin[loggedInPin].role));

  // requestPin(onOk, onCancel?) — opens the PIN modal. onOk is called with the staff name on success.
  // onCancel (optional) is called if the user dismisses the modal without submitting — useful for callers
  // that need to unwind state when the staff member bails out.
  const requestPin = (cb,cancelCb)=>{pmCb.current=cb;pmCancelCb.current=cancelCb||null;setPmVal("");setPmErr("");setPinModal(true)};
  const cancelPin = ()=>{const c=pmCancelCb.current;setPinModal(false);pmCb.current=null;pmCancelCb.current=null;if(c)c()};
  // Auto-submit when the 4th digit lands — no Submit button needed. valOverride is used by the keypad
  // so we don't hit a state-update race between the 4th tap and the submit dispatch.
  const submitPin = async(valOverride)=>{
    const p=(valOverride!==undefined?valOverride:pmVal).trim();
    if(p.length!==4){setPmErr("Enter 4 digits");return}
    // Optimistic close: if the PIN is in the local staff directory (loaded at
    // mount via pinsForLogin + cached in localStorage), trust it and dismiss
    // the modal immediately — matches the login numpad feel. The action's own
    // server POST still carries the PIN for audit + server-side re-check,
    // same as before. Only fall back to the validatePin roundtrip when the
    // PIN isn't in the cached roster (e.g. a staffer added mid-session).
    // A1 leak-close: when server PIN verify is on, never DECIDE from the
    // downloaded roster — force a fall-through to the single-PIN server check
    // (validatePin) below. Off everywhere until a shop is flipped, so inert.
    const localName = pinAuth.serverPinVerifyEnabled() ? null : (staffPins && staffPins[p]);
    if (localName) {
      // Admin bypass (2026-04-29 evening): mirror of the backend gate
      // bypass. If the entered PIN belongs to an Admin (JJ + Adam), skip the
      // clock-in pre-check entirely — they aren't required to clock in.
      // Backend would also bypass via canPerformAction → admin role, so this
      // is just a UX-symmetric short-circuit (no useless network round-trip).
      // staffByPin cache may briefly lag a role demotion done via the admin
      // Staff Management tile; backend canPerformAction still gates the
      // actual action so a stale cache only affects the prompt UX, not auth.
      const __entrantRole = (staffByPin && staffByPin[p] && staffByPin[p].role) ? String(staffByPin[p].role).toLowerCase() : "";
      const __isAdminEntrant = (__entrantRole === "admin");
      // Clock-in gate (2026-04-29). Authoritative live check against the
      // backend, not the local cache. Cache-based pre-check missed JJ's
      // test scenario because (a) cache lagged actual state and (b) many
      // per-action POSTs use mode:"no-cors" so they can't surface backend
      // NOT_CLOCKED_IN themselves. Live check is the only bulletproof gate.
      if (CLOCK_IN_ENABLED && !__isAdminEntrant) {
        setPmLoad(true);
        try {
          const live = await fetch(BACKEND, {
            method:"POST",
            headers:{"Content-Type":"text/plain;charset=utf-8"},
            body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
          });
          const liveData = await live.json();
          const liveShifts = (liveData && Array.isArray(liveData.shifts)) ? liveData.shifts : [];
          setStaffOnShift(liveShifts);
          if (!staffIsOnShift(localName, liveShifts)) {
            setPinModal(false); setPmVal(""); setPmErr(""); setPmLoad(false);
            pmCb.current = null; pmCancelCb.current = null;
            setNotClockedInPrompt({ name: localName });
            return;
          }
        } catch(e) {
          if (!staffIsOnShift(localName, staffOnShift)) {
            setPinModal(false); setPmVal(""); setPmErr(""); setPmLoad(false);
            pmCb.current = null; pmCancelCb.current = null;
            setNotClockedInPrompt({ name: localName });
            return;
          }
        }
        setPmLoad(false);
      }
      setPinModal(false);
      setPmVal("");
      setPmErr("");
      const ok=pmCb.current;pmCb.current=null;pmCancelCb.current=null;
      if(ok)ok(localName, p);
      // S22 presence: somebody just tapped in at the kitchen. Fired AFTER the
      // modal has closed and the action has run, deliberately not awaited, so it
      // adds zero delay to the tap and cannot fail it. Write-only.
      presence.recordTap({ siteKey: SITE.key, pin: p, kind: "action", source: "kitchen-action" });
      return;
    }
    setPmLoad(true);
    try{const __di=getDeviceInfo();const r=await fetch(BACKEND+"?action=validatePin&pin="+encodeURIComponent(p)+"&site=kitchen&source=foh-action&device="+encodeURIComponent(__di.device)+"&ua="+encodeURIComponent(__di.ua));if(!r.ok)throw new Error();const d=await r.json();
      if(!d.valid){setPmErr("Invalid PIN");setPmVal("");setPmLoad(false);return}
      // Admin bypass (2026-04-29 evening): validatePin returns role; if Admin,
      // skip the clock-in pre-check. Mirrors backend canPerformAction gate
      // bypass for the Admin role.
      const __vpAdmin = String(d.role||"").toLowerCase() === "admin";
      if (CLOCK_IN_ENABLED && !__vpAdmin && !staffIsOnShift(d.name, staffOnShift)) {
        setPinModal(false); setPmLoad(false); setPmVal(""); setPmErr("");
        pmCb.current = null; pmCancelCb.current = null;
        setNotClockedInPrompt({ name: d.name });
        return;
      }
      setPinModal(false);setPmLoad(false);
      const ok=pmCb.current;pmCb.current=null;pmCancelCb.current=null;
      if(ok)ok(d.name,p);
      // S22 presence — same tap, the path taken when the PIN wasn't in the
      // cached roster. Fire-and-forget.
      presence.recordTap({ siteKey: SITE.key, pin: p, kind: "action", source: "kitchen-action" });
    }catch(e){setPmErr("Connection error - try again");setPmVal("");setPmLoad(false)}
  };
  // Keypad handlers for the PIN modal — mirrors the login numpad flow
  const pmDigit = useCallback((d)=>{
    setPmErr("");
    setPmVal(v=>{
      if(v.length>=4||pmLoad)return v;
      const next=v+d;
      if(next.length===4){setTimeout(()=>submitPin(next),60)}
      return next;
    });
  },[pmLoad]);
  const pmDel = useCallback(()=>{setPmErr("");setPmVal(v=>v.slice(0,-1))},[]);

  // Clock-In: 30s poller for the live roster. Tablet-only — phones + desktops
  // never see the banner so don't waste a fetch. Caches to localStorage so a
  // fresh page load paints the banner from cache before the first response.
  useEffect(()=>{
    if (!CLOCK_IN_ENABLED || !isTabletKiosk) return;
    let cancelled = false;
    const fetchOnShift = ()=>{
      fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
      })
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if (cancelled || !d || d.status !== "ok") return;
        const shifts = Array.isArray(d.shifts) ? d.shifts : [];
        setStaffOnShift(shifts);
        try { localStorage.setItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY, JSON.stringify({shifts: shifts, ts: Date.now()})); } catch(e) {}
      })
      .catch(()=>{ /* keep cached state, will retry next tick */ });
    };
    fetchOnShift();
    const t = setInterval(fetchOnShift, 30000);
    return ()=>{ cancelled = true; clearInterval(t); };
  }, [isTabletKiosk]);

  const refreshOnShift = useCallback(()=>{
    fetch(BACKEND, {
      method:"POST",
      headers:{"Content-Type":"text/plain;charset=utf-8"},
      body: JSON.stringify({action:"getStaffOnShift", site:CLOCK_IN_SITE_KEY})
    })
    .then(r=>r.ok?r.json():null)
    .then(d=>{
      if (!d || d.status !== "ok") return;
      const shifts = Array.isArray(d.shifts) ? d.shifts : [];
      setStaffOnShift(shifts);
      try { localStorage.setItem("staffOnShiftCache::"+CLOCK_IN_SITE_KEY, JSON.stringify({shifts: shifts, ts: Date.now()})); } catch(e) {}
    })
    .catch(()=>{});
  }, []);

  const openClockIn = useCallback(()=>{
    setClockFlow({mode:"in", step:"pin", pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);
  const openClockOut = useCallback(()=>{
    setClockFlow({mode:"out", step:"picker", pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);
  const closeClock = useCallback(()=>{
    if (clockCloseTimerRef.current) {
      clearTimeout(clockCloseTimerRef.current);
      clockCloseTimerRef.current = null;
    }
    setClockFlow({mode:null, step:null, pin:"", photo:null, target:null, busy:false, err:"", successName:"", successTime:""});
  }, []);

  // Clock-IN POST. OPTIMISTIC UI: flip to success scene immediately, fire
  // the fetch in background. Saves the user ~1-2s of camera-frame waiting.
  const submitClockIn = useCallback(async (pin, photoBase64, verifiedName)=>{
    // A1 (row A): name from the PIN scrim's verify (threaded in) on a gated host,
    // else the downloaded roster. OFF path byte-identical.
    const localName = pinAuth.serverPinVerifyEnabled() ? (verifiedName || null) : (staffPins && staffPins[pin]);
    if (!localName) {
      setClockFlow(f=>({...f, busy:false, err:"Wrong PIN", step:"pin", pin:""}));
      return;
    }
    const t = new Date().toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
    setClockFlow(f=>({...f, step:"success", busy:false, err:"", successName: localName, successTime: t}));
    // Optimistically add to on-shift roster (closes the cache lag).
    setStaffOnShift(s=>{
      const list = Array.isArray(s) ? s : [];
      if (list.some(x => x && String(x.name||"").toLowerCase() === String(localName).toLowerCase())) return list;
      return list.concat([{name: localName, role: "", siteKey: CLOCK_IN_SITE_KEY, clockedInAt: new Date().toISOString()}]);
    });
    const clientUuid = uuid4();
    try {
      const r = await fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({
          action:"staffShiftStart",
          site: CLOCK_IN_SITE_KEY,
          staffPin: pin,
          photoBase64: photoBase64 || "",
          uuid: clientUuid
        })
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      const d = await r.json();
      if (!d || d.status !== "ok") throw new Error((d && d.message) || "Clock-in failed");
      setClockFlow(f=>(f.step === "success" ? {...f, successName: d.staffName || f.successName} : f));
      refreshOnShift();
    } catch (e) {
      setClockFlow(f=>{
        if (f.mode !== "in" || f.step !== "success") return f;
        return {...f, step:"camera", err: (e && e.message) || "Connection error — try again", successName:"", successTime:""};
      });
    }
  }, [staffPins, refreshOnShift]);

  // Clock-OUT POST. Same OPTIMISTIC UI pattern as submitClockIn.
  const submitClockOut = useCallback(async (pin, targetName, photoBase64, verifiedName)=>{
    // A1 (row B): name from the verify (threaded in) on a gated host, else the
    // roster. OFF path byte-identical.
    const localName = pinAuth.serverPinVerifyEnabled() ? (verifiedName || null) : (staffPins && staffPins[pin]);
    // PIN privacy backstop (memory pin_privacy.md): both validation paths
    // surface the same opaque "Wrong PIN" message.
    if (rosterFree.clockOutRejected(localName, targetName)) {
      setClockFlow(f=>({...f, busy:false, err:rosterFree.WRONG_PIN_MESSAGE, pin:""}));
      return;
    }
    const t = new Date().toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
    setClockFlow(f=>({...f, step:"success", busy:false, err:"", successName: localName, successTime: t}));
    // Optimistically remove from on-shift roster (closes cache lag).
    setStaffOnShift(s=>{
      const list = Array.isArray(s) ? s : [];
      return list.filter(x => x && String(x.name||"").toLowerCase() !== String(localName).toLowerCase());
    });
    try {
      const r = await fetch(BACKEND, {
        method:"POST",
        headers:{"Content-Type":"text/plain;charset=utf-8"},
        body: JSON.stringify({action:"staffShiftEnd", site:CLOCK_IN_SITE_KEY, staffPin: pin, photoBase64: photoBase64 || ""})
      });
      if (!r.ok) throw new Error("HTTP "+r.status);
      const d = await r.json();
      if (!d || d.status !== "ok") throw new Error((d && d.message) || "Clock-out failed");
      setClockFlow(f=>(f.step === "success" ? {...f, successName: d.staffName || f.successName} : f));
      refreshOnShift();
    } catch (e) {
      setClockFlow(f=>{
        if (f.mode !== "out" || f.step !== "success") return f;
        return {...f, step:"camera", err: (e && e.message) || "Connection error — try again", successName:"", successTime:""};
      });
    }
  }, [staffPins, refreshOnShift]);

  const day=now.toLocaleDateString("en-GB",{weekday:"long"});
  const date=now.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  const time=now.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  const greetWord=now.getHours()<12?"Good morning":now.getHours()<17?"Good afternoon":"Good evening";

  const PinModal = pinModal ? (
    <div style={S.overlay}><div style={{...S.modal,display:"flex",flexDirection:"column",alignItems:"center",padding:isPortrait?24:16,maxWidth:isPortrait?380:340}}>
      <div style={{...S.modalTitle,textAlign:"center",fontSize:isPortrait?"1.1rem":"0.95rem"}}>Staff PIN</div>
      <div style={{...S.modalDesc,textAlign:"center",marginBottom:isPortrait?18:8,fontSize:isPortrait?"0.8rem":"0.72rem"}}>Enter your 4-digit PIN to confirm</div>
      <div style={{...S.dots,marginBottom:isPortrait?10:6,gap:isPortrait?20:14}}>
        {[0,1,2,3].map(i=>(<div key={i} style={{...S.dot,...(i<pmVal.length?S.dotFilled:S.dotEmpty),width:isPortrait?20:16,height:isPortrait?20:16}}/>))}
      </div>
      <div style={{color:B.coral,fontSize:"0.78rem",fontWeight:600,height:18,marginBottom:isPortrait?6:2,textAlign:"center"}}>{pmErr||" "}</div>
      <div style={{...S.pad,gap:isPortrait?10:6,maxWidth:isPortrait?280:220}}>
        {[1,2,3,4,5,6,7,8,9,null,0,"del"].map((k,i)=>{
          if(k===null) return (<div key={i}/>);
          return (<button key={i} style={{...S.key,aspectRatio:isPortrait?"1.35":"1.8",fontSize:isPortrait?"1.6rem":"1.2rem",borderRadius:isPortrait?14:10}} onClick={()=>k==="del"?pmDel():pmDigit(String(k))} disabled={pmLoad}>{k==="del"?"⌫":k}</button>);
        })}
      </div>
      <button style={{...S.modalCancel,marginTop:isPortrait?18:8,width:"100%",maxWidth:isPortrait?280:220,padding:isPortrait?12:8,fontSize:"0.8rem"}} onClick={cancelPin} disabled={pmLoad}>{pmLoad?"Checking...":"Cancel"}</button>
    </div></div>
  ) : null;

  // Clock-In flow scrim — extracted into ClockFlowScrimRenderer above. Mounted
  // only while clockFlow.mode is set so the camera hook (useClockInCamera)
  // starts/stops cleanly with the modal lifecycle.
  const NotClockedInPromptModal = notClockedInPrompt ? (
    <NotClockedInPrompt
      staffName={notClockedInPrompt.name}
      onClockIn={()=>{ setNotClockedInPrompt(null); openClockIn(); }}
      onDismiss={()=>setNotClockedInPrompt(null)}
    />
  ) : null;

  const ClockFlowScrim = (CLOCK_IN_ENABLED && clockFlow.mode) ? (
    <ClockFlowScrimRenderer
      clockFlow={clockFlow}
      setClockFlow={setClockFlow}
      submitClockIn={submitClockIn}
      submitClockOut={submitClockOut}
      closeClock={closeClock}
      staffOnShift={staffOnShift}
      staffPins={staffPins}
    />
  ) : null;

  // ==== LOGIN ====
  if(view==="login") return (
    <div style={{...S.loginRoot,height:"100dvh",overflow:"hidden"}}>
      <div style={S.strip}><div style={S.stripDot}/><span style={S.stripTxt}>{SITE.name}</span></div>
      <div style={{...S.loginBody,flex:1,justifyContent:"center",paddingTop:40}}>
        <RolledLogo height={isPortrait?52:36} />
        <div style={{fontSize:"0.82rem",fontWeight:600,color:B.kewpie,opacity:0.4,marginTop:8,marginBottom:isPortrait?"2rem":"1rem",letterSpacing:"0.06em"}}>Kitchen App</div>
        <div style={{...S.dots,...(shake?{animation:"shake 0.4s ease"}:{})}}>
          {[0,1,2,3].map(i=>(<div key={i} style={{...S.dot,...(i<pin.length?S.dotFilled:S.dotEmpty)}}/>))}
        </div>
        {err?<div style={S.pinErr}>{err}</div>:<div style={S.pinHint}>Enter your staff PIN</div>}
        <div style={{...S.pad,gap:isPortrait?10:6,maxWidth:isPortrait?280:260}}>
          {[1,2,3,4,5,6,7,8,9,null,0,"del"].map((k,i)=>{
            if(k===null) return (<div key={i}/>);
            return (<button key={i} style={{...S.key,aspectRatio:isPortrait?"1.35":"1.6",fontSize:isPortrait?"1.6rem":"1.3rem"}} onClick={()=>k==="del"?del():digit(String(k))}>{k==="del"?"⌫":k}</button>);
          })}
        </div>
      </div>
      <div style={{color:B.kewpie,opacity:0.25,fontSize:"0.75rem",textAlign:"center",paddingBottom:isPortrait?20:12,paddingTop:8,flexShrink:0}}>{date} · {time}</div>
      {flash && <div style={S.flash}><div style={S.greet}>G'day, {staff}</div></div>}
    </div>
  );

  // ==== HOME ====
  if(view==="home") return (
    <div style={{...S.homeRoot,...(isMobile?{padding:"0 1rem 1rem"}:{})}}>
      <div style={S.topBar}>
        <div style={S.topL}><RolledLogo height={isMobile?20:24}/>{!isMobile&&<span style={S.hSite}>{SITE.name}</span>}</div>
        <div style={S.topR}><div style={S.chip}><div style={S.chipAv}>{staff[0]}</div><span style={S.chipName}>{staff}</span></div><button style={S.logoutBtn} onClick={logout}>Log out</button></div>
      </div>
      <div style={{...S.greetBox,...(isMobile?{padding:"1.2rem 0 1rem"}:{})}}><div style={{...S.greetHi,...(isMobile?{fontSize:"1.5rem"}:{})}}>{greetWord}, <span style={{color:B.wasabi}}>{staff}</span></div><div style={S.greetD}>{day} · {date} · {time}</div></div>
      {CLOCK_IN_ENABLED && isTabletKiosk && !isAdminLoggedIn && (
        <ClockInRosterBanner
          staffOnShift={staffOnShift}
          onClockIn={openClockIn}
          onClockOut={openClockOut}
        />
      )}
      <KioskBindPanel site={SITE} isAdmin={isAdminLoggedIn}/>{/* CB: renders null unless KIOSK_BIND_HOSTS lists this host (it is empty) */}
      {(()=>{
        const effectiveTools = TOOLS.map(t => applyTileOverride(t, tileOverrides));
        const avail = effectiveTools.filter(t=>t.on);
        const soon = effectiveTools.filter(t=>!t.on);
        const gridCols = isPortrait?"repeat(2,1fr)":"repeat(3,1fr)";
        return (<>
          <div className="rolled-grid" style={{...S.grid,gridTemplateColumns:gridCols}}>
            {avail.map((t,i)=>(
              <button key={t.id} className="rolled-card" style={{...S.card,...(isPortrait?{aspectRatio:"auto",padding:"1.2rem"}:{}),animationDelay:i*40+"ms"}} onClick={()=>setView(t.id)}>
                <div style={S.cTop}><div style={S.cIcon}>{t.icon}</div></div>
                <div style={S.cLabel}>{t.label}</div><div style={S.cDesc}>{t.desc}</div>
              </button>
            ))}
          </div>
          {soon.length>0 && (<>
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"28px 4px 14px"}}>
              <div style={{flex:1,height:1,background:"rgba(212,251,111,0.12)"}}/>
              <span style={{fontFamily:"Manrope,sans-serif",fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.4,letterSpacing:"0.2em",textTransform:"uppercase"}}>Coming Soon</span>
              <div style={{flex:1,height:1,background:"rgba(212,251,111,0.12)"}}/>
            </div>
            <div className="rolled-grid" style={{...S.grid,gridTemplateColumns:gridCols}}>
              {soon.map((t,i)=>(
                <button key={t.id} className="rolled-card" style={{...S.card,...(isPortrait?{aspectRatio:"auto",padding:"1.2rem"}:{}),...S.cardOff,animationDelay:(avail.length+i)*40+"ms"}} onClick={()=>{}} disabled>
                  <div style={S.cTop}><div style={S.cIcon}>{t.icon}</div><span style={S.cSoon}>{t.tag||"Soon"}</span></div>
                  <div style={S.cLabel}>{t.label}</div><div style={S.cDesc}>{t.desc}</div>
                </button>
              ))}
            </div>
          </>)}
        </>);
      })()}
      <div style={S.hFoot}>Rolled Kitchen App v1.0 · {SITE.name}</div>{PinModal}{ClockFlowScrim}{NotClockedInPromptModal}
    </div>
  );

  // ==== TOOL SHELL with sidebar ====
  const activeTools = TOOLS.map(t => applyTileOverride(t, tileOverrides)).filter(t=>t.on);
  const hasPending = ()=>(view==="wastage"&&wastageDirty);
  const goBack = ()=>{if(hasPending()){setPendingNavDest("home");setBackConfirm(true);return}setView("home")};
  const forceBack = ()=>{const dest=pendingNavDest;setBackConfirm(false);setWastageDirty(false);setView(dest)};
  const navTo = (id)=>{if(id===view)return;if(hasPending()){setPendingNavDest(id);setBackConfirm(true);return}setView(id)};

  const BackConfirmModal = backConfirm ? (
    <div style={S.overlay}><div style={S.modal}>
      <div style={S.modalTitle}>Unsubmitted Wastage</div>
      <div style={{fontSize:"0.85rem",color:B.kewpie,opacity:0.7,lineHeight:1.5,marginBottom:16}}>You have unsubmitted wastage entries. They haven't been logged yet.</div>
      <div style={S.modalBtns}><button style={S.modalCancel} onClick={forceBack}>Leave anyway</button><button style={S.modalOk} onClick={()=>setBackConfirm(false)}>Go back</button></div>
    </div></div>
  ) : null;

  const toolContent = {
    // key={dayKey} on day-scoped views forces a clean remount when the
    // day-rollover watchdog bumps dayKey, so internal form state (forecast
    // confirmations, wastage entries, stock-request acknowledgements,
    // inbound delivery state) starts fresh each day.
    forecast: <ProductionForecast key={dayKey} backend={BACKEND} requestPin={requestPin}/>,
    wastage: <WastageTracking key={dayKey} backend={BACKEND} requestPin={requestPin} wastageLog={wastageLog} setWastageLog={setWastageLog} setDirty={setWastageDirty}/>,
    stockreq: <StockRequestReceiver key={dayKey} backend={BACKEND} requestPin={requestPin} stockCatalog={stockCatalog} catalogSource={catalogSource}/>,
    contacts: <EmergencyContacts/>,
    compliance: <ComplianceModule site={SITE} backend={BACKEND} requestPin={requestPin} staffByPin={staffByPin}/>,
    inbound: <InboundDeliveries backend={BACKEND} requestPin={requestPin} stockCatalog={stockCatalog}/>,
    // Phase A: kitchen's Previous Week Insights defaults to Trafford only.
    // Multi-site Trafford/Arndale toggle is Phase B — admin "End of Day
    // Insights" already has the full cross-site comparison view.
    insights: <PreviousWeekInsights site={{key:"tc",name:"Trafford"}} backend={BACKEND} availableSites={[{key:"tc",name:"Trafford"}]}/>,
  };
  const currentTool = TOOLS.find(t=>t.id===view);

  return (
    <div style={S.shellRoot}>
      <div className="fc-shell-bar" style={{...S.shellBar,position:"sticky",top:0,zIndex:100,background:B.nori}}>
        <button className="fc-shell-back" style={S.shellBack} onClick={goBack}>← Back</button>
        <span className="fc-shell-title" style={{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi}}>{currentTool&&currentTool.icon} {currentTool?currentTool.label:""}</span>
        <span className="fc-shell-site" style={S.shellSiteFull}>{SITE.name}</span>
      </div>
      <div style={{display:"flex",minHeight:"calc(100vh - 52px)"}}>
        {/* Collapsible sidebar — hidden on mobile, sticky on tablet/desktop */}
        {!isMobile && (
        <div style={{...S.sidebar,...(sideOpen?{}:S.sidebarCollapsed),position:"sticky",top:52,alignSelf:"flex-start",height:"calc(100vh - 52px)",overflowY:"auto"}}>
          <button style={S.sideToggle} onClick={()=>setSideOpen(o=>!o)} title={sideOpen?"Collapse menu":"Expand menu"} aria-label={sideOpen?"Collapse menu":"Expand menu"}>
            <span style={{display:"inline-block",transition:"transform 0.2s ease",transform:sideOpen?"rotate(0deg)":"rotate(180deg)"}}>◀</span>
          </button>
          {activeTools.map(t=>(
            <button key={t.id} style={{...S.sideItem,...(t.id===view?S.sideItemActive:{}),...(sideOpen?{}:S.sideItemCollapsed)}} onClick={()=>navTo(t.id)} title={sideOpen?"":t.label}>
              <span style={{fontSize:"1.05rem",marginRight:sideOpen?10:0,flexShrink:0}}>{t.icon}</span>
              {sideOpen&&<span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>}
            </button>
          ))}
        </div>
        )}
        {/* Content — no overflowY here, page scrolls naturally so position:fixed modals always centre on viewport */}
        <div style={{flex:1,paddingTop:isMobile?8:16,transition:"margin-left 0.2s ease"}}>
          {toolContent[view] || <Placeholder label={currentTool?currentTool.label:""} icon={currentTool?currentTool.icon:"?"} msg="Coming soon."/>}
        </div>
      </div>
      {PinModal}{BackConfirmModal}{ClockFlowScrim}{NotClockedInPromptModal}
    </div>
  );
}

// ===========================================
// PRODUCTION FORECAST
// ===========================================
function ProductionForecast({backend,requestPin}){
  const [fcDate,setFcDate] = useState(new Date());
  const [pickerOpen,setPickerOpen] = useState(false);
  const dateStr = fcDate.toISOString().slice(0,10);
  const isToday = dateStr === todayKey();
  const dayName = fcDate.toLocaleDateString("en-GB",{weekday:"long"});
  const dateDisplay = fcDate.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  const changeDate=(delta)=>{setFcDate(d=>{const n=new Date(d);n.setDate(n.getDate()+delta);return n})};

  return (
    <div className="fc-forecast-page" style={S.content}>
      {/* Shared date nav — arrows for ±1 day, tap to open calendar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:8}}>
        <button style={S.navBtn} onClick={()=>changeDate(-1)} aria-label="Previous day">◀</button>
        <button onClick={()=>setPickerOpen(true)} style={S.dateTrigger} aria-label="Open calendar">
          <span style={{fontSize:"1rem",lineHeight:1,marginRight:4}}>📅</span>
          <span>
            <span style={{display:"block",fontFamily:"gelica,serif",fontWeight:700,fontSize:"1.1rem",color:B.kewpie,lineHeight:1.1}}>{dayName}</span>
            <span style={{display:"block",fontSize:"0.78rem",color:B.kewpie,opacity:0.45,marginTop:2}}>{dateDisplay}</span>
          </span>
          {isToday && <span style={S.dateTriggerBadge}>TODAY</span>}
        </button>
        <button style={S.navBtn} onClick={()=>changeDate(1)} aria-label="Next day">▶</button>
      </div>
      {!isToday && (
        <div style={{textAlign:"center",marginBottom:6}}>
          <button style={S.todayJumpBtn} onClick={()=>setFcDate(new Date())}>Jump to today</button>
        </div>
      )}

      {pickerOpen && <DatePickerModal value={fcDate} onChange={setFcDate} onClose={()=>setPickerOpen(false)}/>}

      <div className="fc-sites-grid">
        {Object.entries(SITES).map(([key,s])=>(
          <SiteForecastSection key={key} site={key} siteName={s.name} date={fcDate} dateStr={dateStr} backend={backend} requestPin={requestPin}/>
        ))}
      </div>
    </div>
  );
}

function DatePickerModal({value,onChange,onClose}){
  const [viewDate,setViewDate] = useState(()=>{ const d=new Date(value); d.setDate(1); return d; });
  const monthLabel = viewDate.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const firstOfMonth = new Date(viewDate.getFullYear(),viewDate.getMonth(),1);
  const leadMondayOffset = (firstOfMonth.getDay()+6)%7; // 0 for Mon, 6 for Sun
  const gridStart = new Date(firstOfMonth); gridStart.setDate(gridStart.getDate()-leadMondayOffset);
  const days = Array.from({length:42},(_,i)=>{ const d=new Date(gridStart); d.setDate(gridStart.getDate()+i); return d; });
  const sameDay=(a,b)=>a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();
  const today = new Date();
  const prevMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()-1);setViewDate(d);};
  const nextMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()+1);setViewDate(d);};
  const pick=(d)=>{onChange(d);onClose();};
  const weekdays = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{...S.modal,maxWidth:400}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button style={S.navBtn} onClick={prevMonth} aria-label="Previous month">◀</button>
          <div style={{fontFamily:"gelica,serif",fontSize:"1.15rem",fontWeight:700,color:B.wasabi}}>{monthLabel}</div>
          <button style={S.navBtn} onClick={nextMonth} aria-label="Next month">▶</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
          {weekdays.map((w,i)=>(<div key={i} style={S.calWeekday}>{w}</div>))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {days.map((d,i)=>{
            const inMonth = d.getMonth()===viewDate.getMonth();
            const dToday = sameDay(d,today);
            const dSelected = sameDay(d,value);
            return (
              <button key={i} onClick={()=>pick(d)} style={{...S.calDay,...(inMonth?{}:{opacity:0.22}),...(dToday&&!dSelected?S.calDayToday:{}),...(dSelected?S.calDaySelected:{})}}>{d.getDate()}</button>
            );
          })}
        </div>
        <div style={{display:"flex",gap:8,marginTop:16}}>
          <button style={S.modalCancel} onClick={onClose}>Cancel</button>
          <button style={S.modalOk} onClick={()=>pick(new Date())}>Today</button>
        </div>
      </div>
    </div>
  );
}

// The backend sends each roll's share of the day as a whole-number percent
// (e.g. 15 = 15%). We must NOT guess fraction-vs-percent per roll: a genuine
// low-volume roll at 1% (s.pct === 1) looks identical to a 1.0 fraction, and
// the old `pct<=1 ? pct*100 : pct` test blew every 1% roll up to 100% of the
// whole site. That doubled the site total once the summer specials (Fish &
// Chips, Honey Soy) landed at ~1% each. Decide ONCE across the whole list:
// only treat the values as fractions if the entire list sums to ~1.
function skuSharePct(sku, allSkus){
  const sum = (allSkus||[]).reduce((a,s)=>a+(Number(s.pct)||0),0);
  const listIsFractions = sum>0 && sum<=1.5;
  return listIsFractions ? (Number(sku.pct)||0)*100 : (Number(sku.pct)||0);
}

function SiteForecastSection({site,siteName,date,dateStr,backend,requestPin}){
  const [forecast,setForecast] = useState(null);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState(null);
  const [showInfo,setShowInfo] = useState(false);
  const [override,setOverride] = useState(0);
  const [skuAdj,setSkuAdj] = useState({});
  const [confirmed,setConfirmed] = useState(false);
  const [confirming,setConfirming] = useState(false);
  const [undoing,setUndoing] = useState(false);
  // Inline error after a failed undo so the user can see the rejection
  // (e.g. "role does not permit this action" when a FOH staffer enters
  // their PIN). Without this, no-cors swallowed the rejection silently.
  const [undoErr,setUndoErr] = useState("");
  // Has this site's forecast already been confirmed today? Null = not checked
  // yet, object = state from backend. Shapes:
  //   { confirmed: true, by, at, portions, rolls, resubmit? }
  //   { confirmed: false, undone: true, undoneBy, undoneAt, previousPortions, previousRolls }
  //   { confirmed: false }
  // false (not null) means we successfully checked and it's pending.
  const [confirmedInfo,setConfirmedInfo] = useState(null);
  // Lane BG: where the forecast was built. null = the Google engine (today's
  // behaviour, and the only value on a non-gated host); "supabase"/"supabase_fallback"
  // only ever appears on a FORECAST_READ_HOSTS host or with ?forecastread=on.
  const [fcSource,setFcSource] = useState(null);
  // Lane BG: an honest secondary message when the Supabase confirm/undo mirror
  // DISAGREES with the Sheet (role refusal or error). Never blocks the Sheet path.
  const [fcShadowNote,setFcShadowNote] = useState("");

  // Re-fetch the confirmedInfo banner state without remounting the form so
  // chefs don't lose in-progress slider/SKU tweaks. Used after a same-session
  // undo so the banner flips from green-confirmed to yellow-undone notice.
  const reloadStatus = useCallback(()=>{
    const dateBE = date.toLocaleDateString("en-GB");
    // Lane BG: on a gated host read the status from Supabase (ISO date — NEVER the
    // en-GB string), falling back to the Google status. OFF branch is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
      const apply=(data)=>setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false);
      const google=()=>fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE)).then(r=>r.ok?r.json():{confirmed:false});
      forecastReader.kioskStatus({site,date:y+"-"+m+"-"+d}).then(s=>s?apply(s):google().then(apply)).catch(()=>setConfirmedInfo(false));
    } else {
      fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE))
        .then(r=>r.ok?r.json():{confirmed:false})
        .then(data=>{ setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
        .catch(()=>{ setConfirmedInfo(false); });
    }
  },[backend,site,date]);

  useEffect(()=>{
    let cancelled=false;
    setLoading(true);setError(null);setForecast(null);setShowInfo(false);setOverride(0);setSkuAdj({});setConfirmed(false);setConfirmedInfo(null);setFcSource(null);setFcShadowNote("");
    const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
    // Lane BG: on a gated host (FORECAST_READ_HOSTS — empty today) build the forecast
    // from Supabase, falling back to the Google call on any failure/stale bench. The
    // OFF branch below is byte-for-byte today's call, so a non-gated host is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const iso=y+"-"+m+"-"+d;
      const googleFetch=()=>fetch(backend+"?action=forecast&site="+site+"&date="+iso).then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()});
      forecastReader.fetchForecastDay({site,date:iso,googleFetch})
        .then(({data,source})=>{if(!cancelled){if(!data)throw new Error("no forecast");if(data.error)throw new Error(data.error);setForecast(data);setFcSource(source);setLoading(false)}})
        .catch(e=>{if(!cancelled){setError(e.message);setLoading(false)}});
    } else {
      fetch(backend+"?action=forecast&site="+site+"&date="+y+"-"+m+"-"+d)
        .then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})
        .then(data=>{if(!cancelled){if(data.error)throw new Error(data.error);setForecast(data);setLoading(false)}})
        .catch(e=>{if(!cancelled){setError(e.message);setLoading(false)}});
    }
    // Parallel: has today's forecast already been confirmed for this site?
    const dateBE = date.toLocaleDateString("en-GB");
    // Lane BG: on a gated host read the status from Supabase (ISO date — NEVER the
    // en-GB string), falling back to the Google status. OFF branch is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const applyS=(data)=>{ if(!cancelled) setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); };
      const googleS=()=>fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE)).then(r=>r.ok?r.json():{confirmed:false});
      forecastReader.kioskStatus({site,date:y+"-"+m+"-"+d}).then(s=>s?applyS(s):googleS().then(applyS)).catch(()=>{ if(!cancelled) setConfirmedInfo(false); });
    } else {
      fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE))
        .then(r=>r.ok?r.json():{confirmed:false})
        .then(data=>{ if(!cancelled) setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
        .catch(()=>{ if(!cancelled) setConfirmedInfo(false); });
    }
    return()=>{cancelled=true};
  },[site,dateStr]);

  // Forecast target portions — drives initial per-SKU roll distribution.
  const calcTarget = ()=>{
    if(!forecast)return 0;
    let b = forecast.forecast.weightedBase, am=1;
    forecast.autoFlags.forEach(f=>{am+=f.mult});
    let adj = b*am;
    if(forecast.wastageAdjustment){adj*=(forecast.wastageAdjustment.type==="trim"?0.95:1.05)}
    adj *= (1+override/100);
    const TB = forecast.forecast.tgtgBuffer;
    let tp = Math.round(adj+TB);
    if(forecast.forecast.minApplied && override===0) tp = forecast.forecast.totalPortions;
    return tp;
  };
  const target = calcTarget();

  // Largest-remainder allocation across historical rolls, PLUS a fixed floor for
  // brand-new specials. A new roll has no same-weekday sales history yet, so the
  // backend returns it at pct 0 (getSixWeekHistory only reads the same weekday, 6
  // weeks back — the specials that launched 01/09 hadn't traded on any of those
  // days). A pure pct-proportional split therefore gives every new roll 0 and the
  // kitchen makes none of them (the 04/09 "new rolls show 0" bug). The backend
  // already reserves each new roll a minimum (newSkuMinimum -> s.rolls); honour
  // that floor here and split the REMAINING rolls across the historical SKUs by
  // share, so the hero total is unchanged — we just stop over-making old rolls.
  const baseRollsBySku = useMemo(()=>{
    if(!forecast||!forecast.skuBreakdown)return {};
    const skus = forecast.skuBreakdown;
    const targetRolls = Math.round(target/2);
    const isNewSku = s => (s.isSpecial||s.isNew) && skuSharePct(s, skus)<=0;
    const out = {};

    // 1) Reserve each new special its backend floor (rolls, or portions/2 if the
    //    older field name comes back). Additive to the historical pct split below.
    let reserved = 0;
    skus.forEach(s=>{
      if(isNewSku(s)){
        const floor = Math.max(0, Math.round(Number(s.rolls) || Math.ceil((Number(s.portions)||0)/2) || 0));
        out[s.name] = floor;
        reserved += floor;
      }
    });

    // 2) Distribute the remaining rolls across historical SKUs by pct share.
    const hist = skus.filter(s=>!isNewSku(s));
    const histTarget = Math.max(0, targetRolls - reserved);
    const raw = hist.map(s=>histTarget*(skuSharePct(s, skus)/100));
    const floors = raw.map(r=>Math.floor(r));
    hist.forEach((s,i)=>{out[s.name]=floors[i]});
    let need = histTarget - floors.reduce((a,b)=>a+b,0);
    if(need>0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>b.r-a.r);
      for(let k=0;k<Math.min(need,rem.length);k++) out[hist[rem[k].i].name]++;
    } else if(need<0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>a.r-b.r);
      for(let k=0;k<Math.min(-need,rem.length);k++){
        const n=hist[rem[k].i].name;
        if(out[n]>0)out[n]--;
      }
    }
    return out;
  },[forecast,target]);

  const getSkuBase=(name)=>baseRollsBySku[name]||0;

  const totalRolls = Object.values(baseRollsBySku).reduce((a,b)=>a+b,0) + Object.values(skuAdj).reduce((a,b)=>a+b,0);
  const totalPortions = totalRolls*2;

  const adjSku=(name,delta)=>{
    setSkuAdj(prev=>{
      const base = getSkuBase(name);
      const cur = prev[name]||0;
      if(base+cur+delta<0)return prev;
      return {...prev,[name]:cur+delta};
    });
  };

  // Phase 5 Option 2 (2026-04-21): capture a full calculation snapshot so the
  // admin Forecast Submissions drill-down can show the exact roll mix staff
  // saw at confirm time (backend's weighted base, auto flags, wastage trim,
  // per-SKU rolls before + after the user's adjustment). Fires in the same
  // forecastConfirm POST body as a new `snapshot` field.
  const buildSnapshot = ()=>{
    if (!forecast) return null;
    const finalSkuRolls = {};
    Object.keys(baseRollsBySku).forEach(k => {
      finalSkuRolls[k] = (baseRollsBySku[k]||0) + (skuAdj[k]||0);
    });
    Object.keys(skuAdj).forEach(k => { if (!(k in finalSkuRolls)) finalSkuRolls[k] = skuAdj[k]; });
    const skuPcts = {};
    (forecast.skuBreakdown||[]).forEach(s => {
      skuPcts[s.name] = skuSharePct(s, forecast.skuBreakdown);
    });
    const fc = forecast.forecast || {};
    const range = (fc.rangeLow != null && fc.rangeHigh != null)
      ? {low: fc.rangeLow, high: fc.rangeHigh, safetyBuffer: fc.safetyBuffer != null ? fc.safetyBuffer : null}
      : null;
    return {
      weightedBase: fc.weightedBase,
      basePredictedPortions: fc.totalPortions,
      tgtgBuffer: fc.tgtgBuffer,
      minApplied: !!fc.minApplied,
      autoFlags: (forecast.autoFlags||[]).map(f => ({label:f.label, mult:f.mult, emoji:f.emoji||"", positive:!!f.positive})),
      wastageAdjustment: forecast.wastageAdjustment ? forecast.wastageAdjustment.type : null,
      wastageAdjustmentDetail: forecast.wastageAdjustment || null,
      overridePct: override,
      confirmedPortions: totalPortions,
      confirmedRolls: totalRolls,
      baseSkuRolls: Object.assign({}, baseRollsBySku),
      userSkuAdjustments: Object.assign({}, skuAdj),
      finalSkuRolls,
      skuPcts,
      inputs: forecast.inputs || null,
      calc: forecast.calc || null,
      range,
      schemaVersion: 1
    };
  };

  // 2026-05-04 (optimistic): banner state flips on tap, POST runs in
  // background, rollback + inline error on failure. JJ flagged the prior
  // wait-for-fetch-then-flip pattern as feeling broken — this makes the
  // confirm <-> undone <-> resubmitted transitions feel instant.
  const handleConfirm = ()=>{
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const wasUndone = !!(priorInfo && priorInfo.undone);
      // Apply the new confirmed state immediately.
      setConfirmedInfo({
        confirmed: true,
        by: staffName,
        at: new Date().toISOString(),
        portions: totalPortions,
        rolls: totalRolls,
        resubmit: wasUndone
      });
      setConfirmed(true);
      appData.forecastConfirmed[site+"-"+dateStr]=true; persistAppData();
      setConfirming(true);
      setUndoErr("");
      // Background POST. text/plain skips the CORS preflight Apps Script
      // doesn't answer (memory: apps_script_cors_preflight_trap). Response
      // body still readable so role-gate rejections surface.
      let ok = false, errMsg = "";
      try{
        const r = await fetch(backend,{
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body:JSON.stringify({action:"forecastConfirm",site,date:dateStr,totalPortions,totalRolls,override,skuAdjustments:skuAdj,snapshot:buildSnapshot(),staffName,staffPin})
        });
        const result = await r.json().catch(()=>null);
        if (r.ok && result && result.status === "success") ok = true;
        else errMsg = (result && (result.error || result.message)) || ("HTTP " + r.status);
      }catch(e){ errMsg = e.message || "Network error"; }
      setConfirming(false);
      if (!ok) {
        // Roll back the optimistic flip.
        setConfirmedInfo(priorInfo);
        setConfirmed(priorConfirmed);
        if (!priorConfirmed && !(priorInfo && priorInfo.confirmed)) {
          delete appData.forecastConfirmed[site+"-"+dateStr]; persistAppData();
        }
        setUndoErr(errMsg || "Couldn't confirm — try again or check with admin.");
      } else if (forecastReader.forecastReadEnabled()) {
        // Lane BG: the Sheet is confirmed (still the record). Mirror into the
        // Supabase door (1121) as an honest secondary — never undoes the Sheet
        // confirm, only surfaces a disagreement (role refusal / error). Not fired
        // when the Sheet write failed, so we never show a Supabase success the
        // Sheet refused. staffPin is used only to obtain the login pass.
        const cy=date.getFullYear(),cm=String(date.getMonth()+1).padStart(2,"0"),cd=String(date.getDate()).padStart(2,"0");
        const iso=cy+"-"+cm+"-"+cd;
        const shadow = await forecastReader.kioskConfirm({site,date:iso,portions:totalPortions,rolls:totalRolls,override,skuAdjustments:skuAdj,snapshot:buildSnapshot(),idemKey:"kc:"+site+":"+iso+":"+Date.now(),staffPin});
        setFcShadowNote(forecastReader.shadowDisagrees(shadow) ? ("Saved to the sheet. The Supabase copy did not record ("+(shadow.reason||shadow.status)+").") : "");
      }
    });
  };

  // Undo & resubmit (2026-05-04, optimistic). Banner flips to yellow
  // "previously undone" the instant the user taps; POST runs in background;
  // rollback + inline error on failure. Server enforces BOH/Admin role via
  // canPerformAction.
  const handleUndo = ()=>{
    setUndoErr("");
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const undonePortions = (priorInfo && priorInfo.portions != null) ? priorInfo.portions : totalPortions;
      const undoneRolls    = (priorInfo && priorInfo.rolls    != null) ? priorInfo.rolls    : totalRolls;
      // Optimistically apply the undone state.
      setConfirmedInfo({
        confirmed: false,
        undone: true,
        undoneBy: staffName,
        undoneAt: new Date().toISOString(),
        previousPortions: undonePortions,
        previousRolls: undoneRolls
      });
      setConfirmed(false);
      delete appData.forecastConfirmed[site+"-"+dateStr]; persistAppData();
      setUndoing(true);
      setUndoErr("");
      // Background POST. See handleConfirm for the text/plain reasoning.
      let ok = false, errMsg = "";
      try{
        const r = await fetch(backend,{
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body:JSON.stringify({
            action:"forecastUndo",
            site,date:dateStr,
            undonePortions, undoneRolls, undoneOverride: override,
            staffName, staffPin
          })
        });
        const result = await r.json().catch(()=>null);
        if (r.ok && result && result.status === "success") ok = true;
        else errMsg = (result && (result.error || result.message)) || ("HTTP " + r.status);
      }catch(e){ errMsg = e.message || "Network error"; }
      setUndoing(false);
      if (!ok) {
        // Roll back: banner returns to whatever was there before.
        setConfirmedInfo(priorInfo);
        setConfirmed(priorConfirmed);
        if (priorConfirmed || (priorInfo && priorInfo.confirmed)) {
          appData.forecastConfirmed[site+"-"+dateStr] = true; persistAppData();
        }
        setUndoErr(errMsg || "Couldn't undo — try again or check with admin.");
      } else if (forecastReader.forecastReadEnabled()) {
        // Lane BG: mirror the undo into the Supabase door (1121) as an honest
        // secondary — never re-does the Sheet undo, only surfaces a disagreement.
        const cy=date.getFullYear(),cm=String(date.getMonth()+1).padStart(2,"0"),cd=String(date.getDate()).padStart(2,"0");
        const iso=cy+"-"+cm+"-"+cd;
        const shadow = await forecastReader.kioskUndo({site,date:iso,idemKey:"ku:"+site+":"+iso+":"+Date.now(),staffPin});
        setFcShadowNote(forecastReader.shadowDisagrees(shadow) ? ("Undone on the sheet. The Supabase copy did not record ("+(shadow.reason||shadow.status)+").") : "");
      }
    });
  };

  const retry=()=>{
    setLoading(true);setError(null);
    const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
    // Lane BG: mirror the initial load's gated path (OFF branch byte-identical to today).
    if (forecastReader.forecastReadEnabled()) {
      const iso=y+"-"+m+"-"+d;
      const googleFetch=()=>fetch(backend+"?action=forecast&site="+site+"&date="+iso).then(r=>r.json());
      forecastReader.fetchForecastDay({site,date:iso,googleFetch})
        .then(({data,source})=>{setForecast(data);setFcSource(source);setLoading(false)}).catch(e=>{setError(e.message);setLoading(false)});
    } else {
      fetch(backend+"?action=forecast&site="+site+"&date="+y+"-"+m+"-"+d)
        .then(r=>r.json()).then(data=>{setForecast(data);setLoading(false)}).catch(e=>{setError(e.message);setLoading(false)});
    }
  };

  const isRemotelyConfirmed = !!(confirmedInfo && confirmedInfo.confirmed);
  const isConfirmed = confirmed || isRemotelyConfirmed;
  const showPending = !!forecast && !loading && !error && !isConfirmed;
  const showConfirmed = !!forecast && !loading && !error && isConfirmed;

  const fmtConfirmedAt = (iso) => {
    try { return new Date(iso).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}); }
    catch(e) { return ""; }
  };

  return (
    <div>
      <div className="fc-site-header" style={S.siteSectionHeader}>
        <span style={S.siteSectionDot}/>
        {siteName}
        {showPending && <span style={S.pendingPill}><span style={S.pendingDot}/>Awaiting confirmation</span>}
        {showConfirmed && <span style={S.confirmedPill}>✓ Confirmed</span>}
        {/* Lane BG source badge — only ever shows on a gated host; OFF hosts never set fcSource. */}
        {(fcSource==="supabase"||fcSource==="supabase_fallback") && <span title="Forecast built from Supabase (Lane BG)" style={{marginLeft:8,fontSize:"0.6rem",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:B.wasabi,opacity:0.7}}>◆ supabase{fcSource==="supabase_fallback"?" · fallback":""}</span>}
      </div>

      {isConfirmed && (
        // Single confirmed banner — fires whether the lock-in came from this
        // session (`confirmed`) or another tab/device (`isRemotelyConfirmed`).
        // The Undo & resubmit button gives chefs an escape hatch when the
        // forecast turns out wrong; gated server-side by canPerformAction.
        <div style={{background:"rgba(111,175,143,0.12)",border:"1px solid rgba(111,175,143,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:"#9DD0B0",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>✓</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              {confirmedInfo && confirmedInfo.resubmit
                ? "Forecast resubmitted"
                : (isRemotelyConfirmed ? "Forecast already confirmed" : siteName + " forecast confirmed")}
              {confirmedInfo && confirmedInfo.by ? " by " + confirmedInfo.by : ""}
              {confirmedInfo && confirmedInfo.at ? " at " + fmtConfirmedAt(confirmedInfo.at) : ""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.75,marginTop:2}}>
              {confirmedInfo && confirmedInfo.portions != null
                ? <>Locked in at <strong>{confirmedInfo.portions} portions</strong>{confirmedInfo.rolls != null && <> / {confirmedInfo.rolls} rolls</>}. </>
                : null}
              Mistake? <strong>Undo &amp; resubmit</strong> to edit and re-confirm.
            </div>
          </div>
          <button
            onClick={handleUndo}
            disabled={undoing}
            style={{flexShrink:0,padding:"8px 14px",borderRadius:10,border:"1px solid rgba(255,179,71,0.4)",background:"rgba(255,179,71,0.08)",color:B.sunshine,fontFamily:"inherit",fontSize:"0.78rem",fontWeight:700,letterSpacing:"0.02em",cursor:undoing?"wait":"pointer"}}
          >
            {undoing?"Undoing…":"↩ Undo & resubmit"}
          </button>
          {undoErr && (
            <div style={{flexBasis:"100%",fontSize:"0.78rem",color:B.coral,marginTop:6,padding:"8px 10px",borderRadius:8,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.3)"}}>
              Undo failed: {undoErr}
            </div>
          )}
        </div>
      )}

      {!isConfirmed && confirmedInfo && confirmedInfo.undone && (
        <div style={{background:"rgba(255,179,71,0.07)",border:"1px solid rgba(255,179,71,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:B.sunshine,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>↩</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              Last submission undone{confirmedInfo.undoneBy?" by "+confirmedInfo.undoneBy:""}{confirmedInfo.undoneAt?" at "+fmtConfirmedAt(confirmedInfo.undoneAt):""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.85,marginTop:2}}>
              {confirmedInfo.previousPortions!=null && <>Was <strong>{confirmedInfo.previousPortions} portions</strong>{confirmedInfo.previousRolls!=null && <> / {confirmedInfo.previousRolls} rolls</>}. </>}
              Review the numbers and confirm again.
            </div>
          </div>
        </div>
      )}

      {loading && <div style={S.loadingBox}><div style={S.spinner}/><div style={{marginTop:12,color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>Loading {siteName} forecast…</div></div>}
      {error && <div style={{...S.sectionCard,borderColor:"rgba(255,122,92,0.3)"}}><div style={{color:B.coral,fontSize:"0.85rem",fontWeight:600}}>Failed to load forecast</div><div style={{color:B.kewpie,opacity:0.5,fontSize:"0.78rem",marginTop:4}}>{error}</div><button style={{...S.markBtn,marginTop:10}} onClick={retry}>Retry</button></div>}

      {forecast && !loading && !error && (<div style={confirmed?S.confirmedWrap:S.pendingWrap}>
        {/* Hero — live total from per-SKU commitments */}
        <div style={S.heroCard}>
          <div style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"3px",textTransform:"uppercase",marginBottom:4}}>TOTAL PORTIONS</div>
          <div style={{fontFamily:"gelica,serif",fontSize:"3.5rem",fontWeight:700,color:B.wasabi,lineHeight:1,letterSpacing:"-1px"}}>{totalPortions}</div>
          <div style={{fontSize:"0.82rem",color:B.kewpie,opacity:0.5,marginTop:4}}>{totalRolls} rolls to make</div>
          <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.3,marginTop:6}}>Incl. {forecast.forecast.tgtgBuffer} TGTG buffer</div>
          <button style={{position:"absolute",top:10,right:10,width:24,height:24,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.2)",background:"none",color:B.kewpie,opacity:0.4,fontSize:"0.7rem",fontStyle:"italic",cursor:"pointer"}} onClick={()=>setShowInfo(!showInfo)}>i</button>
        </div>

        {/* Data sources */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>DATA SOURCES</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {forecast.inputs.lastSameDay!==null
              ? <span style={S.pill}><span style={S.pillDot}/> Last {forecast.day}: <strong>{forecast.inputs.lastSameDay}</strong></span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> Last {forecast.day}: <strong>baseline</strong></span>}
            {forecast.inputs.sixWeekAvg!==null
              ? <span style={S.pill}><span style={S.pillDot}/> 6wk avg: <strong>{forecast.inputs.sixWeekAvg}</strong> ({forecast.inputs.weeksOfData}wk)</span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> 6wk avg: <strong>baseline</strong></span>}
            {forecast.inputs.lastWastage>0 && <span style={S.pill}><span style={S.pillDot}/> Wastage: <strong>{forecast.inputs.lastWastage}%</strong></span>}
          </div>
        </div>

        {/* Previous day wastage — read-only echo of yesterday's EOD total
            with the per-SKU mix so production can spot the patterns. */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>PREVIOUS DAY WASTAGE <span style={{marginLeft:6,color:B.kewpie,opacity:0.3,fontWeight:400,textTransform:"none",letterSpacing:0}}>(read-only)</span></div>
          {forecast.inputs.previousDayWaste && forecast.inputs.previousDayWaste.totalPortions!=null ? (
            <>
              <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap",marginBottom:forecast.inputs.previousDayWaste.items&&forecast.inputs.previousDayWaste.items.length?10:0}}>
                <span style={{fontFamily:"gelica,serif",fontSize:"1.8rem",fontWeight:700,color:forecast.inputs.previousDayWaste.totalPortions===0?B.wasabi:B.kewpie,lineHeight:1}}>{forecast.inputs.previousDayWaste.totalPortions}</span>
                <span style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.5}}>portion{forecast.inputs.previousDayWaste.totalPortions===1?"":"s"} wasted on {forecast.inputs.previousDayWaste.date}</span>
              </div>
              {forecast.inputs.previousDayWaste.items && forecast.inputs.previousDayWaste.items.length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {forecast.inputs.previousDayWaste.items.map(it=>(
                    <span key={it.name} style={S.pill}><span style={S.pillDot}/> {it.name}: <strong>{it.qty}</strong></span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.4,fontStyle:"italic"}}>No EOD report submitted yesterday</div>
          )}
        </div>

        {/* Context flags — already applied to the total above */}
        {forecast.autoFlags.length>0 && (
          <div style={S.flagsAppliedCard}>
            <div style={S.flagsAppliedBadge}>✓ Already applied</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginBottom:8,marginTop:4,lineHeight:1.5}}>These factors are baked into the total above — no further action needed.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {forecast.autoFlags.map((f,i)=>(
                <span key={i} style={{...S.flag,...(f.positive?{}:{background:"rgba(255,179,71,0.06)",color:B.sunshine})}}>{f.emoji} {f.label} {f.mult>0?"+":""}{Math.round(f.mult*100)}%</span>
              ))}
              {override!==0&&<span style={S.flag}>🔧 Override {override>0?"+":""}{override}%</span>}
            </div>
          </div>
        )}

        {/* Wastage warning */}
        {forecast.wastageAdjustment && (
          <div style={{...S.infoBanner,borderColor:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.2)":"rgba(212,251,111,0.15)",background:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.05)":"rgba(212,251,111,0.04)",color:forecast.wastageAdjustment.type==="trim"?B.coral:B.wasabi}}>
            {forecast.wastageAdjustment.type==="trim"?"⚠":"ℹ️"} {forecast.wastageAdjustment.message}
          </div>
        )}

        {/* Min floor notice */}
        {forecast.forecast.minApplied && override===0 && (
          <div style={{...S.infoBanner,borderColor:"rgba(212,251,111,0.12)",background:"rgba(212,251,111,0.04)",color:B.wasabi}}>
            ℹ️ Minimum floor applied ({forecast.forecast.totalPortions} portions)
          </div>
        )}

        {/* Info panel */}
        {showInfo && forecast.calc && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>MODEL BREAKDOWN</div>
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.6,lineHeight:2}}>
              <div style={S.calcRow}><span>Last same-day (×{forecast.calc.lastSameDayWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.lastSameDaySignal}</strong></div>
              <div style={S.calcRow}><span>6wk avg (×{forecast.calc.sixWeekAvgWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.sixWeekAvgSignal}</strong></div>
              <div style={S.calcRow}><span>Baseline (×{forecast.calc.baselineWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.baselineSignal}</strong></div>
              <div style={{...S.calcRow,borderTop:"1px solid rgba(212,251,111,0.1)",paddingTop:4,color:B.wasabi}}><span>Weighted base</span><strong>{forecast.forecast.weightedBase}</strong></div>
              <div style={S.calcRow}><span>TGTG buffer</span><strong>+{forecast.forecast.tgtgBuffer}</strong></div>
              <div style={{...S.calcRow,color:B.wasabi}}><span>Total</span><strong>{totalPortions}</strong></div>
            </div>
          </div>
        )}

        {/* Override slider — 1% steps, ruled scale, direction labels */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"2px",textTransform:"uppercase"}}>MANUAL OVERRIDE</span>
            <span style={{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:override!==0?B.wasabi:B.kewpie,opacity:override!==0?1:0.3,transition:"color 0.2s"}}>{override>0?"+":""}{override}%</span>
          </div>
          <input type="range" min="-30" max="30" step="1" value={override} onChange={e=>setOverride(Number(e.target.value))} style={{width:"100%",accentColor:B.wasabi,display:"block"}}/>
          <div style={S.scaleRow}>
            <span style={S.scaleTickEdge}>−30%</span>
            <span style={S.scaleTick}>−15%</span>
            <span style={S.scaleTick}>0</span>
            <span style={S.scaleTick}>+15%</span>
            <span style={S.scaleTickEdge}>+30%</span>
          </div>
          <div style={S.overrideHint}>← less than forecast &nbsp;·&nbsp; more than forecast →</div>
        </div>

        {/* SKU breakdown — rolls adjustable, portions auto-derived */}
        {forecast.skuBreakdown && forecast.skuBreakdown.length>0 && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>PER-SKU BREAKDOWN</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.4,marginBottom:10,lineHeight:1.5}}>Adjust rolls (1 roll = 2 portions). The total above updates live.</div>
            <div style={S.skuColHeader}>
              <span style={S.skuColHeaderLeft}>Roll type</span>
              <span className="fc-sku-col-rolls" style={S.skuColHeaderRolls}>Rolls</span>
              <span className="fc-sku-col-portions" style={S.skuColHeaderPortions}>Portions</span>
            </div>
            {forecast.skuBreakdown.map(s=>{
              const base=getSkuBase(s.name);
              const adj=skuAdj[s.name]||0;
              const rolls=base+adj;
              const portions=rolls*2;
              return (
                <div key={s.name} className="fc-sku-row" style={S.skuRow}>
                  <div style={S.skuRowLeft}>
                    <span style={S.itemName}>{s.name}</span>{s.isSpecial&&<span style={{fontSize:"0.55rem",color:B.sunshine,fontWeight:700,marginLeft:6,letterSpacing:"1px"}}>NEW</span>}
                    <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.3}}>{s.pct.toFixed(1)}%</div>
                  </div>
                  <div className="fc-sku-ctrls" style={{...S.ctrls,width:140,justifyContent:"center"}}>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,-1)}>−</button>
                    <span style={{...S.cVal,...(adj!==0?{color:adj>0?B.wasabi:B.sunshine,opacity:1}:{})}}>{rolls}</span>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,1)}>+</button>
                  </div>
                  <div className="fc-sku-portions" style={S.skuPortionsVal}>{portions}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Confirm — disabled when already confirmed locally OR by someone else on a different device */}
        <button style={{...S.submitBtn,...(isConfirmed?{opacity:0.6}:{}),marginBottom:10}} onClick={handleConfirm} disabled={isConfirmed||confirming}>
          {confirming
            ? "Confirming…"
            : confirmed
              ? "✓ "+siteName+" Confirmed"
              : isRemotelyConfirmed
                ? "✓ Already confirmed"
                : (confirmedInfo && confirmedInfo.undone
                    ? "Resubmit "+siteName+" Forecast"
                    : "Confirm "+siteName+" Forecast")}
        </button>
        {/* Lane BG: honest secondary note when the Supabase mirror disagreed with the Sheet. */}
        {fcShadowNote && <div style={{fontSize:"0.72rem",color:B.sunshine,opacity:0.85,marginTop:2,marginBottom:8}}>ℹ️ {fcShadowNote}</div>}
      </div>)}
    </div>
  );
}

// ===========================================
// WASTAGE TRACKING
// ===========================================
function WastageTracking({backend,requestPin,wastageLog,setWastageLog,setDirty}){
  const [entries,setEntries] = useState({});
  const [reasons,setReasons] = useState({});
  const [openCats,setOpenCats] = useState({});
  const [submitting,setSubmitting] = useState(false);
  const [submitted,setSubmitted] = useState(false);
  // Honest save state (2026-09-12, Lane AX — area close). Since the Google write
  // was removed, wastage_events is the ONLY record, so the tile must never say
  // "logged" unless a row actually landed. null = nothing to show; "failed" = a
  // live-host write that did not save (keep the entries, let them tap again);
  // "skipped" = a preview/staging host where dual-run is off so nothing records.
  const [saveError,setSaveError] = useState(null);
  // Synchronous in-flight guard (2026-06-27) — see handleSubmit. `submitting`
  // state lands a render too late to block a quick second PIN entry from
  // logging the same wastage batch twice; this ref blocks re-entry instantly.
  const submittingRef = useRef(false);
  // Stable idempotency token for THIS batch (2026-09-12, Lane AX). The entry ids
  // (and so the wastage_events idem_key) must NOT change between a failed submit
  // and the retry — otherwise a write that landed but whose response was lost to a
  // network timeout would be re-inserted as a duplicate on "tap again" instead of
  // collapsing on idem_key. So the token is minted once per batch and reused for
  // every retry; it is cleared only when the form empties (a genuinely new batch)
  // or a save succeeds. See the retry test in wastageRows.test.mjs.
  const batchIdRef = useRef(null);
  const isPortrait = useOrientation();

  const REASONS = ["Expired","Damaged","Prep error","Overproduction","Spillage","Other"];
  const allIngredients = Object.values(INGREDIENTS).flat();
  const hasEntries = Object.values(entries).some(v=>v>0);

  // When the form empties (submitted, or every ingredient zeroed), the next batch
  // is genuinely new — drop the idempotency token so it isn't reused across two
  // unrelated submissions. While entries stay on screen (incl. after a failure)
  // the token is kept so a retry reuses the same idem_keys.
  useEffect(()=>{setDirty(hasEntries); if(!hasEntries) batchIdRef.current=null;},[hasEntries]);

  const adjust = (name,delta)=>{
    setEntries(prev=>{
      const cur = prev[name]||0;
      const next = Math.max(0,cur+delta);
      if(next===0){const n={...prev};delete n[name];return n}
      return {...prev,[name]:next};
    });
  };

  const toggleCat = (cat)=>setOpenCats(prev=>({...prev,[cat]:!prev[cat]}));

  const handleSubmit = ()=>{
    if(!hasEntries)return;
    requestPin(async(staffName,staffPin)=>{
      // In-flight guard (2026-06-27): block re-entry while a submit is pending.
      // The PIN modal closes one render before `submitting` state lands, so a
      // quick second PIN entry could otherwise log the same wastage batch twice
      // (the BOH sibling of the EOD double-submit fix). Reset in finally so a
      // failed write never locks the tile out.
      if(submittingRef.current)return;
      submittingRef.current=true;
      setSubmitting(true);
      setSaveError(null);
      try{
        const now = new Date();
        const time = now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0");
        const dateStr = now.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
        // Mint the batch idempotency token once and reuse it on every retry, so a
        // silently-succeeded write is collapsed on idem_key rather than duplicated.
        if(!batchIdRef.current) batchIdRef.current = Date.now()+"-"+Math.random().toString(36).slice(2,6);
        const batchId = batchIdRef.current;
        const newEntries = Object.entries(entries).filter(([,q])=>q>0).map(([name,quantity])=>({
          // Stable per (batch, ingredient): the same tap-again produces the same
          // id -> same idem_key -> the unique index collapses the retry to one row.
          id: batchId+"-"+name,
          ingredient: name,
          quantity,
          reason: reasons[name]||"Not specified",
          staffName,
          time,
          dateStr,
        }));

        // Supabase is now THE record (2026-09-12, Lane AX — area close). The old
        // fire-and-forget Apps Script POST was removed here: it put the staff PIN
        // on the wire, nothing anywhere read the Sheet copy, and its no-cors
        // response was unreadable so a failed write was silent. wastage_events
        // (migration 0060, on prod since July) is the only place a wastage tap
        // now lands — so we AWAIT the write and only say "logged" once a row has
        // actually landed. The staff PIN now leaves the device only for the
        // per-action login pass (ensurePass), never in a payload.
        //
        // dualRunEnabled() stays the ONE estate-wide switch (same gate as
        // sell-outs/stock): on the four kiosk hosts (or ?dualrun=on) it writes;
        // on a preview/staging host it is skipped exactly as before — but we now
        // surface that honestly rather than showing a ✓ for a save that never
        // happened. Wrapped in try/catch so the write can never throw into the tile.
        let result;
        try{
          result = await wastageSync.recordKitchenWastage({ siteKey: SITE.key, staffPin, entries: newEntries });
        }catch(e){ result = { ok:false, error:e }; }

        if(result && result.ok && !result.skipped){
          // A wastage_events row landed — now it is truthful to log it locally,
          // clear the form and show the ✓. Drop the batch token so the next
          // submission is a fresh batch (belt-and-braces; the empty-form effect
          // also clears it).
          batchIdRef.current=null;
          setWastageLog(prev=>[...prev,...newEntries]);
          setEntries({});setReasons({});setDirty(false);setSubmitted(true);
          setTimeout(()=>setSubmitted(false),3000);
        }else if(result && result.skipped){
          // Preview/staging host: dual-run is off, nothing recorded. Keep the
          // entries and say so plainly instead of a false ✓.
          setSaveError("skipped");
        }else{
          // A live-host write that did not save (offline, DB rejected, no login
          // pass). Keep the entries in the form so the kitchen can tap again.
          setSaveError("failed");
        }
      }finally{
        submittingRef.current=false;
        setSubmitting(false);
      }
    });
  };

  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>Ingredient Wastage</div>
        <div style={S.secDesc}>Log wasted ingredients. PIN required to submit.</div>
      </div>

      {/* Today's log summary */}
      {wastageLog.length>0 && (
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>TODAY'S LOG ({wastageLog.length} {wastageLog.length===1?"entry":"entries"})</div>
          {wastageLog.slice(-5).reverse().map(e=>(
            <div key={e.id} style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.6,lineHeight:1.8}}>
              {e.time} — {e.quantity}× {e.ingredient} ({e.reason}) — {e.staffName}
            </div>
          ))}
          {wastageLog.length>5 && <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.3,marginTop:4}}>...and {wastageLog.length-5} more</div>}
        </div>
      )}

      {/* Ingredient list by category */}
      {Object.entries(INGREDIENTS).map(([cat,items])=>(
        <div key={cat} style={{marginBottom:4}}>
          <button style={S.catHeader} onClick={()=>toggleCat(cat)}>
            <span>{cat}</span>
            <span style={{fontSize:"0.7rem",opacity:0.5}}>{openCats[cat]?"▲":"▼"} {items.filter(it=>entries[it]>0).length>0?`(${items.filter(it=>entries[it]>0).length})`:""}</span>
          </button>
          {openCats[cat] && items.map(item=>(
            <div key={item} style={{...S.row,...(entries[item]>0?{borderColor:"rgba(212,251,111,0.2)",background:"rgba(212,251,111,0.04)"}:{})}}>
              <div>
                <span style={S.itemName}>{item}</span>
                {entries[item]>0 && (
                  <div style={{marginTop:4}}>
                    <select value={reasons[item]||""} onChange={e=>setReasons(prev=>({...prev,[item]:e.target.value}))} style={{background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:6,color:B.kewpie,fontSize:"0.72rem",padding:"3px 6px",fontFamily:"Manrope,sans-serif"}}>
                      <option value="">Reason...</option>
                      {REASONS.map(r=>(<option key={r} value={r}>{r}</option>))}
                    </select>
                  </div>
                )}
              </div>
              <div style={S.ctrls}>
                <button style={S.cBtn} onClick={()=>adjust(item,-1)}>−</button>
                <span style={{...S.cVal,...(entries[item]>0?{color:B.wasabi,opacity:1}:{})}}>{entries[item]||0}</span>
                <button style={S.cBtn} onClick={()=>adjust(item,1)}>+</button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {submitted && <div style={{...S.infoBanner,borderColor:"rgba(111,175,143,0.3)",background:"rgba(111,175,143,0.08)",color:B.eucalyptus,marginTop:12}}>✓ Wastage logged successfully</div>}
      {saveError==="failed" && <div style={{...S.infoBanner,borderColor:"rgba(255,122,92,0.3)",background:"rgba(255,122,92,0.08)",color:B.coral,marginTop:12}}>Didn't save — check the wifi and tap Submit again. Your entries are still here.</div>}
      {saveError==="skipped" && <div style={{...S.infoBanner,borderColor:"rgba(212,251,111,0.15)",background:"rgba(212,251,111,0.04)",color:B.wasabi,marginTop:12}}>Preview build — not recorded. Wastage only saves on the kiosk hosts.</div>}

      <button style={{...S.submitBtn,marginTop:12,...(!hasEntries?{opacity:0.3,cursor:"default"}:{})}} onClick={handleSubmit} disabled={!hasEntries||submitting}>
        {submitting?"Submitting...":"Submit Wastage Log"}
      </button>
    </div>
  );
}

// ===========================================
// CATALOG THUMBNAIL — 2026-05-04 (next session)
// ===========================================
// Shared between StockRequestItemDetail and the kitchen-side
// InboundDeliveries running list. The thumb is a div (not a button) so it
// can sit inside a row that's itself a button without breaking HTML
// nesting; stopPropagation keeps the row's own onClick from firing when
// staff tap to zoom. Sizes were bumped ~30% (42→52, 44→56) after JJ
// flagged the existing thumbs as "barely readable" against retina tablets.
function CatalogThumb({item, imageUrl, size, onZoom, marginRight, showPlaceholder}){
  const px = size || 48;
  const wrap = {width:px,height:px,borderRadius:8,background:"rgba(254,250,240,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",marginRight:marginRight||0};
  if (!imageUrl) {
    if (!showPlaceholder) return null;
    return <div style={wrap}><span style={{fontSize:Math.round(px*0.42),opacity:0.4}}>📦</span></div>;
  }
  const open = (e)=>{ if(e){e.stopPropagation(); e.preventDefault();} onZoom && onZoom({item, imageUrl}); };
  return (
    <div role="button" tabIndex={0} aria-label={"View "+(item||"item")+" image"}
      onClick={open}
      onKeyDown={e=>{ if(e.key==='Enter'||e.key===' '){ open(e); } }}
      style={{...wrap,cursor:"zoom-in"}}>
      <img src={imageUrl} alt={item||""} draggable={false}
        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
        onError={e=>{
          const p = e.currentTarget.parentNode;
          if(!p)return;
          p.innerHTML = '<span style="font-size:'+Math.round(px*0.42)+'px;opacity:0.4">📦</span>';
        }}/>
    </div>
  );
}

// Catalog image lightbox — header strip + dark backdrop + full-size image
// + close ×. Tap backdrop to close, tap image does nothing. zIndex 700
// keeps it above any in-flight modal underneath.
function CatalogThumbLightbox({item, imageUrl, onClose}){
  if (!imageUrl) return null;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.88)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()}
        style={{maxWidth:720,width:"100%",maxHeight:"calc(100dvh - 48px)",background:"#fff",borderRadius:14,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 22px 48px rgba(0,0,0,0.5)"}}>
        <div style={{padding:"14px 18px",background:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,borderBottom:"1px solid rgba(0,57,44,0.15)"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>📦 Catalogue item</div>
            <div style={{fontFamily:"gelica,serif",fontSize:18,fontWeight:700,color:B.wasabi,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item||""}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,0.3)",color:B.wasabi,border:"none",cursor:"pointer",fontSize:16,fontWeight:700,lineHeight:1}}>×</button>
        </div>
        <div style={{flex:1,minHeight:0,background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={imageUrl} alt={item||""} draggable={false}
            style={{maxWidth:"100%",maxHeight:"calc(100dvh - 160px)",display:"block"}}
            onError={ev=>{ev.target.style.display="none";ev.target.parentNode.innerHTML='<div style="color:#fff;padding:40px;text-align:center;font-size:13px;opacity:0.7">Image could not load.</div>';}}/>
        </div>
      </div>
    </div>
  );
}

// ===========================================
// STOCK REQUEST RECEIVER
// ===========================================
// Photo-required Confirm Delivery modal — mounted only for ORDERED rows
// (admin pre-logged inbound, awaiting delivery). Staff MUST capture a
// photo to clear the row from the running list. Ports the housed
// Meadowhall pattern 1:1 so the kitchen receiver behaves identically.
function DeliveryConfirmModal({item, backend, site, requestPin, onClose, onConfirmed, onActionFailed, events, propagatedEvents, title, kitchenCoords}){
  const [photoDataUrl,setPhotoDataUrl] = useState(null);
  const [photoMime,setPhotoMime] = useState("image/jpeg");
  const [photoFilename,setPhotoFilename] = useState("");
  // 2026-05-12 (pm): EXIF + source detection (mirrors InboundDeliveries +
  // outbound DeliveryOrders). See InboundDeliveries handleFile for the
  // 3-signal resolution logic.
  const [capturedAt,setCapturedAt] = useState(null);
  const [gpsLat,setGpsLat] = useState(null);
  const [gpsLng,setGpsLng] = useState(null);
  const [exifPresent,setExifPresent] = useState(false);
  const [photoSource,setPhotoSource] = useState("unknown");
  const [note,setNote] = useState("");
  const [submitting,setSubmitting] = useState(false);
  const [error,setError] = useState("");
  const refMeta = item && item._orderedMeta || {};
  const refImage = refMeta.refImageUrl || "";
  const supplierRef = refMeta.supplierRef || "";
  // events prop optional — when provided, fire those actions sequentially
  // (each gets the photoUrl + note attached). Default = single stockDelivered
  // (preserves the original ORDERED-row flow). Used by OOS "New stock arrived"
  // to fire backInStock + photo, and by "+Collected" to fire backInStock then
  // stockDelivered.
  const eventsToFire = (events && events.length) ? events : [{action:"stockDelivered"}];

  // Source param ('camera' | 'library') comes from which input fired — more
  // reliable than time-gap heuristic which mis-classified library picks as
  // 'camera' when Samsung's picker reset file.lastModified to now on copy.
  const handleFile = async (file, source) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { setError("Photo too large — keep it under 6MB"); return; }
    setError("");
    setPhotoMime(file.type || "image/jpeg");
    setCapturedAt(null); setGpsLat(null); setGpsLng(null); setExifPresent(false); setPhotoSource("unknown");
    setPhotoSource(source === "camera" || source === "library" ? source : "unknown");
    let resolvedTs = null;
    let hasExifEvidence = false;
    try {
      if (window.exifr && typeof window.exifr.parse === "function") {
        const meta = await window.exifr.parse(file, { tiff: true, exif: true, gps: true, ifd0: true });
        if (meta) {
          const dt = meta.DateTimeOriginal || meta.CreateDate;
          if (dt) { resolvedTs = new Date(dt).getTime(); hasExifEvidence = true; }
          if (typeof meta.latitude === "number") { setGpsLat(meta.latitude); hasExifEvidence = true; }
          if (typeof meta.longitude === "number") setGpsLng(meta.longitude);
        }
      }
    } catch (e) { /* non-fatal */ }
    if (!resolvedTs && file.name) {
      const m = file.name.match(/(\d{4})(\d{2})(\d{2})[_-]?(\d{2})(\d{2})(\d{2})/);
      if (m) {
        const ts = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])).getTime();
        if (!isNaN(ts) && ts > 0) resolvedTs = ts;
      }
    }
    if (!resolvedTs && file.lastModified) resolvedTs = file.lastModified;
    if (resolvedTs) setCapturedAt(new Date(resolvedTs).toISOString());
    setExifPresent(hasExifEvidence);
    const reader = new FileReader();
    reader.onload = () => { setPhotoDataUrl(String(reader.result || "")); setPhotoFilename(file.name || "delivery.jpg"); };
    reader.onerror = () => setError("Could not read file");
    reader.readAsDataURL(file);
  };
  const retake = () => {
    setPhotoDataUrl(null); setPhotoFilename("");
    setCapturedAt(null); setGpsLat(null); setGpsLng(null); setExifPresent(false); setPhotoSource("unknown");
  };
  const canConfirm = !!photoDataUrl && !submitting;
  // Optimistic submit (2026-05-07): close modal + fire onConfirmed instantly
  // on PIN entry. Photo upload + lifecycle/propagated events fire in the
  // background. Mirrors the Meadowhall fix — was awaiting the upload
  // response which made the modal feel stuck (and silently swallowed
  // network errors via the catch). Photo still lands in Drive even on
  // slow uploads; backend audit can match by staff+item+timestamp if
  // photoUrl wasn't ready in time.
  const submit = () => {
    if (!canConfirm) return;
    requestPin(async (staffName, staffPin) => {
      const b64 = String(photoDataUrl || "").split(",")[1] || "";
      const today = new Date().toLocaleDateString("en-GB");
      // Lane BP (13 Sep 2026): ONE kitchen write-drop gate — Lane BE's
      // STOCK_WRITE_DROP_HOSTS (ships EMPTY). plan.sheetPost is false only on a
      // dropped host: the photo then goes straight to Supabase Storage (no
      // uploadDeliveryPhoto POST to Drive) and a missed upload is an honest
      // failure below, never a photo-less save. On every other host everything
      // in this function is today's code, untouched.
      const plan = deliveriesSync.planInboundSubmit({ dropOn: stockSync.stockWriteDropEnabled() });
      // OPTIMISTIC: close modal + fire onConfirmed instantly.
      onConfirmed && onConfirmed({photoUrl: "", staffName: staffName});
      // BACKGROUND: photo upload with 30s soft-timeout via AbortController.
      // 2026-05-19: bumped from 4.5s — iPhone-over-4G uploads from the
      // kitchen kept being killed mid-flight, dropping the photo before
      // it reached Drive. The chef's "I just confirmed it" prompts had
      // no photo in the audit folder because of this. 30s is a generous
      // ceiling — Drive writes typically complete in 2-5s, but slow
      // shopping-centre wifi/4G needs more headroom.
      const uploadPromise = plan.sheetPost ? (async () => {
        try {
          const ctl = (typeof AbortController !== "undefined") ? new AbortController() : null;
          const tmo = ctl ? setTimeout(()=>{ try { ctl.abort(); } catch(e){} }, 30000) : null;
          const res = await fetch(backend, {
            method: "POST",
            headers: {"Content-Type":"text/plain;charset=utf-8"},
            body: JSON.stringify({
              action: "uploadDeliveryPhoto",
              site: site,
              item: item.item,
              staffName, staffPin,
              base64: b64,
              mimeType: photoMime,
              filename: photoFilename,
              capturedAt: capturedAt,
              gpsLat: gpsLat,
              gpsLng: gpsLng,
              exifPresent: exifPresent,
              source: photoSource
            }),
            signal: ctl ? ctl.signal : undefined
          });
          if (tmo) clearTimeout(tmo);
          const data = await res.json().catch(()=>null);
          return (data && data.ok && data.url) ? data.url : "";
        } catch (e) { return ""; }
      })() : (async () => {
        // Lane BP ON path: Supabase Storage is the photo's home — the same fast
        // public-bucket upload the Inbound Deliveries tile already tries first.
        // null on ANY failure; handled honestly below, never a Drive fallback.
        // Review fix (13 Sep 2026): the same 30 s ceiling the Drive path has. The
        // modal has ALREADY closed optimistically, so a Storage upload that never
        // settles would mean no events, no alert, no revert — a green tick over a
        // dropped write. Past the ceiling it resolves null -> the honest-failure
        // path below (alert + revert); a late-but-successful upload just re-uses
        // its key on the retry.
        try {
          const upload = deliveriesSync.uploadDeliveryPhotoFast({ siteKey: site, item: item.item, staffPin, base64: b64, mime: photoMime, capturedAt });
          const ceiling = new Promise(resolve => setTimeout(() => resolve(null), 30000));
          return await Promise.race([upload, ceiling]);
        } catch (e) { return null; }
      })();
      // BACKGROUND: lifecycle + propagated events fire-and-forget once the
      // upload resolves (or times out) — photoUrl linked when available.
      // 2026-05-19: geofenced kitchen actions (backInStock, stockDelivered,
      // stockOutOfStock, stockRunningLow) need lat/lng on the body or the
      // backend rejects them with OUTSIDE_KITCHEN_GEOFENCE. kitchenCoords
      // is captured by the parent BEFORE this modal opens, so by the time
      // we're here the device's GPS has already been read.
      // 2026-05-24: parsed-response fires (was raw mode:"no-cors" loop —
      // silently swallowed every backend rejection including geofence
      // rejects). If ANY event fails for this item we revert the optimistic
      // close so the row reappears as pending on the next poll, and we
      // surface the error to the staff member. The modal closed
      // optimistically up at line 2757, so there's nothing to reopen — an
      // alert + revert is the right shape.
      uploadPromise.then(photoUrl => {
        // Lane BP ON path: no photo, no write. The Sheet path tolerates a missed
        // Drive upload (the row still lands photo-less and the audit matches it up
        // later); on a dropped host the Supabase row is the ONLY record of a
        // photo-required confirm, so a missing photo fails honestly — the same
        // revert + alert path a rejected event takes. Nothing claims saved.
        if (plan.supabaseAuthoritative && !photoUrl) {
          const fail = { ok: false, code: "PHOTO", message: "The delivery photo didn't upload — check the connection and try again." };
          try { if (typeof onActionFailed === "function") onActionFailed(item, fail); } catch(_){}
          try { alert(kitchenActionErrorMessage(fail, item.item)); } catch(_){}
          return;
        }
        // Lane CF (14 Sep 2026, closes BP-5): on the write-drop ON path, land the
        // deliveries_inbound evidence row right after ITS OWN stock event, AWAITED,
        // all-or-nothing with it — the same sequence the Inbound Deliveries tile
        // uses (Lane BP): stock event first, evidence second, either failing fails
        // the pair (the pair's result is what Promise.all below sees). planModalEvidence
        // is pure so the OFF path (never records — see the test) needs no window: on a
        // non-dropped host recordEvidence is always false, so this resolves to `res`
        // straight through, one extra microtask, the exact value postKitchenAction
        // already resolved — the OFF path's own call is untouched by this.
        const recordEvidenceIfOn = (res, body) => {
          const step = deliveriesSync.planModalEvidence({ supabaseAuthoritative: plan.supabaseAuthoritative, stockEventOk: !!(res && res.ok) });
          if (!step.recordEvidence) return res;
          // Same defensive site mapping BP's own review fix used for the tile: a
          // kitchen one-off can carry the literal "kitchen" (there is no such site —
          // the kitchen is `mcr`); everything else already carries a real site key.
          const evidenceSite = (body.site && body.site !== "kitchen") ? body.site : SITE.key;
          return deliveriesSync.recordInboundDelivery({
            siteKey: evidenceSite, action: body.action, item: body.item, kind: "stock",
            staffPin, note: body.note, photoUrl: body.photoUrl, base64: b64, mime: photoMime,
            capturedAt: body.capturedAt, gpsLat: body.gpsLat, gpsLng: body.gpsLng,
            exifPresent: body.exifPresent, captureSource: body.source, strictPhoto: true,
          }).then(ev => (ev && ev.ok) ? res : { ok: false, code: "supabase", message: "The delivery evidence didn't save — try again." });
        };
        const fires = [];
        eventsToFire.forEach(ev => {
          const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1;
          const body = {
            action: ev.action,
            site: site,
            date: today,
            item: item.item,
            staffName, staffPin,
            photoUrl: photoUrl,
            note: note,
            capturedAt: capturedAt,
            gpsLat: gpsLat,
            gpsLng: gpsLng,
            exifPresent: exifPresent,
            source: photoSource
          };
          if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
          fires.push(postKitchenAction(backend, body, isGeofenced).then(res => recordEvidenceIfOn(res, body)));
        });
        // Cross-site storeroom propagation — kitchen's shared-inventory model.
        (propagatedEvents || []).forEach(p => {
          const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(p.action) !== -1;
          const body = {
            action: p.action,
            site: p.site,
            date: today,
            item: item.item,
            staffName, staffPin,
            photoUrl: photoUrl,
            note: note,
            capturedAt: capturedAt,
            gpsLat: gpsLat,
            gpsLng: gpsLng,
            exifPresent: exifPresent,
            source: photoSource
          };
          if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
          fires.push(postKitchenAction(backend, body, isGeofenced).then(res => recordEvidenceIfOn(res, body)));
        });
        Promise.all(fires).then(results => {
          const firstFail = results.find(r => r && r.ok === false);
          if (!firstFail) return;
          // Tell the parent the action failed so it can revert the
          // optimistic mark / state it set when the modal closed.
          try {
            if (typeof onActionFailed === "function") onActionFailed(item, firstFail);
          } catch(_){}
          try { alert(kitchenActionErrorMessage(firstFail, item.item)); } catch(_){}
        });
      });
    });
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.78)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:600,padding:16,backdropFilter:"blur(2px)",overflow:"auto"}}>
      <div onClick={e=>e.stopPropagation()} style={{maxWidth:520,width:"100%",background:B.nori,borderRadius:18,overflow:"hidden",border:"1px solid rgba(212,251,111,0.18)",boxShadow:"0 18px 44px rgba(0,0,0,0.45)",color:B.kewpie,maxHeight:"calc(100dvh - 32px)",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"18px 20px",borderBottom:"1px solid rgba(212,251,111,0.1)"}}>
          <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>{title || "📦 Confirm delivery"}</div>
          <div style={{fontFamily:"gelica,serif",fontSize:22,fontWeight:700,color:B.kewpie,marginTop:4}}>{item.item}</div>
          {supplierRef && <div style={{fontSize:11,color:B.wasabi,opacity:0.85,marginTop:3,fontWeight:600}}>{supplierRef}</div>}
          <div style={{fontSize:12,color:B.kewpie,opacity:0.65,marginTop:4,lineHeight:1.4}}>Show the item clearly in the photo so the audit row has proof of what arrived.</div>
        </div>
        <div style={{padding:20,overflowY:"auto",flex:1,display:"flex",flexDirection:"column",gap:14}}>
          {error && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600}}>{error}</div>}
          {refImage && (
            <div style={{padding:"10px 12px",borderRadius:10,background:"rgba(212,251,111,0.05)",border:"1px solid rgba(212,251,111,0.18)"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#C8EAD8",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:8}}>📷 Reference from admin · what to expect</div>
              <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                <img src={refImage} alt="Reference" style={{width:84,height:84,objectFit:"cover",borderRadius:8,flexShrink:0,background:"rgba(0,0,0,0.3)"}} onError={ev=>{ev.target.style.display="none";}}/>
                <div style={{flex:1,fontSize:12,color:B.kewpie,opacity:0.7,lineHeight:1.45}}>
                  Compare against the delivery box. If the box doesn't match, tick "doesn't match" in the note below before confirming.
                  {refMeta.note ? <div style={{marginTop:4,fontStyle:"italic"}}>"{refMeta.note}"</div> : null}
                </div>
              </div>
            </div>
          )}
          {!photoDataUrl && (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,aspectRatio:"4/3",borderRadius:14,border:"2px dashed rgba(212,251,111,0.3)",background:"rgba(212,251,111,0.04)",cursor:"pointer",padding:20,textAlign:"center"}}>
                <span style={{fontSize:48}}>📷</span>
                <div style={{fontWeight:800,fontSize:"1rem",color:B.wasabi,letterSpacing:"0.02em"}}>Take photo of delivery</div>
                <div style={{fontSize:12,color:B.kewpie,opacity:0.65,maxWidth:280,lineHeight:1.4}}>Make sure the items + delivery box are clearly visible. Up to 6MB.</div>
                <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "camera")}/>
              </label>
              <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",borderRadius:10,cursor:"pointer",color:B.kewpie,opacity:0.7,fontSize:"0.82rem",fontWeight:600}}>
                <span style={{fontSize:18}}>🖼️</span>
                <span>Or upload from camera roll</span>
                <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files && e.target.files[0], "library")}/>
              </label>
            </div>
          )}
          {photoDataUrl && (
            <div>
              <div style={{position:"relative",borderRadius:14,overflow:"hidden",border:"1px solid rgba(212,251,111,0.25)",background:"rgba(0,0,0,0.4)"}}>
                <img src={photoDataUrl} alt="Delivery" style={{width:"100%",display:"block"}}/>
                <button type="button" onClick={retake} style={{position:"absolute",top:10,right:10,padding:"6px 12px",borderRadius:8,background:"rgba(0,28,22,0.78)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.3)",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",letterSpacing:"0.02em"}}>↻ Retake</button>
                <div style={{position:"absolute",bottom:10,left:10,right:10,display:"flex",gap:6,flexWrap:"wrap"}}>
                  {photoSource === "camera" && (
                    <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.02em"}}>📷 Taken in app</span>
                  )}
                  {photoSource === "library" && (
                    <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:"#FFD68A",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.02em"}}>🖼️ From camera roll</span>
                  )}
                  {capturedAt && (
                    <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.02em"}}>🕒 {new Date(capturedAt).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>
                  )}
                  {(gpsLat !== null && gpsLng !== null) && (
                    <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:B.wasabi,fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.02em"}}>📍 Location ✓</span>
                  )}
                  {photoSource === "unknown" && !capturedAt && (
                    <span style={{padding:"4px 10px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:"#FFD68A",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.02em"}}>⚠ No photo metadata</span>
                  )}
                </div>
              </div>
              <div style={{fontSize:11,color:B.kewpie,opacity:0.55,marginTop:6}}>📁 {photoFilename}</div>
            </div>
          )}
          <div>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:B.kewpie,opacity:0.7,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Note (optional)</label>
            <input value={note} onChange={e=>setNote(e.target.value.slice(0,200))} placeholder="e.g. 2 boxes short — supplier called" style={{padding:"10px 12px",borderRadius:8,border:"1px solid rgba(212,251,111,0.2)",background:"rgba(254,250,240,0.04)",color:B.kewpie,fontFamily:"inherit",fontSize:"0.85rem",width:"100%",boxSizing:"border-box"}}/>
          </div>
        </div>
        <div style={{padding:"14px 20px",borderTop:"1px solid rgba(212,251,111,0.1)",display:"flex",gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.12)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.75}}>Cancel</button>
          <button onClick={submit} disabled={!canConfirm} style={{flex:2,padding:"14px 16px",borderRadius:12,border:"none",background:canConfirm?B.wasabi:"rgba(212,251,111,0.2)",color:B.nori,fontFamily:"inherit",fontWeight:800,fontSize:"0.9rem",cursor:canConfirm?"pointer":"not-allowed",opacity:canConfirm?1:0.45,letterSpacing:"0.02em"}}>{submitting?"Confirming…":canConfirm?"✓ Confirm delivery":"📷 Photo required"}</button>
        </div>
      </div>
    </div>
  );
}

function StockRequestReceiver({backend,requestPin,stockCatalog,catalogSource}){
  // Build a name → catalog item lookup so each request card can show the
  // item's image and per-site default quantity without an extra fetch.
  const catalogByName = useMemo(()=>{
    const m = {};
    (stockCatalog||[]).forEach(i=>{ if(i&&i.name) m[i.name.toLowerCase()] = i; });
    return m;
  },[stockCatalog]);

  // Cross-site OOS + Running Low view — the storeroom is shared between TC
  // and Arndale so flagged items belong at the kitchen level, not split by
  // requesting site. Per-site cards below still show Pending + Collected
  // (those are kiosk-specific). Same modal pattern; back-in-stock routes
  // through the photo-required DeliveryConfirmModal so every clearance is
  // audit-trailed.
  // Skip CLOSED shops (Arndale shut 16 Aug 2026) — the kitchen shouldn't track a
  // shut shop's stock, and once the kitchen read is on Supabase we must not surface
  // a closed shop's frozen/divergent rows (they'd read as a spurious change). History
  // stays readable elsewhere; this is only the live cross-site storeroom loop.
  // 2026-09-19 (kitchen → Trafford merge): a site that collects from its own
  // storeroom (sites.js selfReceivesStock — Trafford since the kitchen moved in
  // on 24 Sep 2026) is NOT fulfilled from here any more; its till closes its own
  // requests with Collect…, and a second close path from this old host would
  // race it. This host only keeps working as a fallback until its closure PR.
  const SITE_KEYS = Object.keys(SITES).filter(k => !(SITES[k] && SITES[k].closed) && !((getSite(k) || {}).selfReceivesStock));
  const SITE_LABEL_SHORT = {tc:"TC", arndale:"ARN", meadowhall:"MWH", kitchen:"KIT"};
  const SITE_LABEL_FULL  = {tc:"Trafford Centre", arndale:"Arndale", meadowhall:"Meadowhall", kitchen:"Kitchen"};

  // Seed from localStorage cache so the STOREROOM STATUS banner renders
  // instantly on mount instead of waiting for the cross-site fetch round-trip.
  // Cache is updated after every successful fetch; data may be up to 5s
  // stale until the next poll completes.
  const XS_CACHE_KEY = "rolled.kitchen.crossSiteRequests";
  const [xsRequests, setXsRequests] = useState(()=>{
    try {
      const c = localStorage.getItem(XS_CACHE_KEY);
      if(c){ const parsed = JSON.parse(c); if(Array.isArray(parsed)) return parsed; }
    } catch(e) {}
    return [];
  });
  const [xsLastFetch, setXsLastFetch] = useState(null);
  const [, setXsTick] = useState(0);
  const [actionItem, setActionItem] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [runningLowChecked, setRunningLowChecked] = useState(false);
  const [deliveryConfirm, setDeliveryConfirm] = useState(null);
  const [thumbZoom, setThumbZoom] = useState(null);
  // Collapse state for the storeroom-wide sections — defaults to expanded
  // so flagged items are visible on first open. Tap the section header
  // to toggle. Persisted in sessionStorage so the choice survives a
  // back→forward navigation within a shift.
  const [xsOosCollapsed, setXsOosCollapsed] = useState(()=>{ try { return sessionStorage.getItem("rolled.kitchen.xsOosCollapsed") === "1"; } catch(e) { return false; } });
  const [xsLowCollapsed, setXsLowCollapsed] = useState(()=>{ try { return sessionStorage.getItem("rolled.kitchen.xsLowCollapsed") === "1"; } catch(e) { return false; } });
  useEffect(()=>{ try { sessionStorage.setItem("rolled.kitchen.xsOosCollapsed", xsOosCollapsed ? "1" : "0"); } catch(e) {} },[xsOosCollapsed]);
  useEffect(()=>{ try { sessionStorage.setItem("rolled.kitchen.xsLowCollapsed", xsLowCollapsed ? "1" : "0"); } catch(e) {} },[xsLowCollapsed]);
  const xsLoadRef = useRef(null);
  const xsIntervalRef = useRef(null);
  const xsLoadedAtRef = useRef(0);
  // Doorbell: bellTick bumps on every (debounced) realtime ring so the per-site
  // cards below can refetch outside their own poll cadence.
  const [bellTick,setBellTick] = useState(0);
  const bellTimerRef = useRef(null);
  // Soak indicator: which source served the last cross-site read on a FLIPPED
  // kitchen ('supabase' green / 'sheet' amber). Mirrors the sell-outs pattern.
  const [xsReadSource,setXsReadSource] = useState(null);

  // The active-catalog name set the Supabase reader filters emitted rows by —
  // the same rule the Apps Script feed applies server-side (fail-open when the
  // catalog hasn't loaded: null = no filter, exactly like the sheet handler
  // when ITS catalog read errors). ?action=stockCatalog returns ACTIVE items
  // only, so membership == active.
  const activeNamesForReader = ()=>{
    const names = Object.keys(catalogByName||{});
    return names.length ? new Set(names) : null;
  };

  const xsLoadAll = ()=>{
    // READ FLIP (gated OFF by default — stockReader.STOCK_READ_HOSTS is empty):
    // on a flipped kitchen the content comes from Supabase (with the CC-ordered
    // overlay + sheet fallback inside fetchStockRequests); everywhere else this
    // is byte-for-byte the old sheet fetch.
    // CATALOG-LOAD GUARD: only read Supabase once the ACTIVE CATALOG has loaded.
    // With a null catalog the reader fails OPEN (emits deactivated items) on a
    // cold load / cache miss / catalog-fetch failure, briefly surfacing ancient
    // discontinued items; the sheet path is server-filtered and always safe, so
    // stay on the sheet until activeNamesForReader() is populated.
    const flipped = stockReader.stockReadEnabled() && activeNamesForReader() !== null;
    Promise.all(SITE_KEYS.map(s => {
      const sheetUrl = backend+"?action=getStockRequests&site="+s;
      return (flipped
        ? stockReader.fetchStockRequests(s, { sheetUrl, activeItemNames: activeNamesForReader() })
        : fetch(sheetUrl).then(r=>r.ok?r.json():{requests:[]}))
        .then(d=>({ rows: ((d&&d.requests)||[]).map(r=>({...r, _site: s})), source: (d&&d._source)||null }))
        .catch(()=>({ rows: [], source: null }));
    })).then(results => {
      const flat = results.flatMap(r=>r.rows);
      setXsRequests(flat);
      setXsLastFetch(Date.now());
      xsLoadedAtRef.current = Date.now();
      try { localStorage.setItem(XS_CACHE_KEY, JSON.stringify(flat)); } catch(e) {}
      // Soak indicator is AGGREGATE, not last-writer-wins: GREEN only when EVERY
      // site is cleanly reading Supabase. Amber the moment any site is degraded —
      // whether it fell back to the sheet (source "sheet") OR went fully dark
      // (source null: Supabase AND its sheet fallback both failed, so its rows are
      // missing too). filter(Boolean) used to drop the dark case, letting a dead
      // site hide behind three healthy ones and read green; require-all-supabase
      // closes that. Only meaningful on a flipped kitchen.
      if (flipped) {
        const sources = results.map(r=>r.source);
        const allLive = sources.length > 0 && sources.every(s=>s==="supabase");
        setXsReadSource(sources.length ? (allLive ? "supabase" : "sheet") : null);
      }
    }).catch(()=>{});
  };
  xsLoadRef.current = xsLoadAll;

  useEffect(()=>{
    xsLoadAll();
    xsIntervalRef.current = setInterval(()=>{
      // Doorbell live + data fresh → the poll is only the safety net; skip.
      if(DOORBELL.live && Date.now() - xsLoadedAtRef.current < POLL_RELAXED_MS) return;
      xsLoadRef.current && xsLoadRef.current();
    }, REFRESH_INTERVAL_MS);
    return ()=>{ if(xsIntervalRef.current) clearInterval(xsIntervalRef.current); };
  },[]);

  // The doorbell itself (kitchen host only — stockSync gates it). A stock event
  // anywhere → refetch everything, debounced so a bulk submit (N rows in one
  // tap) rings once. Unsubscribes cleanly when the tile closes.
  useEffect(()=>{
    const unsub = stockSync.subscribeDoorbell({
      onRing: ()=>{
        if(bellTimerRef.current) clearTimeout(bellTimerRef.current);
        bellTimerRef.current = setTimeout(()=>{
          xsLoadRef.current && xsLoadRef.current();
          setBellTick(t=>t+1);
        }, 400);
      },
      onLive: (live)=>{ DOORBELL.live = live; },
    });
    return ()=>{ if(bellTimerRef.current) clearTimeout(bellTimerRef.current); DOORBELL.live = false; unsub(); };
  },[]);

  // Reset modal state when a row is opened/closed, pre-tick checkbox where
  // the row already carries running-low (so un-tick is the affirmative
  // restock-acknowledgement). Mirrors the per-site SiteStockRequests logic.
  useEffect(()=>{
    if(!actionItem){ setSelectedPrimary(null); setRunningLowChecked(false); return; }
    setSelectedPrimary(null);
    const closed = actionItem.closed || actionItem.status === "delivered" || actionItem.status === "collected";
    setRunningLowChecked(!closed && actionItem.flag === "running_low");
  },[actionItem]);
  useEffect(()=>{
    if (selectedPrimary === "out_of_stock") setRunningLowChecked(false);
  },[selectedPrimary]);

  const xsMerged = xsRequests.map(r=>{
    const local = getLocalMark(r.id);
    if(!local) return r;
    // 2026-05-06: stale-mark guard. Local marks are keyed by id (sheet
    // rowIndex). When an item later moves to a new id (e.g. delivered event
    // appended at a new row), the mark at the old id becomes orphaned. If
    // a different item later happens to be emitted with that orphaned id,
    // the stale mark would incorrectly apply. Verify item match before
    // applying — old marks (no _item) are treated as stale.
    if(!local._item || local._item !== r.item) return r;
    const fresh = localMarkIsFresh(local);
    const baseStatus = r.status||"requested";
    if(local.status && (baseStatus === "requested" || fresh)){
      return {...r, status: local.status, flag: local.flag !== undefined ? local.flag : r.flag, collectedBy: local.by, collectedAt: local.at, _resolvedToday: local._resolvedToday === true};
    }
    if(local.flag !== undefined && fresh){
      return {...r, flag: local.flag};
    }
    if(local.flag !== undefined && baseStatus === "requested"){
      return {...r, flag: local.flag};
    }
    return r;
  });
  const xsOos = xsMerged.filter(r => r.flag === "out_of_stock" && isRequestVisible(r, catalogByName)).sort(byOldestRequested);
  const xsLow = xsMerged.filter(r => r.flag === "running_low" && isRequestVisible(r, catalogByName)).sort(byOldestRequested);

  const openCollectAction = (item) => {
    if (item && item.ordered) {
      setDeliveryConfirm({...item, _orderedMeta: {refImageUrl: item.refImageUrl||"", supplierRef: item.supplierRef||"", eta: item.eta||"", note: item.adminNote||""}, _events: [{action:"stockDelivered"}], _title: "📦 Confirm delivery"});
    } else {
      setActionItem(item);
    }
  };

  const primaryOptions = (item)=>{
    const isClosed = item.closed || item.status === "delivered" || item.status === "collected";
    if (isClosed) {
      if (item.flag === "running_low") {
        return [
          {key:"replenished", label:"🪫 Replenished stock", sub:"Store room is restocked — clears the running-low flag. Item was already collected.", tone:"green"}
        ];
      }
      return [
        {key:"back_in_stock", label:"✓ New stock arrived", sub:"Store room restocked — clears the flag and removes it from the list.", tone:"green"}
      ];
    }
    if (item.flag === "out_of_stock") {
      return [
        {key:"back_in_stock",           label:"✓ New stock arrived",             sub:"Store room restocked — clears the flag. Kiosk still needs it.", tone:"green"},
        {key:"back_in_stock_collected", label:"✓ New stock arrived + Collected", sub:"Stock is back AND I brought it to the kiosk — closes the request.", tone:"primary"}
      ];
    }
    if (item.flag === "running_low") {
      return [
        {key:"collected",    label:"✓ Collected",         sub:"Brought back to the kiosk — closes the request. Running-low flag stays.", tone:"primary"},
        {key:"out_of_stock", label:"❗ Out of Stock",      sub:"Store room is now empty — switches the flag from low to OOS.", tone:"red"},
        {key:"replenished",  label:"🪫 Replenished stock", sub:"Store room is restocked — clears the running-low flag. Item still needs collecting.", tone:"green"}
      ];
    }
    return [
      {key:"collected",    label:"✓ Collected",   sub:"Brought back to the kiosk — closes the request.", tone:"primary"},
      {key:"out_of_stock", label:"❗ Out of Stock", sub:"Store room was empty — keeps the request visible with a red flag.", tone:"red"}
    ];
  };

  const eventsForConfirm = ()=>{
    if (!actionItem) return [];
    const events = [];
    const isFlowB = actionItem.flag === "out_of_stock";
    const isFlowA = !isFlowB;
    const wasPreTicked = actionItem.flag === "running_low";

    if (selectedPrimary === "collected") {
      const keepLow = wasPreTicked;
      events.push({action:"stockDelivered", closing:true, newFlag: keepLow ? "running_low" : null, localStatus:"collected"});
    }
    else if (selectedPrimary === "out_of_stock")
      events.push({action:"stockOutOfStock", closing:false, newFlag:"out_of_stock", localStatus:null});
    else if (selectedPrimary === "back_in_stock")
      events.push({action:"backInStock",    closing:false, newFlag:null, localStatus:"requested"});
    else if (selectedPrimary === "back_in_stock_collected") {
      events.push({action:"backInStock",    closing:false, newFlag:null, localStatus:"requested"});
      events.push({action:"stockDelivered", closing:true,  newFlag:null, localStatus:"collected"});
    }
    else if (selectedPrimary === "replenished") {
      // 2026-05-06: optimistic localStatus depends on the row's current
      // closed state. Open + running_low → row goes back to PENDING (still
      // owed). Closed + running_low → stays 'collected' (already delivered).
      const wasClosed = actionItem.closed === true || actionItem.status === "delivered" || actionItem.status === "collected";
      events.push({action:"backInStock", closing:false, newFlag:null, localStatus: wasClosed ? "collected" : "requested"});
    }

    if (isFlowA && selectedPrimary !== "out_of_stock" && selectedPrimary !== "replenished") {
      if (runningLowChecked && !wasPreTicked) {
        events.push({action:"stockRunningLow", closing:false, newFlag:"running_low", localStatus:null});
      } else if (!runningLowChecked && wasPreTicked) {
        // Standalone un-tick — same lifecycle as Replenished (backInStock).
        const wasClosedUntick = actionItem.closed === true || actionItem.status === "delivered" || actionItem.status === "collected";
        events.push({action:"backInStock", closing:false, newFlag:null, localStatus: wasClosedUntick ? "collected" : "requested"});
      } else if (runningLowChecked && wasPreTicked && selectedPrimary === "collected") {
        events.push({action:"stockRunningLow", closing:false, newFlag:"running_low", localStatus:null});
      }
    }
    return events;
  };

  const _isFlowB_forConfirm = actionItem && actionItem.flag === "out_of_stock";
  const runningLowWasPreTicked = actionItem && actionItem.flag === "running_low";
  const runningLowChanged = runningLowChecked !== runningLowWasPreTicked;
  const canConfirm = _isFlowB_forConfirm
    ? !!selectedPrimary
    : (!!selectedPrimary || runningLowChanged);

  const sendActionSequence = (item, events)=>{
    if (!events.length) return;
    // 2026-05-19: kitchen-only events (stockDelivered + storeroom flags) must
    // happen at the central kitchen. Capture GPS BEFORE the PIN prompt so the
    // user gets a clear blocker if location is denied/unavailable instead of
    // a confusing post-PIN failure.
    const needsGeofence = events.some(ev => KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1);
    const proceedAfterCoords = (kitchenCoords)=>requestPin(async(staffName,staffPin)=>{
      const now = new Date();
      const timeStr = now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0");
      const finalFlag = (() => {
        for (var i = events.length - 1; i >= 0; i--) {
          if (events[i].newFlag !== undefined) return events[i].newFlag;
        }
        return null;
      })();
      const finalLocalStatus = (() => {
        for (var i = events.length - 1; i >= 0; i--) {
          if (events[i].localStatus) return events[i].localStatus;
        }
        return null;
      })();
      const closingEvent = events.find(e=>e.closing);
      const isResolving = !!closingEvent;
      if (finalLocalStatus) {
        setLocalMark(item.id, {status: finalLocalStatus, flag: finalFlag, by: staffName, at: timeStr, _resolvedToday: isResolving, _item: item.item});
      } else {
        setLocalMark(item.id, {flag: finalFlag, _item: item.item});
      }
      setXsTick(t=>t+1);
      const today = new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"2-digit",year:"numeric"});

      // 2026-05-06: cross-site storeroom propagation. The kitchen storeroom
      // is a shared inventory, so flag changes (OOS / Running Low / Back in
      // Stock) at one site should also write to any matching open request
      // at the OTHER site. stockDelivered + stockRunningLow audit-only
      // propagations stay per-kiosk (collecting at one kiosk doesn't
      // collect at the other).
      const STOREROOM_WIDE_ACTIONS = ["stockOutOfStock", "stockRunningLow", "backInStock"];
      const findOtherSiteMatches = (action, newFlag) => {
        return xsRequests.filter(r =>
          r && r.item === item.item &&
          r._site && r._site !== item._site &&
          !r.closed &&
          (action === "backInStock"
            ? !!r.flag                          // clearing — must currently have a flag
            : action === "stockOutOfStock"
              ? r.flag !== "out_of_stock"       // setting OOS — skip if already OOS
              : action === "stockRunningLow"
                ? r.flag !== "running_low"      // setting RL — skip if already RL
                : false)
        );
      };

      const fireOne = (body, isGeofenced, revertId, itemLabel)=>{
        postKitchenAction(backend, body, isGeofenced).then(res=>{
          if (!res.ok) {
            // Backend rejection of any flavour — geofence, network, role gate,
            // 500. Revert the optimistic mark so the row reappears as pending
            // and surface the message instead of silently letting the item
            // stick (the original silent-fail caused items to ghost on the
            // collect list for days — see Notion postmortem 2026-05-24).
            if (revertId) { setLocalMark(revertId, null); setXsTick(t=>t+1); }
            try { alert(kitchenActionErrorMessage(res, itemLabel)); } catch(_){}
          }
        });
      };

      events.forEach(ev => {
        const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1;
        const baseBody = {
          action: ev.action,
          site: item._site,
          date: today,
          item: item.item,
          requestId: item.id,
          staffName, staffPin,
          requester_type: item.requester_type||"FOH",
          destination: "kitchen"
        };
        if (isGeofenced && kitchenCoords) { baseBody.lat = kitchenCoords.lat; baseBody.lng = kitchenCoords.lng; baseBody.accuracy = kitchenCoords.accuracy; }
        fireOne(baseBody, isGeofenced, item.id, item.item);

        // Storeroom-wide propagation
        if (STOREROOM_WIDE_ACTIONS.indexOf(ev.action) !== -1) {
          const otherRows = findOtherSiteMatches(ev.action, ev.newFlag);
          otherRows.forEach(otherRow => {
            // Optimistic local mark for the other-site row
            const otherFlag = ev.newFlag !== undefined ? ev.newFlag : null;
            if (ev.localStatus) {
              setLocalMark(otherRow.id, {
                status: ev.localStatus,
                flag: otherFlag,
                by: staffName,
                at: timeStr,
                _resolvedToday: false,
                _item: otherRow.item
              });
            } else {
              setLocalMark(otherRow.id, {flag: otherFlag, _item: otherRow.item});
            }
            const otherBody = {
              action: ev.action,
              site: otherRow._site,
              date: today,
              item: otherRow.item,
              requestId: otherRow.id,
              staffName, staffPin,
              requester_type: otherRow.requester_type||"FOH",
              destination: "kitchen"
            };
            if (isGeofenced && kitchenCoords) { otherBody.lat = kitchenCoords.lat; otherBody.lng = kitchenCoords.lng; otherBody.accuracy = kitchenCoords.accuracy; }
            fireOne(otherBody, isGeofenced, otherRow.id, otherRow.item);
          });
        }
      });
    });

    if (needsGeofence) {
      captureKitchenCoords()
        .then(coords => proceedAfterCoords(coords))
        .catch(err => { try { alert(err && err.message ? err.message : "Location required for this action."); } catch(_){} });
    } else {
      proceedAfterCoords(null);
    }
  };

  const confirmAction = ()=>{
    const events = eventsForConfirm();
    if (!events.length) return;
    const item = actionItem;
    // Photo-required path for any back_in_stock variant — staff must
    // capture a photo of the delivery to clear the OOS flag. Same path
    // covers "+ Collected" so a single photo backs both the restock and
    // the collection event.
    if (selectedPrimary === "back_in_stock" || selectedPrimary === "back_in_stock_collected" || selectedPrimary === "replenished") {
      // Strip metadata fields the backend doesn't need; preserve flow
      // events for DeliveryConfirmModal to fire with the photoUrl.
      // 2026-05-06: replenished folded in — same lifecycle event (backInStock),
      // same audit requirement.
      const photoEvents = events.map(ev => ({action: ev.action}));
      // 2026-05-06: cross-site propagation through the photo path. The
      // storeroom is shared inventory — when the kitchen confirms new
      // stock arrived for site X, any OPEN flagged row at site Y for the
      // same item should also clear (one photo, one PIN, one storeroom
      // restock). Without this branch, photo-path back_in_stock skips the
      // sendActionSequence propagation entirely.
      const photoPropagated = [];
      events.forEach(ev => {
        if (ev.action === "backInStock") {
          (xsRequests || []).forEach(r => {
            if (r && r.item === item.item &&
                r._site && r._site !== item._site &&
                !r.closed && !!r.flag) {
              photoPropagated.push({action: "backInStock", site: r._site, _otherRowId: r.id});
            }
          });
        }
      });
      // 2026-05-19: backInStock + stockDelivered are geofenced. Capture
      // browser GPS BEFORE opening the photo modal — if GPS is denied or
      // unavailable, block the user with a clear error instead of letting
      // them photo-confirm an action the backend will silently reject
      // (because the no-cors event POSTs can't read the rejection).
      const needsGeofence = [...photoEvents, ...photoPropagated]
        .some(ev => KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1);
      const openModalWithCoords = (kitchenCoords)=>{
        setActionItem(null);
        setDeliveryConfirm({
          ...item,
          _orderedMeta: {refImageUrl:"", supplierRef:"", eta:"", note:""},
          _events: photoEvents,
          _propagatedEvents: photoPropagated,
          _selectedPrimary: selectedPrimary,
          _kitchenCoords: kitchenCoords,
          _title: "📦 New stock arrived"
        });
      };
      if (needsGeofence) {
        captureKitchenCoords()
          .then(coords => openModalWithCoords(coords))
          .catch(err => { try { alert(err && err.message ? err.message : "Location required for this action."); } catch(_){} });
      } else {
        openModalWithCoords(null);
      }
      return;
    }
    setActionItem(null);
    sendActionSequence(item, events);
  };

  // Custom inline render for cross-site rows. Instead of reusing
  // StockRequestItemDetail (which embeds a redundant "🪫 Running low —
  // notify to reorder" annotation that overlapped with the right-side
  // pills on narrow widths), we lay out two distinct rows:
  //   1. Thumbnail + (name / qty / dateStr) + right-aligned site/collected pills.
  //   2. Optional reorder-notify line, full-width below — never overlaps.
  // The cross-site section header (❗ OUT OF STOCK / 🪫 RUNNING LOW)
  // already communicates the flag, so the inline annotation can be
  // tightened to just the responsible-person prompt.
  const renderXsRow = (r, opts)=>{
    const variant = opts && opts.variant;
    const catEntry = catalogByName ? catalogByName[(r.item||"").toLowerCase()] : null;
    const isClosedRow = r.status === "delivered" || r.status === "collected" || r.status === "received" || r.closed;
    const rowBaseStyle = {
      background: variant === "oos" ? "rgba(255,122,92,0.05)"
                : variant === "collected" ? "rgba(111,175,143,0.05)"
                : "rgba(241,197,68,0.05)",
      border: "1px solid " + (variant === "oos" ? "rgba(255,122,92,0.25)"
                            : variant === "collected" ? "rgba(111,175,143,0.25)"
                            : "rgba(241,197,68,0.22)"),
      borderRadius: 12,
      padding: "10px 12px",
      marginBottom: 6,
      cursor: variant === "collected" ? "default" : "pointer",
      transition: "all 0.12s ease",
      display: "flex",
      flexDirection: "column",
      gap: 8
    };
    const leftIcon = variant === "oos" ? "❗" : variant === "collected" ? "✅" : "🪫";
    const imageUrl = catEntry && catEntry.imageUrl;
    const qty = catEntry && (catEntry.siteDefaults && catEntry.siteDefaults[r._site] !== undefined && catEntry.siteDefaults[r._site] !== ""
          ? catEntry.siteDefaults[r._site]
          : (catEntry.defaultQty !== null && catEntry.defaultQty !== "" ? catEntry.defaultQty : null));
    const unit = catEntry && catEntry.unit ? String(catEntry.unit).trim() : "";
    const sitePillStyle = {fontSize:"0.62rem",fontWeight:800,letterSpacing:"0.05em",padding:"3px 8px",borderRadius:999,whiteSpace:"nowrap",background:"rgba(212,251,111,0.08)",color:B.wasabi,border:"1px solid rgba(212,251,111,0.25)"};
    const collectedPillStyle = {fontSize:"0.62rem",fontWeight:800,letterSpacing:"0.05em",padding:"3px 8px",borderRadius:999,whiteSpace:"nowrap",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,border:"1px solid rgba(111,175,143,0.3)"};
    // Responsible-person prompt — show the ROLE the catalogue carries (Adam / JJ /
    // the kitchen (BOH)), never a PIN. responsibleLabel is an allowlist on top of
    // cleanResponsibleRole, so a blank/unknown role — or an opaque token — yields
    // "" and the line below stays hidden. No staffPinsCache lookup, no PIN roster.
    const responsibleLabel = stockCatalogSrc.responsibleLabel(catEntry && catEntry.responsibleRole);
    return (
      <div key={(r.id||r.item)+"@"+r._site} style={rowBaseStyle} onClick={variant === "collected" ? undefined : ()=>openCollectAction(r)}>
        {/* Top row: icon + thumb + name/dateStr + right-aligned pills */}
        <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
          <div style={{width:22,flexShrink:0,display:"flex",justifyContent:"center",fontSize:"1.05rem"}}>{leftIcon}</div>
          <CatalogThumb item={r.item} imageUrl={imageUrl} size={48} onZoom={setThumbZoom} showPlaceholder/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:B.kewpie,fontSize:"0.86rem",fontWeight:700,letterSpacing:"-0.01em",display:"flex",alignItems:"baseline",gap:6,flexWrap:"wrap"}}>
              <span style={{whiteSpace:"normal",overflowWrap:"anywhere"}}>{r.item}</span>
              {qty != null && qty !== "" && (
                <span style={{fontSize:"0.62rem",fontWeight:700,color:B.wasabi,background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.3)",padding:"1px 7px",borderRadius:999,letterSpacing:"0.02em"}}>Bring {qty}{unit?" "+unit:""}</span>
              )}
            </div>
            <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.5,marginTop:2}}>
              {variant === "collected"
                ? (()=>{ const at = fmtDeliveryClock(r.collectedAt || r.deliveredAt); return <>Collected by {r.collectedBy || r.deliveredBy || "Staff"}{at ? " · " + at : ""}{r.dateStr ? " · " + r.dateStr : ""}</>; })()
                : <>{(r.requestedBy || r.staffName || "Staff")} · {r.time || "--:--"}{r.dateStr ? " · " + r.dateStr : ""}</>
              }
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
            <span style={sitePillStyle}>{SITE_LABEL_SHORT[r._site]||r._site}</span>
            {isClosedRow && (r.collectedBy || r.deliveredBy) && <span style={collectedPillStyle}>✅ collected</span>}
          </div>
        </div>
        {/* Bottom row: notify-to-reorder prompt — own line, no overlap. Hidden
            when the catalogue carries no recognised role (blank/unknown) to keep
            the row tight, exactly as before — the difference is the target is now
            the role, not a PIN-resolved name. */}
        {responsibleLabel && (
          <div style={{fontSize:"0.7rem",fontWeight:600,color:variant==="oos"?B.coral:"#F1C544",borderTop:"1px solid rgba(254,250,240,0.06)",paddingTop:6,lineHeight:1.4}}>
            Notify <strong style={{color:B.kewpie}}>{responsibleLabel}</strong> to reorder
          </div>
        )}
      </div>
    );
  };

  // Third badge state (Lane CP): catalogBadge() turns "none" (neither source
  // answered) into its own red warning, instead of the old two-way ternary
  // mis-reading it as "Fallback — item list from Google Sheet" (which implies
  // Google DID answer). Same lookup foh.jsx uses, so the words always match.
  // Does NOT touch xsReadSource/stockReader below — a different read-flip.
  const catalogBadgeInfo = stockCatalogSrc.catalogBadge(catalogSource);
  return (
    <div className="sr-page" style={S.content}>
      {/* Cross-site OOS + Running Low — kitchen storeroom is one shared
          inventory across both kiosks, so flagged items belong here, not
          split by per-site card. Each section is collapsible (tap header
          to toggle) — both default to expanded so flagged items are
          visible without an extra tap. */}
      {(xsOos.length > 0 || xsLow.length > 0) && (
        <div style={{marginBottom:18,padding:"14px 12px",borderRadius:14,background:"rgba(254,250,240,0.025)",border:"1px solid rgba(212,251,111,0.1)"}}>
          <div style={{fontFamily:"gelica,serif",fontSize:"0.95rem",fontWeight:700,color:B.kewpie,letterSpacing:"-0.01em",marginBottom:10,opacity:0.85,textAlign:"center"}}>STOREROOM STATUS</div>
          {/* Soak indicator — renders ONLY on a flipped kitchen so staff (and
              Adam's PIN'd pre-merge check) can see at a glance which system is
              serving the running list. Green = Supabase live; amber = the
              reader fell back to the sheet (greppable [stock-read] warn). */}
          {stockReader.stockReadEnabled()&&xsReadSource&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:xsReadSource==="supabase"?"#6FAF8F":"#FF7A5C"}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{xsReadSource==="supabase"?"Live — reading stock from Supabase":"⚠ Fallback — reading stock from Google Sheet"}</div>}
          {stockCatalogSrc.catalogReadEnabled()&&catalogBadgeInfo&&<div style={{fontSize:"0.68rem",fontWeight:600,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:catalogBadgeInfo.tone}}><span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>{catalogBadgeInfo.text}</div>}
          {xsOos.length > 0 && (<>
            <button type="button" onClick={()=>setXsOosCollapsed(v=>!v)} style={{...S.secTitle,marginBottom:6,color:"#FF9B83",background:"transparent",border:"none",fontFamily:"inherit",cursor:"pointer",width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0"}}>
              <span>❗ OUT OF STOCK · {xsOos.length}</span>
              <span style={{fontSize:"0.62rem",fontWeight:700,opacity:0.7,letterSpacing:"0.05em"}}>{xsOosCollapsed ? "Show" : "Hide"}</span>
            </button>
            {!xsOosCollapsed && xsOos.map(r => renderXsRow(r, {variant:"oos"}))}
          </>)}
          {xsLow.length > 0 && (<>
            <button type="button" onClick={()=>setXsLowCollapsed(v=>!v)} style={{...S.secTitle,marginTop:xsOos.length>0?14:0,marginBottom:6,color:"#F1C544",background:"transparent",border:"none",fontFamily:"inherit",cursor:"pointer",width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0"}}>
              <span>🪫 RUNNING LOW · {xsLow.length}</span>
              <span style={{fontSize:"0.62rem",fontWeight:700,opacity:0.7,letterSpacing:"0.05em"}}>{xsLowCollapsed ? "Show" : "Hide"}</span>
            </button>
            {!xsLowCollapsed && xsLow.map(r => renderXsRow(r, {variant:"running_low"}))}
          </>)}
        </div>
      )}

      {/* Compute parent-level live + collected-today derived sets. Live = open
          rows (excluding flagged-OOS which lives in the storeroom panel above)
          AND open running-low rows (which sit in per-site PENDING/RUNNING LOW).
          Used to drive both the empty-state banner and the COLLECTED TODAY
          section below. 2026-05-06. */}
      {(()=>{
        const today = (()=>{const d=new Date();return ("0"+d.getDate()).slice(-2)+"/"+("0"+(d.getMonth()+1)).slice(-2)+"/"+d.getFullYear();})();
        const xsLiveOpen = xsMerged.filter(r => {
          const isClosed = r.closed === true || r.status === "delivered" || r.status === "collected" || r.status === "received";
          if (isClosed) return false;
          if (r.flag === "out_of_stock") return false;
          return true;
        });
        const xsCollectedToday = xsMerged.filter(r => {
          if (r.flag) return false;
          if (!isRequestVisible(r, catalogByName)) return false;
          const closed = r.closed === true || r.status === "delivered" || r.status === "collected" || r.status === "received";
          if (!closed) return false;
          return r._resolvedToday === true || r.resolvedDate === today || r.dateStr === today;
        }).sort(byOldestRequested);
        return (
          <>
            {xsLiveOpen.length === 0 ? (
              /* Combined empty state — clearer than two per-site "All clear"
                 pills when neither kiosk has anything pending. */
              <div style={{padding:"22px 16px",borderRadius:14,background:"rgba(212,251,111,0.04)",border:"1px dashed rgba(212,251,111,0.2)",textAlign:"center"}}>
                <div style={{fontSize:"1.6rem",marginBottom:6}}>✓</div>
                <div style={{fontFamily:"gelica,serif",fontSize:"1.05rem",fontWeight:700,color:B.kewpie,letterSpacing:"-0.01em"}}>No items currently requested</div>
                <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.55,marginTop:4}}>{SITE_KEYS.length===0 ? "Trafford collects from its own storeroom since 24 Sep 2026 — its requests are closed on the Trafford app, not here." : "Trafford is all clear. New requests appear here live."}</div>
              </div>
            ) : (
              <div className="sr-grid">
                {Object.entries(SITES).filter(([key])=>SITE_KEYS.indexOf(key)!==-1).map(([key,s])=>(
                  <SiteStockRequests key={key} site={key} siteName={s.name} backend={backend} requestPin={requestPin} catalogByName={catalogByName} hideFlaggedSections={true} hideCollectedToday={true} bellTick={bellTick}/>
                ))}
              </div>
            )}

            {/* COLLECTED TODAY — combined across all sites, rendered below the
                live per-site lists so kitchen staff don't have to scroll past
                TC's collected items to see Arndale's still-pending requests.
                Each row shows a site pill so the kitchen knows which kiosk it
                went to. 32px gap from the live area for clear separation.
                2026-05-06. */}
            {xsCollectedToday.length > 0 && (
              <div style={{marginTop:32,paddingTop:18,borderTop:"1px solid rgba(254,250,240,0.08)"}}>
                <div style={{padding:"14px 12px",borderRadius:14,background:"rgba(111,175,143,0.04)",border:"1px solid rgba(111,175,143,0.18)"}}>
                  <div style={{fontFamily:"gelica,serif",fontSize:"0.95rem",fontWeight:700,color:B.kewpie,letterSpacing:"-0.01em",marginBottom:10,opacity:0.85,textAlign:"center"}}>
                    ✅ COLLECTED TODAY · {xsCollectedToday.length}
                  </div>
                  {xsCollectedToday.map(r => renderXsRow(r, {variant:"collected"}))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Action modal — Meadowhall housed-model port. */}
      {actionItem && (()=>{
        const isClosed = actionItem.closed || actionItem.status === "delivered" || actionItem.status === "collected";
        const isFlowB = actionItem.flag === "out_of_stock";
        const opts = primaryOptions(actionItem);
        const catEntry = catalogByName ? catalogByName[(actionItem.item||"").toLowerCase()] : null;
        const img = catEntry && catEntry.imageUrl;
        const qty = catEntry && (catEntry.siteDefaults && catEntry.siteDefaults[actionItem._site] !== undefined && catEntry.siteDefaults[actionItem._site] !== ""
              ? catEntry.siteDefaults[actionItem._site]
              : (catEntry.defaultQty !== null && catEntry.defaultQty !== "" ? catEntry.defaultQty : null));
        const unit = catEntry && catEntry.unit ? String(catEntry.unit).trim() : "";
        const toneStyle = (tone, selected) => {
          const styles = {
            primary: {bg:"rgba(212,251,111,0.15)", border:"1px solid rgba(212,251,111,0.45)", fg:B.wasabi},
            red:     {bg:"rgba(255,122,92,0.12)",  border:"1px solid rgba(255,122,92,0.3)",   fg:"#FF9B83"},
            green:   {bg:"rgba(111,175,143,0.14)", border:"1px solid rgba(111,175,143,0.3)",  fg:"#9DD0B0"}
          };
          if (!selected) return {bg:"transparent", border:"1px solid rgba(212,251,111,0.12)", fg:B.kewpie};
          return styles[tone] || styles.primary;
        };
        const runningLowDisabled = isClosed || (selectedPrimary === "out_of_stock");
        return (
          <div role="dialog" aria-modal="true" onClick={()=>setActionItem(null)} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.72)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:600,padding:20,backdropFilter:"blur(2px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:440,maxHeight:"calc(100vh - 40px)",overflowY:"auto",background:"#002822",borderRadius:20,padding:"22px 22px 24px",color:B.kewpie,boxShadow:"0 12px 48px rgba(0,0,0,0.5)",animation:"fadeUp 0.22s ease both"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <CatalogThumb item={actionItem.item} imageUrl={img} size={64} onZoom={setThumbZoom}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"gelica,serif",fontSize:"1.2rem",fontWeight:700,letterSpacing:"-0.01em"}}>{actionItem.item}</div>
                  <div style={{fontSize:"0.75rem",opacity:0.55,marginTop:2}}>
                    {qty != null && qty !== "" && <>Bring {qty}{unit?" "+unit:""} · </>}
                    {SITE_LABEL_FULL[actionItem._site] || actionItem._site} · Raised {actionItem.time || "earlier"}{actionItem.requestedBy ? " · "+actionItem.requestedBy : ""}
                  </div>
                </div>
              </div>
              {!isClosed && isFlowB && (
                <div style={{background:"rgba(255,122,92,0.14)",border:"1px solid rgba(255,122,92,0.32)",color:"#FF9B83",padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,fontWeight:700}}>🚨 Currently flagged Out of Stock</div>
              )}
              {!isClosed && !isFlowB && actionItem.flag === "running_low" && (
                <div style={{background:"rgba(241,197,68,0.12)",border:"1px solid rgba(241,197,68,0.3)",color:"#F1C544",padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,fontWeight:700}}>⚠ Currently flagged Running Low</div>
              )}
              <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:B.kewpie,opacity:0.5,marginBottom:8}}>{isClosed ? "Stock update" : isFlowB ? "What did you find?" : "What happened?"}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:isFlowB?18:14}}>
                {opts.map(opt => {
                  const selected = selectedPrimary === opt.key;
                  const style = toneStyle(opt.tone, selected);
                  return (
                    <button key={opt.key} onClick={()=>setSelectedPrimary(opt.key)} style={{
                      width:"100%",textAlign:"left",padding:"12px 14px",borderRadius:12,cursor:"pointer",
                      background:style.bg,border:style.border,color:style.fg,fontFamily:"inherit",
                      transition:"all 0.15s ease"
                    }}>
                      <div style={{fontWeight:800,fontSize:"0.92rem",letterSpacing:"0.02em"}}>{opt.label}</div>
                      <div style={{fontSize:"0.72rem",opacity:0.75,marginTop:3,lineHeight:1.4}}>{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
              {!isFlowB && !isClosed && !actionItem.flag && (<>
                <div style={{height:1,background:"rgba(254,250,240,0.08)",margin:"4px 0 14px"}}/>
                <button onClick={()=>{if(!runningLowDisabled) setRunningLowChecked(!runningLowChecked)}} disabled={runningLowDisabled} style={{
                  width:"100%",textAlign:"left",padding:"12px 14px",borderRadius:12,fontFamily:"inherit",
                  background: runningLowDisabled ? "rgba(254,250,240,0.03)" : runningLowChecked ? "rgba(241,197,68,0.14)" : "transparent",
                  border: "1px solid " + (runningLowDisabled ? "rgba(254,250,240,0.06)" : runningLowChecked ? "rgba(241,197,68,0.35)" : "rgba(241,197,68,0.18)"),
                  color: runningLowDisabled ? "rgba(254,250,240,0.35)" : "#F1C544",
                  cursor: runningLowDisabled ? "not-allowed" : "pointer",
                  display:"flex",alignItems:"center",gap:12,marginBottom:18
                }}>
                  <div style={{width:22,height:22,borderRadius:6,flexShrink:0,border:"2px solid "+(runningLowDisabled?"rgba(254,250,240,0.18)":runningLowChecked?"#F1C544":"rgba(241,197,68,0.45)"),background:runningLowChecked?"#F1C544":"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.85rem",color:B.nori,fontWeight:800}}>
                    {runningLowChecked ? "✓" : ""}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:"0.88rem",letterSpacing:"0.02em"}}>⚠ Also flag as running low</div>
                    <div style={{fontSize:"0.7rem",opacity:0.7,marginTop:2,lineHeight:1.4}}>
                      {runningLowDisabled ? "Not available when Out of Stock is selected" : "Store room was thin — flag for the kitchen + admin (separate PIN-audited event)"}
                    </div>
                  </div>
                </button>
              </>)}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setActionItem(null)} style={{flex:1,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.12)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.75}}>Cancel</button>
                <button onClick={confirmAction} disabled={!canConfirm} style={{flex:2,padding:"14px 16px",borderRadius:12,border:"none",background:canConfirm?B.wasabi:"rgba(212,251,111,0.2)",color:B.nori,fontFamily:"inherit",fontWeight:800,fontSize:"0.9rem",cursor:canConfirm?"pointer":"not-allowed",opacity:canConfirm?1:0.45,letterSpacing:"0.02em"}}>Confirm</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DeliveryConfirmModal — fires for ORDERED rows AND for any back_in_stock
          path so every storeroom restock has a photo + audit trail. */}
      {deliveryConfirm && (
        <DeliveryConfirmModal
          item={deliveryConfirm}
          backend={backend}
          site={deliveryConfirm._site || ""}
          requestPin={requestPin}
          events={deliveryConfirm._events}
          propagatedEvents={deliveryConfirm._propagatedEvents}
          kitchenCoords={deliveryConfirm._kitchenCoords}
          title={deliveryConfirm._title}
          onClose={()=>setDeliveryConfirm(null)}
          onConfirmed={()=>{
            const target = deliveryConfirm;
            const wasBackInStockOnly = target._selectedPrimary === "back_in_stock";
            const wasBackInStockCollected = target._selectedPrimary === "back_in_stock_collected";
            setDeliveryConfirm(null);
            if (target && target.id) {
              const now = new Date();
              const t = now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0");
              if (wasBackInStockCollected) {
                setLocalMark(target.id, {status:"collected", flag:null, by:"(photo-confirmed)", at:t, _resolvedToday:true, _item: target.item});
              } else if (wasBackInStockOnly) {
                setLocalMark(target.id, {status:"requested", flag:null, by:"(photo-confirmed)", at:t, _resolvedToday:false, _item: target.item});
              } else {
                // ORDERED → delivered flow
                setLocalMark(target.id, {status:"delivered", flag:null, by:"(photo-confirmed)", at:t, _resolvedToday:true, _item: target.item});
              }
              // Apply optimistic local marks for any propagated other-site rows
              // so the UI clears them simultaneously (cross-site storeroom restock).
              (target._propagatedEvents || []).forEach(p => {
                if (p && p._otherRowId) {
                  setLocalMark(p._otherRowId, {status:"requested", flag:null, by:"(photo-confirmed)", at:t, _resolvedToday:false, _item: target.item});
                }
              });
              setXsTick(s=>s+1);
            }
            xsLoadRef.current && xsLoadRef.current();
          }}
          onActionFailed={(failedItem)=>{
            // 2026-05-24: revert the optimistic close set in onConfirmed so
            // the row re-appears as pending on the next poll rather than
            // sticking with the local mark for 5 min and silently coming
            // back. The modal already alert()s the user with the backend
            // error message, so no further UI needed here.
            if (failedItem && failedItem.id) {
              setLocalMark(failedItem.id, null);
              (failedItem._propagatedEvents || []).forEach(p => {
                if (p && p._otherRowId) setLocalMark(p._otherRowId, null);
              });
              setXsTick(s=>s+1);
              xsLoadRef.current && xsLoadRef.current();
            }
          }}
        />
      )}

      {thumbZoom && <CatalogThumbLightbox item={thumbZoom.item} imageUrl={thumbZoom.imageUrl} onClose={()=>setThumbZoom(null)}/>}
    </div>
  );
}

// Item card — image thumbnail + name + per-site qty pill. Pure presentational.
// 2026-05-19: kitchen-only actions (stockDelivered + storeroom flags) are
// geofenced server-side via the kitchenGeofence Script Property. The frontend
// captures GPS up-front so the user gets a clear error before the PIN prompt
// when GPS is denied/unavailable, and so the lat/lng can be forwarded on the
// POST body for the backend distance check.
const KITCHEN_GEOFENCED_ACTIONS = ["stockDelivered","stockOutOfStock","stockRunningLow","backInStock"];
// 2026-06-02: best-effort, NEVER blocks. The kitchen geofence is now a
// FLAG, not a gate — the backend stamps offsite distance instead of
// rejecting (see ManchesterReportWriter computeKitchenOffsite_). So a
// missing / denied / timed-out fix must not stop a genuine delivery. We
// still TRY for a fix so the backend can flag genuinely-offsite logs, but
// on any failure we resolve null and let the action proceed un-flagged.
// (The old reject() paths blocked real chefs whenever indoor GPS faltered.)
function captureKitchenCoords(){
  return new Promise(function(resolve){
    if (typeof navigator === "undefined" || !navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      function(pos){ resolve({lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy}); },
      function(){ resolve(null); },
      {enableHighAccuracy: true, timeout: 15000, maximumAge: 60000}
    );
  });
}

// -----------------------------------------------------------------------------
// Phase 2 strangler dual-run (kitchen side). Best-effort shadow of a kitchen
// stock write into Supabase alongside the unchanged Apps Script write — the
// mirror of foh.jsx's dualRun. It can NEVER throw into the live till path: the
// body is wrapped and stockSync's recorders swallow their own errors. The staff
// PIN the chef just typed mints the ~15-min pass (minted once per window).
// Reads stay on the 5s Apps Script poll until parity is proven; this only
// shadows the WRITES so we can reconcile row-for-row first. See supabase/README.md.
function shadowKitchenWrite({ action, site, item, staffPin, supplierRef = null, eta = null, note = null, photoPath = null, qty = null }) {
  try {
    if (!action || !item || !staffPin) return;
    // The kitchen tile serves multiple kiosks and tags its own one-offs "kitchen";
    // that's this deployment's site (mcr) in Supabase terms.
    const siteKey = (site && site !== "kitchen") ? site : SITE.key;
    // Call the recorder DIRECTLY at the tap: it enqueues the row synchronously
    // (durable BEFORE the login) then verifies the pin + stamps attribution and
    // drains. It returns the resolved pass, which we reuse for the reorder alert so
    // there's a single login per tap.
    stockSync.recordKitchen({ siteKey, item, action, supplierRef, eta, note, photoPath, qty, staffPin })
      .then((res) => {
        // The same tap also raises a reorder alert (running low / out of stock),
        // or closes one (delivered / back in stock) — see lib/notificationsSync.js.
        // Fired BESIDE the stock shadow: a stock_activity hiccup must not swallow the
        // alert, and an alert hiccup must not touch the stock row. It swallows its own
        // errors and never throws. Nothing sends an email — the alert queues and
        // waits. One-line kill: NOTIFY_SYNC_ENABLED in lib/notificationsSync.js.
        try { notificationsSync.recordReorderSignal({ siteKey, item, action, pass: res && res.pass }); } catch (_) {}
      })
      .catch((e) => { try { console.warn("[stock-sync] kitchen shadow failed:", e?.message || e); } catch (_) {} });
  } catch (e) { /* best-effort: never break the live kitchen path */ }
}

// Fire a kitchen action POST. For geofenced events we use the readable-response
// pattern (text/plain + no no-cors = simple CORS, no preflight, response is
// JSON-readable) so we can detect OUTSIDE_KITCHEN_GEOFENCE and tell the user.
// Non-geofenced events keep the legacy no-cors fire-and-forget pattern.
// Returns a Promise<{ok, code?, message?}> — caller handles the rejection.
// 2026-05-24: now ALWAYS reads the parsed response so backend rejections
// (geofence, role gate, network, 500s) surface to the caller instead of
// being silently swallowed by no-cors. Previously the !geofenced branch
// fired no-cors fire-and-forget and returned {ok:true} unconditionally —
// that branch is the root cause of items persisting on the to-be-collected
// list for days when stockDelivered events were rejected by the kitchen
// geofence. text/plain content-type skips the CORS preflight Apps Script
// can't answer (same trick as the prior geofenced branch). The `geofenced`
// param is kept for callsite clarity but no longer changes behaviour.
function postKitchenAction(backend, body, geofenced){
  // Phase 2 dual-run: shadow this kitchen write into Supabase (best-effort). Every
  // flag/delivery event in the kitchen funnels through here, incl. cross-site
  // storeroom propagation, so one mirror catches them all. photo_path now carries
  // the SAME photo URL the sheet row gets (an absolute Drive URL during dual-run;
  // the reader passes absolute URLs through) — a photo-confirmed delivery must
  // clear the item's flag on BOTH sides, and the Supabase walker can only apply
  // that rule when it can see the photo (photo_path was null on all 205 delivered
  // rows to 17 Jul, so flagged items lingered forever on the Supabase side).
  try { shadowKitchenWrite({ action: body && body.action, site: body && body.site, item: body && body.item, staffPin: body && body.staffPin, supplierRef: body && body.supplierRef, eta: body && body.eta, note: body && body.note, photoPath: (body && body.photoUrl) || null, qty: (body && body.qty != null) ? body.qty : null }); } catch(_){}
  // Write-drop gate (STOCK_WRITE_DROP_HOSTS — ships EMPTY). ON -> the awaited
  // Supabase landing IS the record and the Apps Script POST is SKIPPED; the shadow
  // + reorder signal above still fire exactly once with the SAME pass. The kitchen
  // idem_key has no per-tap nonce, so shadowKitchenWrite's enqueue and this awaited
  // write COLLAPSE onto ONE row (a 23505 = landed) — no double write. A landing
  // failure returns the same {ok:false,code,message} shape the callers resolve on,
  // so the geofence/error UI path is unchanged. OFF -> the fetch below, untouched.
  // Lane BP (13 Sep 2026): the kitchen offsite geofence flag now rides the ON path
  // too (closes DECISIONS_NEEDED BE-4). The device fix a caller already puts on a
  // GEOFENCED body (lat/lng/accuracy — the same three fields the Sheet's
  // computeKitchenOffsite_ reads) is handed to the row as device_lat/lng/accuracy_m
  // and the DATABASE evaluates + stamps offsite / offsite_distance_m (migration
  // 1300, fence config ships empty = no flag, admin never flagged). A non-geofenced
  // action (customDeliveryReceived) sends no fix, exactly as the Sheet never flags it.
  // The optional qty (the Inbound Deliveries tile) rides the same entry.
  if (stockSync.stockWriteDropEnabled()) {
    var _dropSite = (body && body.site && body.site !== "kitchen") ? body.site : SITE.key;
    var _dropArgs = {
      siteKey: _dropSite, item: body && body.item, action: body && body.action,
      supplierRef: (body && body.supplierRef) || null, eta: (body && body.eta) || null,
      note: (body && body.note) || null, photoPath: (body && body.photoUrl) || null,
      qty: (body && body.qty != null) ? body.qty : null,
      staffPin: body && body.staffPin,
    };
    if (geofenced && body && body.lat != null && body.lng != null) {
      _dropArgs.deviceLat = body.lat;
      _dropArgs.deviceLng = body.lng;
      _dropArgs.deviceAccuracyM = (body.accuracy != null) ? body.accuracy : null;
    }
    return stockSync.stockWriteAuthoritative({ kind: "kitchen", args: _dropArgs }).then(function(res){
      return (res && res.ok)
        ? { ok: true }
        : { ok: false, code: "supabase", message: (res && res.errMsg) || "That didn't save to the kitchen store — try again." };
    });
  }
  return new Promise(function(resolve){
    try {
      fetch(backend, {method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: JSON.stringify(body)})
        .then(function(r){
          if (!r.ok) return {ok: false, code: "HTTP_"+r.status, message: "Backend returned HTTP " + r.status + "."};
          return r.json().then(function(d){ return {ok: true, data: d}; });
        })
        .then(function(out){
          if (!out || out.ok === false) {
            resolve(out || {ok: false, code: "UNKNOWN", message: "Action failed."});
            return;
          }
          var d = out.data || {};
          // Apps Script handlers return {status:'error', code, message} on
          // failure and {status:'success'} (or a payload) on success. Any
          // explicit error → surface to caller; everything else → ok.
          if (d.status === "error" || d.code === "OUTSIDE_KITCHEN_GEOFENCE") {
            resolve({ok: false, code: d.code || "BACKEND_ERROR", message: d.message || "Backend rejected the action."});
          } else {
            resolve({ok: true, data: d});
          }
        })
        .catch(function(){ resolve({ok: false, code: "NETWORK", message: "Couldn't reach the kitchen backend. Try again."}); });
    } catch(_){
      resolve({ok: false, code: "NETWORK", message: "Couldn't reach the kitchen backend. Try again."});
    }
  });
}

// User-facing message for a postKitchenAction failure. Keeps the kitchen
// staff message short + actionable. OUTSIDE_KITCHEN_GEOFENCE is the most
// common cause when items silently stuck on the list.
function kitchenActionErrorMessage(res, itemLabel){
  if (!res || res.ok) return "";
  var label = itemLabel ? "“" + itemLabel + "”: " : "";
  if (res.code === "OUTSIDE_KITCHEN_GEOFENCE") return label + (res.message || "This action can only be performed at the kitchen.");
  if (res.code === "NETWORK") return label + "Couldn't reach the kitchen backend. Try again.";
  if (res.code && res.code.indexOf("HTTP_") === 0) return label + "Backend error (" + res.code + "). Try again.";
  return label + (res.message || "Action failed. Try again.");
}

// Deactivated catalog items shouldn't surface anywhere in the kitchen running
// list — once admin sets `status: "inactive"` we don't sell it any more, so
// the lingering OOS / running-low / pending rows in the StockRequests history
// are noise that confuses the runner. Custom items (no catalog entry, custom:
// true on the row) keep showing — they're not catalog-managed, deactivation
// doesn't apply.
//
// Distinguishing custom from deactivated: the public ?action=stockcatalog
// feed only returns ACTIVE items. So an item missing from catalogByName is
// either a custom request (row.custom === true) OR a deactivated catalog
// item (row.custom === false). The row's own `custom` boolean is the right
// signal, not the catalog lookup.
function isRequestVisible(r, catalogByName){
  if (!r) return false;
  if (r.custom === true) return true;                 // custom request — always visible
  if (!catalogByName) return true;                    // catalog not loaded yet — fail open
  const entry = catalogByName[(r.item || "").toLowerCase()];
  return !!entry;                                     // present in active catalog → visible
}

// Sort comparator for stock-request lists — oldest-requested at the top so
// items that have been on the running list longest surface first across every
// kitchen section (OOS, Running Low, Pending, Collected Today + cross-site
// strips). Falls back to flag/order timestamps for rows that have no original
// request event (e.g. BOH-only OOS flags). Rows with no timestamp at all sink
// to the bottom so they don't outrank dated rows.
function byOldestRequested(a, b){
  const __t = (r)=>{
    if(!r) return Infinity;
    const ts = r.requestedFullTs || r.flaggedFullTs || r.orderedFullTs;
    if(!ts) return Infinity;
    const t = Date.parse(ts);
    return isNaN(t) ? Infinity : t;
  };
  return __t(a) - __t(b);
}

// Defensive HH:MM extractor for deliveredAt / collectedAt — backend currently
// returns a Date.toString() ("Sat Dec 30 1899 09:23:00 GMT+0000 ...") for
// time-only sheet cells because handleGetStockRequests doesn't run them
// through formatSellOutTime_. Pull the HH:MM out of either shape so the UI
// never renders the 1899-epoch garbage. Returns "" for null/empty.
function fmtDeliveryClock(v){
  if(v == null) return "";
  const s = String(v).trim();
  if(!s) return "";
  if(/^\d{1,2}:\d{2}$/.test(s)) return s;
  const m = s.match(/\b(\d{1,2}:\d{2})(?::\d{2})?\b/);
  return m ? m[1] : "";
}

function StockRequestItemDetail({req, catalogEntry, siteKey, onZoom, variant}){
  const qty = catalogEntry
    ? (catalogEntry.siteDefaults && catalogEntry.siteDefaults[siteKey] !== undefined && catalogEntry.siteDefaults[siteKey] !== ""
        ? catalogEntry.siteDefaults[siteKey]
        : (catalogEntry.defaultQty !== null && catalogEntry.defaultQty !== "" ? catalogEntry.defaultQty : null))
    : null;
  const imageUrl = catalogEntry && catalogEntry.imageUrl;
  // Reorder responsibility prompt. When an item is flagged Running Low / Out of
  // Stock, show which ROLE to notify (from the catalogue). The reorder email is
  // sent server-side (sendReorderEmail_, routed by responsibleRole; 0700
  // reorder-dispatch) — the card no longer offers a mailto.
  const reorderFlag = req.flag === "running_low" || req.flag === "out_of_stock" ? req.flag : null;
  // Responsible-person prompt — show the ROLE the catalogue carries (Adam / JJ /
  // the kitchen (BOH)), never a PIN and never an email. responsibleLabel is an
  // allowlist, so a blank/unknown role — or an opaque token — is "" and we fall
  // back to the generic "an Admin" wording, exactly as this card read before.
  // The one-tap mailto is intentionally gone (it exposed responsibleEmail):
  // sendReorderEmail_ still fires server-side routed by responsibleRole, and the
  // 0700 reorder-dispatch is the path — the card no longer surfaces an address.
  const responsibleLabel = reorderFlag && catalogEntry ? stockCatalogSrc.responsibleLabel(catalogEntry.responsibleRole) : "";
  // 2026-05-06: unit of measurement (e.g. "stickers", "bottles") so the
  // "Bring N" pill reads as "Bring 50 stickers" instead of just "Bring 50".
  // Empty / missing on legacy rows — falls back to bare "Bring N".
  const unit = catalogEntry && catalogEntry.unit ? String(catalogEntry.unit).trim() : "";
  // Days-open counter: an unfulfilled, in-stock request that's been sitting
  // more than a day gets a small "N DAYS OPEN" pill so the driver/kitchen can
  // see it's been waiting. Excludes OOS / Running Low (those have their own
  // flags) and collected rows. (JJ 2026-06-04 — replaces the auto-close.)
  const __reqTs = req.requestedFullTs ? Date.parse(req.requestedFullTs) : NaN;
  const __daysOpen = isNaN(__reqTs) ? 0 : Math.floor((Date.now() - __reqTs) / 86400000);
  const showDaysOpen = variant !== "collected" && !reorderFlag && __daysOpen >= 1;
  return (
    <>
      <CatalogThumb item={req.item} imageUrl={imageUrl} size={56} onZoom={onZoom} showPlaceholder/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{...S.itemName,marginBottom:2,display:"flex",alignItems:"baseline",gap:6,flexWrap:"wrap"}}>
          <span>{req.item}</span>
          {showDaysOpen && <span style={{fontSize:"0.66rem",fontWeight:700,color:"#F4A460",background:"rgba(244,164,96,0.18)",padding:"2px 8px",borderRadius:999,letterSpacing:"0.04em"}}>⏳ {__daysOpen} {__daysOpen===1?"DAY":"DAYS"} OPEN</span>}
          {qty !== null && qty !== undefined && qty !== "" && (
            <span style={{fontSize:"0.7rem",fontWeight:700,color:B.wasabi,background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.3)",padding:"2px 8px",borderRadius:999,letterSpacing:"0.02em"}}>Bring {qty}{unit?" "+unit:""}</span>
          )}
          {req.custom && <span style={{fontSize:"0.6rem",color:B.sunshine,fontWeight:700,letterSpacing:"0.05em"}}>CUSTOM</span>}
        </div>
        <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.5}}>
          {variant === "collected" ? (()=>{ const at = fmtDeliveryClock(req.collectedAt || req.deliveredAt); return (
            <>
              <span>Collected by {req.collectedBy || req.deliveredBy || "Staff"}</span>
              {at && <><span style={{opacity:0.5,margin:"0 6px"}}>·</span>{at}</>}
              {req.dateStr && <><span style={{opacity:0.5,margin:"0 6px"}}>·</span>{req.dateStr}</>}
            </>
          ); })() : (
            <>
              {req.requestedBy||req.staffName||"Staff"}
              <span style={{opacity:0.5,margin:"0 6px"}}>·</span>
              {req.time||"--:--"}
              {req.dateStr && <><span style={{opacity:0.5,margin:"0 6px"}}>·</span>{req.dateStr}</>}
            </>
          )}
        </div>
        {reorderFlag && (
          <div style={{fontSize:"0.72rem",fontWeight:600,marginTop:4,color:reorderFlag==="out_of_stock"?B.coral:B.sunshine}}>
            {reorderFlag==="out_of_stock"?"❗ Out of stock":"🪫 Running low"} — notify <strong>{responsibleLabel || "an Admin"}</strong> to reorder
          </div>
        )}
      </div>
    </>
  );
}

// Local storage of kitchen-side actions. Backend paper trail arrives in Phase 2b;
// until then we hold Collected / Out of Stock state client-side so the UI is
// coherent across refreshes on the same tablet.
function getLocalMark(requestId){ return requestId ? (appData.stockLocalMarks||{})[requestId] : null; }
function setLocalMark(requestId, mark){
  if(!requestId) return;
  // Stamp every local mark with _ts so the merge layer can keep optimistic
  // state visible for ~30s regardless of what the next backend poll returns.
  // Without the TTL, a slow Apps Script write means the 5s poll briefly
  // returns the row as still "requested" which makes the optimistic
  // "collected" state flicker out and back in.
  const stamped = {...mark, _ts: mark._ts || Date.now()};
  appData.stockLocalMarks = {...(appData.stockLocalMarks||{}), [requestId]: stamped};
  persistAppData();
}
// 5 minutes of local-mark dominance — enough that a slow Apps Script write
// can't cause the optimistic state to flicker back to the old value mid-poll.
// After 5 minutes the backend has had ample time to acknowledge any write,
// so the local mark is dropped and canonical state wins.
const LOCAL_MARK_TTL_MS = 300000;
function localMarkIsFresh(mark){
  if(!mark || !mark._ts) return false;
  return (Date.now() - mark._ts) < LOCAL_MARK_TTL_MS;
}

// Poll every 5 seconds while the kitchen tab is visible so drivers never see
// stale requests on the way out. Polling pauses when the tab is hidden to
// stay inside Apps Script URL fetch quotas.
const REFRESH_INTERVAL_MS = 5000;

// Phase 2 read-side (step 1, kitchen only): the Supabase doorbell. While the
// realtime subscription is healthy, every stock event pushes an instant refetch
// of the sheet feed, so the 5s poll relaxes to a 60s safety net. If the
// subscription drops, DOORBELL.live flips false and the next tick polls at 5s
// again. The DATA still comes from the sheet feed either way — Supabase only
// says "something changed", it doesn't render (the sheet alone knows about
// Command Centre 'ordered' events until the Phase 3 fold-in).
const POLL_RELAXED_MS = 60000;
const DOORBELL = { live: false };

function FreshnessLabel({lastFetchedAt, now, onRefresh}){
  const secs = Math.max(0, Math.floor((now - lastFetchedAt)/1000));
  // With the doorbell live, quiet ≠ stale: any change would have pushed a
  // refetch, and the 60s safety poll still runs. Beyond ~70s even the safety
  // poll has missed — show the stale colours so staff tap-refresh.
  const live = secs < 6 || (DOORBELL.live && secs < 70);
  const label = secs < 2 ? "Live" : secs < 60 ? "Updated "+secs+"s ago · Tap to refresh" : "Updated "+Math.floor(secs/60)+"m ago · Tap to refresh";
  const color = live ? B.eucalyptus : (secs < 30 ? B.kewpie : B.sunshine);
  const bg = live ? "rgba(111,175,143,0.1)" : "rgba(254,250,240,0.05)";
  const border = live ? "1px solid rgba(111,175,143,0.25)" : "1px solid rgba(254,250,240,0.08)";
  return (
    <button
      type="button"
      onClick={onRefresh}
      style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:"0.7rem",fontWeight:600,color,background:bg,border,borderRadius:999,padding:"2px 9px",letterSpacing:"0.03em",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}
    >
      <span style={{width:6,height:6,borderRadius:"50%",background:color,boxShadow:live?"0 0 6px "+color:"none"}}/>
      {label}
    </button>
  );
}

function SiteStockRequests({site,siteName,backend,requestPin,catalogByName,hideFlaggedSections,hideCollectedToday,bellTick}){
  // Seed from localStorage so Pending + Collected Today survive a close +
  // reopen — without this the per-site card briefly renders empty until the
  // first poll completes (was making "Collected Today" appear to vanish).
  const SITE_CACHE_KEY = "rolled.kitchen.siteRequests."+site;
  const [requests,setRequests] = useState(()=>{
    try {
      const c = localStorage.getItem(SITE_CACHE_KEY);
      if(c){ const parsed = JSON.parse(c); if(Array.isArray(parsed)) return parsed; }
    } catch(e) {}
    return [];
  });
  // Loading is false on cache hit so cached rows render instantly without
  // a spinner flash. Background poll updates as normal.
  const hadCache = (()=>{ try { const c = localStorage.getItem(SITE_CACHE_KEY); return !!(c && JSON.parse(c) && Array.isArray(JSON.parse(c)) && JSON.parse(c).length); } catch(e) { return false; } })();
  const [loading,setLoading] = useState(!hadCache);
  const [showSpinner,setShowSpinner] = useState(false);
  const [error,setError] = useState(null);
  // Per-row tap action modal state — for the exception-flow path (already-collected
  // items, OOS rows, ordered rows). Plain pending + running-low rows go through the
  // bulk-collect flow below (2026-05-07 Pass 1).
  const [actionItem,setActionItem] = useState(null);
  const [selectedPrimary,setSelectedPrimary] = useState(null);
  const [runningLowChecked,setRunningLowChecked] = useState(false);
  const [deliveryConfirm,setDeliveryConfirm] = useState(null);
  // Bulk-collect state (Pass 1 — pending + running-low only; OOS / ordered / closed
  // keep the per-item modal flow as the exception hatch).
  // step "pick" → tickboxes on rows, "Review N →" sticky bar.
  // step "review" → segmented Plenty / Low / Empty per item, "Confirm with PIN" sticky bar.
  // One PIN confirms all picked items, fires backend events (stockDelivered + optional
  // running-low / OOS + backInStock-to-clear) in parallel — same backend actions as the
  // existing single-item path, just batched.
  const [bulkPicked,setBulkPicked] = useState(()=>new Set());
  const [bulkStep,setBulkStep] = useState("pick");
  const [bulkStockState,setBulkStockState] = useState({});
  // Tap-to-enlarge for catalogue thumbnails on this site's stock-request rows.
  const [thumbZoom,setThumbZoom] = useState(null);
  const [, setMarksTick] = useState(0); // force re-render when local marks change
  const [lastFetchedAt,setLastFetchedAt] = useState(null);
  const [nowTick,setNowTick] = useState(Date.now());
  const [justArrivedIds,setJustArrivedIds] = useState(()=>new Set());
  const refreshRef = useRef(null);
  const spinnerTimerRef = useRef(null);
  const loadRef = useRef(null);
  const loadedAtRef = useRef(0);        // last successful fetch — the doorbell-relaxed poll reads this
  const knownIdsRef = useRef(null);     // Set of IDs seen by the last successful poll (null = first poll)
  const justArrivedTimerRef = useRef(null);

  const loadRequests = (isInitial)=>{
    if(isInitial) setLoading(true);
    setError(null);
    if(isInitial){
      if(spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      spinnerTimerRef.current = setTimeout(()=>setShowSpinner(true), 400);
    }
    // READ FLIP (gated — see stockReader.STOCK_READ_HOSTS): flipped kitchen
    // reads Supabase (CC overlay + sheet fallback inside); otherwise the old
    // sheet fetch, unchanged. Active-catalog set mirrors the server-side
    // emission filter; catalogByName's keys ARE the active names (the public
    // catalog feed only returns active items). Empty catalog = null = fail-open.
    const sheetUrl = backend+"?action=getStockRequests&site="+site;
    // CATALOG-LOAD GUARD: only serve the Supabase reader once the ACTIVE CATALOG
    // has loaded. A null catalog makes the reader fail OPEN (emits deactivated
    // items) on a cold load; the sheet path is server-filtered and always safe,
    // so a flipped kitchen falls back to the sheet until catalogByName populates.
    const catNames = Object.keys(catalogByName||{}).length ? new Set(Object.keys(catalogByName)) : null;
    ((stockReader.stockReadEnabled() && catNames)
      ? stockReader.fetchStockRequests(site, { sheetUrl, activeItemNames: catNames })
        .then(d=>{ if(!d) throw new Error("stock read unavailable"); return d; })
      : fetch(sheetUrl).then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()}))
      .then(data=>{
        if(data.error)throw new Error(data.error);
        const list = Array.isArray(data.requests)?data.requests:Array.isArray(data)?data:[];
        // Detect newly-arrived request IDs since the last poll (skip the first
        // poll so existing items don't all flash on page load).
        const idSet = new Set(list.map(r=>r.id));
        if(knownIdsRef.current){
          const fresh = [];
          idSet.forEach(id=>{ if(!knownIdsRef.current.has(id)) fresh.push(id); });
          if(fresh.length){
            setJustArrivedIds(new Set(fresh));
            if(justArrivedTimerRef.current) clearTimeout(justArrivedTimerRef.current);
            justArrivedTimerRef.current = setTimeout(()=>setJustArrivedIds(new Set()), 2400);
          }
        }
        knownIdsRef.current = idSet;
        setRequests(list);
        try { localStorage.setItem(SITE_CACHE_KEY, JSON.stringify(list)); } catch(e) {}
        setLoading(false);setShowSpinner(false);
        setLastFetchedAt(Date.now());
        loadedAtRef.current = Date.now();
        if(spinnerTimerRef.current){clearTimeout(spinnerTimerRef.current);spinnerTimerRef.current=null;}
      })
      .catch(e=>{
        setError(e.message);setLoading(false);setShowSpinner(false);
        if(spinnerTimerRef.current){clearTimeout(spinnerTimerRef.current);spinnerTimerRef.current=null;}
      });
  };
  loadRef.current = loadRequests; // always point at the latest version for interval callers

  // Initial load + steady-state polling. Deps kept to [site] so re-renders
  // from the 'updated Xs ago' ticker don't re-arm the interval every second.
  useEffect(()=>{
    loadRef.current(true);
    refreshRef.current = setInterval(()=>{
      // Doorbell live + data fresh → the poll is only the safety net; skip.
      if(DOORBELL.live && Date.now() - loadedAtRef.current < POLL_RELAXED_MS) return;
      loadRef.current(false);
    }, REFRESH_INTERVAL_MS);
    return ()=>{
      if(refreshRef.current){ clearInterval(refreshRef.current); refreshRef.current = null; }
      if(spinnerTimerRef.current){ clearTimeout(spinnerTimerRef.current); spinnerTimerRef.current = null; }
      if(justArrivedTimerRef.current){ clearTimeout(justArrivedTimerRef.current); justArrivedTimerRef.current = null; }
    };
  },[site]);

  // Doorbell nudge from the parent — a realtime ring already refetched the
  // cross-site view; this refetches the per-site card outside its poll cadence.
  useEffect(()=>{ if(bellTick) loadRef.current && loadRef.current(false); },[bellTick]);

  // Tick the "updated Xs ago" label forward every second.
  useEffect(()=>{
    const t = setInterval(()=>setNowTick(Date.now()), 1000);
    return()=>clearInterval(t);
  },[]);

  // Merge backend status with any local kitchen-side marks. Fresh marks
  // (within LOCAL_MARK_TTL_MS) override backend state — this prevents the
  // optimistic "Collected" row from flickering out when the 5s poll returns
  // canonical state before the Apps Script write has durably landed.
  // _resolvedToday is set ONLY by closing events in sendActionSequence; the
  // merge reads it explicitly rather than inferring from local.status, so a
  // subsequent flag-only update (e.g. Replenished) cannot wrongly inherit
  // a stale collected status into the Collected Today section.
  const mergedRequests = requests.map(r=>{
    const local = getLocalMark(r.id);
    if(!local) return r;
    // 2026-05-06: stale-mark guard — see xsMerged for the rationale.
    if(!local._item || local._item !== r.item) return r;
    const fresh = localMarkIsFresh(local);
    const baseStatus = r.status||"requested";
    if(local.status && (baseStatus === "requested" || fresh)){
      return {...r, status: local.status, flag: local.flag !== undefined ? local.flag : r.flag, collectedBy: local.by, collectedAt: local.at, _resolvedToday: local._resolvedToday === true};
    }
    if(local.flag !== undefined && fresh){
      return {...r, flag: local.flag};
    }
    if(local.flag !== undefined && baseStatus === "requested"){
      return {...r, flag: local.flag};
    }
    return r;
  });
  // OUT OF STOCK is sticky — visible regardless of date. Stays until the
  // kitchen marks "new stock arrived" (optionally with collected).
  const oosItems = mergedRequests.filter(r => r.flag === "out_of_stock" && isRequestVisible(r, catalogByName)).sort(byOldestRequested);
  // RUNNING LOW is sticky — also a running list. Includes both still-pending
  // and already-collected rows. Stays visible until the kitchen marks the
  // item as "Replenished stock" (which clears the running-low flag without
  // re-collecting if it was already collected).
  const runningLowItems = mergedRequests.filter(r => r.flag === "running_low" && isRequestVisible(r, catalogByName)).sort(byOldestRequested);
  // 2026-05-06: uncollected-running-low rows belong in the per-site PENDING
  // surface as well as the cross-site STOREROOM panel — kitchen still owes
  // the kiosk stock; the running-low flag is a storeroom warning, not a
  // delivery acknowledgement. Once the row is collected/closed it drops to
  // the cross-site banner only (with a ✓ collected pill).
  const runningLowOpen = runningLowItems.filter(r => {
    const status = r.status || "requested";
    const isClosed = r.closed === true || status === "delivered" || status === "collected" || status === "received";
    return !isClosed;
  });
  // PENDING — open requests with NO flag. Running-low rows are pulled into
  // their own section above; OOS rows live in the OOS section.
  const pending = mergedRequests.filter(r => (r.status||"requested")==="requested" && !r.flag && isRequestVisible(r, catalogByName)).sort(byOldestRequested);
  // COLLECTED TODAY — items collected/delivered today with no flag. Flagged
  // rows are tracked in the OOS or Running Low sticky lists instead.
  const __todayDDMMYYYY = (()=>{ const d=new Date(); const dd=String(d.getDate()).padStart(2,"0"); const mm=String(d.getMonth()+1).padStart(2,"0"); return dd+"/"+mm+"/"+d.getFullYear(); })();
  const __dateMatchesToday = (s)=>{
    if(!s) return false;
    const t = String(s).trim();
    if(t === __todayDDMMYYYY) return true;
    const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if(!m) return false;
    const dd = m[1].padStart(2,"0"), mm = m[2].padStart(2,"0");
    return dd+"/"+mm+"/"+m[3] === __todayDDMMYYYY;
  };
  const collectedToday = mergedRequests.filter(r => {
    const status = r.status||"requested";
    if(status === "requested") return false;
    if(r.flag) return false; // flagged rows live in their sticky section
    if(!isRequestVisible(r, catalogByName)) return false; // deactivated catalog → hide
    // _resolvedToday is set when the optimistic local mark flipped this row
    // from requested→collected this session — show it in Collected Today
    // regardless of original request date (which can be from days ago).
    if(r._resolvedToday) return true;
    // 2026-05-05: backend's resolvedDate is the close-event date (when we
    // actually marked it done), distinct from the request date. Prefer it
    // so an item requested days ago + collected today shows up here.
    // Falls back to dateStr for backward-compat with backend pre-deploy.
    return __dateMatchesToday(r.resolvedDate || r.dateStr || r.date);
  }).sort(byOldestRequested);

  // Reset modal state when actionItem changes; pre-tick running-low checkbox
  // if the row is already flagged that way so an un-tick is the affirmative
  // restock-acknowledgement action.
  useEffect(()=>{
    if(!actionItem){ setSelectedPrimary(null); setRunningLowChecked(false); return; }
    setSelectedPrimary(null);
    const closed = actionItem.closed || actionItem.status === "delivered" || actionItem.status === "collected";
    setRunningLowChecked(!closed && actionItem.flag === "running_low");
  },[actionItem]);

  // Out of Stock supersedes Running Low — auto-uncheck if user picks OOS as
  // primary so the UI and event sequence agree.
  useEffect(()=>{
    if (selectedPrimary === "out_of_stock") setRunningLowChecked(false);
  },[selectedPrimary]);

  // Open the action modal — divert ORDERED items to the photo-required
  // Confirm Delivery flow (admin pre-logged inbound waiting for photo).
  const openCollectAction = (item) => {
    if (item && item.ordered) {
      setDeliveryConfirm({...item, _orderedMeta: {refImageUrl: item.refImageUrl||"", supplierRef: item.supplierRef||"", eta: item.eta||"", note: item.adminNote||""}});
    } else {
      setActionItem(item);
    }
  };

  // Primary-option definitions, keyed by current state.
  // Flow A  (open, no flag):                Collected | Out of Stock
  // Flow A2 (open, running_low):            Collected | Out of Stock | Replenished stock
  // Flow B  (open, out_of_stock):           New stock arrived | New stock arrived + Collected
  // Flow C  (closed, running_low):          Replenished stock (clears flag, stays closed)
  // Flow D  (closed, out_of_stock):         New stock arrived (flag clear only)
  const primaryOptions = (item)=>{
    const isClosed = item.closed || item.status === "delivered" || item.status === "collected";
    if (isClosed) {
      if (item.flag === "running_low") {
        return [
          {key:"replenished", label:"🪫 Replenished stock", sub:"Store room is restocked — clears the running-low flag. Item was already collected.", tone:"green"}
        ];
      }
      return [
        {key:"back_in_stock", label:"✓ New stock arrived", sub:"Store room restocked — clears the flag and removes it from the list.", tone:"green"}
      ];
    }
    if (item.flag === "out_of_stock") {
      return [
        {key:"back_in_stock",           label:"✓ New stock arrived",             sub:"Store room restocked — clears the flag. Kiosk still needs it.", tone:"green"},
        {key:"back_in_stock_collected", label:"✓ New stock arrived + Collected", sub:"Stock is back AND I brought it to the kiosk — closes the request.", tone:"primary"}
      ];
    }
    if (item.flag === "running_low") {
      return [
        {key:"collected",    label:"✓ Collected",         sub:"Brought back to the kiosk — closes the request. Running-low flag stays.", tone:"primary"},
        {key:"out_of_stock", label:"❗ Out of Stock",      sub:"Store room is now empty — switches the flag from low to OOS.", tone:"red"},
        {key:"replenished",  label:"🪫 Replenished stock", sub:"Store room is restocked — clears the running-low flag. Item still needs collecting.", tone:"green"}
      ];
    }
    return [
      {key:"collected",    label:"✓ Collected",   sub:"Brought back to the kiosk — closes the request.", tone:"primary"},
      {key:"out_of_stock", label:"❗ Out of Stock", sub:"Store room was empty — keeps the request visible with a red flag.", tone:"red"}
    ];
  };

  // Build the ordered sequence of backend events based on the modal's state.
  const eventsForConfirm = ()=>{
    if (!actionItem) return [];
    const events = [];
    const isFlowB = actionItem.flag === "out_of_stock";
    const isFlowA = !isFlowB;
    const wasPreTicked = actionItem.flag === "running_low";

    if (selectedPrimary === "collected") {
      // Collected on a running-low row keeps the flag (audit row); collected
      // on a no-flag row clears nothing.
      const keepLow = wasPreTicked;
      events.push({action:"stockDelivered", closing:true, newFlag: keepLow ? "running_low" : null, localStatus:"collected"});
    }
    else if (selectedPrimary === "out_of_stock")
      events.push({action:"stockOutOfStock", closing:false, newFlag:"out_of_stock", localStatus:null});
    else if (selectedPrimary === "back_in_stock")
      // localStatus:"requested" forces the optimistic row back to Pending,
      // overriding any stale "delivered" status the backend may carry from
      // an older closed lifecycle for the same item (Apps Script state
      // walker groups by item name; if the row was previously delivered
      // weeks ago, that status sticks even after a fresh stockOutOfStock).
      events.push({action:"backInStock",    closing:false, newFlag:null, localStatus:"requested"});
    else if (selectedPrimary === "back_in_stock_collected") {
      events.push({action:"backInStock",    closing:false, newFlag:null, localStatus:"requested"});
      events.push({action:"stockDelivered", closing:true,  newFlag:null, localStatus:"collected"});
    }
    else if (selectedPrimary === "replenished") {
      // Clears the running-low flag without changing the collection status.
      // Backend uses backInStock (it's the same lifecycle event — restored).
      // 2026-05-06: optimistic localStatus depends on whether the row was
      // already closed. Open + running_low → must go back to PENDING (kitchen
      // still owes). Closed + running_low → stays as 'collected' (already
      // delivered, just clearing the storeroom flag). Without this, the
      // merge kept the stale 'running_low' status, which made the row
      // briefly land in the wrong COLLECTED TODAY bucket before the next
      // 5s poll corrected it.
      const wasClosed = actionItem && (actionItem.closed === true || actionItem.status === "delivered" || actionItem.status === "collected");
      events.push({action:"backInStock", closing:false, newFlag:null, localStatus: wasClosed ? "collected" : "requested"});
    }

    // Flow A: running-low checkbox delta is its own audit event. Skipped
    // when the primary is OOS (mutual exclusion) or Replenished (Replenished
    // already represents the same backInStock intent — running it through
    // the delta block would fire a redundant duplicate POST).
    if (isFlowA && selectedPrimary !== "out_of_stock" && selectedPrimary !== "replenished") {
      if (runningLowChecked && !wasPreTicked) {
        events.push({action:"stockRunningLow", closing:false, newFlag:"running_low", localStatus:null});
      } else if (!runningLowChecked && wasPreTicked) {
        // Standalone un-tick — same lifecycle as Replenished (backInStock).
        // 2026-05-06: optimistic localStatus mirrors the Replenished branch
        // so the row routes correctly without a 5s flicker.
        const wasClosedUntick = actionItem && (actionItem.closed === true || actionItem.status === "delivered" || actionItem.status === "collected");
        events.push({action:"backInStock", closing:false, newFlag:null, localStatus: wasClosedUntick ? "collected" : "requested"});
      } else if (runningLowChecked && wasPreTicked && selectedPrimary === "collected") {
        events.push({action:"stockRunningLow", closing:false, newFlag:"running_low", localStatus:null});
      }
    }
    return events;
  };

  const _isFlowB_forConfirm = actionItem && actionItem.flag === "out_of_stock";
  const runningLowWasPreTicked = actionItem && actionItem.flag === "running_low";
  const runningLowChanged = runningLowChecked !== runningLowWasPreTicked;
  const canConfirm = _isFlowB_forConfirm
    ? !!selectedPrimary
    : (!!selectedPrimary || runningLowChanged);

  // Fire the event sequence under one PIN with optimistic local mark.
  const sendActionSequence = (item, events)=>{
    if (!events.length) return;
    // 2026-05-19: kitchen-only events (stockDelivered + storeroom flags) must
    // happen at the central kitchen. Capture GPS BEFORE the PIN prompt so the
    // user gets a clear blocker if location is denied/unavailable instead of
    // a confusing post-PIN failure.
    const needsGeofence = events.some(ev => KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1);
    const proceedAfterCoords = (kitchenCoords)=>requestPin(async(staffName,staffPin)=>{
      const now = new Date();
      const timeStr = now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0");
      // Resolve the final flag (last newFlag in the sequence wins).
      const finalFlag = (() => {
        for (var i = events.length - 1; i >= 0; i--) {
          if (events[i].newFlag !== undefined) return events[i].newFlag;
        }
        return null;
      })();
      // Resolve the final optimistic status (last event with a localStatus
      // wins). This lets non-closing events also force a status — e.g.
      // "New stock arrived" forces status:"requested" so a row whose backend
      // status is stale-"delivered" still routes into Pending optimistically.
      const finalLocalStatus = (() => {
        for (var i = events.length - 1; i >= 0; i--) {
          if (events[i].localStatus) return events[i].localStatus;
        }
        return null;
      })();
      const closingEvent = events.find(e=>e.closing);
      // _resolvedToday is set ONLY when a closing event fired this session.
      // The merge layer reads it explicitly, so subsequent flag-only updates
      // can't leak it back in.
      const isResolving = !!closingEvent;

      if (finalLocalStatus) {
        setLocalMark(item.id, {
          status: finalLocalStatus,
          flag: finalFlag,
          by: staffName,
          at: timeStr,
          _resolvedToday: isResolving,
          _item: item.item
        });
      } else {
        // Pure flag-only update (e.g. Replenished, OOS toggle without status
        // change). Write just the flag so no stale fields leak through.
        setLocalMark(item.id, {flag: finalFlag, _item: item.item});
      }
      setMarksTick(t=>t+1);

      const today = new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"2-digit",year:"numeric"});
      events.forEach(ev => {
        const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1;
        const body = {
          action: ev.action,
          site,
          date: today,
          item: item.item,
          requestId: item.id,
          staffName, staffPin,
          requester_type: item.requester_type||"FOH",
          destination: "kitchen"
        };
        if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
        postKitchenAction(backend, body, isGeofenced).then(res=>{
          if (!res.ok && res.code === "OUTSIDE_KITCHEN_GEOFENCE") {
            setLocalMark(item.id, null);
            setMarksTick(t=>t+1);
            try { alert(res.message || "This action can only be performed at the kitchen."); } catch(_){}
          }
        });
      });
    });

    if (needsGeofence) {
      captureKitchenCoords()
        .then(coords => proceedAfterCoords(coords))
        .catch(err => { try { alert(err && err.message ? err.message : "Location required for this action."); } catch(_){} });
    } else {
      proceedAfterCoords(null);
    }
  };

  // ---------- BULK COLLECT (Pass 1) ----------
  // Plain pending rows + running-low rows are bulk-tickable. OOS / ordered /
  // already-collected stay on the single-item modal path (exception hatch).
  const isBulkable = (r)=>{
    if (!r) return false;
    if (r.ordered) return false;
    const status = r.status || "requested";
    if (status === "delivered" || status === "collected" || status === "received" || r.closed) return false;
    if (r.flag === "out_of_stock") return false;
    return true;
  };
  const togglePick = (id)=>{
    setBulkPicked(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const goToReview = ()=>{
    if (bulkPicked.size === 0) return;
    const allRows = mergedRequests;
    setBulkStockState(prev => {
      const next = {};
      Array.from(bulkPicked).forEach(id => {
        if (prev[id]) { next[id] = prev[id]; return; }
        const r = allRows.find(x => x.id === id);
        next[id] = r && r.flag === "running_low" ? "low" : "plenty";
      });
      return next;
    });
    setBulkStep("review");
  };
  // One PIN confirms every picked item. For each item we derive the correct
  // backend event sequence (mirrors the single-item primaryOptions logic) and
  // set an optimistic local mark so the row re-routes immediately.
  const submitBulk = ()=>{
    if (bulkPicked.size === 0) return;
    const ids = Array.from(bulkPicked);
    const items = ids.map(id => mergedRequests.find(r => r.id === id)).filter(Boolean);
    if (items.length === 0) return;
    // 2026-05-19: bulk-collect can fire geofenced actions (backInStock when
    // a flag changes, stockDelivered when collected, stockRunningLow /
    // stockOutOfStock on new flag). Pre-compute whether ANY item in the
    // batch will need geofence + capture GPS up front so the user gets a
    // clear blocker before the PIN prompt rather than silent backend
    // rejection through the no-cors fetches below.
    const willFireGeofencedAction = items.some(item => {
      const target = bulkStockState[item.id] || "plenty";
      const finalFlag = target === "low" ? "running_low" : target === "empty" ? "out_of_stock" : null;
      const wasFlag = item.flag || null;
      const isCollected = target !== "empty";
      if (wasFlag && wasFlag !== finalFlag) return true;             // backInStock
      if (isCollected) return true;                                  // stockDelivered
      if (finalFlag === "running_low" && wasFlag !== "running_low") return true;
      if (finalFlag === "out_of_stock" && wasFlag !== "out_of_stock") return true;
      return false;
    });
    const runSubmit = (kitchenCoords)=>requestPin(async(staffName,staffPin)=>{
      const now = new Date();
      const timeStr = now.getHours().toString().padStart(2,"0") + ":" + now.getMinutes().toString().padStart(2,"0");
      const today = new Date().toLocaleDateString("en-GB",{day:"2-digit",month:"2-digit",year:"numeric"});
      // Track per-item failures so we can revert their optimistic marks +
      // surface a single summary alert at the end. 2026-05-24: replaced the
      // raw mode:"no-cors" fetch loop that silently swallowed every backend
      // rejection (geofence, network, role gate) and left items ghosting on
      // the collect list for days after the local mark TTL expired.
      const perItemFailures = [];
      const allPromises = [];
      items.forEach(item => {
        const target = bulkStockState[item.id] || "plenty";
        const finalFlag = target === "low" ? "running_low" : target === "empty" ? "out_of_stock" : null;
        const wasFlag = item.flag || null;
        // 2026-05-07: Empty = "storeroom was empty, couldn't collect" — flag-only, request
        // stays open. Plenty / Low = "collected, here's what's left in the storeroom".
        const isCollected = target !== "empty";
        // Sequence: backInStock (clear prior flag) → stockDelivered (only if collected) → optional new flag
        const sequence = [];
        if (wasFlag && wasFlag !== finalFlag) sequence.push({action:"backInStock"});
        if (isCollected) sequence.push({action:"stockDelivered"});
        if (finalFlag === "running_low" && wasFlag !== "running_low") sequence.push({action:"stockRunningLow"});
        else if (finalFlag === "out_of_stock" && wasFlag !== "out_of_stock") sequence.push({action:"stockOutOfStock"});
        setLocalMark(item.id, {
          // Empty stays "requested" so the row remains in the OOS banner as PENDING,
          // not as "Collected" — was tagging OOS rows with status:collected which made
          // the subtitle read "Collected HH:MM" even though staff couldn't collect.
          status: isCollected ? "collected" : "requested",
          flag: finalFlag,
          by: staffName,
          at: timeStr,
          _resolvedToday: isCollected,
          _item: item.item
        });
        // Fire each event in this item's sequence with parsed-response. If
        // any action fails, revert this item's local mark (the optimistic
        // "collected" stamp) so the row reappears as pending on the next
        // poll instead of disappearing for 5 minutes and silently coming
        // back. Sequence promises are awaited in parallel inside the
        // item — order doesn't matter to the user-visible state since the
        // local mark is the optimistic carrier.
        const itemFireResults = sequence.map(ev => {
          const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1;
          const body = {
            action: ev.action,
            site,
            date: today,
            item: item.item,
            requestId: item.id,
            staffName, staffPin,
            requester_type: item.requester_type || "FOH",
            destination: "kitchen"
          };
          if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
          return postKitchenAction(backend, body, isGeofenced);
        });
        const itemPromise = Promise.all(itemFireResults).then(results => {
          const firstFail = results.find(r => r && r.ok === false);
          if (firstFail) {
            // Revert this item's local mark so the row reappears as pending
            // immediately, not after the 5-min local-mark TTL.
            setLocalMark(item.id, null);
            setMarksTick(s => s + 1);
            perItemFailures.push({item: item.item, msg: kitchenActionErrorMessage(firstFail, item.item)});
          }
        });
        allPromises.push(itemPromise);
      });
      setMarksTick(s => s + 1);
      setBulkPicked(new Set());
      setBulkStockState({});
      setBulkStep("pick");
      // After every per-item sequence resolves, surface a single summary
      // alert if any failed. One alert beats N alerts when a fleet of items
      // all hit the same geofence reject.
      Promise.all(allPromises).then(() => {
        if (perItemFailures.length === 0) return;
        try {
          const heading = perItemFailures.length === 1
            ? "Couldn't save 1 item"
            : "Couldn't save " + perItemFailures.length + " items";
          alert(heading + ":\n\n" + perItemFailures.map(f => "• " + f.msg).join("\n"));
        } catch(_){}
      });
    });
    if (willFireGeofencedAction) {
      captureKitchenCoords()
        .then(coords => runSubmit(coords))
        .catch(err => { try { alert(err && err.message ? err.message : "Location required for this action."); } catch(_){} });
    } else {
      runSubmit(null);
    }
  };
  // ---------- end BULK COLLECT ----------

  const confirmAction = ()=>{
    const events = eventsForConfirm();
    if (!events.length) return;
    const item = actionItem;
    // Every back_in_stock-shaped action requires a photo so the storeroom
    // restock is audit-trailed. Mirrors the parent StockRequestReceiver
    // path. Replenished folded in 2026-05-06 — same lifecycle event,
    // same audit requirement.
    if (selectedPrimary === "back_in_stock" ||
        selectedPrimary === "back_in_stock_collected" ||
        selectedPrimary === "replenished") {
      const photoEvents = events.map(ev => ({action: ev.action}));
      // 2026-05-19: backInStock + stockDelivered are kitchen-geofenced.
      // Capture GPS BEFORE opening the photo modal — if denied/unavailable,
      // block the user instead of letting them photo-confirm an action the
      // backend will silently reject via no-cors.
      const needsGeofence = photoEvents
        .some(ev => KITCHEN_GEOFENCED_ACTIONS.indexOf(ev.action) !== -1);
      const openModalWithCoords = (kitchenCoords)=>{
        setActionItem(null);
        setDeliveryConfirm({
          ...item,
          _orderedMeta: {refImageUrl:"", supplierRef:"", eta:"", note:""},
          _events: photoEvents,
          _selectedPrimary: selectedPrimary,
          _kitchenCoords: kitchenCoords,
          _title: selectedPrimary === "replenished" ? "🪫 Replenished stock" : "📦 New stock arrived"
        });
      };
      if (needsGeofence) {
        captureKitchenCoords()
          .then(coords => openModalWithCoords(coords))
          .catch(err => { try { alert(err && err.message ? err.message : "Location required for this action."); } catch(_){} });
      } else {
        openModalWithCoords(null);
      }
      return;
    }
    setActionItem(null);
    sendActionSequence(item, events);
  };

  // Loose time formatter for the modal header subline.
  const fmtTimeLoose = (v) => {
    if (!v) return "earlier";
    const s = String(v).trim();
    if (!s) return "earlier";
    if (/^\d{1,2}:\d{2}$/.test(s)) return s;
    const m = s.match(/(\d{1,2}):(\d{2})/);
    if (m) return m[1].padStart(2,"0") + ":" + m[2];
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
    return "earlier";
  };

  const pendingCount = pending.length;

  // Render helper — a single OOS / Running Low / Pending / Collected row.
  // Symbol-led (❗ / 🪫 / ✅), tap opens the action modal except for the
  // read-only Collected variant.
  const srCardLow = {background:"rgba(241,197,68,0.05)",border:"1px solid rgba(241,197,68,0.22)"};
  const renderRow = (r, opts)=>{
    const variant = opts && opts.variant; // "pending" | "oos" | "running_low" | "collected"
    const isJustArrived = justArrivedIds.has(r.id);
    const isOrdered = !!r.ordered;
    const isClosedRow = r.status === "delivered" || r.status === "collected" || r.status === "received" || r.closed;
    const tapOpens = variant !== "collected";
    // Bulk-collect Pass 1: pending + running-low rows are tickable. Other variants
    // (oos / collected / ordered) keep the single-item modal path.
    const bulkOk = isBulkable(r);
    const isPicked = bulkOk && bulkPicked.has(r.id);
    const rowStyle = {
      ...S.srCard,
      ...(variant === "oos" ? S.srCardOos
        : variant === "running_low" ? srCardLow
        : variant === "collected" ? S.srCardCollected
        : {}),
      ...(isJustArrived?{animation:"justArrivedFlash 2.4s ease-out"}:{}),
      ...(isPicked ? {boxShadow:"inset 4px 0 0 0 "+B.wasabi, background:"rgba(212,251,111,0.07)"} : {}),
      cursor: tapOpens ? "pointer" : "default"
    };
    const catEntry = catalogByName ? catalogByName[(r.item||"").toLowerCase()] : null;
    let leftIcon = null;
    if (variant === "oos") leftIcon = "❗";
    else if (variant === "running_low") leftIcon = "🪫";
    else if (variant === "collected") leftIcon = "✅";
    let rightPill = null;
    const __collectedByName = r.collectedBy || r.deliveredBy;
    const __collectedAtTime = fmtDeliveryClock(r.collectedAt || r.deliveredAt);
    if (variant === "collected" && __collectedByName) {
      rightPill = {label: "✅ "+(r.status==="received"?"Received":"Collected")+" by "+__collectedByName+(__collectedAtTime?" · "+__collectedAtTime:""), bg:"rgba(111,175,143,0.12)", fg:B.eucalyptus, border:"1px solid rgba(111,175,143,0.3)"};
    } else if (variant === "running_low" && isClosedRow && __collectedByName) {
      rightPill = {label:"✅ Already collected · "+__collectedByName, bg:"rgba(111,175,143,0.12)", fg:B.eucalyptus, border:"1px solid rgba(111,175,143,0.3)"};
    } else if (variant === "pending" && isOrdered) {
      rightPill = {label:"📦 ORDERED", bg:"rgba(212,251,111,0.12)", fg:B.wasabi, border:"1px solid rgba(212,251,111,0.35)"};
    }
    const onRowTap = bulkOk
      ? ()=>togglePick(r.id)
      : (tapOpens ? ()=>openCollectAction(r) : undefined);
    return (
      <div key={r.id||r.item} style={rowStyle} onClick={onRowTap}>
        {bulkOk && (
          <div aria-label={isPicked?"Untick":"Tick to collect"} style={{
            width:36,height:36,borderRadius:10,flexShrink:0,
            background: isPicked ? B.wasabi : "transparent",
            border: "2px solid " + (isPicked ? B.wasabi : "rgba(212,251,111,0.4)"),
            color: isPicked ? B.nori : "transparent",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:"1rem",fontWeight:800,marginRight:6,transition:"all 0.12s"
          }}>{isPicked ? "✓" : ""}</div>
        )}
        {leftIcon && <div style={{width:24,flexShrink:0,display:"flex",justifyContent:"center",fontSize:"1.05rem"}}>{leftIcon}</div>}
        <StockRequestItemDetail req={r} catalogEntry={catEntry} siteKey={site} onZoom={setThumbZoom} variant={variant}/>
        {rightPill && (
          <span style={{
            marginLeft:"auto",fontSize:"0.66rem",fontWeight:800,letterSpacing:"0.04em",
            padding:"3px 8px",borderRadius:999,whiteSpace:"nowrap",
            background: rightPill.bg, color: rightPill.fg, border: rightPill.border
          }}>{rightPill.label}</span>
        )}
      </div>
    );
  };

  const totalRows = oosItems.length + runningLowItems.length + pending.length + collectedToday.length;

  return (
    <div>
      {/* Header: site name on its own row, then a status row beneath with the
          PENDING/All clear pill on the LEFT and a simple Live pulse pill on
          the RIGHT. */}
      <div className="fc-site-header" style={{...S.siteSectionHeader, justifyContent:"center", flexDirection:"column", gap:6}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
          <span style={S.siteSectionDot}/>
          {siteName}
        </div>
      </div>
      {!loading && !error && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 4px",marginBottom:10,gap:8,flexWrap:"wrap"}}>
          <div>
            {(()=>{
              // Per-site count: when flagged sections are rendered cross-site
              // by the parent, they're not "pending here" — only count rows
              // that this site card actually displays (pending no-flag + the
              // flagged ones if hidden=false).
              const visibleCount = pending.length + (hideFlaggedSections ? runningLowOpen.length : (oosItems.length + runningLowItems.length));
              return visibleCount > 0
                ? <span style={S.pendingPill}><span style={S.pendingDot}/>{visibleCount} pending</span>
                : <span style={S.confirmedPill}>✓ All clear</span>;
            })()}
          </div>
          {lastFetchedAt && (() => {
            // Negative animation-delay computed from wall-clock so every Live
            // pill on the page pulses in the same phase regardless of when
            // its parent component mounted. Both site cards therefore breathe
            // together on screen.
            const __pillDelay = "-" + ((Date.now() % 2400) / 1000) + "s";
            const __dotDelay  = "-" + ((Date.now() % 1800) / 1000) + "s";
            return (
              <button
                type="button"
                onClick={()=>loadRef.current && loadRef.current(true)}
                title="Tap to refresh"
                style={{
                  display:"inline-flex",alignItems:"center",gap:6,
                  fontSize:"0.62rem",fontWeight:800,color:B.eucalyptus,
                  background:"rgba(111,175,143,0.1)",border:"1px solid rgba(111,175,143,0.3)",
                  borderRadius:999,padding:"3px 10px",letterSpacing:"0.08em",textTransform:"uppercase",
                  cursor:"pointer",fontFamily:"Manrope,sans-serif",
                  animation:"livePulse 2.4s ease-in-out infinite",
                  animationDelay:__pillDelay
                }}>
                <span style={{width:6,height:6,borderRadius:"50%",background:B.eucalyptus}}/>
                Live
              </button>
            );
          })()}
        </div>
      )}

      {/* Loading — only shows spinner after 400ms delay to avoid flash */}
      {loading && showSpinner && <div style={S.loadingBox}><div style={S.spinner}/><div style={{marginTop:12,color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>Loading {siteName} requests…</div></div>}
      {loading && !showSpinner && <><div style={S.srSkeletonCard}/><div style={S.srSkeletonCard}/></>}

      {error && <div style={{...S.sectionCard,borderColor:"rgba(255,122,92,0.3)"}}>
        <div style={{color:B.coral,fontSize:"0.85rem",fontWeight:600}}>Failed to load</div>
        <div style={{color:B.kewpie,opacity:0.5,fontSize:"0.78rem",marginTop:4}}>{error}</div>
        <button style={{...S.markBtn,marginTop:10}} onClick={()=>loadRequests(true)}>Retry</button>
      </div>}

      {!loading && !error && totalRows===0 && (
        <div style={S.emptyState}>No stock requests from {siteName}</div>
      )}

      {/* STEP 1 (PICK) — list of stock-request rows. Replaced by Review screen when bulkStep="review". */}
      {bulkStep === "pick" && <>
      {/* OUT OF STOCK + RUNNING LOW — only render here when the parent is
          NOT showing them cross-site. With the kitchen receiver's storeroom
          aggregation enabled (hideFlaggedSections=true), these sections are
          rendered above this card by StockRequestReceiver. */}
      {!hideFlaggedSections && !loading && !error && oosItems.length>0 && (
        <div style={{...S.secTitle,marginTop:4,marginBottom:6,color:"#FF9B83"}}>❗ OUT OF STOCK</div>
      )}
      {!hideFlaggedSections && !loading && !error && oosItems.map(r => renderRow(r, {variant:"oos"}))}

      {!hideFlaggedSections && !loading && !error && runningLowItems.length>0 && (
        <div style={{...S.secTitle,marginTop:oosItems.length>0?16:4,marginBottom:6,color:"#F1C544"}}>🪫 RUNNING LOW</div>
      )}
      {!hideFlaggedSections && !loading && !error && runningLowItems.map(r => renderRow(r, {variant:"running_low"}))}

      {/* RUNNING LOW (uncollected) — when flagged sections are hidden cross-site,
          the per-site card still shows running-low rows that haven't been
          collected yet, because Arndale/TC are still owed stock. Collected
          running-low rows live ONLY in the cross-site STOREROOM banner with
          a ✓ collected pill. 2026-05-06. */}
      {hideFlaggedSections && !loading && !error && runningLowOpen.length>0 && (
        <div style={{...S.secTitle,marginTop:4,marginBottom:6,color:"#F1C544"}}>🪫 RUNNING LOW</div>
      )}
      {hideFlaggedSections && !loading && !error && runningLowOpen.map(r => renderRow(r, {variant:"running_low"}))}

      {/* PENDING — open requests with no flag. Tap to open the action modal. */}
      {!loading && !error && pending.length>0 && (
        <div style={{...S.secTitle,marginTop:((!hideFlaggedSections && (oosItems.length+runningLowItems.length)>0) || (hideFlaggedSections && runningLowOpen.length>0))?16:4,marginBottom:6}}>PENDING</div>
      )}
      {!loading && !error && pending.map(r => renderRow(r, {variant:"pending"}))}

      {/* COLLECTED TODAY — items resolved today with no flag, read-only.
          Hidden in the per-site card when the parent kitchen receiver is
          rendering a combined COLLECTED TODAY section below all live lists
          (hideCollectedToday=true). 2026-05-06. */}
      {!hideCollectedToday && !loading && !error && collectedToday.length>0 && (
        <div style={{...S.secTitle,marginTop:(pending.length>0||(!hideFlaggedSections && (oosItems.length+runningLowItems.length)>0))?16:4,marginBottom:6,color:B.eucalyptus}}>✅ COLLECTED TODAY</div>
      )}
      {!hideCollectedToday && !loading && !error && collectedToday.map(r => renderRow(r, {variant:"collected"}))}
      </>}

      {/* STEP 2 (REVIEW) — replaces the list with a per-item Plenty / Low / Empty
          segmented control. One PIN at the bottom confirms the whole batch. */}
      {bulkStep === "review" && (()=>{
        const pickedRows = Array.from(bulkPicked).map(id => mergedRequests.find(r => r.id === id)).filter(Boolean);
        const lowCount = pickedRows.filter(r => bulkStockState[r.id] === "low").length;
        const emptyCount = pickedRows.filter(r => bulkStockState[r.id] === "empty").length;
        const opts = [
          {key:"plenty", label:"Plenty", icon:"⚡", activeBg:"#6FAF8F", activeColor:B.nori, color:"#9adcb6"},
          {key:"low",    label:"Low",    icon:"🪫", activeBg:"#F1C544", activeColor:B.nori, color:"#f5cf6c"},
          {key:"empty",  label:"Empty",  icon:"❌", activeBg:"#FF7A5C", activeColor:B.nori, color:"#ff8d7a"}
        ];
        return (
          <div style={{padding:"4px 0 8px"}}>
            <div style={{padding:"12px 14px",borderRadius:12,background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.2)",marginBottom:14}}>
              <div style={{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi,letterSpacing:"-0.01em",marginBottom:4}}>🪫 Quick check before confirming</div>
              <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.82,lineHeight:1.5}}>What did you find in the storeroom? <strong>Plenty</strong> = collected, plenty left. <strong>Low</strong> = collected, flag the storeroom. <strong>Empty</strong> = couldn't collect, flag as out of stock.</div>
            </div>
            <div style={{display:"grid",gap:8}}>
              {pickedRows.map(r => {
                const s = bulkStockState[r.id] || "plenty";
                const catEntry = catalogByName ? catalogByName[(r.item||"").toLowerCase()] : null;
                const img = catEntry && catEntry.imageUrl;
                const qty = catEntry && (catEntry.siteDefaults && catEntry.siteDefaults[site] !== undefined && catEntry.siteDefaults[site] !== ""
                      ? catEntry.siteDefaults[site]
                      : (catEntry.defaultQty !== null && catEntry.defaultQty !== "" ? catEntry.defaultQty : null));
                const unit = catEntry && catEntry.unit ? String(catEntry.unit).trim() : "";
                return (
                  <div key={r.id} style={{padding:"12px 14px",borderRadius:12,background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.16)",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <CatalogThumb item={r.item} imageUrl={img} size={44} onZoom={setThumbZoom}/>
                    <div style={{flex:1,minWidth:140}}>
                      <div style={{fontWeight:700,fontSize:"0.92rem",color:B.kewpie,letterSpacing:"-0.01em"}}>{r.item}</div>
                      <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.6,marginTop:2}}>
                        {qty != null && qty !== "" && <>Bring {qty}{unit?" "+unit:""} · </>}
                        {r.requestedBy || "Kiosk"}
                      </div>
                    </div>
                    <div role="radiogroup" aria-label="Storeroom state after collection" style={{display:"flex",gap:4,padding:4,borderRadius:11,background:"rgba(0,28,22,0.6)",border:"1px solid rgba(212,251,111,0.12)"}}>
                      {opts.map(o => {
                        const active = s === o.key;
                        return (
                          <button key={o.key} role="radio" aria-checked={active} onClick={()=>setBulkStockState(st => ({...st, [r.id]: o.key}))} style={{
                            flex:"1 1 0",minWidth:78,padding:"10px 8px",borderRadius:8,border:"none",
                            background: active ? o.activeBg : "transparent",
                            color: active ? o.activeColor : o.color,
                            fontSize:"0.7rem",fontWeight:800,cursor:"pointer",fontFamily:"inherit",
                            letterSpacing:"0.04em",textTransform:"uppercase",
                            display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5,minHeight:42,transition:"all 0.12s"
                          }}><span style={{fontSize:"0.85rem"}}>{o.icon}</span>{o.label}</button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"rgba(0,28,22,0.55)",border:"1px solid rgba(212,251,111,0.1)",fontSize:"0.78rem",color:B.kewpie,textAlign:"center",opacity:0.85}}>
              {(()=>{
                const collectedCount = pickedRows.length - emptyCount;
                if (lowCount + emptyCount === 0) return <>All <strong style={{color:B.eucalyptus}}>{pickedRows.length}</strong> items will be marked collected · no storeroom flags</>;
                return <>
                  {collectedCount > 0 && <>Collecting <strong style={{color:B.wasabi}}>{collectedCount}</strong></>}
                  {lowCount > 0 && <>{collectedCount>0?" · ":""}<strong style={{color:"#F1C544"}}>{lowCount}</strong> running low</>}
                  {emptyCount > 0 && <>{(collectedCount>0||lowCount>0)?" · ":""}flagging <strong style={{color:"#FF7A5C"}}>{emptyCount}</strong> out of stock <span style={{opacity:0.7}}>(not collected)</span></>}
                </>;
              })()}
            </div>
          </div>
        );
      })()}

      {/* Bulk-collect bar — sits at the bottom of this site's card. Step 1: Review N →. Step 2: Back / Confirm. */}
      {(bulkStep === "pick" && bulkPicked.size > 0) && (
        <div style={{marginTop:14,padding:"12px 14px",borderRadius:14,background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,251,111,0.6)",animation:"fadeUp 0.22s ease both"}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:"0.62rem",fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"rgba(0,57,44,0.65)"}}>Step 1 of 3</div>
            <div style={{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,letterSpacing:"-0.01em"}}>{bulkPicked.size} item{bulkPicked.size===1?"":"s"} ticked</div>
          </div>
          <button onClick={()=>setBulkPicked(new Set())} style={{padding:"10px 14px",borderRadius:10,background:"transparent",border:"1px solid rgba(0,57,44,0.25)",color:B.nori,fontSize:"0.7rem",fontWeight:800,letterSpacing:"0.04em",textTransform:"uppercase",cursor:"pointer",fontFamily:"inherit",minHeight:44}}>Clear</button>
          <button onClick={goToReview} style={{padding:"12px 18px",borderRadius:11,background:B.nori,color:B.wasabi,border:"none",fontSize:"0.85rem",fontWeight:800,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.02em",whiteSpace:"nowrap",minHeight:44}}>Review →</button>
        </div>
      )}
      {bulkStep === "review" && (
        <div style={{marginTop:14,padding:"12px 14px",borderRadius:14,background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,boxShadow:"0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,251,111,0.6)",animation:"fadeUp 0.22s ease both"}}>
          <button onClick={()=>setBulkStep("pick")} style={{padding:"12px 16px",borderRadius:10,background:"transparent",border:"1px solid rgba(0,57,44,0.3)",color:B.nori,fontSize:"0.85rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",minHeight:44}}>← Back</button>
          <button onClick={submitBulk} disabled={bulkPicked.size===0} style={{padding:"12px 18px",borderRadius:11,background:bulkPicked.size===0?"rgba(0,57,44,0.18)":B.nori,color:bulkPicked.size===0?"rgba(0,57,44,0.5)":B.wasabi,border:"none",fontSize:"0.85rem",fontWeight:800,cursor:bulkPicked.size===0?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:"0.02em",whiteSpace:"nowrap",minHeight:44}}>📦 Confirm with PIN →</button>
        </div>
      )}

      {/* Action modal — Meadowhall housed-model port. Two flows depending on
          the row's current flag state. ORDERED rows never reach this modal —
          they divert to DeliveryConfirmModal via openCollectAction. */}
      {actionItem && (()=>{
        const isClosed = actionItem.closed || actionItem.status === "delivered" || actionItem.status === "collected";
        const isFlowB = actionItem.flag === "out_of_stock";
        const opts = primaryOptions(actionItem);
        const catEntry = catalogByName ? catalogByName[(actionItem.item||"").toLowerCase()] : null;
        const img = catEntry && catEntry.imageUrl;
        const qty = catEntry && (catEntry.siteDefaults && catEntry.siteDefaults[site] !== undefined && catEntry.siteDefaults[site] !== ""
              ? catEntry.siteDefaults[site]
              : (catEntry.defaultQty !== null && catEntry.defaultQty !== "" ? catEntry.defaultQty : null));
        const unit = catEntry && catEntry.unit ? String(catEntry.unit).trim() : "";
        const toneStyle = (tone, selected) => {
          const styles = {
            primary: {bg:"rgba(212,251,111,0.15)", border:"1px solid rgba(212,251,111,0.45)", fg:B.wasabi},
            red:     {bg:"rgba(255,122,92,0.12)",  border:"1px solid rgba(255,122,92,0.3)",   fg:"#FF9B83"},
            green:   {bg:"rgba(111,175,143,0.14)", border:"1px solid rgba(111,175,143,0.3)",  fg:"#9DD0B0"}
          };
          if (!selected) return {bg:"transparent", border:"1px solid rgba(212,251,111,0.12)", fg:B.kewpie};
          return styles[tone] || styles.primary;
        };
        const runningLowDisabled = isClosed || (selectedPrimary === "out_of_stock");
        return (
          <div role="dialog" aria-modal="true" onClick={()=>setActionItem(null)} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.72)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:600,padding:20,backdropFilter:"blur(2px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:440,maxHeight:"calc(100vh - 40px)",overflowY:"auto",background:"#002822",borderRadius:20,padding:"22px 22px 24px",color:B.kewpie,boxShadow:"0 12px 48px rgba(0,0,0,0.5)",animation:"fadeUp 0.22s ease both"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                <CatalogThumb item={actionItem.item} imageUrl={img} size={64} onZoom={setThumbZoom}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"gelica,serif",fontSize:"1.2rem",fontWeight:700,letterSpacing:"-0.01em"}}>{actionItem.item}</div>
                  <div style={{fontSize:"0.75rem",opacity:0.55,marginTop:2}}>
                    {qty != null && qty !== "" && <>Bring {qty}{unit?" "+unit:""} · </>}
                    Raised {actionItem.time || "earlier"}{actionItem.requestedBy ? " · "+actionItem.requestedBy : ""}
                  </div>
                </div>
              </div>

              {!isClosed && isFlowB && (
                <div style={{background:"rgba(255,122,92,0.14)",border:"1px solid rgba(255,122,92,0.32)",color:"#FF9B83",padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,fontWeight:700}}>🚨 Currently flagged Out of Stock</div>
              )}
              {!isClosed && !isFlowB && actionItem.flag === "running_low" && (
                <div style={{background:"rgba(241,197,68,0.12)",border:"1px solid rgba(241,197,68,0.3)",color:"#F1C544",padding:"10px 12px",borderRadius:10,fontSize:"0.78rem",marginBottom:14,fontWeight:700}}>⚠ Currently flagged Running Low</div>
              )}

              <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:B.kewpie,opacity:0.5,marginBottom:8}}>{isClosed ? "Stock update" : isFlowB ? "What did you find?" : "What happened?"}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:isFlowB?18:14}}>
                {opts.map(opt => {
                  const selected = selectedPrimary === opt.key;
                  const style = toneStyle(opt.tone, selected);
                  return (
                    <button key={opt.key} onClick={()=>setSelectedPrimary(opt.key)} style={{
                      width:"100%",textAlign:"left",padding:"12px 14px",borderRadius:12,cursor:"pointer",
                      background:style.bg,border:style.border,color:style.fg,fontFamily:"inherit",
                      transition:"all 0.15s ease"
                    }}>
                      <div style={{fontWeight:800,fontSize:"0.92rem",letterSpacing:"0.02em"}}>{opt.label}</div>
                      <div style={{fontSize:"0.72rem",opacity:0.75,marginTop:3,lineHeight:1.4}}>{opt.sub}</div>
                    </button>
                  );
                })}
              </div>

              {/* Running Low checkbox — Flow A no-flag only. Running-low rows
                  use the explicit "Replenished stock" primary option instead. */}
              {!isFlowB && !isClosed && !actionItem.flag && (<>
                <div style={{height:1,background:"rgba(254,250,240,0.08)",margin:"4px 0 14px"}}/>
                <button onClick={()=>{if(!runningLowDisabled) setRunningLowChecked(!runningLowChecked)}} disabled={runningLowDisabled} style={{
                  width:"100%",textAlign:"left",padding:"12px 14px",borderRadius:12,fontFamily:"inherit",
                  background: runningLowDisabled ? "rgba(254,250,240,0.03)" : runningLowChecked ? "rgba(241,197,68,0.14)" : "transparent",
                  border: "1px solid " + (runningLowDisabled ? "rgba(254,250,240,0.06)" : runningLowChecked ? "rgba(241,197,68,0.35)" : "rgba(241,197,68,0.18)"),
                  color: runningLowDisabled ? "rgba(254,250,240,0.35)" : "#F1C544",
                  cursor: runningLowDisabled ? "not-allowed" : "pointer",
                  display:"flex",alignItems:"center",gap:12,marginBottom:18
                }}>
                  <div style={{width:22,height:22,borderRadius:6,flexShrink:0,border:"2px solid "+(runningLowDisabled?"rgba(254,250,240,0.18)":runningLowChecked?"#F1C544":"rgba(241,197,68,0.45)"),background:runningLowChecked?"#F1C544":"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.85rem",color:B.nori,fontWeight:800}}>
                    {runningLowChecked ? "✓" : ""}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:"0.88rem",letterSpacing:"0.02em"}}>⚠ {actionItem.flag === "running_low" ? "Keep running-low flag" : "Also flag as running low"}</div>
                    <div style={{fontSize:"0.7rem",opacity:0.7,marginTop:2,lineHeight:1.4}}>
                      {runningLowDisabled
                        ? "Not available when Out of Stock is selected"
                        : actionItem.flag === "running_low" && !runningLowChecked
                          ? "Un-ticked = stock is back to normal (logs an audit row)"
                          : "Store room was thin — flag for the kitchen + admin (separate PIN-audited event)"}
                    </div>
                  </div>
                </button>
              </>)}

              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setActionItem(null)} style={{flex:1,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.12)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.75}}>Cancel</button>
                <button onClick={confirmAction} disabled={!canConfirm} style={{flex:2,padding:"14px 16px",borderRadius:12,border:"none",background:canConfirm?B.wasabi:"rgba(212,251,111,0.2)",color:B.nori,fontFamily:"inherit",fontWeight:800,fontSize:"0.9rem",cursor:canConfirm?"pointer":"not-allowed",opacity:canConfirm?1:0.45,letterSpacing:"0.02em"}}>Confirm</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Photo-required Confirm Delivery modal — for ORDERED rows only */}
      {deliveryConfirm && (
        <DeliveryConfirmModal
          item={deliveryConfirm}
          backend={backend}
          site={site}
          requestPin={requestPin}
          events={deliveryConfirm._events}
          kitchenCoords={deliveryConfirm._kitchenCoords}
          title={deliveryConfirm._title}
          onClose={()=>setDeliveryConfirm(null)}
          onConfirmed={()=>{
            const target = deliveryConfirm;
            setDeliveryConfirm(null);
            if (target && target.id) {
              const now = new Date();
              const t = now.getHours().toString().padStart(2,"0")+":"+now.getMinutes().toString().padStart(2,"0");
              setLocalMark(target.id, {status:"delivered", flag:null, by:"(photo-confirmed)", at:t, _item: target.item});
              setMarksTick(s=>s+1);
            }
            loadRef.current && loadRef.current(false);
          }}
          onActionFailed={(failedItem)=>{
            // 2026-05-24: revert the optimistic close so the row reappears
            // as pending on the next poll instead of silently ghosting.
            if (failedItem && failedItem.id) {
              setLocalMark(failedItem.id, null);
              setMarksTick(s=>s+1);
              loadRef.current && loadRef.current(false);
            }
          }}
        />
      )}

      {thumbZoom && <CatalogThumbLightbox item={thumbZoom.item} imageUrl={thumbZoom.imageUrl} onClose={()=>setThumbZoom(null)}/>}
    </div>
  );
}

// ===========================================
// INBOUND DELIVERIES — 2026-05-04
// ===========================================
// BOH/site staff confirm a delivery with a photo. Two paths:
//   1) From running list — pick a stock item that's currently outstanding
//      (running_low / out_of_stock / ordered) so the photo + delivery
//      event ties back to the in-flight stock record.
//   2) Custom — free-text for one-offs that aren't in the catalogue
//      (e.g. slushie machine, marketing signage, a replacement door key).
// Photo is mandatory in both paths — there's no skip-photo path off this
// surface. Submitting fires `stockDelivered` (running list) or a new
// `customDeliveryReceived` (custom path) event with the photo URL + note.
function InboundDeliveries({backend,requestPin,stockCatalog,site}){
  // Per-site running list — fetched on mount + refreshed every 60s while
  // mounted. Mirrors the FOH Collect tab's data source so the items the
  // staff are matching against are exactly what's currently outstanding.
  //
  // 2026-05-26: stale-while-revalidate cache. The read is now Supabase on a
  // flipped host (see loadRunningList; the old Apps Script round-trip was 2–5s
  // per site on the kitchen tablets) but still not instant, so the SWR seed
  // stands: the previous "Loading running list…" blank-state on every mount was
  // killing efficiency. Seed from localStorage so the list paints instantly,
  // then refresh in the background. Mirrors the XS_CACHE_KEY pattern in
  // StockRequestReceiver.
  const RUNNING_LIST_CACHE_KEY = (site && site.key)
    ? "rolled.inbound.runningList::" + site.key
    : "rolled.kitchen.inbound.runningList";
  const seedRunningListFromCache = () => {
    try {
      const c = localStorage.getItem(RUNNING_LIST_CACHE_KEY);
      if (c) { const parsed = JSON.parse(c); if (Array.isArray(parsed)) return parsed; }
    } catch (e) {}
    return null;
  };
  const cachedSeed = seedRunningListFromCache();
  const [runningList,setRunningList] = useState(cachedSeed || []);
  // Spinner only shows on truly cold start (no cache entry at all). A
  // cached empty array is a meaningful answer — paint the empty state
  // immediately, refresh in the background, no loading flash.
  const [loadingList,setLoadingList] = useState(cachedSeed === null);
  const [listErr,setListErr] = useState(null);
  // Holds the freshest loadRunningList closure for the 60s poll (see below).
  const loadListRef = useRef(null);

  // Recent inbound deliveries — populated optimistically as staff log new
  // deliveries this session. Persisted history lives in admin Stock Activity;
  // this is just a session-local strip so staff can confirm "yes, I just
  // logged that" without flipping tabs.
  const [recentDeliveries,setRecentDeliveries] = useState([]);

  // MULTI-PHOTO (2026-09-10): a delivery can have SEVERAL photos (different
  // pallets/boxes). Each entry: {id, dataUrl, mime, filename, capturedAt,
  // gpsLat, gpsLng, exifPresent, source}. EXIF/source resolved per photo
  // (2026-05-12 3-signal resolver: EXIF DateTimeOriginal → Samsung filename
  // pattern → file.lastModified). The first photo is the primary (shown as the
  // per-item thumbnail + used for the kitchen geofence); the rest ride along.
  const [photos,setPhotos] = useState([]);
  const [note,setNote] = useState("");
  const [submitting,setSubmitting] = useState(false);
  const [uploadStatus,setUploadStatus] = useState("");   // progress line while photos upload
  // Idempotency token for the batch write — stable across retries (internal +
  // a manual re-tap) so a flaky-/exec false-failure can be re-sent without
  // double-logging; the backend dedups per (token,item). Cleared on a clean log.
  const clientTokenRef = useRef("");
  const [error,setError] = useState("");
  const [search,setSearch] = useState("");
  const [customText,setCustomText] = useState("");
  const [tab,setTab] = useState("running"); // "running" | "custom"
  const [photoLightbox,setPhotoLightbox] = useState(null);
  // Tap-to-enlarge for catalogue thumbnails on the running list.
  const [thumbZoom,setThumbZoom] = useState(null);
  // "Did you mean…" modal when the typed custom text clashes with a catalogue
  // SKU. Prevents one-off entries like Iraj's "Rolled stickers" (2026-05-26)
  // bypassing the running list. Mirrors the FOH Stock Requests matcher.
  const [customBlock,setCustomBlock] = useState(null);

  // ---- BULK RECEIVE (2026-09-09) --------------------------------------
  // The tile logs a whole delivery in ONE pass: tick many items into a cart
  // (optional per-item count), take ONE photo of the drop, submit once → a
  // single receiveInboundBatch POST. Replaces the old one-item-at-a-time flow
  // (own photo + own PIN + two writes PER item) that made a big drop — like
  // Sonam's 8-line sweets delivery on 2026-09-08 — take minutes at the tablet.
  // Cart entries: {key,item,site,kind:"stock"|"custom",type,qty|null,row}.
  const [cart,setCart] = useState([]);
  const [stage,setStage] = useState("build");   // "build" (pick items) | "review" (photo + confirm)
  const cartKey = (kind,item,site) => kind+"|"+String(item||"").toLowerCase()+"|"+(site||"");
  const inCart = (kind,item,site) => cart.some(c => c.key === cartKey(kind,item,site));
  const addToCart = (entry) => {
    const key = cartKey(entry.kind, entry.item, entry.site);
    // Count defaults to 1 (the stepper shows immediately — JJ 2026-09-10); staff
    // bump it up for multi-packs, or leave it if the exact count doesn't matter.
    setCart(prev => prev.some(c=>c.key===key) ? prev : [...prev, {...entry, key, qty: entry.qty ?? 1}]);
  };
  const removeFromCart = (key) => setCart(prev => prev.filter(c => c.key !== key));
  // Count stepper: clamped to [1, 999] — never below 1 (remove the row instead).
  const bumpQty = (key, d) => setCart(prev => prev.map(c =>
    c.key === key ? {...c, qty: Math.max(1, Math.min(999, (c.qty || 1) + d))} : c
  ));

  // Inbound = deliveries arriving AT this site from suppliers. Kiosk stock
  // requests are not inbound deliveries for the kitchen — they're handed off
  // via the driver flow + tracked in the Stock Requests tile (the kitchen
  // staging board). When `site` prop is supplied (Meadowhall mount), fetch
  // that site's running list; in kitchen mode, scope to kitchen only.
  // Either way: filter out closed rows so the picker shows only currently-
  // open items expected at this site.
  const loadRunningList = () => {
    // Background refresh — don't touch loadingList. The spinner is only
    // for the truly-first-ever-load case, set during useState init from
    // the cache miss. Once a fetch completes (success OR empty), the list
    // is "loaded" forever for this component lifetime.
    setListErr(null);
    // Kitchen mode (no `site` prop): the Inbound running list is the
    // "what's arriving" board. Show everything the kitchen is expecting to
    // replenish from a supplier — items marked ORDERED or flagged Out of
    // Stock / Running Low — across BOTH kiosks + the kitchen storeroom.
    // (2026-06-08: was site=kitchen open-requests only, so reordered kiosk
    // items like Menu Flyers never appeared here to be received.) The
    // Meadowhall mount keeps its single-site open-request behaviour.
    const isKitchenMode = !(site && site.key);
    const targets = isKitchenMode ? ["tc", "kitchen"] : [site.key];  // arndale dropped 2026-08-17 (site closed)
    const awaitingReplenish = (x) => x.ordered === true || x.flag === "out_of_stock" || x.flag === "running_low";
    const listFilter = isKitchenMode ? awaitingReplenish : (x => !x.closed);
    // READ FLIP (2026-09-09) — this was the LAST screen still fetching the raw
    // Apps Script getStockRequests feed (2–5s per site; the kitchen mount does
    // TWO, so the Inbound list opened cold behind ~4–10s while every other
    // stock screen already read Supabase after the 31 Aug estate flip). Route it
    // through the same live reader (CC-ordered overlay + sheet fallback baked in)
    // when the host is flipped AND the active catalog has loaded — a null catalog
    // makes the reader fail OPEN (emits deactivated items), so stay on the
    // server-filtered sheet until stockCatalog populates. Field-for-field
    // compatible: the reader is a faithful port of the feed, so listFilter
    // (ordered/flag/closed) applies unchanged. Mirrors StockRequestReceiver +
    // the FOH Collect tab. Revert = the flip lives in stockReader.STOCK_READ_HOSTS.
    const activeNames = (() => {
      const n = (stockCatalog || []).filter(c => c && c.name && c.status !== "inactive").map(c => String(c.name).toLowerCase());
      return n.length ? new Set(n) : null;
    })();
    const flipped = stockReader.stockReadEnabled() && activeNames !== null;
    Promise.all(targets.map(s => {
      const sheetUrl = backend + "?action=getStockRequests&site=" + s;
      return (flipped
        ? stockReader.fetchStockRequests(s, { sheetUrl, activeItemNames: activeNames })
        : fetch(sheetUrl, {redirect:"follow", credentials:"omit"}).then(r => r.ok ? r.json() : {requests:[]}))
        .then(d => ((d && d.requests) || []).filter(listFilter).map(x => ({...x, site:s})))
        .catch(() => []);
    })).then(results => {
      const flat = results.flat();
      setRunningList(flat);
      setLoadingList(false);
      // Persist for next mount — SWR seed.
      try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(flat)); } catch (e) {}
    }).catch(e => {
      setListErr(e.message || "Failed to load running list");
      setLoadingList(false);
    });
  };
  // Poll through a ref (reassigned every render) so the 60s refresh always runs
  // the LATEST closure — since the read now depends on the stockCatalog prop for
  // its active-name filter, a stale closure could otherwise pin the very first
  // render's (possibly empty) catalog for the tile's whole life and never engage
  // the Supabase flip. Mirrors StockRequestReceiver (loadRef) + xsLoadAll (xsLoadRef).
  loadListRef.current = loadRunningList;
  useEffect(()=>{ loadListRef.current(); const t=setInterval(()=>{ loadListRef.current && loadListRef.current(); },60000); return ()=>clearInterval(t); },[]);

  const filteredList = useMemo(()=>{
    const q = search.trim().toLowerCase();
    if (!q) return runningList;
    return runningList.filter(r => (r.item||"").toLowerCase().includes(q) || (r.requestedBy||"").toLowerCase().includes(q));
  },[runningList, search]);

  // Source param ('camera' | 'library') comes from which input fired — more
  // reliable than time-gap heuristic which mis-classified library picks as
  // 'camera' when Samsung's picker reset file.lastModified to now on copy.
  // Resolve one file's EXIF/source and APPEND it to the photo set (multi-photo).
  const handleFile = async (file, source) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { setError("Photo too large — keep it under 6MB"); return; }
    setError("");
    const mime = file.type || "image/jpeg";
    const src = (source === "camera" || source === "library") ? source : "unknown";
    let resolvedTs = null, hasExifEvidence = false, gLat = null, gLng = null;
    try {
      if (window.exifr && typeof window.exifr.parse === "function") {
        const meta = await window.exifr.parse(file, { tiff: true, exif: true, gps: true, ifd0: true });
        if (meta) {
          const dt = meta.DateTimeOriginal || meta.CreateDate;
          if (dt) { resolvedTs = new Date(dt).getTime(); hasExifEvidence = true; }
          if (typeof meta.latitude === "number") { gLat = meta.latitude; hasExifEvidence = true; }
          if (typeof meta.longitude === "number") gLng = meta.longitude;
        }
      }
    } catch (e) { /* non-fatal */ }
    if (!resolvedTs && file.name) {
      const m = file.name.match(/(\d{4})(\d{2})(\d{2})[_-]?(\d{2})(\d{2})(\d{2})/);
      if (m) {
        const ts = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])).getTime();
        if (!isNaN(ts) && ts > 0) resolvedTs = ts;
      }
    }
    if (!resolvedTs && file.lastModified) resolvedTs = file.lastModified;
    const capturedAt = resolvedTs ? new Date(resolvedTs).toISOString() : null;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotos(prev => [...prev, {
        id: "p" + Date.now() + "-" + prev.length,
        dataUrl: String(reader.result || ""),
        mime, filename: file.name || "delivery.jpg",
        capturedAt, gpsLat: gLat, gpsLng: gLng, exifPresent: hasExifEvidence, source: src
      }]);
    };
    reader.onerror = () => setError("Could not read file");
    reader.readAsDataURL(file);
  };
  const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

  // Full clear — after a successful batch log. (Stage-2 "Back" keeps the cart
  // and photo and just flips stage to "build".)
  const reset = () => {
    setCart([]); setStage("build");
    setPhotos([]); setNote("");
    setSearch(""); setCustomText(""); setSubmitting(false); setUploadStatus(""); setError("");
    clientTokenRef.current = "";   // fresh idempotency token for the next delivery
  };

  // Label for the shared photo upload + the recent-deliveries strip: the item
  // name for a single-line drop, else an "N-item delivery" summary.
  const batchLabel = cart.length === 1 ? cart[0].item : (cart.length + "-item delivery");

  const canSubmit = photos.length > 0 && cart.length > 0 && !submitting;

  // Catalog-match guard for the Custom tab. Mirrors the FOH Stock Requests
  // matcher (Trafford/Arndale index.html → findCatalogMatches_). Scoring:
  //   - distinctive token hits (×1.5 each) so "rolled stickers" hits Rolled
  //     Stickers even in a verbose note;
  //   - full-string / reverse-substring / subsequence boosts;
  //   - threshold ≥1.0 to drop single-shared-word noise.
  //   - off-catalogue guard: if the typed text contains a distinctive token
  //     that appears in NO catalogue item (e.g. "slushie machine" → "machine"
  //     isn't in any SKU), only allow matches with a full-substring or
  //     codeName match. Stops single-shared-token false positives like
  //     "Slushie machine" surfacing Slushie Mix (JJ 2026-05-26).
  // Iraj logged "Rolled stickers" as +CUSTOM INBOUND on 2026-05-26 when the
  // SKU is in the catalogue — this stops that path.
  // Pre-compute the union of distinctive tokens across the whole active
  // catalogue so the off-catalogue check is O(1) per typed token.
  const catalogDistinctiveTokens = useMemo(() => {
    const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").replace(/\s+/g," ").trim();
    const stem = w => (w && w.length>3 && w.endsWith("s")) ? w.slice(0,-1) : w;
    const STOP = new Set([
      "the","a","an","of","for","in","on","at","by","with","to","from","and","or","is","are","be","not","this","that","i","we","you","they","it","my","our",
      "please","pls","need","needs","want","wanted","have","had","get","got","also","again","just","very","really","still","more","some","any","all","none","each","extra","spare","plenty",
      "urgent","asap","quick","quickly","soon","today","tomorrow","now","when","can","could","would","should","might",
      "one","two","three","four","five","six","seven","eight","nine","ten","few","couple",
      "box","boxes","bag","bags","packet","packets","pack","packs","piece","pieces","bottle","bottles","can","cans","tub","tubs","tray","trays","unit","units","carton","cartons","case","cases",
      "if","enough","ok","okay","sure","thanks","thx","ta","cheers","pl"
    ]);
    const set = new Set();
    (stockCatalog||[]).filter(c=>c && c.name && c.status!=="inactive").forEach(c => {
      norm(c.name).split(" ").filter(Boolean).map(stem).forEach(w => {
        if (w && w.length >= 3 && !STOP.has(w)) set.add(w);
      });
      // Also include codeName tokens so "ginger" / "wasabi" code aliases count.
      if (c.codeName) {
        norm(c.codeName).split(" ").filter(Boolean).map(stem).forEach(w => {
          if (w && w.length >= 3 && !STOP.has(w)) set.add(w);
        });
      }
    });
    return set;
  }, [stockCatalog]);
  const findCatalogMatches = (typed, max) => {
    max = max || 8;
    const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9 ]+/g," ").replace(/\s+/g," ").trim();
    const stem = w => (w && w.length>3 && w.endsWith("s")) ? w.slice(0,-1) : w;
    const tokens = s => norm(s).split(" ").filter(Boolean).map(stem);
    const isSubseq = (needle, haystack) => {
      if (!needle || !haystack) return false;
      let j = 0;
      for (let i = 0; i < haystack.length && j < needle.length; i++) {
        if (haystack[i] === needle[j]) j++;
      }
      return j === needle.length;
    };
    const STOP_WORDS = new Set([
      "the","a","an","of","for","in","on","at","by","with","to","from","and","or","is","are","be","not","this","that","i","we","you","they","it","my","our",
      "please","pls","need","needs","want","wanted","have","had","get","got","also","again","just","very","really","still","more","some","any","all","none","each","extra","spare","plenty",
      "urgent","asap","quick","quickly","soon","today","tomorrow","now","when","can","could","would","should","might",
      "one","two","three","four","five","six","seven","eight","nine","ten","few","couple",
      "box","boxes","bag","bags","packet","packets","pack","packs","piece","pieces","bottle","bottles","can","cans","tub","tubs","tray","trays","unit","units","carton","cartons","case","cases",
      "if","enough","ok","okay","sure","thanks","thx","ta","cheers",
      "pl"
    ]);
    const isDistinctive = (w) => !!w && w.length >= 3 && !STOP_WORDS.has(w);
    const t = norm(typed);
    if(!t) return [];
    const tTokens = tokens(typed);
    const tTokenSet = new Set(tTokens);
    // Off-catalogue distinctive tokens in the typed text — if any are
    // present, the user is naming something the catalogue doesn't cover.
    // Only allow matches with a fullSubMatch or codeMatch (high-confidence
    // user typed the SKU explicitly). Stops "Slushie machine" → Slushie Mix.
    let typedOffCatalogCount = 0;
    for (const w of tTokens) {
      if (w && w.length >= 3 && !STOP_WORDS.has(w) && !catalogDistinctiveTokens.has(w)) {
        typedOffCatalogCount++;
      }
    }
    const items = (stockCatalog||[]).filter(c=>c && c.name && c.status!=="inactive");
    const scored = [];
    for(const c of items){
      const cName = norm(c.name);
      const cTokens = tokens(c.name);
      const cTokenSet = new Set(cTokens);
      if(!cTokens.length) continue;
      let cHits = 0; for(const w of cTokens){ if(tTokenSet.has(w)) cHits++; }
      let tHits = 0; for(const w of tTokens){ if(cTokenSet.has(w)) tHits++; }
      const catCoverage = cHits / cTokens.length;
      const typedCoverage = tTokens.length ? tHits / tTokens.length : 0;
      const fullSubMatch = cName && t.indexOf(cName) !== -1;
      const reverseSubMatch = cName && cName.indexOf(t) !== -1;
      const subseqMatch = !reverseSubMatch && t.length >= 3 && isSubseq(t, cName);
      const codeName = norm(c.codeName);
      const codeMatch = codeName && codeName.length >= 3 && (t.indexOf(codeName) !== -1 || codeName.indexOf(t) !== -1);
      let distinctiveHits = 0;
      for (const w of cTokens) { if (isDistinctive(w) && tTokenSet.has(w)) distinctiveHits++; }
      let score = typedCoverage * 2 + catCoverage;
      if (fullSubMatch) score += 3;
      if (reverseSubMatch) score += 1.5;
      if (subseqMatch) score += 1;
      if (codeMatch) score += 1;
      if (distinctiveHits) score += 1.5 * distinctiveHits;
      if (score < 1.0) continue;
      // 2026-05-26 (JJ "slushie machine" false positive). Off-catalogue token
      // guard: if the typed text contains a distinctive token that appears
      // in NO catalogue item, only allow a match when the catalogue name
      // is a literal substring of (or shares a codeName with) the typed
      // text. Stops one shared category word ("slushie") from surfacing a
      // different SKU when the user clearly named an off-catalogue item.
      if (typedOffCatalogCount > 0 && !fullSubMatch && !codeMatch) continue;
      scored.push({ item: c, score, typedCoverage, nameLen: (c.name||"").length });
    }
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.typedCoverage !== a.typedCoverage) return b.typedCoverage - a.typedCoverage;
      return a.nameLen - b.nameLen;
    });
    return scored.slice(0, max).map(x => x.item);
  };

  // Continue from Custom tab → if the typed text clashes with a catalogue
  // SKU, surface the matches and block. Otherwise fall through to the
  // normal capture-photo step.
  const continueCustom = () => {
    const t = customText.trim();
    if (!t) return;
    const matches = findCatalogMatches(t);
    if (matches && matches.length) {
      setCustomBlock({ typed: t, items: matches });
      return;
    }
    // Add the one-off to the cart (custom items log at this site / kitchen).
    addToCart({ kind:"custom", item:t, site:(site && site.key) ? site.key : "kitchen", type:"custom" });
    setCustomText("");
  };

  // Two-step: capture kitchen GPS (stock path only — geofenced server-side)
  // → PIN-gate → upload photo to Drive (uploadDeliveryPhoto) → POST
  // stockDelivered (running-list path) or customDeliveryReceived (custom
  // one-off path). Backend's state walker treats delivered+photoUrl as
  // auto-back-in-stock so the row drops off; custom_inbound surfaces only
  // on admin Stock Activity, never on the running list.
  //
  // 2026-05-26: GPS was missing from the stockDelivered POST so the
  // kitchen geofence gate (added 2026-05-19 on backInStock + storeroom
  // flags + stockDelivered) silently rejected every Confirm Delivery from
  // this tile with OUTSIDE_KITCHEN_GEOFENCE. EXIF gpsLat/gpsLng aren't
  // read by the geofence assert — it reads data.lat / data.lng (device
  // GPS, same shape as the StockRequestReceiver photo-confirm modal).
  // BULK SUBMIT — one photo upload, then ONE receiveInboundBatch POST that
  // appends a row per cart item (server reuses the same per-item handlers:
  // backInStock in kitchen mode / stockDelivered single-site / customDelivery-
  // Received for one-offs). Reads the response — never no-cors — and honours a
  // per-item results[] so a partial failure keeps only the failed lines in the
  // cart to retry. Supabase shadows loop per succeeded item, sharing the one
  // photo. Device GPS (2026-05-26 fix) is forwarded for the kitchen offsite FLAG.
  const runSubmit = (kitchenCoords) => {
    requestPin(async (staffName, staffPin) => {
      setSubmitting(true);
      setError("");
      const primary = photos[0] || {};   // first photo = primary (per-item thumb + geofence meta)
      const isKitchenMode = !(site && site.key);
      const homeSite = (site && site.key) ? site.key : "kitchen";
      // Resolve each cart line to its write action + originating site. Kitchen
      // stock = backInStock (replenish, leaves the kiosk request open); single-
      // site stock = stockDelivered; custom one-off = customDeliveryReceived.
      const batch = cart.map(c => ({
        key: c.key,
        item: c.item,
        site: c.site || homeSite,
        kind: c.kind,
        action: c.kind === "custom" ? "customDeliveryReceived" : (isKitchenMode ? "backInStock" : "stockDelivered"),
        type: c.type || (c.kind === "custom" ? "custom" : "standard"),
        qty: (c.qty == null ? null : c.qty),
        row: c.row || null,
      }));
      // Lane BP (13 Sep 2026): ONE kitchen write-drop gate — Lane BE's
      // STOCK_WRITE_DROP_HOSTS (ships EMPTY). On a dropped host the plan turns off
      // the Drive fallback and the receiveInboundBatch POST and makes the Supabase
      // writes the awaited record (the branch below step 1). Everywhere else this
      // function is today's code, untouched.
      const plan = deliveriesSync.planInboundSubmit({ dropOn: stockSync.stockWriteDropEnabled() });
      try {
        // 1) Upload EVERY photo of the drop (sequential). FAST path first: straight
        //    to the PUBLIC Supabase bucket (sub-second) → a permanent public URL.
        //    FALLBACK: Apps Script -> Drive (~8s, and the /exec endpoint flakes
        //    under load returning a non-JSON page → null; foh_flaky_exec_nonjson_
        //    false_success), retried up to 3x — a retried upload only makes a spare
        //    file (SAFE, unlike a non-idempotent stock write). A real backend error
        //    (too large / bad type) is shown and NOT retried.
        const photoUrls = [];
        for (let pi = 0; pi < photos.length; pi++) {
          const p = photos[pi];
          const pb64 = String(p.dataUrl || "").split(",")[1] || "";
          let gotUrl = null, hardError = null;
          // 1a) FAST — direct to Supabase public bucket. null on any failure.
          try {
            setUploadStatus("Uploading photo " + (pi + 1) + " of " + photos.length + "…");
            gotUrl = await deliveriesSync.uploadDeliveryPhotoFast({ siteKey: homeSite, item: batchLabel, staffPin, base64: pb64, mime: p.mime, capturedAt: p.capturedAt });
          } catch (e) { gotUrl = null; }
          // 1b) FALLBACK — Apps Script -> Drive, retried, if the fast path missed.
          //     (Lane BP: never on a dropped host — Google is not written there.)
          if (!gotUrl && plan.driveFallback) {
            const uploadBody = JSON.stringify({
              action: "uploadDeliveryPhoto",
              site: homeSite,
              item: batchLabel,
              staffName, staffPin,
              base64: pb64, mimeType: p.mime, filename: p.filename,
              capturedAt: p.capturedAt, gpsLat: p.gpsLat, gpsLng: p.gpsLng, exifPresent: p.exifPresent, source: p.source
            });
            for (let attempt = 1; attempt <= 3 && !gotUrl && !hardError; attempt++) {
              setUploadStatus("Uploading photo " + (pi + 1) + " of " + photos.length + (attempt > 1 ? " · retry " + (attempt - 1) : "") + " (slow)…");
              let uploadData = null;
              try {
                const uploadRes = await fetch(backend, { method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: uploadBody });
                uploadData = await uploadRes.json().catch(()=>null);
              } catch (netErr) { uploadData = null; }
              if (uploadData && uploadData.ok && uploadData.url) { gotUrl = uploadData.url; }
              else if (uploadData && (uploadData.error || uploadData.message)) { hardError = uploadData.error || uploadData.message; }  // deterministic — don't retry
              else if (attempt < 3) { await new Promise(res => setTimeout(res, 900 * attempt)); }   // flaky — back off + retry
            }
          }
          if (!gotUrl) {
            setUploadStatus("");
            setError(hardError || (plan.driveFallback
              ? ("Photo " + (pi + 1) + " didn't upload after 3 tries — the connection looks flaky. Tap Log again.")
              : ("Photo " + (pi + 1) + " didn't upload to the kitchen store — check the connection and tap Log again.")));
            setSubmitting(false);
            return;
          }
          photoUrls.push(gotUrl);
        }
        setUploadStatus("Saving delivery…");
        const photoUrl = photoUrls[0];   // primary — the per-item row + admin thumbnail
        if (plan.supabaseAuthoritative) {
          // ===== Lane BP ON path (this host is in STOCK_WRITE_DROP_HOSTS): Supabase IS the record. =====
          // No receiveInboundBatch POST to Google. Per cart line, in parallel (one
          // login for the burst): (a) the stock event through postKitchenAction — on
          // a dropped host that is Lane BE's AWAITED outbox landing, carrying the
          // photo's Storage URL, the qty and (for a geofenced action) the device fix
          // the database turns into the offsite flag (1300); it also fires the
          // shadow + reorder signal exactly once, so no separate shadowKitchenWrite
          // here; (b) the evidence row + private photo copy through
          // recordInboundDelivery, AWAITED with strictPhoto. Either failing keeps
          // that line in the cart with the same per-item retry UI a Sheet reject
          // gets, and the message is the kitchen's usual one.
          const todayStr = new Date().toLocaleDateString("en-GB");
          const primaryB64 = String(primary.dataUrl||"").split(",")[1]||"";
          const outcomes = await Promise.all(batch.map(async (b) => {
            const isGeofenced = KITCHEN_GEOFENCED_ACTIONS.indexOf(b.action) !== -1;
            const body = {
              action: b.action, site: b.site, date: todayStr, item: b.item, staffName, staffPin,
              photoUrl, note, qty: b.qty,
              capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, source: primary.source
            };
            if (isGeofenced && kitchenCoords) { body.lat = kitchenCoords.lat; body.lng = kitchenCoords.lng; body.accuracy = kitchenCoords.accuracy; }
            let res;
            try { res = await postKitchenAction(backend, body, isGeofenced); }
            catch (e) { res = { ok: false, code: "supabase", message: (e && e.message) || "That didn't save — try again." }; }
            if (!res || res.ok === false) return { key: b.key, ok: false, message: kitchenActionErrorMessage(res || { ok: false }, b.item) };
            let ev;
            try {
              // Review fix (13 Sep 2026): the evidence row keys on a REAL sites row. In
              // kitchen mode a one-off carries the literal "kitchen" (there is no such
              // site — the kitchen is `mcr`), which postKitchenAction already maps to
              // SITE.key; without the same mapping here siteIdForKey("kitchen") is null
              // and every kitchen one-off failed "evidence didn't save" for ever.
              const evidenceSite = (b.site && b.site !== "kitchen") ? b.site : SITE.key;
              // Lane CF (14 Sep 2026, BP-7): qty was missing from THIS call entirely —
              // the body above already carries it (line ~5604) for the stock event, but
              // the evidence row never got it, so the ON path (the one that actually
              // runs once a host is armed) would have shipped with the same qty gap the
              // OFF-path shadow call below already avoided.
              ev = await deliveriesSync.recordInboundDelivery({ siteKey: evidenceSite, action: b.action, item: b.item, kind: b.kind, staffPin, note, photoUrl, base64: primaryB64, mime: primary.mime, capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, captureSource: primary.source, qty: b.qty, strictPhoto: true });
            } catch (e) { ev = { ok: false, error: e }; }
            if (!ev || !ev.ok) return { key: b.key, ok: false, message: kitchenActionErrorMessage({ ok: false, code: "supabase", message: "The delivery evidence didn't save — try again." }, b.item) };
            return { key: b.key, ok: true };
          }));
          const failedKeysOn = new Set(outcomes.filter(o => !o.ok).map(o => o.key));
          const succeededOn = batch.filter(b => !failedKeysOn.has(b.key));
          // Optimistic UI — same as the Sheet path: recent strip + prune succeeded stock rows.
          const nowOn = Date.now();
          const newRowsOn = succeededOn.map((b,i) => ({ id:"d-"+nowOn+"-"+i, item:b.item, qty:b.qty, custom:b.kind==="custom", ts:nowOn, staffName, site:b.site, photoUrl, photoUrls, note }));
          setRecentDeliveries(prev => [...newRowsOn, ...prev].slice(0,20));
          const prunedIdsOn = new Set(succeededOn.filter(b => b.kind==="stock" && b.row && b.row.id).map(b => b.row.id+"|"+b.site));
          if (prunedIdsOn.size) {
            setRunningList(prev => {
              const next = prev.filter(x => !prunedIdsOn.has(x.id+"|"+x.site));
              try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(next)); } catch (e) {}
              return next;
            });
          }
          if (failedKeysOn.size) {
            setCart(prev => prev.filter(c => failedKeysOn.has(c.key)));
            setStage("build");
            setSubmitting(false);
            const firstMsg = (outcomes.find(o => !o.ok) || {}).message || "";
            const names = batch.filter(b => failedKeysOn.has(b.key)).map(b => b.item).join(", ");
            setError(succeededOn.length + " logged. Couldn't log: " + names + " — " + (firstMsg || "try again."));
            return;
          }
          reset();
          return;
        }
        // Stable idempotency token for this delivery (generate once; survives
        // retries + a manual re-tap; cleared on a clean log in reset()).
        if (!clientTokenRef.current) clientTokenRef.current = "ib-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
        // 2) ONE batch write. Read the response so backend rejections surface
        //    (silent_loss_unread_error_body). The write is idempotent per
        //    (clientToken,item) on the backend, so — unlike a bare non-idempotent
        //    retry — a flaky-/exec non-JSON response CAN be retried safely without
        //    double-logging (flaky_exec_causes_duplicate_writes, handled server-side).
        const writeBody = {
          action: "receiveInboundBatch",
          site: homeSite,
          date: new Date().toLocaleDateString("en-GB"),
          staffName, staffPin,
          photoUrl, photoUrls: JSON.stringify(photoUrls), note,
          clientToken: clientTokenRef.current,
          capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, source: primary.source,
          items: JSON.stringify(batch.map(b => ({ key: b.key, item: b.item, site: b.site, action: b.action, type: b.type, qty: b.qty })))
        };
        // Device GPS for the kitchen offsite FLAG (no longer a block, @293).
        if (kitchenCoords) { writeBody.lat = kitchenCoords.lat; writeBody.lng = kitchenCoords.lng; writeBody.accuracy = kitchenCoords.accuracy; }
        const writeBodyStr = JSON.stringify(writeBody);
        // Retry on ANY non-success up to 3x. Because the write is idempotent per
        // (clientToken,item), this is SAFE — and it must cover more than non-JSON:
        // a flaky /exec under load can also return a CROSSED response, i.e. a
        // well-formed JSON error meant for a different request (JJ saw a stray
        // "Unknown action" on a POST that hard-codes the action). So we no longer
        // treat a JSON error as deterministic — we keep the last one to show only
        // if all 3 attempts fail.
        let writeData = null, writeLastError = null;
        for (let attempt = 1; attempt <= 3 && !writeData; attempt++) {
          setUploadStatus(attempt > 1 ? "Saving delivery · retry " + (attempt - 1) + "…" : "Saving delivery…");
          let d = null;
          try {
            const writeRes = await fetch(backend, { method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body: writeBodyStr });
            d = await writeRes.json().catch(()=>null);
          } catch (e) { d = null; }
          if (d && (d.status === "success" || d.status === "ok")) { writeData = d; break; }
          if (d && (d.message || d.error)) writeLastError = d.message || d.error;
          if (attempt < 3) { await new Promise(res => setTimeout(res, 900 * attempt)); }
        }
        if (!writeData) {
          setUploadStatus("");
          setError((writeLastError ? writeLastError + " — " : "") + "delivery didn't save after 3 tries. Tap Log again — it's safe, it won't double‑log.");
          setSubmitting(false);
          return;
        }
        // Per-item outcomes: the batch handler returns results[]; treat all as
        // logged if it's absent (older backend / all-ok), and keep only the
        // failures in the cart to retry.
        const failedKeys = new Set();
        if (Array.isArray(writeData.results)) {
          // Match results by the STABLE cart key — item name alone is ambiguous
          // (the same SKU can appear for tc AND kitchen in kitchen mode, so two
          // cart lines share a name). Fall back to name only if the backend
          // didn't echo a key (older backend); the current backend always does.
          const byKey = new Map(), byName = new Map();
          writeData.results.forEach(r => {
            if (!r) return;
            if (r.key != null) byKey.set(String(r.key), r);
            else byName.set(String(r.item||"").toLowerCase(), r);
          });
          batch.forEach(b => {
            const r = byKey.get(String(b.key)) || byName.get(String(b.item).toLowerCase());
            if (r && r.ok === false) failedKeys.add(b.key);
          });
        }
        const succeeded = batch.filter(b => !failedKeys.has(b.key));
        // 3) Supabase shadows — per succeeded item, sharing the one photo. Best-effort.
        succeeded.forEach(b => {
          try { shadowKitchenWrite({ action: b.action, site: b.site, item: b.item, staffPin, note, photoPath: photoUrl, qty: b.qty }); } catch (_) {}
          try { deliveriesSync.recordInboundDelivery({ siteKey: b.site, action: b.action, item: b.item, kind: b.kind, staffPin, note, photoUrl, photoUrls, base64: String(primary.dataUrl||"").split(",")[1]||"", mime: primary.mime, capturedAt: primary.capturedAt, gpsLat: primary.gpsLat, gpsLng: primary.gpsLng, exifPresent: primary.exifPresent, captureSource: primary.source, qty: b.qty }).catch(()=>{}); } catch (_) {}
        });
        // 4) Optimistic UI — recent strip + prune succeeded stock rows from the list.
        const now = Date.now();
        const newRows = succeeded.map((b,i) => ({ id:"d-"+now+"-"+i, item:b.item, qty:b.qty, custom:b.kind==="custom", ts:now, staffName, site:b.site, photoUrl, photoUrls, note }));
        setRecentDeliveries(prev => [...newRows, ...prev].slice(0,20));
        const prunedIds = new Set(succeeded.filter(b => b.kind==="stock" && b.row && b.row.id).map(b => b.row.id+"|"+b.site));
        if (prunedIds.size) {
          setRunningList(prev => {
            const next = prev.filter(x => !prunedIds.has(x.id+"|"+x.site));
            try { localStorage.setItem(RUNNING_LIST_CACHE_KEY, JSON.stringify(next)); } catch (e) {}
            return next;
          });
        }
        if (failedKeys.size) {
          setCart(prev => prev.filter(c => failedKeys.has(c.key)));
          setStage("build");
          setSubmitting(false);
          const names = batch.filter(b => failedKeys.has(b.key)).map(b => b.item).join(", ");
          setError(succeeded.length + " logged. Couldn't log: " + names + " — try again.");
          return;
        }
        reset();
      } catch (e) {
        setError(e.message || "Network error — try again");
        setSubmitting(false);
      }
    });
  };

  const submit = () => {
    if (!canSubmit) return;
    // Any storeroom stock line makes the batch geofenced, so capture device GPS
    // BEFORE the PIN modal (a clear "enable location" message instead of a
    // post-PIN bounce). An all-custom drop skips it — custom isn't geofenced.
    if (cart.some(c => c.kind === "stock")) {
      setError("");
      captureKitchenCoords()
        .then(coords => runSubmit(coords))
        .catch(err => setError((err && err.message) || "Location required — storeroom deliveries must be confirmed at the kitchen."));
      return;
    }
    runSubmit(null);
  };

  const fmtTs = (ms) => {
    try {
      const d = new Date(ms);
      const today = new Date();
      const isSame = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
      return isSame ? "Today " + time : d.toLocaleDateString("en-GB", {day:"2-digit", month:"short"}) + " · " + time;
    } catch (e) { return ""; }
  };

  const SITE_LABEL = {tc:"Trafford", arndale:"Arndale", meadowhall:"Meadowhall", kitchen:"Kitchen", "(custom)":"Custom"};

  // ===== STAGE 1: pick a target =====
  if (stage === "build") {
    return (
      <div style={S.content}>
        <div style={S.sectionCard}>
          <div style={S.secLabel}>🚛 Inbound Deliveries</div>
          <div style={S.secDesc}>What just arrived? Tick everything from the running list, or add one-off customs — as many as you like. One photo covers the whole delivery.</div>
        </div>

        {/* Tab strip */}
        <div style={{display:"flex",gap:0,marginTop:8,padding:4,background:"rgba(254,250,240,0.04)",borderRadius:10,border:"1px solid rgba(212,251,111,0.08)"}}>
          {["running","custom"].map(k => (
            <button key={k} type="button" onClick={()=>setTab(k)} style={{
              flex:1,padding:"10px 12px",borderRadius:8,border:"none",
              background: tab===k ? B.wasabi : "transparent",
              color: tab===k ? B.nori : B.kewpie,
              fontFamily:"inherit", fontWeight:tab===k?800:600, fontSize:"0.86rem",
              letterSpacing:"0.04em", cursor:"pointer", textTransform:"uppercase"
            }}>{k==="running"?"📋 From running list":"✨ Custom item"}</button>
          ))}
        </div>

        {tab === "running" && (
          <div style={{marginTop:12}}>
            <input style={{...S.input,marginBottom:10,paddingLeft:36,backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23D4FB6F' opacity='0.4' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.156a5 5 0 1 1 0-10 5 5 0 0 1 0 10z'/%3E%3C/svg%3E\")",backgroundRepeat:"no-repeat",backgroundPosition:"12px center",backgroundSize:"14px"}} placeholder="Search item or requester..." value={search} onChange={e=>setSearch(e.target.value)}/>
            {loadingList && <div style={{padding:"24px 0",textAlign:"center",color:B.kewpie,opacity:0.5,fontSize:"0.85rem"}}>Loading running list…</div>}
            {listErr && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:8}}>{listErr}</div>}
            {!loadingList && !listErr && filteredList.length === 0 && (
              <div style={{padding:"24px 16px",textAlign:"center",color:B.kewpie,opacity:0.45,fontSize:"0.85rem",borderRadius:10,background:"rgba(254,250,240,0.03)",border:"1px dashed rgba(254,250,240,0.1)",marginTop:8}}>
                {search.trim() ? <>No items on the list match <em>"{search.trim()}"</em>. Switch to <strong>Custom item</strong> for one-off arrivals.</>
                              : <>Nothing's on order or flagged low right now. If a one-off delivery arrived that wasn't on the list, switch to <strong>Custom item</strong>.</>}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
              {filteredList.map(it => {
                const flagPill = it.flag === "out_of_stock" ? {label:"⚠ OOS", bg:"rgba(255,122,92,0.18)", fg:"#FF9B83"}
                              : it.flag === "running_low" ? {label:"⚠ LOW", bg:"rgba(241,197,68,0.22)", fg:"#F1C544"}
                              : null;
                const cat = (stockCatalog||[]).find(c => c && c.name === it.item);
                const img = cat && cat.imageUrl;
                const added = inCart("stock", it.item, it.site);
                return (
                  <button key={(it.id||"x")+"-"+it.site+"-"+it.item} onClick={()=> added
                      ? removeFromCart(cartKey("stock", it.item, it.site))
                      : addToCart({kind:"stock", item:it.item, site:it.site, type: it.custom?"custom":"standard", row:it})} style={{
                    width:"100%",textAlign:"left",fontFamily:"inherit",
                    display:"flex",alignItems:"center",gap:12,
                    padding:"12px 14px",borderRadius:12,
                    background: added ? "rgba(212,251,111,0.14)" : "rgba(212,251,111,0.04)",
                    border:"1px solid " + (added ? "rgba(212,251,111,0.5)" : "rgba(212,251,111,0.12)"),
                    cursor:"pointer", color:B.kewpie
                  }}>
                    <CatalogThumb item={it.item} imageUrl={img} size={52} onZoom={setThumbZoom} showPlaceholder/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:"0.92rem",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span>{it.item}</span>
                        {it.custom && <span style={{color:B.sunshine,fontSize:"0.65rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>extra</span>}
                        {flagPill && <span style={{background:flagPill.bg,color:flagPill.fg,padding:"2px 8px",borderRadius:6,fontSize:"0.66rem",fontWeight:700,letterSpacing:"0.05em"}}>{flagPill.label}</span>}
                        {it.ordered && <span style={{background:"rgba(212,251,111,0.18)",color:B.wasabi,padding:"2px 8px",borderRadius:6,fontSize:"0.66rem",fontWeight:700,letterSpacing:"0.05em"}}>📦 ON ORDER</span>}
                      </div>
                      <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginTop:2}}>
                        {SITE_LABEL[it.site]||it.site}
                        {it.requestedBy && <> · raised by {it.requestedBy}</>}
                        {it.time && <> · {it.time}</>}
                      </div>
                    </div>
                    <div style={{fontSize:"0.82rem",fontWeight:800,color: added ? B.wasabi : B.kewpie, opacity: added ? 1 : 0.45, flexShrink:0}}>{added ? "✓ Added" : "+ Add"}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "custom" && (
          <div style={{marginTop:12}}>
            <div style={{padding:"12px 14px",borderRadius:10,background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.18)",fontSize:"0.78rem",color:B.kewpie,marginBottom:10,lineHeight:1.5}}>
              For one-off arrivals only — equipment, signage, a delivery for someone else, anything not in the catalogue. <strong>If it's a catalogue SKU (sauces, packaging, drinks, snacks, etc.) use the running list.</strong>
            </div>
            <label style={{display:"block",fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:B.kewpie,opacity:0.6,marginBottom:6}}>What arrived?</label>
            <input value={customText} onChange={e=>setCustomText(e.target.value.slice(0,200))} placeholder="e.g. Slushie machine, marketing flyers, replacement door key" style={{...S.input,width:"100%"}}/>
            {customText.trim() && (
              <button type="button" onClick={continueCustom} style={{...S.submitBtn, marginTop:10, width:"100%"}}>
                + Add "{customText.trim().length>40 ? customText.trim().slice(0,40)+"…" : customText.trim()}"
              </button>
            )}
          </div>
        )}

        {/* In this delivery — the cart. One photo covers all of these. */}
        {cart.length > 0 && (
          <div style={{marginTop:16, padding:14, borderRadius:14, background:"rgba(212,251,111,0.06)", border:"1px solid rgba(212,251,111,0.22)"}}>
            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,marginBottom:10}}>
              <div style={{fontSize:"0.72rem",fontWeight:800,letterSpacing:"0.06em",textTransform:"uppercase",color:B.wasabi}}>In this delivery · {cart.length}</div>
              <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.5}}>counts optional</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {cart.map(c => {
                // Show the catalogue thumbnail so staff can eyeball what each
                // line is (customs have no image → CatalogThumb placeholder).
                const cat = (stockCatalog||[]).find(x => x && x.name === c.item);
                const img = cat && cat.imageUrl;
                return (
                <div key={c.key} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:10,background:"rgba(0,0,0,0.14)",border:"1px solid rgba(212,251,111,0.1)"}}>
                  <CatalogThumb item={c.item} imageUrl={img} size={44} onZoom={setThumbZoom} showPlaceholder/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:"0.86rem",color:B.kewpie,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.item}</div>
                    <div style={{fontSize:"0.66rem",color:B.kewpie,opacity:0.5,marginTop:1}}>{c.kind==="custom" ? "Custom one-off" : (SITE_LABEL[c.site]||c.site)}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",border:"1px solid rgba(212,251,111,0.3)",borderRadius:9,overflow:"hidden",flexShrink:0}}>
                    <button type="button" onClick={()=>bumpQty(c.key,-1)} disabled={c.qty<=1} aria-label="less" style={{width:34,height:34,border:"none",background:"rgba(212,251,111,0.08)",color:c.qty<=1?"rgba(212,251,111,0.3)":B.wasabi,fontSize:"1.15rem",fontWeight:800,cursor:c.qty<=1?"default":"pointer",lineHeight:1}}>–</button>
                    <span style={{minWidth:36,textAlign:"center",fontWeight:800,color:B.kewpie,fontSize:"0.92rem",fontVariantNumeric:"tabular-nums"}}>{c.qty}</span>
                    <button type="button" onClick={()=>bumpQty(c.key,1)} aria-label="more" style={{width:34,height:34,border:"none",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"1.05rem",fontWeight:800,cursor:"pointer",lineHeight:1}}>+</button>
                  </div>
                  <button type="button" onClick={()=>removeFromCart(c.key)} aria-label={"Remove "+c.item} style={{width:30,height:30,borderRadius:8,border:"none",background:"transparent",color:B.kewpie,opacity:0.5,fontSize:"1rem",cursor:"pointer",flexShrink:0,lineHeight:1}}>✕</button>
                </div>
                );
              })}
            </div>
            <button type="button" onClick={()=>{setError("");setStage("review");}} style={{...S.submitBtn, width:"100%", marginTop:12, fontSize:"0.95rem"}}>
              📷 Take photo &amp; log {cart.length} item{cart.length===1?"":"s"} →
            </button>
          </div>
        )}

        {/* Recent deliveries history */}
        <div style={{...S.sectionCard, marginTop:18}}>
          <div style={S.secLabel}>Recent deliveries</div>
          <div style={S.secDesc}>{recentDeliveries.length} log{recentDeliveries.length===1?"":"s"} in the last 7 days. Tap a photo to view full size.</div>
        </div>
        {recentDeliveries.length === 0 ? (
          <div style={{padding:"24px 16px",textAlign:"center",color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>No deliveries logged yet.</div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
            {recentDeliveries.map(d => (
              <div key={d.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,background:"rgba(254,250,240,0.04)",border:"1px solid rgba(212,251,111,0.08)"}}>
                {d.photoUrl ? (
                  <button type="button" onClick={()=>setPhotoLightbox(d)} style={{width:46,height:46,padding:0,border:"1px solid rgba(212,251,111,0.18)",borderRadius:8,background:"rgba(0,0,0,0.2)",cursor:"pointer",overflow:"hidden",flexShrink:0}}>
                    <img src={d.photoUrl} alt="Delivery" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} onError={e=>{e.target.style.display="none";e.target.parentNode.innerText="📷";}}/>
                  </button>
                ) : (
                  <div style={{width:46,height:46,borderRadius:8,background:"rgba(254,250,240,0.06)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,opacity:0.4,color:B.kewpie}}>📦</div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:"0.86rem",color:B.kewpie,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{d.item}</span>
                    {d.qty!=null && <span style={{color:B.wasabi,fontWeight:800}}>×{d.qty}</span>}
                    {d.custom && <span style={{color:B.sunshine,fontSize:"0.62rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>custom</span>}
                  </div>
                  <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.5,marginTop:2}}>
                    {fmtTs(d.ts)}{d.staffName?" · "+d.staffName:""}{d.site && d.site !== "(custom)" ? " · " + (SITE_LABEL[d.site]||d.site) : ""}
                  </div>
                  {d.note && <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.6,marginTop:3,fontStyle:"italic"}}>"{d.note}"</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {photoLightbox && (
          <div onClick={()=>setPhotoLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,28,22,0.88)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:24,backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{maxWidth:720,width:"100%",maxHeight:"calc(100dvh - 48px)",background:"#fff",borderRadius:14,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 22px 48px rgba(0,0,0,0.5)"}}>
              <div style={{padding:"14px 18px",background:B.nori,color:B.wasabi,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",color:"#C8EAD8"}}>📷 Delivery photo</div>
                  <div style={{fontFamily:"gelica,serif",fontSize:18,fontWeight:700,color:B.wasabi,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{photoLightbox.item}</div>
                </div>
                <button onClick={()=>setPhotoLightbox(null)} style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,0.3)",color:B.wasabi,border:"none",cursor:"pointer",fontSize:16,fontWeight:700,lineHeight:1}}>×</button>
              </div>
              <div style={{flex:1,overflow:"auto",background:"#0a0a0a",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <img src={photoLightbox.photoUrl} alt="Delivery" style={{maxWidth:"100%",maxHeight:"100%",display:"block"}}/>
              </div>
              <div style={{padding:"12px 18px",borderTop:"1px solid rgba(0,57,44,0.1)",fontSize:13,color:"#0d2820",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <span style={{fontWeight:600}}>{SITE_LABEL[photoLightbox.site]||photoLightbox.site}</span>
                {photoLightbox.staffName && <span style={{opacity:0.65}}>· confirmed by <strong>{photoLightbox.staffName}</strong></span>}
                <span style={{opacity:0.55}}>· {fmtTs(photoLightbox.ts)}</span>
                {photoLightbox.note && <div style={{flex:"1 0 100%",fontSize:12,color:"rgba(0,57,44,0.7)",fontStyle:"italic",borderLeft:"2px solid rgba(0,57,44,0.18)",paddingLeft:10,marginTop:6}}>"{photoLightbox.note}"</div>}
              </div>
            </div>
          </div>
        )}

        {thumbZoom && <CatalogThumbLightbox item={thumbZoom.item} imageUrl={thumbZoom.imageUrl} onClose={()=>setThumbZoom(null)}/>}

        {/* "Did you mean…" — typed custom text clashed with a catalogue SKU. */}
        {customBlock && (
          <div onClick={()=>setCustomBlock(null)} style={{position:"fixed",inset:0,background:"rgba(0,57,44,0.78)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
            <div onClick={e=>e.stopPropagation()} style={{maxWidth:480,width:"100%",maxHeight:"calc(100vh - 32px)",overflowY:"auto",background:B.nori,border:"1px solid rgba(212,251,111,0.25)",borderRadius:14,padding:"22px 22px 18px",color:B.kewpie,boxShadow:"0 18px 44px rgba(0,0,0,0.45)"}}>
              <div style={{fontSize:"0.7rem",fontWeight:800,color:B.sunshine,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>⚠ This is a catalogue item</div>
              <div style={{fontFamily:"gelica,serif",fontSize:"1.25rem",lineHeight:1.25,marginBottom:6,color:B.kewpie}}>Use the running list</div>
              <div style={{fontSize:"0.82rem",lineHeight:1.5,opacity:0.75,marginBottom:14}}>You typed <em>"{customBlock.typed}"</em>. That matches a catalogue SKU — tap one below to find it on the running list. Custom is only for one-off arrivals that aren't in the catalogue.</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                {(customBlock.items||[]).map(it=>(
                  <button key={it.name} style={{textAlign:"left",padding:"12px 14px",borderRadius:10,background:"rgba(212,251,111,0.05)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.18)",fontFamily:"inherit",cursor:"pointer",display:"flex",alignItems:"center",gap:10}} onClick={()=>{
                    setTab("running");
                    setSearch(it.name);
                    setCustomText("");
                    setCustomBlock(null);
                  }}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:"0.92rem",color:B.wasabi,whiteSpace:"normal",overflowWrap:"anywhere"}}>{it.name}</div>
                      <div style={{fontSize:"0.7rem",opacity:0.55,marginTop:2}}>{it.category||"Catalogue"}</div>
                    </div>
                    <span style={{fontSize:"0.7rem",fontWeight:700,opacity:0.6}}>Find ›</span>
                  </button>
                ))}
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button style={{flex:"1 1 110px",padding:"12px 16px",borderRadius:10,background:"transparent",color:B.kewpie,border:"1px solid rgba(254,250,240,0.25)",fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer"}} onClick={()=>setCustomBlock(null)}>Edit my text</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== STAGE 2: capture photo + confirm =====
  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>📷 Photos of the delivery{photos.length>1 ? " · "+photos.length : ""}</div>
        <div style={S.secDesc}>Snap the whole drop — add more than one if it helps (different pallets/boxes). At least one is required; they all attach to this delivery of {cart.length} item{cart.length===1?"":"s"}.</div>
      </div>

      {/* What's being logged — the cart, read-only here */}
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
        {cart.map(c => (
          <span key={c.key} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 11px",borderRadius:999,background:"rgba(212,251,111,0.1)",border:"1px solid rgba(212,251,111,0.25)",color:B.kewpie,fontSize:"0.78rem",fontWeight:600}}>
            {c.item}{c.qty!=null && <strong style={{color:B.wasabi}}>×{c.qty}</strong>}
          </span>
        ))}
      </div>

      {error && <div style={{padding:"10px 12px",borderRadius:8,background:"rgba(255,122,92,0.18)",color:"#FF9B83",fontSize:13,fontWeight:600,marginTop:10}}>{error}</div>}

      {/* Captured photos — a grid; the first is the PRIMARY (per-item thumb). */}
      {photos.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10,marginTop:12}}>
          {photos.map((p,idx) => (
            <div key={p.id} style={{position:"relative",borderRadius:12,overflow:"hidden",border:"1px solid rgba(212,251,111,0.25)",background:"rgba(0,0,0,0.4)",aspectRatio:"4/3"}}>
              <img src={p.dataUrl} alt={"Delivery photo "+(idx+1)} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
              <button type="button" onClick={()=>removePhoto(p.id)} aria-label={"Remove photo "+(idx+1)} style={{position:"absolute",top:6,right:6,width:26,height:26,borderRadius:"50%",background:"rgba(0,28,22,0.82)",color:B.kewpie,border:"1px solid rgba(212,251,111,0.3)",cursor:"pointer",fontSize:14,fontWeight:700,lineHeight:1}}>×</button>
              <div style={{position:"absolute",bottom:6,left:6,display:"flex",gap:4,flexWrap:"wrap"}}>
                {idx===0 && <span style={{padding:"2px 7px",borderRadius:999,background:B.wasabi,color:B.nori,fontSize:"0.58rem",fontWeight:800,letterSpacing:"0.04em"}}>PRIMARY</span>}
                <span style={{padding:"2px 7px",borderRadius:999,background:"rgba(0,57,44,0.85)",color:p.source==="library"?"#FFD68A":B.wasabi,fontSize:"0.58rem",fontWeight:700}}>{p.source==="camera"?"📷 App":p.source==="library"?"🖼️ Roll":"⚠ No meta"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add photo(s) — camera-first, plus camera-roll. Kept available so staff
          can keep adding shots. Resetting input.value lets the same file re-fire. */}
      {photos.length < 6 ? (
      <div style={{display:"flex",gap:10,marginTop:12, flexDirection: photos.length ? "row" : "column"}}>
        <label style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,borderRadius:14,border:"2px dashed rgba(212,251,111,0.3)",background:"rgba(212,251,111,0.04)",cursor:"pointer",textAlign:"center",color:B.kewpie, ...(photos.length ? {padding:"16px 12px"} : {aspectRatio:"4/3",padding:20})}}>
          <span style={{fontSize: photos.length ? 26 : 48}}>📷</span>
          <div style={{fontWeight:800,fontSize: photos.length ? "0.86rem" : "1rem",color:B.wasabi,letterSpacing:"0.02em"}}>{photos.length ? "Add another photo" : "Take photo of delivery"}</div>
          {!photos.length && <div style={{fontSize:12,opacity:0.65,maxWidth:280,lineHeight:1.4}}>Make sure the items + delivery box are clearly visible. Up to 6MB each — add up to 6.</div>}
          <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{handleFile(e.target.files && e.target.files[0], "camera"); e.target.value="";}}/>
        </label>
        <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",borderRadius:10,cursor:"pointer",color:B.kewpie,opacity:0.7,fontSize:"0.82rem",fontWeight:600}}>
          <span style={{fontSize:18}}>🖼️</span>
          <span>{photos.length ? "From camera roll" : "Or upload from camera roll"}</span>
          <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{handleFile(e.target.files && e.target.files[0], "library"); e.target.value="";}}/>
        </label>
      </div>
      ) : (
        <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.18)",color:B.kewpie,fontSize:"0.8rem",opacity:0.8,textAlign:"center"}}>Maximum of 6 photos — remove one to add another.</div>
      )}

      {/* Note */}
      <div style={{marginTop:12}}>
        <label style={{display:"block",fontSize:11,fontWeight:700,color:B.kewpie,opacity:0.7,letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Note (optional)</label>
        <input value={note} onChange={e=>setNote(e.target.value.slice(0,500))} placeholder="e.g. 2 boxes short — supplier called · or · Driver left at back door, looks fine" style={{...S.input,width:"100%"}}/>
      </div>

      {/* Upload progress — photo uploads are slow (~8s each), so show which one. */}
      {submitting && uploadStatus && (
        <div style={{marginTop:12,padding:"10px 14px",borderRadius:10,background:"rgba(212,251,111,0.08)",border:"1px solid rgba(212,251,111,0.2)",color:B.kewpie,fontSize:"0.85rem",fontWeight:600,display:"flex",alignItems:"center",gap:10}}>
          <span style={{width:14,height:14,border:"2px solid rgba(212,251,111,0.3)",borderTopColor:B.wasabi,borderRadius:"50%",display:"inline-block",animation:"spin 0.8s linear infinite",flexShrink:0}}/>
          {uploadStatus}
        </div>
      )}

      {/* Footer actions */}
      <div style={{display:"flex",gap:10,marginTop:18}}>
        <button onClick={()=>{setError("");setStage("build");}} style={{flex:1,padding:"14px 16px",borderRadius:12,border:"1px solid rgba(254,250,240,0.18)",background:"transparent",color:B.kewpie,fontFamily:"inherit",fontWeight:700,fontSize:"0.88rem",cursor:"pointer",opacity:0.85}}>← Back</button>
        <button onClick={submit} disabled={!canSubmit} style={{flex:2,padding:"14px 16px",borderRadius:12,border:"none",background:canSubmit?B.wasabi:"rgba(212,251,111,0.2)",color:B.nori,fontFamily:"inherit",fontWeight:800,fontSize:"0.9rem",cursor:canSubmit?"pointer":"not-allowed",opacity:canSubmit?1:0.45,letterSpacing:"0.02em"}}>{submitting?"Logging…":canSubmit?("✓ Log "+cart.length+" item"+(cart.length===1?"":"s")):"📷 Photo required"}</button>
      </div>
    </div>
  );
}

// ===========================================
// EMERGENCY CONTACTS
// ===========================================
// Reads from backend per-site, groups by `group` field, sorted by sortOrder.
// SWR-style cache: paint stale immediately, refresh in background. Admin
// edits propagate within EMERGENCY_CONTACTS_TTL_MS (5 min) on each tablet.
function EmergencyContacts(){
  const [contacts, setContacts] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(()=>{
    // Paint cached rows synchronously on mount if still fresh-ish (ignore
    // TTL on paint — we'll refresh from network anyway, but a stale paint
    // beats an empty tile in the brief gap before the fetch resolves).
    try {
      const raw = localStorage.getItem(EMERGENCY_CONTACTS_CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && Array.isArray(cached.contacts)) setContacts(cached.contacts);
      }
    } catch(e) {}
    // Refresh on mount from Supabase (was the Apps Script ?action=emergencycontacts feed).
    let cancelled = false;
    (async ()=>{
      try {
        const rows = await fetchEmergencyContacts("kitchen");
        if (cancelled) return;
        setContacts(rows);
        try { localStorage.setItem(EMERGENCY_CONTACTS_CACHE_KEY, JSON.stringify({contacts: rows, ts: Date.now()})); } catch(e) {}
      } catch(e) { if (!cancelled) setError(e.message || "Failed to load contacts"); }
    })();
    return ()=>{ cancelled = true; };
  },[]);

  // Group flat list by `group` field. Admin tile sorts on save; we still
  // sort defensively so a manual sheet edit doesn't scramble the order.
  const grouped = React.useMemo(()=>{
    if (!contacts) return null;
    const map = {};
    contacts.forEach(c => { (map[c.group] = map[c.group] || []).push(c); });
    Object.keys(map).forEach(g => map[g].sort((a,b)=>(a.sortOrder||0)-(b.sortOrder||0)));
    return Object.keys(map).map(g => ({group:g, contacts:map[g]}));
  },[contacts]);

  return (
    <div style={S.content}>
      <div style={S.sectionCard}>
        <div style={S.secLabel}>Emergency Contacts</div>
        <div style={S.secDesc}>Key numbers for kitchen staff. Tap to call.</div>
      </div>

      {!contacts && !error && (
        <div style={{padding:"30px 0",textAlign:"center",color:B.kewpie,opacity:0.5,fontSize:"0.85rem"}}>Loading contacts…</div>
      )}
      {error && (
        <div style={{...S.infoBanner,borderColor:"rgba(255,122,92,0.2)",background:"rgba(255,122,92,0.05)",color:B.coral,marginBottom:12}}>
          Couldn't load contacts. <strong>Always call 999 in an emergency.</strong>
        </div>
      )}
      {contacts && contacts.length === 0 && !error && (
        <div style={{padding:"24px 16px",textAlign:"center",color:B.kewpie,opacity:0.5,fontSize:"0.85rem"}}>
          No contacts configured yet. An admin can add them.
        </div>
      )}

      {grouped && grouped.map(group=>(
        <div key={group.group} style={{marginBottom:12}}>
          <div style={S.secTitle}>{group.group.toUpperCase()}</div>
          {group.contacts.map(c=>(
            <a key={c.contactId} href={"tel:"+c.phone} style={{textDecoration:"none",display:"block"}}>
              <div style={{...S.row,cursor:"pointer",padding:"14px 16px",marginBottom:4}}>
                <div>
                  <div style={{...S.itemName,fontSize:"0.9rem"}}>{c.name}</div>
                  <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.4}}>{c.role}</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:"0.78rem",color:B.wasabi,fontWeight:600}}>{c.phone}</span>
                  <span style={{fontSize:"1.1rem"}}>📞</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      ))}

      <div style={{...S.infoBanner,borderColor:"rgba(255,122,92,0.2)",background:"rgba(255,122,92,0.05)",color:B.coral,marginTop:20}}>
        <strong>In an emergency, always call 999 first.</strong> Then notify management.
      </div>
    </div>
  );
}

// =====================================================================
// COMPLIANCE MODULE — inline lift, real backend (2026-04-29)
// =====================================================================
// Replaces the prior iframe → /compliance-demo.html. Every form below
// hydrates from a blank initial state and writes real rows via
// cmpComplianceLog → handleComplianceLog. No MOCK_* data, no fake
// historical view. Calendar past-day picker (CmpCalendarButton +
// CmpHistoryBanner + CmpHistoricalSignerBanner) lifted in 2026-04-30
// against the live getComplianceFormHistory endpoint.

// ---- Helpers ----
function cmpMondayOf(dateStrOrDate) {
  // TZ-safe: parse YYYY-MM-DD strings as LOCAL midnight (not UTC) and
  // format via local Y/M/D so a BST/GMT offset can't shift the result
  // by a day. Without this, "2026-04-28" parses as UTC midnight, comes
  // out as 01:00 BST Tuesday, and Monday-of-week resolves to the
  // previous Sunday.
  var d;
  if (dateStrOrDate instanceof Date) {
    d = new Date(dateStrOrDate.getFullYear(), dateStrOrDate.getMonth(), dateStrOrDate.getDate());
  } else if (typeof dateStrOrDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStrOrDate)) {
    var parts = dateStrOrDate.split("-").map(Number);
    d = new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    d = new Date(dateStrOrDate);
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  var dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  var yy = d.getFullYear();
  var mm = d.getMonth() + 1;
  var dd = d.getDate();
  return yy + "-" + String(mm).padStart(2, "0") + "-" + String(dd).padStart(2, "0");
}
function cmpFriendly(iso) {
  if (!iso) return "—";
  var d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {day: "2-digit", month: "short"});
}
function cmpInitials(name) {
  return String(name || "").trim().slice(0, 2).toUpperCase() || "??";
}
function cmpIsTestMode() {
  try {
    if (typeof window !== "undefined" && window.__cmpTestMode === true) return true;
    // localStorage flag survives reloads — set with localStorage.setItem('cmpTestMode','1')
    // before kicking off a QA run. Cleared with localStorage.removeItem('cmpTestMode').
    if (typeof localStorage !== "undefined" && localStorage.getItem("cmpTestMode") === "1") return true;
  } catch(_) {}
  return false;
}

var __cmpToastPush = null;
function cmpToast(msg, tone) {
  if (__cmpToastPush) __cmpToastPush({msg: msg, tone: tone || "", id: Date.now() + Math.random()});
}
function CmpToastLayer() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    __cmpToastPush = (t) => {
      setItems(p => [...p, t]);
      setTimeout(() => setItems(p => p.filter(x => x.id !== t.id)), 3200);
    };
    return () => { __cmpToastPush = null; };
  }, []);
  return (
    <div className="cmp-toast-layer">
      {items.map(t => (
        <div key={t.id} className={"cmp-toast " + (t.tone === "success" ? "cmp-success" : t.tone === "warn" ? "cmp-warn" : t.tone === "error" ? "cmp-error" : "")}>{t.msg}</div>
      ))}
    </div>
  );
}

// Single-helper backend writer — envelope keys (action / event / site /
// staffPin / testMode) MUST win over payload (the 95cef98 fix). Order
// matters: payload first, envelope second so Object.assign overwrites.
function cmpComplianceLog(backend, site, event, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "complianceLog",
    event: event,
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow (best-effort, fire-and-forget): mirror the
    // ACCEPTED write into Supabase, carrying the old backend's response so
    // its minted ids (incidentId / batchId / correctiveActionId) become the
    // case links (legacy_ref). Old write failed = nothing happened = no
    // shadow; a shadow failure never reaches this promise chain — the gap
    // shows up in the reconcile, never on the kitchen.
    try {
      complianceSync.recordComplianceLog({
        siteKey: site, event: event, payload: payload || {},
        staffPin: staffPin, testMode: cmpIsTestMode(), response: d
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}

// HACCP uses its own action (haccpEvent / haccpSign) rather than
// complianceLog. Wrappers preserve the testMode plumbing.
function cmpHaccpEvent(backend, site, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "haccpEvent",
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow — same contract as cmpComplianceLog above.
    try {
      var p = payload || {};
      complianceSync.recordHaccpEvent({
        siteKey: site, section: p.section, field: p.field, value: p.value,
        extra: p.extra || null, date: p.date || null,
        staffPin: staffPin, testMode: cmpIsTestMode()
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}
function cmpHaccpSign(backend, site, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "haccpSign",
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow — the chef's day sign-off. The sheet remains
    // the legal record during dual-run; this row is the copy that lets the
    // reconcile prove the new home is trustworthy before any flip.
    try {
      complianceSync.recordHaccpSign({
        siteKey: site, date: (payload && payload.date) || null,
        staffPin: staffPin, testMode: cmpIsTestMode()
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}

var CMP_CONFIG_TTL_MS = 5 * 60 * 1000;
function cmpFetchComplianceConfig(backend, site, opts) {
  var force = opts && opts.force;
  // Wave 6 review fix (2026-04-30): include testMode in the cache key so
  // toggling the QA flag busts the cache instead of serving up to 5
  // minutes of stale-mode payload.
  var cacheKey = "cmpComplianceConfig." + site + "." + (cmpIsTestMode() ? "test" : "prod");
  var nowMs = Date.now();
  if (!force) {
    try {
      var raw = localStorage.getItem(cacheKey);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && (nowMs - cached.ts) < CMP_CONFIG_TTL_MS) {
          return Promise.resolve(cached.payload);
        }
      }
    } catch(_) {}
  }
  // The existing Google-Sheet fetch, unchanged — the OFF path AND the config reader's
  // fallback on any Supabase error/drift. Keeps the 5-min cache write byte-for-byte.
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({action: "getComplianceConfig", site: site, testMode: cmpIsTestMode()})
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "Config fetch error");
      try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: d})); } catch(_) {}
      return d;
    });
  };
  // Lane BK config read-flip (gated OFF by default — COMPLIANCE_CONFIG_READ_HOSTS empty).
  // With the gate off this returns exactly the sheet fetch above (OFF path byte-identical).
  // ON: serve Supabase ONLY when it resolves to the same lists as the live Sheet (the
  // drift guard inside readComplianceConfig); on drift/error -> the Sheet. The returned
  // config is cached under the same 5-min key so the resolvers/screens are unchanged.
  // Lane BV: on an AUTHORITATIVE host (COMPLIANCE_CONFIG_AUTHORITATIVE_HOSTS / ?compconfigauth=on,
  // empty today) there is NO Sheet fallback — the Sheet copy goes stale once the Command Centre
  // stops writing it — so a Supabase failure rejects like a failed Sheet fetch does today (the
  // screens' existing catch -> "Couldn't load …" toast). Guarded hosts keep the Sheet fallback.
  if (complianceReader.complianceConfigReadEnabled()) {
    var fallback = complianceReader.complianceConfigAuthoritativeEnabled()
      ? function() { return Promise.reject(new Error("Supabase config unavailable (this tablet reads Supabase only, no Sheet fallback)")); }
      : sheetFetch;
    return complianceReader.readComplianceConfig({ site: site, testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(cfg) {
        if (!cfg) return fallback();
        try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: cfg})); } catch(_) {}
        return cfg;
      })
      .catch(function() { return fallback(); });
  }
  return sheetFetch();
}

// Resolves "items" array per (surfaceKey, siteKey). Falls back to '*'
// if no per-site override. Kitchen sends site:"mcr"; backend normalises
// to "kitchen" — config.site fallback handles the round-trip.
function cmpResolveSurfaceItems(config, surfaceKey, siteKey) {
  if (!config || !config.surfaces || !config.surfaces[surfaceKey]) return [];
  var sf = config.surfaces[surfaceKey];
  var lookup = (config.site || siteKey);
  var perSite = sf.sites && (sf.sites[lookup] || sf.sites[siteKey] || sf.sites["*"]);
  if (!perSite) return [];
  return (perSite.items || []).filter(function(i) { return (i.status || "active") === "active"; });
}

// Hydrates a form's state from the audit trail. Public endpoint, masked
// PIN attribution, whitelisted tab names. Returns rows newest-first for
// the past `days` (default 7). Each row is {_ts, ...schema columns}.
function cmpFetchFormToday(backend, site, form, days) {
  // The existing Google-Sheet fetch, unchanged — this is the OFF path AND the
  // reader's fallback on any Supabase error.
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "getComplianceFormToday",
        site: site,
        form: form,
        days: days || 7,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "Reader error");
      return (d && d.rows) || [];
    });
  };
  // Lane AK read-flip (gated OFF by default — COMPLIANCE_READ_HOSTS is empty).
  // With the gate off this returns exactly the sheet rows above.
  if (complianceReader.complianceReadEnabled()) {
    return complianceReader.readComplianceFormToday(site, form, days, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return (res && res.rows) || []; })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// Today's ISO date prefix (YYYY-MM-DD) — TZ-safe, formatted from local
// Y/M/D so a UTC offset can't push us into yesterday around midnight.
function cmpTodayIso() {
  var d = new Date();
  var yy = d.getFullYear();
  var mm = d.getMonth() + 1;
  var dd = d.getDate();
  return yy + "-" + String(mm).padStart(2, "0") + "-" + String(dd).padStart(2, "0");
}

// Fetches a date-range slice of historical compliance form rows. Used
// by every form's CmpCalendarButton — when staff tap a past day (or
// past week for cleaning / pest-monday), the form switches to read-only
// mode populated from this response. Same whitelist + masking as
// cmpFetchFormToday but with explicit dateFrom/dateTo bounds.
function cmpFetchFormHistory(backend, site, form, dateFrom, dateTo) {
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "getComplianceFormHistory",
        site: site,
        form: form,
        dateFrom: dateFrom,
        dateTo: dateTo,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "History reader error");
      return (d && d.rows) || [];
    });
  };
  // Lane AK read-flip (gated OFF by default). OFF path = the sheet fetch above.
  if (complianceReader.complianceReadEnabled()) {
    return complianceReader.readComplianceFormHistory(site, form, dateFrom, dateTo, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return (res && res.rows) || []; })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// HACCP single-day reader. Distinct from cmpFetchFormHistory because
// HACCP returns a structured {sheet, signOff, status} object — not a
// flat row array — and lives in two backend tabs (HACCP Daily Sheet +
// HACCP Events) with their own CCP-keyed shape.
function cmpFetchHaccpDay(backend, site, date) {
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "haccpToday",
        site: site,
        date: date,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    });
  };
  // Lane AK read-flip. The HACCP daily sheet is the LEGAL signature, so it has
  // its OWN gate (haccpReadEnabled), flipped separately from the everyday ticks
  // and only on Adam's dated legal sign-off. OFF by default → sheetFetch, i.e.
  // the same structured {date, site, status, sheet, signOff} object as before.
  if (complianceReader.haccpReadEnabled()) {
    return complianceReader.readHaccpDay(site, date, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return res || sheetFetch(); })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// Calendar button → modal grid → onPick(YYYY-MM-DD or YYYY-MM-DD of
// Monday for week-mode). Lifted from compliance-demo.html (2026-04-30).
// Mode "day" picks a single date; mode "week" picks the Monday of the
// chosen week (used by Cleaning + Pest Monday Sweep). Future dates are
// disabled. Pill label: "History" by default, "<friendly date>" when
// viewing past day, "Week of <Mon>" when viewing past week.
function CmpCalendarButton({onPick, viewingDate, mode}) {
  mode = mode || "day";
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const todayStr = cmpTodayIso();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const firstDay = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7; // Mon=0
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // TZ-safe: format from local Y/M/D directly so BST/GMT offset can't
  // shift the date backwards via UTC conversion. Bug seen 2026-04-30:
  // toISOString() on midnight-BST returned the previous day in UTC.
  const iso = (d) => {
    const yy = month.getFullYear();
    const mm = month.getMonth() + 1;
    return yy + "-" + String(mm).padStart(2, "0") + "-" + String(d).padStart(2, "0");
  };
  const now = new Date();
  const isFuture = (d) => new Date(month.getFullYear(), month.getMonth(), d) > now;
  const isToday = (d) => iso(d) === todayStr;
  const viewingMonday = (mode === "week" && viewingDate) ? cmpMondayOf(viewingDate) : viewingDate;
  const isViewing = (d) => mode === "week" ? cmpMondayOf(iso(d)) === viewingMonday : iso(d) === viewingDate;
  const monthLabel = month.toLocaleDateString("en-GB", {month: "long", year: "numeric"});
  const todayMon = cmpMondayOf(todayStr);
  const isDefault = !viewingDate || viewingDate === todayStr || (mode === "week" && viewingMonday === todayMon);
  const btnLabel = isDefault
    ? "History"
    : (mode === "week" ? "Week of " + cmpFriendly(viewingMonday) : cmpFriendly(viewingDate));
  const handlePick = (d) => {
    const picked = mode === "week" ? cmpMondayOf(iso(d)) : iso(d);
    onPick(picked);
    setOpen(false);
  };
  const resetTarget = mode === "week" ? todayMon : todayStr;
  return (
    <>
      <button className={"cmp-btn cmp-btn-sm " + (!isDefault ? "cmp-btn-primary" : "cmp-btn-ghost")} onClick={() => setOpen(true)}>
        📅 {btnLabel}
      </button>
      {open && (
        <div className="cmp-form-modal-scrim" onClick={() => setOpen(false)} style={{zIndex:180}}>
          <div className="cmp-form-modal" onClick={e => e.stopPropagation()} style={{maxWidth:360,padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>‹</button>
              <h3 style={{color:"var(--cm-wasabi)",margin:0}}>{monthLabel}</h3>
              <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>›</button>
            </div>
            {mode === "week" && (
              <div style={{color:"rgba(254,250,240,0.55)",textAlign:"center",marginBottom:8,fontSize:11}}>Tap any day to load that week (Mon–Sun)</div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:8}}>
              {["M","T","W","T","F","S","S"].map((l,i) => (
                <div key={i} style={{textAlign:"center",fontSize:10,letterSpacing:"0.06em",color:"rgba(254,250,240,0.5)",fontWeight:800,padding:"6px 0"}}>{l}</div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
              {cells.map((d,i) => {
                if (d === null) return <div key={i} />;
                const disabled = isFuture(d);
                const today = isToday(d);
                const viewing = isViewing(d);
                return (
                  <button key={i} disabled={disabled}
                    onClick={() => handlePick(d)}
                    style={{
                      background: viewing ? "var(--cm-wasabi)" : today ? "rgba(212,251,111,0.18)" : "rgba(254,250,240,0.06)",
                      color: viewing ? "var(--cm-nori)" : today ? "var(--cm-wasabi)" : "var(--cm-kewpie)",
                      border: "none", borderRadius: 8, padding: "9px 0",
                      fontSize: 12.5, fontWeight: viewing || today ? 800 : 600,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.25 : 1,
                      fontFamily: "inherit"
                    }}>
                    {d}
                  </button>
                );
              })}
            </div>
            <button className="cmp-btn cmp-btn-primary" style={{width:"100%",marginTop:12}} onClick={() => { onPick(resetTarget); setOpen(false); }}>← Back to {mode === "week" ? "this week" : "today"}</button>
            <div style={{color:"rgba(254,250,240,0.55)",textAlign:"center",fontSize:11,marginTop:8}}>{mode === "week" ? "Past weeks open read-only. Future weeks disabled." : "Past days open read-only. Today is editable. Future dates disabled."}</div>
          </div>
        </div>
      )}
    </>
  );
}

// Banner shown at the top of every form when viewing a past date.
// Mirrors compliance-demo.html HistoryBanner. Plum tone signals
// "historical / read-only" so staff don't accidentally try to edit.
// `historical` (optional) lets week-mode forms pass an explicit flag —
// e.g. picking the current week's Monday is NOT historical even though
// the raw date string differs from today.
function CmpHistoryBanner({date, onClear, label, backLabel, historical}) {
  const isHistorical = (typeof historical === "boolean")
    ? historical
    : !!(date && date !== cmpTodayIso());
  if (!isHistorical) return null;
  return (
    <div className="cmp-card" style={{background:"#f0e6f8",border:"1px solid #6b3fa0",display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
      <div style={{fontSize:18}}>🕰️</div>
      <div style={{flex:1}}>
        <strong style={{fontSize:13.5,color:"#6b3fa0"}}>Viewing {label || cmpFriendly(date)}</strong>
        <span style={{marginLeft:8,fontSize:13,color:"var(--cm-nori-70)"}}>Read-only historical view.</span>
      </div>
      <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={onClear}>{backLabel || "← Back to today"}</button>
    </div>
  );
}

// Hook that wraps the calendar / history flow for any compliance form.
// Each form spreads {viewDate, isHistorical, histRows, signedBy, ...}
// straight into its render. Day-mode forms get back a single date;
// week-mode forms get back the Monday of the picked week and the rows
// span Mon–Sun. HACCP uses a different reader (cmpFetchHaccpDay) so
// it's wired by hand below — every other form goes through here.
function useCmpHistoryView(backend, siteKey, form, mode) {
  const today = cmpTodayIso();
  const [viewDate, setViewDateRaw] = useState(today);
  const [histRows, setHistRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const setViewDate = useCallback((d) => {
    if (!d) { setViewDateRaw(today); return; }
    setViewDateRaw(mode === "week" ? cmpMondayOf(d) : d);
  }, [mode, today]);
  const isHistorical = viewDate !== today && (mode !== "week" || viewDate !== cmpMondayOf(today));
  useEffect(() => {
    if (!isHistorical) { setHistRows([]); return; }
    setLoading(true);
    let dateFrom = viewDate, dateTo = viewDate;
    if (mode === "week") {
      // Parse viewDate as LOCAL midnight (not UTC) to avoid the same TZ
      // shift fixed in cmpMondayOf. Format Sunday from local Y/M/D.
      const parts = String(viewDate).split("-").map(Number);
      const mon = new Date(parts[0], parts[1] - 1, parts[2]);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      dateTo = sun.getFullYear() + "-" + String(sun.getMonth() + 1).padStart(2, "0") + "-" + String(sun.getDate()).padStart(2, "0");
    }
    cmpFetchFormHistory(backend, siteKey, form, dateFrom, dateTo)
      .then(rows => { setHistRows(rows || []); setLoading(false); })
      .catch(e => { cmpToast("History load failed: " + e.message, "error"); setLoading(false); });
  }, [backend, siteKey, form, viewDate, isHistorical, mode]);
  // Resolve a "signed by" stamp if the backend rows carry one. Each
  // form has its own conventions (CleaningLog uses signoff rows in
  // SignOffAudit; pest-daily uses the latest row of the day). We
  // surface the freshest row's name + timestamp by default; forms
  // that need a tighter rule pass overrideSignedBy in their render.
  const signedBy = (() => {
    if (!isHistorical || !histRows.length) return null;
    const newest = histRows[0];
    if (newest && newest.StaffName) return {name: newest.StaffName, ts: newest._ts};
    return null;
  })();
  return {viewDate, setViewDate, isHistorical, histRows, signedBy, loading, today};
}

// Banner showing WHO submitted the historical form, lifted from the
// audit trail (signedBy comes from the live backend response — never
// synthesized). Per JJ 2026-04-30: "anything in historical mode needs
// to have who submitted it" — without live signedBy we render nothing
// rather than a fake signer.
function CmpHistoricalSignerBanner({date, formLabel, signedBy, historical}) {
  const isHistorical = (typeof historical === "boolean")
    ? historical
    : !!(date && date !== cmpTodayIso());
  if (!isHistorical) return null;
  if (!signedBy || !signedBy.name) return null;
  const name = String(signedBy.name);
  let timeLabel = "";
  if (signedBy.ts) {
    try {
      const d = new Date(signedBy.ts);
      if (!isNaN(d.getTime())) timeLabel = d.toTimeString().slice(0, 5);
    } catch (_) {}
  } else if (signedBy.timeLabel) {
    timeLabel = signedBy.timeLabel;
  }
  let dateLabel = date;
  try { dateLabel = new Date(date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"}); } catch (_) {}
  return (
    <div className="cmp-card" style={{background:"rgba(212,251,111,0.18)",border:"1px solid var(--cm-wasabi)"}}>
      <h4 style={{color:"var(--cm-nori)",marginBottom:4}}>✓ {formLabel || "Submitted"}</h4>
      <div style={{fontSize:13}}>Submitted by <strong>{name}</strong> on <strong>{dateLabel}</strong>{timeLabel ? " at " + timeLabel : ""}.</div>
    </div>
  );
}

function cmpSundayPillTone(now) {
  var d = now || new Date();
  if (d.getDay() !== 0) return null;
  return d.getHours() >= 17 ? "bad" : "warn";
}

function makeCmpPinPrompt(requestPin, staffByPin) {
  return function cmpPinPrompt(opts) {
    requestPin(async function(staffName, staffPin) {
      // Row H (Lane BF): on a server-PIN-verify host do NOT read the roster cache
      // — the server verify below is the only source of role + homeSite for this
      // one PIN. OFF path byte-identical (reads staffByPin exactly as before).
      // This mirrors the FOH copy (foh.jsx makeCmpPinPrompt), which #256 already
      // gated; the kitchen copy was still reading the roster and now matches.
      var info = pinAuth.serverPinVerifyEnabled() ? null : (staffByPin && staffByPin[staffPin]);
      var role = info && info.role ? String(info.role) : "";
      var homeSite = info && info.homeSite ? info.homeSite : "";
      if (pinAuth.serverPinVerifyEnabled()) {
        try { var srv = await pinAuth.verifyPin(staffPin); if (srv && srv.ok) { role = String(srv.role || role); homeSite = srv.homeSite || homeSite; } } catch (e) {}
      }
      var roles = role.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
      var effRoles = roles.slice();
      if (effRoles.indexOf("BOH") !== -1 && effRoles.indexOf("FOH") === -1) effRoles.push("FOH");
      if (opts.requiredRoles && opts.requiredRoles.length) {
        var ok = opts.requiredRoles.some(function(r) { return effRoles.indexOf(r) !== -1; });
        if (!ok) {
          cmpToast(staffName + " (" + (role || "no role") + ") not authorised — needs " + opts.requiredRoles.join(" or "), "error");
          if (opts.onCancel) opts.onCancel();
          return;
        }
      }
      var staff = {
        pin: staffPin,
        name: staffName,
        role: role,
        roles: roles,
        initials: cmpInitials(staffName),
        home: homeSite
      };
      opts.onConfirm(staff);
    }, opts.onCancel || null);
  };
}

// AM/PM drum-roll TimePicker — lifted from compliance-demo.html
// (2026-04-30, parity sweep). Three scroll-snap wheels (Hour / Minute /
// AM-PM). Pattern: CmpTimePicker shows the selected time as a read-only
// display; tapping opens CmpTimePickerModal hosting the wheels. Returns
// "HH:MM" 24h so the data shape matches a native <input type="time"> —
// CmpFormModal's type:"time" branch renders this in place of the
// browser's native picker.
function cmpParseHHMM(v) {
  if (!v) return {hour:9, minute:0, period:"AM"};
  const [h,m] = v.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12; if (hour12 === 0) hour12 = 12;
  return {hour: hour12, minute: m || 0, period};
}
function cmpFormatHHMM({hour, minute, period}) {
  let h24 = hour % 12;
  if (period === "PM") h24 += 12;
  return String(h24).padStart(2,"0") + ":" + String(minute).padStart(2,"0");
}
function cmpFormatTime12Display(hhmm) {
  if (!hhmm) return "—";
  const {hour, minute, period} = cmpParseHHMM(hhmm);
  return hour + ":" + String(minute).padStart(2,"0") + " " + period;
}
const CMP_TIME_ITEM_H = 44;
const CMP_TIME_VISIBLE = 5;
const CMP_TIME_BODY_H = CMP_TIME_ITEM_H * CMP_TIME_VISIBLE;
const CMP_TIME_CHEV_H = 26;
const CMP_TIME_PAD = ((CMP_TIME_VISIBLE - 1) / 2) * CMP_TIME_ITEM_H;

function CmpTimeWheel({items, selectedIndex, onChange, render}) {
  const ITEM_H = CMP_TIME_ITEM_H;
  const VISIBLE = CMP_TIME_VISIBLE;
  const PAD = CMP_TIME_PAD;
  const ref = useRef(null);
  const settleTimer = useRef(null);
  const programmatic = useRef(false);
  const lastWheelAt = useRef(0);
  const [liveIdx, setLiveIdx] = useState(selectedIndex);
  useEffect(() => {
    if (!ref.current) return;
    const target = selectedIndex * ITEM_H;
    if (Math.abs(ref.current.scrollTop - target) > 1) {
      programmatic.current = true;
      ref.current.scrollTop = target;
      setLiveIdx(selectedIndex);
      setTimeout(() => { programmatic.current = false; }, 60);
    }
  }, [selectedIndex]);
  const onScroll = () => {
    if (!ref.current) return;
    const liveRaw = ref.current.scrollTop / ITEM_H;
    const liveClamped = Math.max(0, Math.min(items.length - 1, Math.round(liveRaw)));
    if (liveClamped !== liveIdx) setLiveIdx(liveClamped);
    if (programmatic.current) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      const targetTop = clamped * ITEM_H;
      if (Math.abs(ref.current.scrollTop - targetTop) > 0.5) {
        programmatic.current = true;
        ref.current.scrollTo({top: targetTop, behavior:"smooth"});
        setTimeout(() => { programmatic.current = false; }, 240);
      }
      if (clamped !== selectedIndex) onChange(clamped);
    }, 110);
  };
  const onWheel = (e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastWheelAt.current < 90) return;
    lastWheelAt.current = now;
    const dir = e.deltaY > 0 ? 1 : -1;
    onItemClick(Math.max(0, Math.min(items.length - 1, liveIdx + dir)));
  };
  const targetIdxRef = useRef(selectedIndex);
  useEffect(() => { targetIdxRef.current = selectedIndex; }, [selectedIndex]);
  const onItemClick = (i) => {
    if (!ref.current) return;
    targetIdxRef.current = i;
    programmatic.current = true;
    ref.current.scrollTo({top: i * ITEM_H, behavior:"smooth"});
    setLiveIdx(i);
    setTimeout(() => { programmatic.current = false; }, 240);
    if (i !== selectedIndex) onChange(i);
  };
  const step = (dir) => {
    const next = Math.max(0, Math.min(items.length - 1, targetIdxRef.current + dir));
    onItemClick(next);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
      <button type="button" tabIndex={-1} onClick={() => step(-1)} style={{height:CMP_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:"var(--cm-wasabi)",fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▲</button>
      <div ref={ref}
        onScroll={onScroll}
        onWheel={onWheel}
        style={{
          height: VISIBLE * ITEM_H,
          overflowY:"scroll",
          width: 80,
          textAlign:"center",
          fontSize:22,
          fontWeight:700,
          color:"var(--cm-kewpie)",
          fontFamily:"inherit",
          msOverflowStyle:"none",
          scrollbarWidth:"none",
          touchAction:"pan-y",
          WebkitOverflowScrolling:"touch",
          position:"relative",
          overscrollBehavior:"contain"
        }}>
        <div style={{height: PAD}} />
        {items.map((it, i) => {
          const dist = Math.abs(i - liveIdx);
          return (
            <div key={i} onClick={() => onItemClick(i)} style={{
              height: ITEM_H,
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              opacity: dist === 0 ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.28 : 0.15,
              transform: `scale(${dist === 0 ? 1 : 0.86})`,
              transition:"opacity 0.16s, transform 0.16s",
              cursor:"pointer",
              userSelect:"none"
            }}>{render ? render(it) : it}</div>
          );
        })}
        <div style={{height: PAD}} />
      </div>
      <button type="button" tabIndex={-1} onClick={() => step(1)} style={{height:CMP_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:"var(--cm-wasabi)",fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▼</button>
    </div>
  );
}

function CmpTimePickerModal({value, onConfirm, onCancel, title}) {
  const [{hour, minute, period}, setState] = useState(() => cmpParseHHMM(value));
  const setH = (idx) => setState(s => {
    // Auto-flip AM↔PM on the 11→12 boundary, per JJ's brief 2026-04-30
    // (parity with mockup). 11→12 forward means crossing noon/midnight,
    // 12→11 backward means crossing back. Anything else stays.
    const newHour = idx + 1;
    let newPeriod = s.period;
    if (s.hour === 11 && newHour === 12) newPeriod = s.period === "AM" ? "PM" : "AM";
    else if (s.hour === 12 && newHour === 11) newPeriod = s.period === "AM" ? "PM" : "AM";
    return {...s, hour: newHour, period: newPeriod};
  });
  const setM = (idx) => setState(s => ({...s, minute: idx}));
  const setP = (idx) => setState(s => ({...s, period: idx === 0 ? "AM" : "PM"}));
  return (
    <div className="cmp-form-modal-scrim" onClick={onCancel} style={{zIndex:200}}>
      <div className="cmp-form-modal" onClick={e => e.stopPropagation()} style={{maxWidth:420,padding:0}}>
        <div style={{padding:"22px 24px 8px",textAlign:"center"}}>
          <h3 style={{color:"var(--cm-wasabi)",marginBottom:6}}>{title || "Pick a time"}</h3>
          <div style={{fontFamily:"monospace",fontWeight:800,fontSize:32,color:"var(--cm-kewpie)",letterSpacing:"0.05em",margin:"6px 0 12px"}}>
            {hour}:{String(minute).padStart(2,"0")} <span style={{color:"var(--cm-wasabi)"}}>{period}</span>
          </div>
        </div>
        <div style={{padding:"4px 24px 12px",position:"relative"}}>
          <div style={{
            position:"absolute",
            top: 4 + CMP_TIME_CHEV_H + CMP_TIME_PAD,
            left: 24, right: 24,
            height: CMP_TIME_ITEM_H,
            background:"rgba(212,251,111,0.10)",
            borderTop:"1px solid rgba(212,251,111,0.45)",
            borderBottom:"1px solid rgba(212,251,111,0.45)",
            borderRadius:6,
            pointerEvents:"none",
            zIndex:0
          }} />
          <div style={{
            display:"grid",
            gridTemplateColumns:"1fr auto 1fr 1fr",
            alignItems:"stretch",
            justifyItems:"center",
            gap:14,
            position:"relative",
            zIndex:1
          }}>
            <CmpTimeWheel items={Array.from({length:12},(_,i)=>i+1)} selectedIndex={hour-1} onChange={setH} />
            <div style={{
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              fontSize:30,
              fontWeight:800,
              color:"var(--cm-wasabi)",
              height: CMP_TIME_CHEV_H * 2 + CMP_TIME_BODY_H,
              lineHeight:1
            }}>:</div>
            <CmpTimeWheel items={Array.from({length:60},(_,i)=>i)} selectedIndex={minute} onChange={setM} render={n => String(n).padStart(2,"0")} />
            <CmpTimeWheel items={["AM","PM"]} selectedIndex={period === "AM" ? 0 : 1} onChange={setP} />
          </div>
        </div>
        <div style={{display:"flex",gap:10,padding:"14px 24px 22px",justifyContent:"flex-end",borderTop:"1px solid rgba(254,250,240,0.08)"}}>
          <button onClick={onCancel} style={{padding:"10px 16px",border:"1px solid rgba(254,250,240,0.25)",background:"transparent",color:"var(--cm-kewpie)",borderRadius:8,fontFamily:"inherit",fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={() => onConfirm(cmpFormatHHMM({hour, minute, period}))} style={{padding:"10px 18px",borderRadius:8,background:"var(--cm-wasabi)",color:"var(--cm-nori)",fontWeight:800,fontSize:13,border:"none",fontFamily:"inherit",cursor:"pointer"}}>Done</button>
        </div>
      </div>
    </div>
  );
}

function CmpTimePicker({value, onChange, label, theme}) {
  const [open, setOpen] = useState(false);
  const isDark = theme === "dark";
  const style = isDark ? {
    width:"100%", padding:"12px 14px", borderRadius:8,
    background:"rgba(254,250,240,0.08)", color: value ? "var(--cm-kewpie)" : "rgba(254,250,240,0.55)",
    border:"1px solid rgba(254,250,240,0.18)",
    fontSize:14, fontFamily:"inherit", cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, textAlign:"left"
  } : {
    width:"100%", padding:"10px 14px", borderRadius:8,
    background: "var(--cm-rice)", color: value ? "var(--cm-nori)" : "var(--cm-nori-60)",
    border:"1px solid var(--cm-nori-18)",
    fontSize:14, fontFamily:"inherit", cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, textAlign:"left"
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={style}>
        <span style={{fontWeight:700,fontFamily:"monospace",fontSize:16,letterSpacing:"0.02em"}}>{value ? cmpFormatTime12Display(value) : "Tap to pick a time"}</span>
        <span style={{color: isDark ? "var(--cm-wasabi)" : "var(--cm-nori)",fontSize:16}}>🕒</span>
      </button>
      {open && (
        <CmpTimePickerModal
          value={value}
          title={label || "Pick a time"}
          onConfirm={(v) => { onChange(v); setOpen(false); }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CmpFormModal({title, desc, fields, initial, submitLabel, onSubmit, onCancel}) {
  const [values, setValues] = useState(() => initial || Object.fromEntries(fields.map(f => [f.key, f.default || ""])));
  const submit = () => {
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (f.required && !String(values[f.key] || "").trim()) {
        cmpToast(f.label + " is required", "error");
        return;
      }
    }
    onSubmit(values);
  };
  return (
    <div className="cmp-form-modal-scrim" onClick={onCancel}>
      <div className="cmp-form-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-form-head">
          <h3>{title}</h3>
          {desc && <div className="cmp-form-desc">{desc}</div>}
        </div>
        <div className="cmp-form-inner">
          {fields.map(f => (
            <div key={f.key} className="cmp-field-block">
              <label>{f.label}{f.required && <span style={{color:"#ff8888"}}> *</span>}</label>
              {f.type === "select" ? (
                <select value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))}>
                  {(f.options || []).map(o => {
                    const opt = (typeof o === "object") ? o : {value: o, label: o};
                    return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                  })}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} rows={3} />
              ) : f.type === "time" ? (
                <CmpTimePicker
                  value={values[f.key] || ""}
                  label={f.label}
                  theme="dark"
                  onChange={(v) => setValues(vals => Object.assign({}, vals, {[f.key]: v}))}
                />
              ) : (
                <input type={f.type || "text"} value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} />
              )}
              {f.hint && <span className="cmp-hint">{f.hint}</span>}
            </div>
          ))}
        </div>
        <div className="cmp-form-foot">
          <button className="cmp-form-cancel" onClick={onCancel}>Cancel</button>
          <button className="cmp-form-submit" onClick={submit}>{submitLabel || "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Documentation Hub data (Phase 2 v1.2 — lifted from compliance-demo.html)
// =====================================================================
// Static reference data — these are the actual Rolled docs/policies. SOPs
// + RAs + SSoWs are stable lists; admin sheet wiring is a v1.3 follow-up.
// Maintenance Records intentionally OUT per Wave 6 of original review.

const CMP_SOPS = [
  {id:"SOP-01", title:"SOP 1 — Food Allergies & Intolerances",            version:"v1", updated:"2026-02-23"},
  {id:"SOP-02", title:"SOP 2 — Cleaning Methods",                         version:"v1", updated:"2026-02-23"},
  {id:"SOP-2a", title:"SOP 2a — Blast Chilling",                          version:"v1", updated:"2026-02-23"},
  {id:"SOP-03", title:"SOP 3 — Colour-Coded Equipment",                   version:"v1", updated:"2026-02-23"},
  {id:"SOP-04", title:"SOP 4 — Cook-Chill & Cook-Freeze",                 version:"v1", updated:"2026-02-23"},
  {id:"SOP-06", title:"SOP 6 — Cooling Food",                             version:"v1", updated:"2026-02-23"},
  {id:"SOP-07", title:"SOP 7 — Defrosting Foods",                         version:"v1", updated:"2026-02-23"},
  {id:"SOP-08", title:"SOP 8 — Food Deliveries",                          version:"v1", updated:"2026-02-23"},
  {id:"SOP-10", title:"SOP 10 — Fitness to Work Guidelines",              version:"v1", updated:"2026-02-23"},
  {id:"SOP-11", title:"SOP 11 — Food Contamination",                      version:"v2", updated:"2026-04-19"},
  {id:"SOP-12", title:"SOP 12 — Food Labelling",                          version:"v1", updated:"2026-03-19"},
  {id:"SOP-13", title:"SOP 13 — Fridge & Freezer Breakdown",              version:"v2", updated:"2026-04-19"},
  {id:"SOP-14", title:"SOP 14 — Food Storage",                            version:"v1", updated:"2026-02-23"},
  {id:"SOP-15", title:"SOP 15 — Safe Use of Ice",                         version:"v1", updated:"2026-02-23", keywords:"ice cubes water freezer"},
  {id:"SOP-16", title:"SOP 16 — Personal Hygiene",                        version:"v2", updated:"2026-04-19", keywords:"handwashing hands uniform jewellery hair nails"},
  {id:"SOP-17", title:"SOP 17 — Pest Control",                            version:"v2", updated:"2026-04-19", keywords:"rodents mice rats insects flies droppings"},
  {id:"SOP-18", title:"SOP 18 — Microwaves",                              version:"v1", updated:"2026-02-23"},
  {id:"SOP-19", title:"SOP 19 — Raw Food Washing",                        version:"v1", updated:"2026-02-23"},
  {id:"SOP-21", title:"SOP 21 — Waste Management",                        version:"v1", updated:"2026-03-19"},
  {id:"SOP-22", title:"SOP 22 — Cold Food Display",                       version:"v1", updated:"2026-02-23"},
  {id:"SOP-24", title:"SOP 24 — Glass & Crockery Safe Handling",          version:"v1", updated:"2026-02-23"},
  {id:"SOP-25", title:"SOP 25 — Enforcement Officers",                    version:"v1", updated:"2026-02-23"},
  {id:"SOP-26", title:"SOP 26 — Sushi Rolling Machine (Suzumo SVR-NXA-CE)", version:"v1", updated:"2026-02-24"},
  {id:"SOP-27", title:"SOP 27 — Rice Mixing Machine (Suzumo MCR-UNC)",    version:"v1", updated:"2026-02-24"},
  {id:"SOP-28", title:"SOP 28 — Compact Rice Mixer (Suzumo MCR-SSC)",     version:"v1", updated:"2026-02-24"},
  {id:"SOP-29", title:"SOP 29 — Sushi Rice Acidification CCP (pH ≤ 4.3)", version:"v1", updated:"2026-03-12", keywords:"rice acidification ph vinegar ambient hold"},
  {id:"SOP-30", title:"SOP 30 — Freezing of Fish (parasite control)",     version:"v1", updated:"2026-03-05", keywords:"salmon tuna sashimi sushi raw fish parasite freezing sailbrand farmed wild halibut trout"}
];

// Per-SOP body summaries (2026-04-30 lift from JJ's Drive folder
// `1eNwqTqManzrzzzVranTo7HWR35O68jE-`). Each entry is a tight
// 4-paragraph summary in Rolled's voice — NOT a verbatim regurgitation
// of the source PDF. Full authoritative PDF lives in the FSMS Drive
// folder; cmpOpenSop falls back to a generic stub if a SOP is missing
// from this map. v2 entries (SOP-11/13/16/17) reflect the v2 PDFs Adam
// signed off on 2026-04-19 with Sheffield EHO Kirstie's input.
const CMP_SOP_BODIES = {
  "SOP-01": [
    "Food allergies and intolerances are immune-system or digestive responses to specific foods. ~1–2% of UK adults and ~5–8% of children are affected. A severe allergic reaction (anaphylactic shock) can be fatal within minutes — staff must take every allergen request seriously and never guess about ingredients.",
    "The 14 declared allergens (Natasha's Law / FIC Regulations) must be communicated accurately for every menu item: cereals containing gluten, crustaceans, eggs, fish, peanuts, soybeans, milk, tree nuts, celery, mustard, sesame, sulphites, lupin, molluscs.",
    "When a customer declares an allergy: stop, ask which allergen, check the ingredient master + supplier specs, prepare in a cleaned workstation with dedicated colour-coded equipment, and double-check the finished item against the label before handing over. If unsure, refuse the order.",
    "Every kiosk holds an allergen matrix on Drive (cross-referenced from the in-app Allergen Matrix tile). All FOH + BOH staff complete Allergen Awareness training within 30 days of hire and refresh every 3 years."
  ],
  "SOP-02": [
    "Cleaning and disinfection prevent cross-contamination — the single biggest driver of foodborne illness in catering. Two distinct steps: cleaning removes visible soil with detergent; disinfection kills the bacteria that remain.",
    "Method matrix: work surfaces use detergent + disinfectant; pans, knives, mixing bowls, utensils run through the mechanical dishwasher; chopping boards rotate by colour code (see SOP 3); fridges, freezers and the rolling/rice machines follow the manufacturer's clean-in-place instructions in the Cleaning Schedule.",
    "Sanitiser must hit BS EN 1276 / BS EN 13697 standard at the dilution and contact time on the bottle. Staff dilute on shift, never store pre-mix overnight. Surfaces are cleaned BEFORE service, AFTER each task, and at end of day.",
    "Sign-off: every cell on the 14-task Cleaning Schedule is initialled the same shift it's done. The Sunday weekly Manager Sign Off is mandatory — any signed-in PIN attests, but the sign-off is the legal record that the week's matrix is complete (FSMS §7.1)."
  ],
  "SOP-2a": [
    "Blast chillers cool cooked food rapidly through the +5°C–+63°C danger zone where pathogenic bacteria multiply fastest. Rapid cooling is one of the highest-leverage controls in the kitchen — a slow cool can re-introduce risk into food that's already been cooked safely.",
    "Procedure: switch the chiller on in advance to pre-cool the chamber. Begin chilling within 30 minutes of cook completion. Portion bulk foods so cool air can circulate. Probe the core temperature at the start and end of every cycle.",
    "Critical limit: cooked food must reach +5°C or below within 90 minutes of cook completion. If a cycle exits >+5°C, extend the run or transfer to refrigeration, log the deviation as a Corrective Action, and probe again before storage.",
    "Records: blast cycles are logged on the Daily HACCP form (delivery/cooking/cooling section) with start time, end time, food item, core temp, and signing initials."
  ],
  "SOP-03": [
    "Colour-coded equipment prevents cross-contamination between food groups. The simpler the system the easier it is to monitor — Rolled's chopping boards, knives, food storage containers and cleaning cloths follow one consistent code across all kiosks.",
    "Standard code: red = raw meat, blue = raw fish, yellow = cooked meats, green = salad / fruit / veg, white = bakery / dairy, brown = root vegetables. Posters are displayed in every prep area for quick reference.",
    "Boards must be in good condition — deeply scored or split boards are replaced immediately because grooves harbour bacteria. Damaged equipment is logged on the Daily HACCP Records and a replacement requisitioned.",
    "Cleaning and storage: each colour is washed and disinfected separately (no mixed-colour batches in the dishwasher). Boards air-dry on dedicated racks colour-segregated to prevent contact between raw and ready-to-eat surfaces."
  ],
  "SOP-04": [
    "Cook-chill and cook-freeze let us prepare large volumes ahead of service. Cook-chill = cook → blast-chill → store cold → reheat to order. Cook-freeze = cook → blast-chill → portion → freeze → defrost + reheat to order.",
    "Time/temperature parameters at every stage are critical control points. Cook to ≥75°C core. Cool to ≤+5°C within 90 minutes (cook-chill) or freeze to ≤−18°C (cook-freeze). Reheat to ≥75°C core for ≥30 seconds before service.",
    "Date-label every product with cook date + use-by. Cook-chill shelf life is 5 days inclusive of cook day at ≤+5°C. Cook-freeze shelf life is governed by the food spec (typically 1–3 months at ≤−18°C).",
    "Every batch is logged on the Daily HACCP Records with cook time, blast/freeze cycle times, core temps, and a final reheat probe at service. A failed cycle goes straight to a Corrective Action — that batch is not served."
  ],
  "SOP-06": [
    "Pathogenic bacteria can't grow above +63°C or below +5°C. Between those limits is the Danger Zone — rapid bacterial multiplication and spore germination. Cooling food correctly is one of the most common causes of foodborne illness when done badly.",
    "Preferred method is a blast chiller within 30 minutes of cook completion (see SOP 2a). At ambient: cool to <+20°C within 90 minutes, then refrigerate immediately. Speed cooling by reducing portion size, using ice/water baths, or pre-chilled shallow trays.",
    "Cool in clean disinfected trays in the coolest part of the kitchen, covered with greaseproof. Never stack hot containers — they'll insulate each other and trap heat. Never put still-warm food in a sealed fridge — it raises the ambient and risks the rest of the load.",
    "All cooling cycles get logged on the Daily HACCP Records. A failed cycle is a Corrective Action: extend cooling, transfer to a faster cool method, or discard the batch if the danger zone exceeded 4 hours."
  ],
  "SOP-07": [
    "Freezing doesn't kill pathogenic bacteria — it just stops them growing. As food warms during defrosting, bacteria reactivate and multiply, especially in the surface layers. Bad defrosting is a major cause of foodborne illness.",
    "Always defrost in a refrigerator (≤+5°C), placed BELOW high-risk / ready-to-eat foods to prevent drip cross-contamination. Remove outer packaging before defrost. Spread items in a single layer in clean trays — never top-up with new product mid-defrost.",
    "Critical limit: food is fully defrosted only when no ice crystals remain in the core (probe-check). Use within 24 hours of full defrost, never re-freeze. Tag every batch with defrost-start date so the next user can verify.",
    "Microwave or running cold water defrosting is permitted only when the food will be cooked or used immediately — these methods raise the surface temperature into the danger zone briefly and need close monitoring."
  ],
  "SOP-08": [
    "Goods receipt is the first critical control point in the chain. Bad deliveries (warm, damaged, expired, contaminated, or from an unapproved supplier) propagate risk into everything downstream. Inspection at the door is non-negotiable.",
    "Check on every delivery: vehicle is clean and segregated (chilled / frozen / ambient kept separate); packaging is intact; product is within use-by; chilled items are ≤+8°C (preferably ≤+5°C); frozen items are ≤−15°C; fish is between 0°C and +3°C. Probe-check one chilled and one frozen item per supplier.",
    "Reject any delivery that fails: damaged packaging, signs of pest activity, broken seals, expired product, or temperature failure. Reject quietly and professionally — the supplier's job is to deliver compliant product, not yours to rescue it.",
    "Every delivery is logged on the Daily HACCP Records (CCP-1) with arrival time, supplier, probe readings, and signing initials. Spec-checked supplier list lives in the Suppliers tile + the Drive folder. Periodic supplier audits document due-diligence."
  ],
  "SOP-10": [
    "Food handlers carrying infections — especially gastro-intestinal — can contaminate food and cause widespread illness. Reg (EC) 852/2004 Annex II Chapter VIII is the legal basis: any handler with a notifiable disease must NOT work with or touch food.",
    "Symptoms requiring immediate exclusion from food handling: diarrhoea, vomiting, infected skin lesions, weeping eczema, sore throat with fever, jaundice, or any infection that could be transmitted via food. Report to a manager BEFORE starting your shift.",
    "Return-to-work rule: 48 hours symptom-free for diarrhoea/vomiting before re-entering the kitchen. Other infections need GP clearance. Manager keeps a Fitness-to-Work record on file (privacy-preserving — symptoms only, no diagnoses).",
    "Cuts and grazes are covered with a blue waterproof dressing + disposable glove. Long-term skin conditions (eczema, dermatitis) need a manager risk-assessment and may require redeployment to a non-food role until cleared."
  ],
  "SOP-11": [
    "v2 (issued 19 April 2026) — references to an external pest contractor removed; Atlantic salmon and tuna referenced explicitly in raw-fish handling sections. Approved by Adam Miller, Director.",
    "Three contamination types to control: microbiological (pathogens — bacteria, viruses, parasites, mould), chemical (cleaning chemicals, allergens, pesticide residue), physical (glass, hair, jewellery, packaging fragments). Every step in the kitchen is a potential introduction point — identify, prevent, monitor.",
    "Microbiological controls: hand-washing (SOP 16), colour-coded equipment (SOP 3), separation of raw and ready-to-eat (especially raw salmon and tuna from rolled product), temperature control across CCP-1 / CCP-3 / CCP-5 / CCP-6, and the Sanitiser Schedule.",
    "Chemical and physical controls: COSHH-managed chemicals stored away from food; allergen segregation per SOP 1; brittle plastic or glass items removed where possible (SOP 24); jewellery and false nails banned from food prep; report any breakage on the Daily HACCP Records as a Corrective Action."
  ],
  "SOP-12": [
    "Food Labelling Regulations 1996 (as amended) plus Trading Standards / Consumer Protection laws. Restaurants are exempt from many provisions but still have core labelling duties — accurate descriptions, allergen disclosure, country of origin where claimed.",
    "Pre-packed for direct sale (PPDS / Natasha's Law): all 14 allergens highlighted in bold inside the ingredient list on the wrapper. Applies to every roll, miso cup, snack, and any item packed in the kiosk before being sold.",
    "Date labelling: 'Use By' for high-risk perishable foods (microbiological safety driver); 'Best Before' for ambient-stable foods (quality driver). Sushi rolls + cooked rice carry Use By dates calculated from cook + acidification time.",
    "In-house batch labels capture cook date, use-by, allergens, batch number, and the staff initial. Labels are printed via the in-kiosk label printer; spent labels go in the waste, not back in the box. Batch records are retained for 90 days for traceability."
  ],
  "SOP-13": [
    "v2 (issued 19 April 2026) — Section 13.3 added covering the customer Display Fridge failure addendum (2-hourly monitoring, +8°C threshold, 4-hour rule, Do Not Use sign, mandatory Corrective Action). Agreed with Sheffield City Council EHO Kirsty on 2 April 2026. Approved by Adam Miller, Director.",
    "Refrigerator breakdown: probe representative samples from top, middle and bottom shelves. Decision tree by core food temperature and time discovered: ≤+8°C is safe to relocate to a working unit; +8°C–+15°C may be sold same day if used within 4 hours of failure; >+15°C or >4 hours = discard.",
    "Freezer breakdown: ice crystals still present + product feels firm = transfer to working freezer. Soft / partially defrosted = treat as defrosted (see SOP 7), use or cook within 24 hours. Fully defrosted with no ice = discard or, if probed ≤+5°C, transfer to refrigerator and use within 24 hours.",
    "Customer Display Fridge addendum: 2-hourly probe checks while a deviation is open; if temp >+8°C for any 2-hour check, raise an Emergency Fridge Incident in the Compliance app, mark Do Not Use, and apply the 4-hour rule. Disposal beyond 4 hours requires admin sign-off in the in-app workflow."
  ],
  "SOP-14": [
    "Shelf life dates and storage conditions are the backbone of food safety in storage. Every item carries a max refrigerated shelf life calculated from the day of production (inclusive). Get this wrong and you risk serving food past its safe window.",
    "Prep-kitchen high-risk items: handheld rolls — same day; cooked sushi rice — same day after acidification (see SOP 29); cooked proteins — 3 days at ≤+5°C; cooked vegetables — 3 days at ≤+5°C. Date-label every container the moment it enters storage.",
    "Raw items: defrosted fish — 24 hours at 0°C–+3°C; raw vegetables — 5–7 days per supplier spec at +1°C–+5°C; opened sauces and pickles — per manufacturer label. Closed packaging follows the printed Use By / Best Before unaltered.",
    "Storage hygiene: raw on the bottom shelf, ready-to-eat on top, allergen-segregated. FIFO (First In First Out) on every shelf. Weekly stock rotation check. Anything missing a date label = discard immediately, don't guess."
  ],
  "SOP-15": [
    "Contaminated ice can carry foodborne pathogens straight into a customer's drink. Hazards: microbiological (bacteria from dirty machines or hands), foreign body (chips, scoops left inside), non-potable water (failed mains supply), chemical (cleaning residue).",
    "Ice machines must sit in a clean, well-maintained food room with proper drainage. Position away from hot equipment, food prep zones, and waste. Use a dedicated scoop stored OUTSIDE the machine — never leave the scoop sitting in the ice.",
    "Cleaning: all accessible parts (seals, hinges, dispensing chute) cleaned and disinfected on the weekly Cleaning Schedule. Quarterly deep clean strips the unit for descaling. Visible mould or biofilm = stop using, deep clean, log a Corrective Action.",
    "Service: scoop ice, never pour from a cup. Don't store anything else inside the machine (bottles, food). If a glass breaks near the ice, the entire batch is discarded, the chamber is sanitised, and the event is logged on Daily HACCP Records."
  ],
  "SOP-16": [
    "v2 (issued 19 April 2026) — hand-washing rules now explicitly include 'after handling or using raw fish (salmon, tuna)'. Approved by Adam Miller, Director.",
    "Personal hygiene is the single highest-leverage food safety control. A smartly dressed, hygiene-conscious team signals professionalism to customers and protects against foodborne illness from cross-contamination.",
    "Hand-wash with soap and hot running water for ≥20 seconds: on arrival, after each break, after using the toilet, after handling raw food (especially raw salmon and tuna), after touching face / hair / waste / cleaning chemicals, and between any task switch. Drying with paper towel only — no shared cloth.",
    "Uniform: clean apron over a Rolled tee, hair tied back / under a cap, no jewellery except a plain wedding band, no false nails or nail polish, no perfume that transfers to food. Cuts covered with a blue waterproof plaster + disposable glove. Report illness BEFORE starting (see SOP 10)."
  ],
  "SOP-17": [
    "v2 (issued 19 April 2026) — sections rewritten to reflect Rolled Meadowhall's internal pest control model: Daily Pest Control Checklist + Weekly Pest Control Checklist, no external contractor. Agreed with Sheffield City Council EHO Kirstie on 2 April 2026. Approved by Adam Miller, Director.",
    "A pest is any unwanted animal, insect or bird that contaminates food or whose presence is undesirable. Common in a sushi kiosk: rats, mice, pigeons, cockroaches, flies, stored-product insects (weevils, beetles, mites). Pests carry both microbiological and physical hazards.",
    "Detection is the daily Pest Control Checklist (in-app form): every staff member checks droppings, gnawed packaging, nests, smells, fly activity, sightings — by zone. The Monday Deep Sweep adds 5 deeper checks (under counters, drains, stock storage edges). Any evidence auto-raises a Corrective Action.",
    "Prevention: keep external doors closed; seal entry points; store bins lidded and away from external walls; rotate and inspect dry stock; inspect deliveries on arrival; clean immediately after spills. An infestation found by an EHO can lead to closure and prosecution — daily diligence is the only safeguard."
  ],
  "SOP-18": [
    "Microwave ovens reheat food fast but they heat unevenly — cold spots in the food can leave bacteria viable even when the surface looks hot. Used wrong, a microwave is a cross-contamination risk dressed up as a quick reheat.",
    "Use only commercial-rated microwaves. Do not microwave food in metal or non-microwave-safe containers. Stir or rotate halfway through every cycle. Cover with a microwave-safe lid to retain steam and even heating.",
    "Critical limit: reheat to a core temperature of ≥75°C for ≥30 seconds. Probe the centre AND the thinnest portion. If either is below target, run an extra cycle and re-probe. Never serve until both readings clear the limit.",
    "Records: every reheat cycle logged on the Daily HACCP Records with food item, reheat duration, two-point probe readings, and signing initials. Microwaves are cleaned and disinfected daily — splatter is a contamination + smell vector."
  ],
  "SOP-19": [
    "Foodborne illness (e.g. dysentery) can transmit via contaminated leaves and vegetables. Anything served raw — salads, cucumber, tomato, fruit garnish — must be thoroughly washed to remove soil, pesticide residue, parasites and pathogens.",
    "Method: separate leaves from the head before washing (lettuce, celery, endive). Wash whole leaves in clean potable water. Disinfect with food-safe sanitiser per the manufacturer's contact-time. Rinse thoroughly with potable water. Drain and store in a clean container at ≤+5°C.",
    "Whole salad veg (tomato, cucumber, peppers): wash and disinfect on the outer surface even when the skin will be peeled — the knife transfers contamination through the cut. Same rule for fruit garnishes that touch the finished plate.",
    "Wash zone is dedicated and colour-coded (green) to prevent cross-contamination from raw fish or meat prep. Wash sink is cleaned before each session. Records: washing tasks appear on the daily Cleaning Schedule with time and signing initials."
  ],
  "SOP-21": [
    "Food waste is a major pest attractor and a source of cross-contamination. Raw scraps (fish trim, vegetable peel, meat) and decaying matter spread bacteria fast — bins are never an afterthought. Internal storage and external collection both need disciplined controls.",
    "Internal: 'clean as you go', wash hands after every waste handling, lidded foot-pedal bins line every prep zone. Bin liners are doubled, knotted before transfer, and never overfilled. Bins are emptied at task changeover and at end of service.",
    "External: skips and bins live in a designated waste area away from food deliveries and the customer entrance. Lids stay closed. Waste contractor empties on a fixed schedule — failure to collect = log a Corrective Action and escalate to operations.",
    "Allergen waste (peanuts, sesame, etc.) goes in a separate bag colour to prevent cross-contact during disposal. Glass and crockery break-glass goes in a dedicated rigid container, never in the food-waste stream (see SOP 24)."
  ],
  "SOP-22": [
    "Cold display units hold high-risk ready-to-eat product (cooked rolls, miso prep, garnishes) in front of the customer. They are CCP-6 chilled storage with a higher-risk profile because the unit door opens repeatedly and ambient air rushes in.",
    "Critical limit: display ≤+5°C at all times. Probe-check at start of service, mid-shift, and pre-close — three readings minimum, all logged on the Daily HACCP Records. Use a sanitised probe between food types to prevent cross-contamination.",
    "Pre-service: clean and disinfect the unit, switch on early to pre-chill, verify the unit holds temp empty before loading. Don't overfill — air flow is what keeps the temperature even, and obstructed flow creates warm pockets.",
    "Service: rotate stock so the oldest goes out first; date-label the day's batch; keep portions covered when not on the front line; remove anything past the day's use-by. Display stock is NEVER carried over to the next day."
  ],
  "SOP-24": [
    "Glass and crockery breakages can lead to foreign body contamination — the worst kind because shards can injure a customer and the legal exposure is severe. Reactions to glass-in-food range from refunds to prosecution.",
    "Safe handling: carry one item at a time; never stack damaged crockery; check rims for chips before each service; rotate to remove worn-out items. Dishwasher loading is single-layer — overcrowding chips edges and breaks handles.",
    "If a breakage happens in or near food: stop service in the affected zone, isolate ALL exposed product, and discard generously. Inspect surrounding food and surfaces for shards. Sanitise and re-probe. Resume only when zone is verified clear.",
    "Every breakage is logged on the Daily HACCP Records (item, location, action taken, signing initials). The log is reviewed monthly for patterns — a recurring breakage in the same zone signals an equipment, training or layout issue."
  ],
  "SOP-25": [
    "Local Authority Environmental Health Officers (EHOs) and Trading Standards Officers regulate every Rolled kiosk. They have right of unannounced entry at any reasonable time, can seize or sample foods, require improvements, and prohibit equipment use.",
    "Approach: be polite, helpful, truthful. Never obstruct or argue. Ask for ID at the door, log the visit (officer name, authority, arrival time), notify the on-call manager immediately, and accompany the officer through the kiosk.",
    "What they'll ask to see: HACCP records (Daily HACCP Records, Pest Log, Cleaning Schedule, Probe Calibration, Fish Freezing log, Corrective Actions), Allergen Matrix, supplier specs and certs, fitness-to-work records, training records, temperature monitoring history.",
    "After the visit: officer writes a report — accept any improvement notice in writing, log the date and required actions, and return the report to the manager + Director (Adam) the same day. Always follow up with action evidence within the deadline."
  ],
  "SOP-26": [
    "The Suzumo SVR-NXA-CE is a semi-automatic norimaki rolling machine — agitating, transferring, rice-sheet cutting and forming mechanisms operate at speed and can be dangerous if used incorrectly. Only trained, authorised staff operate the machine.",
    "Pre-operation checks: confirm authorisation; visually inspect for damage, loose parts, foreign objects; check the rice-sheet former, conveyor belt and cutter housing are seated properly; clean and lubricate per the daily schedule.",
    "Operation: never reach into a moving cutter or former. Stop the machine before clearing jams. Guards stay in place during all running cycles. PPE = closed-toe non-slip shoes + clean apron. Long hair tied back, no loose sleeves or jewellery.",
    "Cleaning + maintenance: end-of-day strip-down per the manufacturer instructions. Sanitise rice-contact parts with food-safe sanitiser. Faults or unusual noise are logged on the Daily HACCP Records (equipment section) and the machine is taken out of service until cleared."
  ],
  "SOP-27": [
    "The Suzumo MCR-UNC (Sharikka) is a cooked-rice mixer for sushi rice (shari) — blends rice with vinegar concentrate via a powered agitator and drum. Max capacity 15 kg per batch; mixing cycle 3–5 minutes. Produces uniform shari at scale; misuse damages rice or causes injury.",
    "Pre-operation: verify training authorisation; visually inspect the mixing drum, drum lid, agitator, sensors; confirm the unit is clean and dry; check the drum is fully seated and the lid latch engages.",
    "Operation: load rice within capacity (no overfilling — agitator stalls and damages the motor); add vinegar concentrate per SOP 29; close the lid before starting; let the cycle complete before opening. Never reach in mid-cycle.",
    "Cleaning + maintenance: end-of-day strip-down per the manufacturer instructions. Drum, agitator and lid go through the dishwasher or hand-clean with food-safe sanitiser. Faults logged + machine taken out of service until repaired (see SOP 13 for fridge breakdown — same disciplined log)."
  ],
  "SOP-28": [
    "The Suzumo MCR-SSC (Sharikka Compact) is the compact variant for the same shari preparation — drum capacity 6.5 kg, mixing cycle ~3 minutes, designed for tighter prep stations. Same operating principles as SOP 27 with smaller-capacity safeguards.",
    "Pre-operation checks: same as SOP 27 — training, inspection, clean / dry / seated drum, working sensors, latched lid.",
    "Operation: respect the 6.5 kg cap — overfilling is the most common failure mode on this unit. Cycle runs ~3 minutes; let it complete and don't open mid-cycle. Vinegar concentrate per SOP 29.",
    "Cleaning + maintenance: identical to SOP 27 — end-of-day strip-down, dishwasher or hand sanitise, faults logged on Daily HACCP Records and the unit taken out of service until repaired."
  ],
  "SOP-29": [
    "Sushi rice acidification is a Critical Control Point (CCP-2). Cooked rice is high-risk — it supports rapid growth of Bacillus cereus and other pathogens at ambient temperature. Acidifying the rice to pH ≤ 4.3 inhibits growth and lets us hold rice ambient for service.",
    "Vinegar concentrate recipe (per FSMS): rice vinegar, sugar, salt — measured to spec, mixed cold, stored in a labelled food-safe container at ≤+5°C until use. Concentrate is prepared fresh weekly and date-labelled.",
    "Per batch: cook rice → mix with concentrate using SOP 27 / 28 → probe pH using a calibrated pH meter (see Probe Calibration in the Compliance app). Critical limit: pH ≤ 4.3. If a batch reads > 4.3, add concentrate, re-mix, re-test. If still > 4.3, discard and start again.",
    "Records: every batch logged on the Daily HACCP Records (rice/pH section) with batch ID, time, pH reading, signing initials. Ambient hold time after acidification is ≤4 hours per the FSMS — anything older is rolled into a sealed cold container and used or discarded per shelf-life."
  ],
  "SOP-30": [
    "Fish parasites (Anisakis larvae and others) are present in certain wild fish — salmon, herring, cod, monkfish among others. Parasites can transfer to humans when fish is eaten raw or undercooked. Freezing is the legally recognised parasite kill step before raw use.",
    "Critical limit: −20°C for ≥24 hours OR −35°C for ≥15 hours, measured at the fish core. The kitchen freezer must reach and hold the target temperature throughout the cycle. Probe the core at start, mid-cycle and end.",
    "Sailbrand farmed exemption: salmon and tuna sourced from approved Sailbrand farmed operations are exempt from the freezing requirement under their certified parasite-control programme. Documentation lives in the Suppliers tile + Drive folder. Wild or non-Sailbrand fish always freezes.",
    "Records: every intake logged in the Fish Freezing form in the Compliance app with batch ID, species, supplier, freezer in/out times, core temps and pass/fail. A failed cycle (warmer than −20°C or shorter than 24h) auto-raises a Corrective Action and the batch is held until re-frozen and re-tested."
  ]
};
const CMP_RISK_ASSESSMENTS = [
  "RA-01 Slips, Trips & Falls","RA-02 Manual Handling","RA-03 Chemical Exposure (COSHH)",
  "RA-04 Knife Use","RA-05 Hot Oil / Deep Fryer","RA-06 Fridge & Freezer Access",
  "RA-07 Electricity (Plug-in Appliances)","RA-08 Rice Cooker Steam & Burns","RA-09 Food Processor / Slicer",
  "RA-10 Waste Handling","RA-11 Lone Working (Close Down)","RA-12 Customer Interaction / Aggression",
  "RA-13 Allergen Exposure","RA-14 Fire","RA-15 First Aid / Injury Response",
  "RA-16 Pregnant & Nursing Workers","RA-17 Young Workers","RA-18 Working at Height (Shelving)",
  "RA-19 Display Fridge Glass","RA-20 Mobile Catering (Delivery)"
];
const CMP_SSOW = [
  "SSOW-01 Rice Cooker Operation","SSOW-02 Slicer Cleaning","SSOW-03 Fryer Oil Change",
  "SSOW-04 Knife Sharpening","SSOW-05 Fridge Coil Cleaning","SSOW-06 Bar Display Changeover",
  "SSOW-07 Walk-in Freezer Entry","SSOW-08 Deep Fat Cleaning","SSOW-09 Chemical Dilution (Cleaning)",
  "SSOW-10 End of Day Lock-up","SSOW-11 Customer Complaint Handling","SSOW-12 Allergen Cross-check",
  "SSOW-13 Incident Reporting","SSOW-14 Probe Sanitising","SSOW-15 Pest Sighting Response",
  "SSOW-16 Emergency Fridge Failure","SSOW-17 Fire Evacuation","SSOW-18 First Aid on Scene",
  "SSOW-19 Bin Area Clean-down","SSOW-20 Uniform & PPE"
];
const CMP_SUPPLIERS = [
  {name:"Sailbrand", cat:"Fish (raw salmon, raw tuna)", cert:"Parasite freezing policy on file (FS SOP 30)",
   docTitle:"Sailbrand · Parasite Freezing Policy",
   docMeta:"Cross-linked to FS SOP 30 · last verified 2026-04-25",
   docBody:[
     "Sailbrand confirms all Atlantic salmon, Atlantic halibut and rainbow trout supplied from approved farmed sources are exempt from on-site freezing per FS SOP 30 / EU Regulation 853/2004.",
     "All wild-caught fish supplied for raw or under-treated use is delivered already frozen at −20°C ≥ 24h or −35°C ≥ 15h, with batch-level evidence retained for 12 months.",
     "Each delivery includes a batch traceability sheet matching the Rolled Fish Freezing Log entry. Discrepancies route to the EHO contact within 24h."
   ]},
  {name:"Urban Foodservices", cat:"Foodservice", cert:"BRCGS Grade AA · expiry 2027-02-18",
   docTitle:"Urban Foodservices · BRCGS Audit Cert",
   docMeta:"BRCGS Grade AA · expiry 2027-02-18",
   docBody:[
     "Urban Foodservices is BRCGS Global Standard for Food Safety certified at Grade AA, audit valid through 2027-02-18.",
     "Cold-chain delivery temps logged at vehicle level; receipt on Rolled side captured via Goods Receipt CCP-1.",
     "Allergen statements per SKU available on request — used to populate the Rolled allergen matrix."
   ]},
  {name:"Green Giggles", cat:"Produce", cert:"BRCGS Grade A · expiry 2026-11-04",
   docTitle:"Green Giggles · BRCGS + Produce Spec",
   docMeta:"BRCGS Grade A · expiry 2026-11-04",
   docBody:[
     "Green Giggles is BRCGS Global Standard for Food Safety certified at Grade A, audit valid through 2026-11-04.",
     "Pesticide residue testing on rotation per crop type; batch-level results filed on supplier portal."
   ]}
];
const CMP_LEGAL = [
  {title:"Public Liability Insurance", type:"Insurance",     issued:"2025-09-01", expires:"2026-09-01"},
  {title:"Halal Certification",        type:"Certification", issued:"2025-05-15", expires:"2026-05-15"}
];
const CMP_REFERENCE = [
  {title:"Cleaning Method Chart", meta:"v3 · Oct 2025"},
  {title:"Shelf Life Chart",      meta:"v2 · Sep 2025"}
];
const CMP_CERT_LABELS = {
  "L2-food-safety": "Level 2 Food Safety",
  "L3-food-safety": "Level 3 Food Safety",
  "h-and-s":        "Health & Safety"
};
// FSMS Appendix B summary (read-only). Full document lives in Drive.
const CMP_FSMS_SUMMARY = {
  title: "Food Safety Management System — Rolled",
  meta:  "v2.1 · 19 Apr 2026 · Adam Miller",
  body: [
    "The Rolled Food Safety Management System is the single source of truth for all CCPs, prerequisites, supplier approval, and corrective-action workflow across Trafford, Arndale, Meadowhall and the Manchester central kitchen.",
    "CCP-1 Goods Receipt: chilled ≤8°C, frozen ≤−15°C, fish 0 to +3°C. Probe one chilled + one frozen item per supplier per delivery.",
    "CCP-2 Sushi Rice Acidification: every batch logged individually; pH ≤ 4.3 to clear ambient hold. SOP 29.",
    "CCP-3 Rice Cooling: 4-hour ambient-hold rule tracked on physical tub labels (no digital tracker — see SOP 29).",
    "CCP-4 Fish Freezing: −20°C ≥ 24h or −35°C ≥ 15h before raw use. SOP 30. Sailbrand exemption on file for approved farmed sources.",
    "CCP-5 Cooking: core 75°C for 30 seconds. Probe first + last item per cook batch.",
    "CCP-6 Chilled Storage: fridge ≤+5°C, freezer ≤−18°C, fish freezer ≤−20°C. AM + PM probe readings logged on the Daily HACCP form.",
    "Pest control: daily visual + Monday Deep Sweep (5 zones). Evidence auto-raises a Corrective Action.",
    "Cleaning Schedule: 14 daily/weekly tasks per kitchen; Sunday weekly Manager Sign Off any signed-in PIN attests.",
    "Emergency Fridge: 2-hourly monitoring while a deviation is open; 4-hour rule for stock disposal (Admin sign-off).",
    "Declaration of Competency: BOH + FOH variants; annual refresh; manager countersign per FSMS Appendix D."
  ]
};

// Single PDF / doc viewer overlay. Components anywhere call openPdf(doc).
let _cmpOpenPdfFn = null;
function cmpOpenPdf(doc) { if (_cmpOpenPdfFn) _cmpOpenPdfFn(doc); }
function CmpPdfLayer() {
  const [doc, setDoc] = useState(null);
  useEffect(() => { _cmpOpenPdfFn = setDoc; return () => { _cmpOpenPdfFn = null; }; }, []);
  if (!doc) return null;
  return (
    <div className="cmp-pdf-scrim" onClick={() => setDoc(null)}>
      <div className="cmp-pdf-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-pdf-head">
          <div style={{flex:1,minWidth:0}}>
            <h3>{doc.title}</h3>
            {doc.meta && <div className="cmp-pdf-meta">{doc.meta}</div>}
          </div>
          <button className="cmp-pdf-close" onClick={() => setDoc(null)}>✕</button>
        </div>
        <div className="cmp-pdf-body">
          {(doc.body || []).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    </div>
  );
}

// Search index over docs + form sub-tiles. Wave 6 follow-up
// (2026-05-01): rebuilt on every query via cmpGetSops() / cmpGetSuppliers()
// / cmpGetLegal() so admin renames + new entries appear in search without
// a tablet reload. Form tiles are static (route names don't change).
function cmpBuildSearchIndex() {
  const idx = [];
  cmpGetSops().forEach(s => idx.push({cat:"SOP", title:s.title, meta:s.id + " · " + s.version, keywords:s.keywords||"", route:"hub-sops"}));
  idx.push({cat:"FSMS", title:CMP_FSMS_SUMMARY.title, meta:CMP_FSMS_SUMMARY.meta, keywords:"fsms appendix policy", route:"hub-fsms"});
  CMP_RISK_ASSESSMENTS.forEach(r => idx.push({cat:"Risk", title:r, meta:"", route:"hub-risk"}));
  CMP_SSOW.forEach(r => idx.push({cat:"SSoW", title:r, meta:"", route:"hub-ssow"}));
  cmpGetSuppliers().forEach(s => idx.push({cat:"Supplier", title:(s.label||s.name||""), meta:(s.cat||"") + " · " + (s.cert||""), keywords:(s.cat||"")+" "+(s.cert||""), route:"hub-suppliers"}));
  cmpGetLegal().forEach(l => idx.push({cat:"Legal", title:(l.label||l.title||""), meta:(l.type||"") + " · expires " + cmpFriendly(l.expires), route:"hub-legal"}));
  CMP_REFERENCE.forEach(r => idx.push({cat:"Reference", title:r.title, meta:r.meta, route:"hub-reference"}));
  const formTiles = [
    {title:"Daily HACCP", meta:"Opening · CCP-6 · closing", keywords:"haccp opening closing daily delivery cooling cook fridge freezer probe ph rice ccp", route:"haccp"},
    {title:"Probe Calibration", meta:"Weekly + on-drop", keywords:"probe calibration thermometer ice boiling weekly", route:"probe"},
    {title:"Pest Daily Check", meta:"Per-area visual check", keywords:"pest mice rats droppings rodents insects flies", route:"pest"},
    {title:"Pest Monday Deep Sweep", meta:"Weekly · 5 deeper checks", keywords:"pest monday sweep weekly", route:"pestmonday"},
    {title:"Fish Freezing", meta:"CCP-4 parasite control", keywords:"fish freezing salmon tuna sashimi parasite ccp4 sailbrand", route:"fish"},
    {title:"Emergency Fridge", meta:"Incident-driven", keywords:"emergency fridge breakdown incident temperature failure", route:"emergency"},
    {title:"Cleaning Schedule", meta:"Weekly cadence", keywords:"cleaning schedule disinfect chemical ppe weekly", route:"cleaning"},
    {title:"Declaration of Competency", meta:"Per-staff sign-off", keywords:"declaration competency food safety boh foh sign-off fsms acceptance", route:"declaration"},
    {title:"Corrective Actions", meta:"Open & resolved stream", keywords:"corrective action concern incident resolve", route:"corrective"}
  ];
  formTiles.forEach(t => idx.push({cat:"Form", title:t.title, meta:t.meta, keywords:t.keywords, route:t.route}));
  return idx;
}

function CmpSearchBar({nav}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return cmpBuildSearchIndex()
      .filter(r => r.title.toLowerCase().includes(needle) || (r.meta||"").toLowerCase().includes(needle) || r.cat.toLowerCase().includes(needle) || (r.keywords||"").toLowerCase().includes(needle))
      .slice(0, 14);
  }, [q]);
  return (
    <div className="cmp-search-bar" ref={ref}>
      <span className="cmp-search-icon">🔍</span>
      <input
        placeholder="Search documents — SOP, risk assessment, supplier, form…"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
      />
      {q && <button className="cmp-search-clear" onClick={() => { setQ(""); setOpen(false); }}>×</button>}
      {open && q && (
        <div className="cmp-search-results">
          {results.length === 0 ? (
            <div className="cmp-search-empty">No matches for "{q}"</div>
          ) : results.map((r, i) => (
            <div key={i} className="cmp-search-result" onClick={() => { setQ(""); setOpen(false); nav(r.route); }}>
              <span className="cmp-cat">{r.cat}</span>
              <div style={{flex:1,minWidth:0}}>
                <div className="cmp-result-title">{r.title}</div>
                {r.meta && <div className="cmp-result-meta">{r.meta}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Runtime SOP list — populated by CmpHubSops + CmpHome when they fetch
// the compliance config (Wave 6.6, 2026-04-30). When set, it overrides
// the hardcoded CMP_SOPS + CMP_SOP_BODIES constants. The hardcoded copy
// is the offline / surface-failure fallback, frozen at 2026-04-30. New
// SOPs and edits go via admin → ComplianceConfig sheet → here.
var cmpRuntimeSops = null;        // [{id, label/title, version, updated, keywords, body[]}]
var cmpRuntimeSopBodies = null;   // {id: [paragraph, ...]}
// Wave 6 follow-up (2026-05-01): same runtime-cache pattern for the
// suppliers + legal-docs surfaces, used by the search index.
var cmpRuntimeSuppliers = null;   // [{id, label, cat, cert, ...}]
var cmpRuntimeLegal = null;       // [{id, label, type, issued, expires, ...}]
function cmpSetRuntimeSuppliers(items) {
  if (Array.isArray(items) && items.length) cmpRuntimeSuppliers = items;
}
function cmpSetRuntimeLegal(items) {
  if (Array.isArray(items) && items.length) cmpRuntimeLegal = items;
}
function cmpGetSuppliers() {
  return cmpRuntimeSuppliers || CMP_SUPPLIERS.map(function(s){return Object.assign({},s,{label:s.name});});
}
function cmpGetLegal() {
  return cmpRuntimeLegal || CMP_LEGAL.map(function(l){return Object.assign({},l,{label:l.title});});
}

function cmpSetRuntimeSops(items) {
  if (!Array.isArray(items) || items.length === 0) return;
  cmpRuntimeSops = items.map(function(it) {
    return {
      id: it.id,
      title: it.label || it.title || "",
      version: it.version || "",
      updated: it.updated || "",
      keywords: it.keywords || "",
      body: Array.isArray(it.body) ? it.body : []
    };
  });
  cmpRuntimeSopBodies = {};
  cmpRuntimeSops.forEach(function(s) {
    if (s.body && s.body.length) cmpRuntimeSopBodies[s.id] = s.body;
  });
}
function cmpGetSops() { return cmpRuntimeSops || CMP_SOPS; }
function cmpGetSopBody(id) {
  if (cmpRuntimeSopBodies && cmpRuntimeSopBodies[id]) return cmpRuntimeSopBodies[id];
  return CMP_SOP_BODIES && CMP_SOP_BODIES[id];
}

// SOP popup body — reads metadata from the runtime SOP catalog (when
// loaded) or falls back to the hardcoded CMP_SOPS catalog. Body text
// comes from cmpGetSopBody which prefers the runtime/admin-edited copy.
function cmpOpenSop(sop) {
  // Re-resolve from the runtime catalog if available — admins may have
  // edited the title or version since this `sop` ref was captured.
  var live = cmpGetSops().find(function(s) { return s.id === sop.id; }) || sop;
  var body = cmpGetSopBody(live.id);
  if (!body || !body.length) {
    var titlePart = String(live.title || "").split("—")[1];
    var topic = titlePart ? titlePart.trim() : (live.title || live.id);
    body = [
      "This SOP defines the standard procedure for " + topic + " across all Rolled sites.",
      "Full PDF lives in the FSMS Drive folder. This in-app view shows metadata + cross-references.",
      live.keywords ? ("Keywords: " + live.keywords) : "Cross-referenced by the FSMS appendix and the Daily HACCP form."
    ];
  }
  cmpOpenPdf({
    title: live.title,
    meta: live.id + " · " + live.version + " · updated " + live.updated,
    body: body
  });
}

// =====================================================================
// Documentation hub sub-pages
// =====================================================================

function CmpHubSops({backend, site}) {
  const [sops, setSops] = useState(cmpGetSops());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "sops", site.key);
        if (items && items.length) {
          cmpSetRuntimeSops(items);
          setSops(cmpGetSops());
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>SOPs · Standard Operating Procedures</h3>
        <div className="cmp-sub">{sops.length} documents · FSMS Appendix A · all current</div>
      </div>
      <div className="cmp-doc-list">
        {sops.map(s => (
          <div key={s.id} className="cmp-doc-row" onClick={() => cmpOpenSop(s)}>
            <div className="cmp-doc-icon">📘</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{s.title}</div>
              <div className="cmp-doc-meta">{s.id} · {s.version} · updated {cmpFriendly(s.updated)}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubFsms() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>{CMP_FSMS_SUMMARY.title}</h3>
        <div className="cmp-sub">{CMP_FSMS_SUMMARY.meta}</div>
      </div>
      <div className="cmp-card">
        {CMP_FSMS_SUMMARY.body.map((p, i) => (
          <p key={i} style={{fontSize:13.5,lineHeight:1.6,color:"var(--cm-nori)",marginBottom:10}}>{p}</p>
        ))}
        <div style={{marginTop:14,padding:"10px 14px",background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",borderRadius:10,fontSize:12,color:"var(--cm-nori-70)"}}>
          Full PDF lives in the FSMS Drive folder. This view shows the executive summary; for SOPs see the SOPs tile, for cross-references see the relevant form.
        </div>
      </div>
    </div>
  );
}

function CmpHubRisk() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Risk Assessments</h3>
        <div className="cmp-sub">{CMP_RISK_ASSESSMENTS.length} on file · review annually</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_RISK_ASSESSMENTS.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r,meta:"Risk Assessment · review annually",body:["This risk assessment covers "+r.split(" ").slice(1).join(" ")+" hazards across the kitchen.","Hazards, control measures, residual risk, and review schedule live in the FSMS Risk Assessments folder."]})}>
            <div className="cmp-doc-icon">⚠️</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r}</div>
              <div className="cmp-doc-meta">Tap to view summary</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubSsow() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Safe Systems of Work</h3>
        <div className="cmp-sub">{CMP_SSOW.length} on file · Rolled-specific in authoring</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_SSOW.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r,meta:"Safe System of Work",body:["This SSoW describes the safe procedure for "+r.split(" ").slice(1).join(" ")+".","PPE, sequence, hazards, supervisor sign-off — full text in the SSoW folder."]})}>
            <div className="cmp-doc-icon">🛠️</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r}</div>
              <div className="cmp-doc-meta">Tap to view summary</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubSuppliers({backend, site}) {
  const [suppliers, setSuppliers] = useState(cmpGetSuppliers());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "suppliers", site.key);
        if (items && items.length) {
          cmpSetRuntimeSuppliers(items);
          setSuppliers(items);
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Approved Suppliers</h3>
        <div className="cmp-sub">{suppliers.length} on file · BRCGS or equivalent due-diligence required for first delivery</div>
      </div>
      <div className="cmp-doc-list">
        {suppliers.map((s, i) => (
          <div key={s.id || i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:s.docTitle, meta:s.docMeta, body: Array.isArray(s.body) ? s.body : (s.docBody || [])})}>
            <div className="cmp-doc-icon">🚚</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{s.label || s.name}</div>
              <div className="cmp-doc-meta">{s.cat} · {s.cert}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubLegal({backend, site}) {
  const [docs, setDocs] = useState(cmpGetLegal());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "legal-docs", site.key);
        if (items && items.length) {
          cmpSetRuntimeLegal(items);
          setDocs(items);
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Insurance & Halal</h3>
        <div className="cmp-sub">Legal & certification documents</div>
      </div>
      <div className="cmp-doc-list">
        {docs.map((l, i) => {
          const title = l.label || l.title || "";
          const days = Math.floor((new Date(l.expires) - new Date()) / (1000*60*60*24));
          const tone = days < 0 ? "bad" : days < 30 ? "warn" : "ok";
          // Body resolution order: surface-supplied body[] (Wave 6.7) →
          // hardcoded title-keyed fallback (legacy CMP_LEGAL) → empty.
          const fallbackBody = title.indexOf("Insurance") !== -1 ? [
            "Public Liability Insurance covers third-party injury or property damage at any Rolled trading location (Trafford, Arndale, Meadowhall, Manchester central kitchen).",
            "Limit of indemnity: £5,000,000. Underwriter: Hiscox. Policy reference held by JJ; auto-renewal flag in 1Password.",
            "Renewed annually. EHO and shopping-centre landlords typically request a copy at year-start; PDF available on the Drive in /Compliance/Legal/."
          ] : [
            "Halal Certification covers all proteins handled at Rolled — Atlantic salmon, tuna, chicken, prawns, eggplant, tofu — sourced through approved suppliers (Sailbrand, Urban Foodservices).",
            "Issuing body: Halal Monitoring Committee (HMC). Certificate number held by JJ on Drive.",
            "Annual renewal cycle. Customer-facing claim 'halal' should never appear on menus until renewal is verified."
          ];
          const body = (Array.isArray(l.body) && l.body.length) ? l.body : fallbackBody;
          return (
            <div key={l.id || i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:title, meta:l.type+" · issued "+cmpFriendly(l.issued)+" · expires "+cmpFriendly(l.expires), body: body})}>
              <div className="cmp-doc-icon">📄</div>
              <div className="cmp-doc-body">
                <div className="cmp-doc-name">{title}</div>
                <div className="cmp-doc-meta">{l.type} · issued {cmpFriendly(l.issued)} · expires {cmpFriendly(l.expires)}</div>
              </div>
              <div className="cmp-doc-right">
                <span className={"cmp-chip cmp-" + tone}><span className="cmp-dot" />{days < 0 ? "expired" : days + "d"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CmpHubReference() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Reference Charts</h3>
        <div className="cmp-sub">Quick-reference posters · printable</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_REFERENCE.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r.title,meta:r.meta,body:["Reference chart — printable poster format. Source PDF in Drive."]})}>
            <div className="cmp-doc-icon">📊</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r.title}</div>
              <div className="cmp-doc-meta">{r.meta}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Staff Certs — grouped by cert type (L2 / L3 Food Safety / H&S).
// Reads the admin-managed list from Supabase (src/lib/staffCerts.js); was the
// public Apps Script getStaffCerts POST until the 15 Jul cutover. Admin adds
// certs through the Command Centre → Staff Certs tile; the PDF lives in Drive
// (FileUrl is just a Drive link). No mockup data — empty state shows
// "No certs on file" until an admin adds some, which is the state today.
// CMP_CERT_LABELS already declared at module top — reused here.
function cmpCertDaysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (1000*60*60*24));
}
function cmpCertFriendly(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {day:"numeric", month:"short", year:"numeric"});
}
// Reads Supabase now (src/lib/staffCerts.js), not the Apps Script getStaffCerts POST.
// Same public exposure, same {certId,staff,cat,issued,expires,fileUrl,notes} shape, so
// everything below this line is untouched.
//
// The testMode split does NOT carry over: it selected the 'StaffCerts_TEST' sheet tab,
// and there is no _TEST table. Both tabs were empty (probed 15 Jul), so nothing is lost
// — but anything in QA that leaned on cmpIsTestMode() for certs now reads live data.
function CmpHubCerts() {
  const [certs, setCerts] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchStaffCerts()
      .then(setCerts)
      .catch(e => setError(e.message));
  }, []);
  const grouped = {};
  (certs || []).forEach(c => { (grouped[c.cat] = grouped[c.cat] || []).push(c); });
  const expected = ["L2-food-safety", "L3-food-safety", "h-and-s"];
  if (error) {
    return <div className="cmp-wrap"><div className="cmp-card cmp-coral"><h3>Couldn't load staff certs</h3><div style={{fontSize:13}}>{error}</div></div></div>;
  }
  if (certs === null) return <div className="cmp-loading">Loading staff certs…</div>;
  if (certs.length === 0) {
    return (
      <div className="cmp-wrap">
        <div className="cmp-card">
          <h3>Staff Certs</h3>
          <div className="cmp-sub">Grouped by cert type. Admin uploads via the Compliance Config tile.</div>
        </div>
        <div className="cmp-empty">No staff certs on file yet. Ask an admin to upload them.</div>
      </div>
    );
  }
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Staff Certs</h3>
        <div className="cmp-sub">Grouped by cert type. Admin manages uploads via the Compliance Config → Staff Certs tile.</div>
      </div>
      {expected.map(cat => {
        const rows = (grouped[cat] || []).slice().sort((a,b) => a.staff.localeCompare(b.staff));
        return (
          <div key={cat} className="cmp-card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h4 style={{margin:0}}>{CMP_CERT_LABELS[cat] || cat}</h4>
              <span className={"cmp-chip cmp-" + (rows.length === 0 ? "warn" : "ok")}>
                <span className="cmp-dot" />{rows.length} on file
              </span>
            </div>
            <div className="cmp-doc-list">
              {rows.length === 0 && (
                <div style={{fontSize:12,color:"var(--cm-nori-60)",fontStyle:"italic",padding:"6px 0"}}>None on file.</div>
              )}
              {rows.map(r => {
                const d = cmpCertDaysUntil(r.expires);
                const tone = d == null ? "ok" : (d < 0 ? "bad" : d < 30 ? "warn" : "ok");
                const expLabel = d == null ? "—" : (d < 0 ? "expired" : d < 30 ? d + "d left" : cmpCertFriendly(r.expires));
                const open = () => {
                  if (r.fileUrl) window.open(r.fileUrl, "_blank", "noopener");
                };
                return (
                  <div key={r.certId} className="cmp-doc-row" onClick={open} style={{cursor: r.fileUrl ? "pointer" : "default"}}>
                    <div className="cmp-doc-icon" style={{fontSize:13,fontWeight:800,fontFamily:"gelica,serif"}}>{r.staff.slice(0,2).toUpperCase()}</div>
                    <div className="cmp-doc-body">
                      <div className="cmp-doc-name">{r.staff}</div>
                      <div className="cmp-doc-meta">{r.issued ? "Issued " + cmpCertFriendly(r.issued) : "Issued —"}{r.fileUrl ? " · PDF on file" : " · no PDF link"}</div>
                    </div>
                    <div className="cmp-doc-right">
                      <span className={"cmp-chip cmp-" + tone}><span className="cmp-dot" />{expLabel}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CmpHome({nav, sundayTone, todayCounts, probesDue}) {
  // todayCounts: {haccpDone, haccpTotal, cleaningCellsToday, pestDoneToday, openIncidents, openCorrective}
  const c = todayCounts || {};
  return (
    <div className="cmp-wrap">
      <CmpSearchBar nav={nav} />
      {probesDue && probesDue.length > 0 && (
        <div className="cmp-card cmp-amber" style={{marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:24}}>🌡️</div>
          <div style={{flex:1}}>
            <h4 style={{fontSize:15,marginBottom:2,color:"var(--cm-amber)"}}>Probe calibration due</h4>
            <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>
              {probesDue.map(p => p.name + " (" + (p.daysAgo === null ? "never calibrated" : p.daysAgo + "d ago")).join(") · ")}{")"} — calibrate before next use.
            </div>
          </div>
          <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => nav("probe")}>Calibrate now</button>
        </div>
      )}
      <div className="cmp-status-strip">
        <span className="cmp-label">Today</span>
        <span className="cmp-chip cmp-info"><span className="cmp-dot" />Manchester Kitchen</span>
        {c.haccpTotal > 0 && (
          <span className={"cmp-chip cmp-" + (c.haccpDone === c.haccpTotal ? "ok" : "warn")}>
            <span className="cmp-dot" />HACCP {c.haccpDone}/{c.haccpTotal}
          </span>
        )}
        {typeof c.cleaningCellsToday === "number" && (
          <span className={"cmp-chip cmp-" + (c.cleaningCellsToday > 0 ? "ok" : "warn")}>
            <span className="cmp-dot" />Cleaning {c.cleaningCellsToday > 0 ? c.cleaningCellsToday + " today" : "Due"}
          </span>
        )}
        <span className={"cmp-chip cmp-" + (c.pestDoneToday ? "ok" : "warn")}>
          <span className="cmp-dot" />Pest {c.pestDoneToday ? "✓" : "Due"}
        </span>
        {c.openIncidents > 0 && (
          <span className="cmp-chip cmp-bad"><span className="cmp-dot" />{c.openIncidents} open incident{c.openIncidents>1?"s":""}</span>
        )}
        {c.openCorrective > 0 && (
          <span className="cmp-chip cmp-warn"><span className="cmp-dot" />{c.openCorrective} open action{c.openCorrective>1?"s":""}</span>
        )}
        {sundayTone && (
          <span className={"cmp-chip cmp-" + sundayTone}><span className="cmp-dot" />Weekly Sign-Off Due</span>
        )}
      </div>

      <div className="cmp-home-grid">
        {/* LEFT — Forms */}
        <div>
          <div className="cmp-col-head">
            <h2>Forms</h2>
            <span className="cmp-col-sub">Daily · Weekly · On demand</span>
          </div>
          <div className="cmp-tile-list">
            <button className="cmp-tile-row" onClick={() => nav("haccp")}>
              <div className="cmp-tile-icon">📋</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Daily HACCP</div>
                <div className="cmp-tile-meta">Opening · CCP-1 deliveries · CCP-6 fridges · CCP-5 cooking · CCP-2 pH · closing</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("cleaning")}>
              <div className="cmp-tile-icon">🧽</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Weekly Cleaning Schedule</div>
                <div className="cmp-tile-meta">Tasks × days · sign off on Sunday</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("probe")}>
              <div className="cmp-tile-icon">🌡️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Probe Calibration</div>
                <div className="cmp-tile-meta">Weekly + on-drop · ±1°C tolerance</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("pest")}>
              <div className="cmp-tile-icon">🔍</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Pest Daily Check</div>
                <div className="cmp-tile-meta">Per-area · droppings, damage, smells, sightings</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("pestmonday")}>
              <div className="cmp-tile-icon">🧹</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Pest Monday Deep Sweep</div>
                <div className="cmp-tile-meta">Weekly · 5 deeper checks · Monday pre-open</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("fish")}>
              <div className="cmp-tile-icon">🐟</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Fish Freezing</div>
                <div className="cmp-tile-meta">Per intake · −20°C × 24h before raw use</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("emergency")}>
              <div className="cmp-tile-icon">⚠️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Emergency Fridge Incident</div>
                <div className="cmp-tile-meta">Raise · 2-hourly checks · disposal sign-off</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("declaration")}>
              <div className="cmp-tile-icon">✍️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Declaration of Competency &amp; FSMS</div>
                <div className="cmp-tile-meta">Self-signed · annual refresh · manager countersign</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("corrective")}>
              <div className="cmp-tile-icon">🧯</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Corrective Actions</div>
                <div className="cmp-tile-meta">Raise + resolve · stream from all forms</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
          </div>
        </div>

        {/* RIGHT — Documentation */}
        <div>
          <div className="cmp-col-head">
            <h2>Documentation</h2>
            <span className="cmp-col-sub">Read-only · EHO-ready</span>
          </div>
          <div className="cmp-hub-grid">
            <div className="cmp-hub-card" onClick={() => nav("hub-certs")}>
              <div className="cmp-hub-icon">👥</div>
              <div className="cmp-hub-title">Staff Certs</div>
              <div className="cmp-hub-meta">L2 / L3 Food Safety · H&amp;S</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-sops")}>
              <div className="cmp-hub-icon">📘</div>
              <div className="cmp-hub-title">SOPs</div>
              <div className="cmp-hub-meta">{cmpGetSops().length} documents</div>
              <div className="cmp-hub-state cmp-state-ok">All current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-fsms")}>
              <div className="cmp-hub-icon">📕</div>
              <div className="cmp-hub-title">FSMS</div>
              <div className="cmp-hub-meta">23-page master document</div>
              <div className="cmp-hub-state cmp-state-ok">{CMP_FSMS_SUMMARY.meta.split(" · ")[0]} · {CMP_FSMS_SUMMARY.meta.split(" · ")[1]}</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-risk")}>
              <div className="cmp-hub-icon">⚠️</div>
              <div className="cmp-hub-title">Risk Assessments</div>
              <div className="cmp-hub-meta">{CMP_RISK_ASSESSMENTS.length} on file</div>
              <div className="cmp-hub-state cmp-state-ok">All current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-ssow")}>
              <div className="cmp-hub-icon">🛠️</div>
              <div className="cmp-hub-title">Safe Systems of Work</div>
              <div className="cmp-hub-meta">{CMP_SSOW.length} on file</div>
              <div className="cmp-hub-state cmp-state-warn">Rolled-specific in authoring</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-suppliers")}>
              <div className="cmp-hub-icon">🚚</div>
              <div className="cmp-hub-title">Approved Suppliers</div>
              <div className="cmp-hub-meta">{CMP_SUPPLIERS.length} on file · due-diligence certs</div>
              <div className="cmp-hub-state cmp-state-ok">Current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-legal")}>
              <div className="cmp-hub-icon">📄</div>
              <div className="cmp-hub-title">Insurance &amp; Halal</div>
              <div className="cmp-hub-meta">Legal & certification docs</div>
              <div className="cmp-hub-state cmp-state-warn">Halal expires soon</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-reference")}>
              <div className="cmp-hub-icon">📊</div>
              <div className="cmp-hub-title">Reference Charts</div>
              <div className="cmp-hub-meta">Cleaning Method · Shelf Life</div>
              <div className="cmp-hub-state cmp-state-ok">v3 · Oct 2025</div>
            </div>
            <div className="cmp-hub-card" onClick={() => cmpToast("Allergen Matrix lives in the dedicated Allergens tile — JJ-owned, in progress.", "warn")}>
              <div className="cmp-hub-icon">🥜</div>
              <div className="cmp-hub-title">Allergen Matrix</div>
              <div className="cmp-hub-meta">Lives in separate Allergens tile</div>
              <div className="cmp-hub-state cmp-state-warn">JJ-owned · in progress</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Cleaning Schedule (Trafford-shipped pattern) ----
function CmpCleaningSchedule({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [weekSig, setWeekSig] = useState(null);
  const [pendingCell, setPendingCell] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "CleaningLog", "week");
  // Week-mode: weekStart tracks the calendar pick. viewDate is already
  // the Monday (the hook normalised it on setViewDate). Today's view
  // falls back to cmpMondayOf(today).
  const weekStart = hist.isHistorical ? hist.viewDate : cmpMondayOf(new Date());
  const todayIdx = (new Date().getDay() + 6) % 7;
  const sundayTone = cmpSundayPillTone();
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const dayDates = days.map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const weekRangeLabel = dayDates[0].toLocaleDateString("en-GB",{day:"numeric",month:"short"}) + " – " + dayDates[6].toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load cleaning tasks: " + e.message, "error"));
  }, [backend, site.key]);

  const tasks = useMemo(() => config ? cmpResolveSurfaceItems(config, "cleaning-tasks", site.key) : [], [config, site.key]);

  // Rehydrate the matrix from the audit trail. Live mode reads the last
  // 14 days, historical mode uses the hook's pre-fetched Mon→Sun slice.
  // Reset matrix + weekSig immediately on weekStart change so the
  // previous week's cells don't briefly bleed through while the new
  // fetch resolves.
  //
  // Wave 6 follow-up (2026-05-01): rows now carry a stable TaskId from
  // the cleaning-tasks surface. Match by TaskId first → admin reorders
  // don't shift historical ticks. Fall back to TaskIndex for legacy
  // rows written before this change.
  useEffect(() => {
    setMatrix({});
    setWeekSig(null);
    const idByPosition = tasks.map(t => t.id);
    const positionById = {};
    tasks.forEach((t, i) => { if (t && t.id) positionById[t.id] = i; });
    const hydrate = (rows) => {
      const m = {};
      rows.forEach(r => {
        if (String(r.WeekStart) !== weekStart) return;
        const di = parseInt(r.DayIndex, 10);
        if (isNaN(di)) return;
        let ti;
        if (r.TaskId && positionById[r.TaskId] !== undefined) {
          ti = positionById[r.TaskId];
        } else {
          ti = parseInt(r.TaskIndex, 10);
          if (isNaN(ti)) return;
        }
        if (m[ti] && m[ti][di]) return;
        if (!m[ti]) m[ti] = {};
        m[ti][di] = {state: r.State || "empty", init: cmpInitials(r.StaffName || "")};
      });
      setMatrix(m);
    };
    if (hist.isHistorical) {
      hydrate(hist.histRows || []);
    } else {
      cmpFetchFormToday(backend, site.key, "CleaningLog", 14)
        .then(hydrate)
        .catch(() => {});
    }
  }, [backend, site.key, weekStart, hist.isHistorical, hist.histRows, tasks]);

  const cellState = (taskIdx, dayIdx) => (matrix[taskIdx] && matrix[taskIdx][dayIdx]) || {state: "empty"};

  const writeCell = (taskIdx, dayIdx, state, staff) => {
    const t = tasks[taskIdx];
    const taskName = t && t.label;
    const taskId = t && t.id;
    cmpComplianceLog(backend, site.key, "cleaning-cell", {
      weekStart: weekStart, taskIndex: taskIdx, dayIndex: dayIdx,
      taskName: taskName, taskId: taskId, state: state
    }, staff.pin).then(() => {
      setMatrix(m => Object.assign({}, m, {
        [taskIdx]: Object.assign({}, m[taskIdx] || {}, {[dayIdx]: {state: state, init: staff.initials}})
      }));
      cmpToast(state === "done" ? "Cell marked complete" : state === "skipped" ? "Cell marked not completed" : "Cell sign-off removed", state === "skipped" ? "warn" : "success");
    }).catch(e => cmpToast("Save failed: " + e.message, "error"));
  };

  const onCellClick = (taskIdx, dayIdx) => {
    if (hist.isHistorical) return; // read-only past weeks
    const cur = cellState(taskIdx, dayIdx);
    if (cur.state === "done" || cur.state === "skipped") {
      pinPrompt({prompt: "Remove cleaning sign-off", requiredRoles: ["Admin"], onConfirm: (s) => writeCell(taskIdx, dayIdx, "empty", s)});
    } else {
      setPendingCell({taskIdx, dayIdx});
    }
  };

  const completeCell = (yes) => {
    const {taskIdx, dayIdx} = pendingCell;
    setPendingCell(null);
    const taskName = tasks[taskIdx] && tasks[taskIdx].label;
    pinPrompt({
      prompt: yes ? "PIN — Task Completed" : "PIN — Task Not Completed",
      desc: taskName + " · " + days[dayIdx],
      onConfirm: (s) => writeCell(taskIdx, dayIdx, yes ? "done" : "skipped", s),
      onCancel: () => setPendingCell({taskIdx, dayIdx})
    });
  };

  const weekSign = () => {
    pinPrompt({
      prompt: "Weekly Manager Sign Off",
      desc: "Confirms the week's cleaning has been reviewed. Any signed-in staff PIN works.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "cleaning-weekly-signoff", {weekStart: weekStart}, s.pin).then(() => {
          setWeekSig({init: s.initials, name: s.name, ts: new Date().toISOString()});
          cmpToast("Week signed off by " + s.name, "success");
        }).catch(e => cmpToast("Sign-off failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading cleaning schedule…</div>;
  if (!tasks.length) return <div className="cmp-empty">No cleaning tasks configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} historical={hist.isHistorical} onClear={() => hist.setViewDate(null)} label={"Week of " + cmpFriendly(weekStart)} backLabel="← Back to this week" />
      <CmpHistoricalSignerBanner date={hist.viewDate} historical={hist.isHistorical} formLabel="Cleaning — historical week" signedBy={hist.signedBy} />
      {!hist.isHistorical && sundayTone && !weekSig && (
        <div className={"cmp-card cmp-" + (sundayTone === "bad" ? "coral" : "amber")}>
          <h4 style={{color:"var(--cm-" + (sundayTone === "bad" ? "coral" : "amber") + ")",marginBottom:4}}>Sunday — Sign-Off Required</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Last day of this cleaning week. Any signed-in staff can sign the week off below before close.</div>
        </div>
      )}
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div style={{flex:1,minWidth:0}}>
            <h3>Weekly Cleaning Schedule</h3>
            <div className="cmp-sub">Week of <strong>{weekRangeLabel}</strong>. Tap a cell to mark Completed (green) or Not completed (grey). Both record initials. Sign the week off on Sunday.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="week" />
            <span style={{fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",padding:"6px 12px",borderRadius:6,background:"var(--cm-wasabi-20)",color:"var(--cm-nori)",fontWeight:800,whiteSpace:"nowrap"}}>📅 {weekRangeLabel}</span>
          </div>
        </div>
      </div>
      {weekSig && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Weekly Sign Off Complete</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Signed by <strong>{weekSig.name}</strong> at <strong>{new Date(weekSig.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</strong>.</div>
        </div>
      )}
      <div className="cmp-card cmp-matrix-scroll" style={{padding:"4px 8px"}}>
        <table className="cmp-matrix">
          <thead><tr>
            <th className="cmp-task">Task</th>
            {days.map((d, i) => (
              <th key={d} style={i === todayIdx ? {color:"var(--cm-nori)"} : null}>
                <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em"}}>{d.toUpperCase()}</div>
                <div style={{fontSize:9,fontWeight:700,opacity:0.65,marginTop:2}}>{dayDates[i].toLocaleDateString("en-GB",{day:"numeric",month:"short"}).toUpperCase()}</div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {tasks.map((t, i) => (
              <tr key={t.id || i}>
                <td className="cmp-task"><div className="cmp-tname">{t.label}</div></td>
                {days.map((_, d) => {
                  const cell = cellState(i, d);
                  const isToday = d === todayIdx;
                  const tooltip = cell.state === "done" ? "Done by " + cell.init : cell.state === "skipped" ? "Not completed (signed by " + cell.init + ")" : "Tap to initial";
                  return (
                    <td key={d}>
                      <button className={"cmp-cell-btn " + (cell.state === "done" ? "cmp-done" : cell.state === "skipped" ? "cmp-skipped" : "") + (isToday ? " cmp-today" : "")} onClick={() => onCellClick(i, d)} title={tooltip}>
                        {(cell.state === "done" || cell.state === "skipped") ? cell.init : "·"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hist.isHistorical && (
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:12,alignItems:"center"}}>
          {!weekSig && <button className="cmp-btn cmp-btn-primary" onClick={weekSign}>Weekly Manager Sign Off (PIN required)</button>}
          <span style={{fontSize:12,color:"var(--cm-nori-60)"}}>Any signed-in staff PIN works. Sign off on Sunday. Unsigned weeks raise an admin alert.</span>
        </div>
      )}
      <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",marginTop:12,padding:"10px 14px"}}>
        <div style={{fontSize:13,lineHeight:1.5,color:"var(--cm-nori-70)"}}>
          <strong>Cell colours:</strong> 🟢 green = completed · ⚪ grey = not completed (initials still captured) · empty = not yet recorded.
        </div>
      </div>
      {!hist.isHistorical && pendingCell && (
        <div className="cmp-confirm-scrim" onClick={() => setPendingCell(null)}>
          <div className="cmp-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{color:"var(--cm-wasabi)",marginBottom:8}}>Was the task completed?</h3>
            <div style={{color:"rgba(254,250,240,0.75)",fontSize:13,marginBottom:20,lineHeight:1.4}}>
              {tasks[pendingCell.taskIdx].label} · {days[pendingCell.dayIdx]}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
              <button onClick={() => completeCell(false)} style={{padding:"14px 22px",borderRadius:10,background:"#7a7a7a",color:"#fff",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>No, not completed</button>
              <button onClick={() => completeCell(true)} style={{padding:"14px 22px",borderRadius:10,background:"var(--cm-wasabi)",color:"var(--cm-nori)",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Yes, completed</button>
            </div>
            <div style={{fontSize:11,color:"rgba(254,250,240,0.55)",marginTop:12}}>Both capture your PIN. No still records who attempted it (cell turns grey, not green).</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pest Daily Check ----
function CmpPestDaily({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [newRow, setNewRow] = useState({area: "", evidence: "No", details: "", action: ""});
  const [todayRows, setTodayRows] = useState([]);
  const hist = useCmpHistoryView(backend, site.key, "PestLog", "day");
  const visibleRows = hist.isHistorical
    ? hist.histRows.filter(r => String(r.Event) === "daily-check").map(r => ({
        ts: r._ts, area: r.Area || "", evidence: r.Evidence || "No",
        details: r.Details || "", action: r.CorrectiveAction || "",
        init: cmpInitials(r.StaffName || ""), incidentId: r.CorrectiveActionId || ""
      }))
    : todayRows;

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        setConfig(c);
        const areas = cmpResolveSurfaceItems(c, "pest-areas", site.key);
        if (areas.length && !newRow.area) setNewRow(r => Object.assign({}, r, {area: areas[0].label}));
      })
      .catch(e => cmpToast("Couldn't load pest areas: " + e.message, "error"));
  }, [backend, site.key]);

  // Rehydrate today's pest rows.
  useEffect(() => {
    const today = cmpTodayIso();
    cmpFetchFormToday(backend, site.key, "PestLog", 1)
      .then(rows => {
        const today_rows = rows
          .filter(r => String(r.Date) === today && String(r.Event) === "daily-check")
          .map(r => ({
            ts: r._ts, area: r.Area || "", evidence: r.Evidence || "No",
            details: r.Details || "", action: r.CorrectiveAction || "",
            init: cmpInitials(r.StaffName || ""),
            incidentId: r.CorrectiveActionId || ""
          }));
        setTodayRows(today_rows);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const areas = useMemo(() => config ? cmpResolveSurfaceItems(config, "pest-areas", site.key) : [], [config, site.key]);

  const submit = () => {
    if (newRow.evidence === "Yes" && (!newRow.details.trim() || !newRow.action.trim())) {
      cmpToast("Yes-evidence requires details + corrective action", "error"); return;
    }
    if (!newRow.area) { cmpToast("Pick an area", "error"); return; }
    pinPrompt({
      prompt: "Sign off pest check",
      desc: "Your PIN stamps this check. Yes-rows raise a corrective action automatically.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "pest-daily", {
          area: newRow.area, evidence: newRow.evidence, details: newRow.details, action: newRow.action
        }, staff.pin).then((d) => {
          setTodayRows(r => [{
            ts: new Date().toISOString(), area: newRow.area, evidence: newRow.evidence,
            details: newRow.details, action: newRow.action, init: staff.initials,
            incidentId: d && d.incidentId ? d.incidentId : ""
          }, ...r]);
          setNewRow({area: (areas[0] && areas[0].label) || "", evidence: "No", details: "", action: ""});
          cmpToast(newRow.evidence === "Yes" ? "Yes-row submitted · corrective action raised" : "Pest check logged", newRow.evidence === "Yes" ? "warn" : "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading pest areas…</div>;
  if (!areas.length) return <div className="cmp-empty">No pest areas configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Pest Daily — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Daily Pest Visual Check</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Check each area for droppings, damage, smells, sightings. Reference: <a style={{color:"var(--cm-nori)",fontWeight:700,borderBottom:"1px dotted",cursor:"pointer"}} onClick={() => cmpOpenSop({id:"SOP-17",title:"SOP 17 — Pest Control",version:"v2",updated:"2026-04-19"})}>SOP 17</a>.</div>
        <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",margin:"12px 0 0",padding:"10px 12px"}}>
          <h5 style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--cm-nori-60)",marginBottom:8}}>What to Look For</h5>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5}}><strong style={{color:"var(--cm-nori)"}}>Droppings:</strong> <span style={{color:"var(--cm-nori-70)"}}>Small, dark pellets.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Damage:</strong> <span style={{color:"var(--cm-nori-70)"}}>Gnaw marks on packaging, cables, or structure.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Smells:</strong> <span style={{color:"var(--cm-nori-70)"}}>Unusual or ammonia-like odours.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Sightings:</strong> <span style={{color:"var(--cm-nori-70)"}}>Live or dead insects/rodents.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Proofing:</strong> <span style={{color:"var(--cm-nori-70)"}}>Any new holes or gaps in the walls/floors.</span></div>
        </div>
        {!hist.isHistorical && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12,marginTop:12,alignItems:"start"}}>
            <div className="cmp-field">
              <label>Area Checked</label>
              <select value={newRow.area} onChange={e => setNewRow(r => Object.assign({}, r, {area: e.target.value}))} style={{height:42}}>
                {areas.map(a => <option key={a.id || a.label} value={a.label}>{a.label}</option>)}
              </select>
            </div>
            <div className="cmp-field">
              <label>Evidence of Pests?</label>
              <select value={newRow.evidence} onChange={e => setNewRow(r => Object.assign({}, r, {evidence: e.target.value}))} style={{height:42}}>
                <option>No</option><option>Yes</option>
              </select>
            </div>
          </div>
        )}
        {!hist.isHistorical && newRow.evidence === "Yes" && (
          <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:12}}>
            <div className="cmp-field cmp-warn">
              <label>Details (required)</label>
              <textarea value={newRow.details} onChange={e => setNewRow(r => Object.assign({}, r, {details: e.target.value}))} placeholder="Droppings? Damage? Smell? Location?" />
            </div>
            <div className="cmp-field cmp-warn">
              <label>Corrective action (required)</label>
              <textarea value={newRow.action} onChange={e => setNewRow(r => Object.assign({}, r, {action: e.target.value}))} placeholder="What did you do? Who else notified?" />
              <span className="cmp-hint" style={{color:"var(--cm-coral)",fontWeight:700}}>⚠ Yes-row raises a Corrective Action automatically</span>
            </div>
          </div>
        )}
        {!hist.isHistorical && (
          <div style={{marginTop:12}}>
            <button className="cmp-btn cmp-btn-primary" onClick={submit}>Submit Check (PIN required)</button>
          </div>
        )}
      </div>
      {visibleRows.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Checks on " + cmpFriendly(hist.viewDate) : "This Session"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Time</th><th>Area</th><th>Evidence</th><th>Details</th><th>Action</th><th>By</th></tr></thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr key={i} style={r.evidence === "Yes" ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>{new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td>{r.area}</td>
                  <td><span className={"cmp-badge " + (r.evidence === "Yes" ? "cmp-bad" : "cmp-ok")}>{r.evidence}</span></td>
                  <td style={{fontSize:12}}>{r.details || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td style={{fontSize:12}}>{r.action || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{r.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hist.isHistorical && !hist.loading && visibleRows.length === 0 && (
        <div className="cmp-empty">No pest checks recorded on {cmpFriendly(hist.viewDate)}.</div>
      )}
    </div>
  );
}

// ---- Pest Monday Deep Sweep ----
// Wave 6.4 (2026-04-30): the 5-item checklist now reads from the
// admin-managed `pest-monday-checks` surface. Items have shape
// {id, n, label, check, action, status}. Historical PestLog rows
// reference TaskNumber by `n` (the displayed integer string), so
// admins can re-order or rename items without breaking ticks.
// The constant below is no longer referenced; backend default seeds the
// surface with these same 5 items so first-mount fallback still renders.
function CmpPestMondaySweep({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [weeklyState, setWeeklyState] = useState({});
  const [correctiveNeeded, setCorrectiveNeeded] = useState(false);
  const [correctiveText, setCorrectiveText] = useState("");
  const [submittedBy, setSubmittedBy] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "PestLog", "week");
  const weekStart = hist.isHistorical ? hist.viewDate : cmpMondayOf(new Date());

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load deep-sweep checks: " + e.message, "error"));
  }, [backend, site.key]);

  const checks = useMemo(
    () => config ? cmpResolveSurfaceItems(config, "pest-monday-checks", site.key) : [],
    [config, site.key]
  );
  const allChecked = Object.keys(weeklyState).length === checks.length && checks.length > 0;
  const correctiveValid = !correctiveNeeded || correctiveText.trim().length > 0;
  const canSubmit = !hist.isHistorical && allChecked && correctiveValid && !submittedBy;

  // Rehydrate this week's tick state from PestLog rows where Event=='deep-sweep-tick'.
  // Live mode reads the past 14 days; historical mode uses the hook's
  // pre-fetched Mon→Sun slice.
  useEffect(() => {
    const hydrate = (rows) => {
      const ws = new Date(weekStart);
      const next = new Date(ws); next.setDate(next.getDate() + 7);
      const w = {};
      let submission = null;
      rows.forEach(r => {
        const rowDate = new Date(r.Date || r._ts);
        if (rowDate < ws || rowDate >= next) return;
        if (String(r.Event) === "deep-sweep-tick") {
          const taskNum = String(r.TaskNumber || "").trim();
          const idx = checks.findIndex(c => c.n === taskNum);
          if (idx < 0) return;
          if (w[idx]) return;
          w[idx] = {init: cmpInitials(r.StaffName || ""), ts: r._ts};
        } else if (String(r.Event) === "deep-sweep-submit" && !submission) {
          submission = {
            name: r.StaffName || "", init: cmpInitials(r.StaffName || ""),
            ts: r._ts,
            corrective: (String(r.CorrectiveAction || "") || null),
            incidentId: r.CorrectiveActionId || ""
          };
        }
      });
      setWeeklyState(w);
      // submittedBy must reflect ONLY the visible week. In live mode,
      // the previous Pest Daily check session may have left a stale
      // submission visible across a week-boundary or after switching
      // back from a historical view — so always set it from the latest
      // hydrate, including null when there's no submission row yet.
      setSubmittedBy(submission);
    };
    if (hist.isHistorical) {
      hydrate(hist.histRows || []);
    } else {
      cmpFetchFormToday(backend, site.key, "PestLog", 14)
        .then(hydrate)
        .catch(() => {});
    }
  }, [backend, site.key, weekStart, hist.isHistorical, hist.histRows]);

  const checkTask = (i) => {
    pinPrompt({
      prompt: "Mark task checked",
      desc: "Task " + checks[i].n + " · " + (checks[i].label || checks[i].title || ""),
      requiredRoles: ["BOH","Admin"],
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "pest-deep-sweep-tick", {
          taskNumber: checks[i].n, weekStart: weekStart
        }, s.pin).then(() => {
          setWeeklyState(t => Object.assign({}, t, {[i]: {init: s.initials, ts: new Date().toISOString()}}));
          cmpToast("Task " + checks[i].n + " checked", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };
  const clearTask = (i) => setWeeklyState(t => { const n = Object.assign({}, t); delete n[i]; return n; });

  const submitWeekly = () => {
    if (!canSubmit) return;
    pinPrompt({
      prompt: "Sign off Monday deep sweep",
      desc: correctiveNeeded ? "Includes a corrective action" : "All tasks checked · no corrective action",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "pest-deep-sweep-submit", {
          weekStart: weekStart, correctiveNeeded: correctiveNeeded,
          correctiveText: correctiveNeeded ? correctiveText.trim() : ""
        }, s.pin).then((d) => {
          setSubmittedBy({name: s.name, init: s.initials, ts: new Date().toISOString(),
            corrective: correctiveNeeded ? correctiveText.trim() : null,
            incidentId: d && d.incidentId ? d.incidentId : ""});
          cmpToast("Submitted by " + s.name, correctiveNeeded ? "warn" : "success");
        }).catch(e => cmpToast("Submit failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} historical={hist.isHistorical} onClear={() => hist.setViewDate(null)} label={"Week of " + cmpFriendly(weekStart)} backLabel="← Back to this week" />
      <CmpHistoricalSignerBanner date={hist.viewDate} historical={hist.isHistorical} formLabel="Pest Monday Deep Sweep — historical week" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Monday Deep Sweep</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="week" />
        </div>
        <div className="cmp-sub">Per FSMS §4.4 + §9.2 — completed Monday pre-open. Tap each task to mark it Checked (PIN required). Capture any corrective action at the bottom before submitting.</div>
      </div>
      {submittedBy && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Submitted</h4>
          <div style={{fontSize:13}}>Submitted by <strong>{submittedBy.name}</strong> at {new Date(submittedBy.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}.{submittedBy.corrective ? " Corrective action captured." : ""}{submittedBy.incidentId ? " " + submittedBy.incidentId : ""}</div>
        </div>
      )}
      {checks.map((c, i) => {
        const state = weeklyState[i];
        return (
          <div key={i} className="cmp-card" style={state ? {borderLeft:"4px solid var(--cm-green)"} : {}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:800,fontSize:15,marginBottom:4}}>{c.n}. {c.label || c.title}</div>
                <div style={{fontSize:12,color:"var(--cm-nori-70)",lineHeight:1.4}}><strong>Check for:</strong> {c.check}</div>
                <div style={{fontSize:12,color:"var(--cm-nori-70)",lineHeight:1.4}}><strong>Action:</strong> {c.action}</div>
              </div>
              {!state ? (
                <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => checkTask(i)} disabled={!!submittedBy || hist.isHistorical}>Checked? (PIN)</button>
              ) : (
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                  <span className="cmp-chip cmp-ok"><span className="cmp-dot" />✓ Checked</span>
                  <span style={{fontSize:11,background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontFamily:"gelica,serif",fontWeight:800}}>{state.init}</span>
                  {!submittedBy && !hist.isHistorical && <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => clearTask(i)}>Undo</button>}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!hist.isHistorical && (
        <div className="cmp-card">
          <h4>Corrective Action Required?</h4>
          <div className="cmp-sub" style={{fontSize:12}}>Anything that needs fixing during this sweep? Tick Yes and describe the action — you can't submit without describing what was done.</div>
          <div style={{marginTop:12,display:"flex",gap:14,alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:!!submittedBy ? "default" : "pointer"}}>
              <input type="radio" name="cmp-pms-cor" checked={!correctiveNeeded} onChange={() => setCorrectiveNeeded(false)} disabled={!!submittedBy} />
              <strong>No</strong>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:!!submittedBy ? "default" : "pointer"}}>
              <input type="radio" name="cmp-pms-cor" checked={correctiveNeeded} onChange={() => setCorrectiveNeeded(true)} disabled={!!submittedBy} />
              <strong>Yes</strong>
            </label>
          </div>
          {correctiveNeeded && (
            <div className="cmp-field cmp-warn" style={{marginTop:12}}>
              <label>Corrective action</label>
              <textarea value={correctiveText} onChange={e => setCorrectiveText(e.target.value)} placeholder="What was the issue? What did you do about it?" disabled={!!submittedBy} />
            </div>
          )}
        </div>
      )}
      {!hist.isHistorical && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <div className="cmp-sub" style={{marginBottom:12}}>{Object.keys(weeklyState).length}/{checks.length} checked{correctiveNeeded ? " · corrective required" : ""}</div>
          <button className="cmp-btn cmp-btn-primary" onClick={submitWeekly} disabled={!canSubmit}>
            {submittedBy ? "Submitted — read-only" : "Submit Deep Sweep (PIN required)"}
          </button>
        </div>
      )}
      {hist.isHistorical && submittedBy && submittedBy.corrective && (
        <div className="cmp-card cmp-amber">
          <h4>Corrective action recorded that week</h4>
          <div style={{fontSize:13,marginTop:6}}>{submittedBy.corrective}</div>
        </div>
      )}
    </div>
  );
}

// ---- Probe Calibration ----
const CMP_PROBES = [
  {id: "P1", name: "Probe 1 · main"},
  {id: "P2", name: "Probe 2 · backup"},
  {id: "P3", name: "Probe 3 · delivery checks"}
];
function CmpProbeCalibration({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [selectedProbe, setSelectedProbe] = useState(CMP_PROBES[0].id);
  const [method, setMethod] = useState("ice");
  const [reading, setReading] = useState("");
  const [action, setAction] = useState("");
  const [todayRows, setTodayRows] = useState([]);
  const [historyByProbe, setHistoryByProbe] = useState({}); // {probeId: {lastTs, lastReading, daysAgo, init}}
  // Wave 6.3 (2026-04-30): tolerance comes from the admin-managed
  // critical-limits surface so the form's UX matches what the backend
  // actually enforces. Falls back to ±1°C until the fetch resolves.
  const [toleranceC, setToleranceC] = useState(1);
  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "critical-limits", site.key);
        const tol = items.find(it => it.id === "probe-tolerance-c");
        if (tol && tol.value !== "" && tol.value != null) {
          const n = Number(tol.value);
          if (!isNaN(n) && n > 0) setToleranceC(n);
        }
      })
      .catch(() => {});
  }, [backend, site.key]);
  const hist = useCmpHistoryView(backend, site.key, "ProbeLog", "day");
  const visibleRows = hist.isHistorical
    ? hist.histRows.map(r => ({
        ts: r._ts, probeId: r.ProbeId || "", probeName: r.ProbeName || "",
        method: r.Method || "ice", reading: r.Reading,
        errorC: typeof r.ErrorC === "number" ? r.ErrorC : parseFloat(r.ErrorC),
        outOfTol: r.OutOfTolerance === "Y",
        action: r.Action || "", init: cmpInitials(r.StaffName || ""),
        incidentId: r.CorrectiveActionId || ""
      }))
    : todayRows;
  const target = method === "ice" ? 0 : 100;
  const numeric = parseFloat(reading);
  const errorC = !isNaN(numeric) ? +(numeric - target).toFixed(1) : null;
  const outOfTol = errorC !== null && Math.abs(errorC) > toleranceC;

  // Rehydrate today's session rows + last-calibration-per-probe from
  // the last 30 days of ProbeLog (so the per-probe status card can show
  // "9 days ago" for any probe).
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "ProbeLog", 30)
      .then(rows => {
        const today = cmpTodayIso();
        const today_rows = rows
          .filter(r => String(r.Date) === today)
          .map(r => ({
            ts: r._ts, probeId: r.ProbeId || "", probeName: r.ProbeName || "",
            method: r.Method || "ice", reading: r.Reading,
            errorC: typeof r.ErrorC === "number" ? r.ErrorC : parseFloat(r.ErrorC),
            outOfTol: r.OutOfTolerance === "Y",
            action: r.Action || "", init: cmpInitials(r.StaffName || ""),
            incidentId: r.CorrectiveActionId || ""
          }));
        setTodayRows(today_rows);
        // Compute last-calibration-per-probe (rows are newest-first)
        const byProbe = {};
        rows.forEach(r => {
          const pId = String(r.ProbeId || "");
          if (!pId || byProbe[pId]) return;
          const ts = r._ts;
          const daysAgo = Math.floor((Date.now() - new Date(ts).getTime()) / (1000*60*60*24));
          byProbe[pId] = {
            lastTs: ts, lastReading: r.Reading, daysAgo: daysAgo,
            init: cmpInitials(r.StaffName || "")
          };
        });
        setHistoryByProbe(byProbe);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submit = () => {
    if (reading === "" || isNaN(numeric)) { cmpToast("Reading required (numeric)", "error"); return; }
    if (outOfTol && !action.trim()) { cmpToast("Out-of-tolerance reading needs a corrective action note", "error"); return; }
    const probe = CMP_PROBES.find(p => p.id === selectedProbe);
    pinPrompt({
      prompt: "Log calibration for " + probe.name,
      desc: "Test in " + (method === "ice" ? "ice water (0°C)" : "boiling water (100°C)") + ", ±1°C tolerance.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "probe-calibration", {
          probeId: selectedProbe, probeName: probe.name, method: method, reading: numeric, action: action.trim()
        }, staff.pin).then(d => {
          setTodayRows(r => [{
            ts: new Date().toISOString(), probeId: selectedProbe, probeName: probe.name, method: method,
            reading: numeric, errorC: d && typeof d.errorC === "number" ? d.errorC : errorC,
            outOfTol: !!(d && d.outOfTolerance), action: action.trim(), init: staff.initials,
            incidentId: d && d.incidentId ? d.incidentId : ""
          }, ...r]);
          setReading(""); setAction("");
          cmpToast(outOfTol ? "Out-of-tolerance · corrective action raised" : "Calibration logged", outOfTol ? "warn" : "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Probe Calibration — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Probe Thermometer Calibration</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Weekly + any time a probe is dropped. Test in iced water; target 0°C ± 1°C tolerance. Out-of-tolerance forces a corrective action.</div>
      </div>
      {!hist.isHistorical && <div className="cmp-card">
        <h4>Probes · Status</h4>
        <div className="cmp-doc-list" style={{marginTop:10}}>
          {CMP_PROBES.map(p => {
            const h = historyByProbe[p.id];
            const dueSoon = !h || h.daysAgo >= 7;
            return (
              <div key={p.id} className="cmp-doc-row" style={{cursor:"default"}}>
                <div className="cmp-doc-icon">🌡️</div>
                <div className="cmp-doc-body">
                  <div className="cmp-doc-name">{p.name}</div>
                  <div className="cmp-doc-meta">
                    {h
                      ? "Last calibrated " + cmpFriendly(h.lastTs) + " (" + h.daysAgo + "d ago) · " + h.init + " · " + h.lastReading + "°C"
                      : "Never calibrated in the last 30 days"}
                  </div>
                </div>
                <div className="cmp-doc-right">
                  <span className={"cmp-chip cmp-" + (dueSoon ? "warn" : "ok")}><span className="cmp-dot" />{dueSoon ? "Due" : "Current"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>}
      {!hist.isHistorical && <div className="cmp-card">
        <h4>Log a Calibration</h4>
        <div className="cmp-sub" style={{fontSize:12}}>FSMS §4.3 CCP-6: ice water is the primary check (0°C ±1°C). Boiling water (100°C ±1°C) is an additional verification.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginTop:12}}>
          <div className="cmp-field">
            <label>Probe</label>
            <select value={selectedProbe} onChange={e => setSelectedProbe(e.target.value)}>
              {CMP_PROBES.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="cmp-field">
            <label>Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)}>
              <option value="ice">Ice water (0°C ±1°C)</option>
              <option value="boiling">Boiling water (100°C ±1°C)</option>
            </select>
          </div>
          <div className={"cmp-field " + (outOfTol ? "cmp-warn" : "")}>
            <label>Reading °C</label>
            <input type="text" inputMode="decimal" value={reading} onChange={e => setReading(e.target.value)} placeholder={method === "ice" ? "e.g. 0.3" : "e.g. 99.6"} />
            {errorC !== null && (
              <span className="cmp-hint" style={{color: outOfTol ? "var(--cm-coral)" : "var(--cm-green)", fontWeight:700}}>
                Error: {errorC > 0 ? "+" : ""}{errorC}°C{outOfTol ? " · out of tolerance" : " · within tolerance"}
              </span>
            )}
          </div>
        </div>
        {outOfTol && (
          <div className="cmp-field cmp-warn" style={{marginTop:12}}>
            <label>Corrective action (required)</label>
            <textarea value={action} onChange={e => setAction(e.target.value)} placeholder="Probe taken out of service / re-calibrated / using backup probe..." />
            <span className="cmp-hint" style={{color:"var(--cm-coral)",fontWeight:700}}>⚠ Out-of-tolerance reading raises a Corrective Action automatically</span>
          </div>
        )}
        <div style={{marginTop:12}}>
          <button className="cmp-btn cmp-btn-primary" onClick={submit}>Log Reading (PIN required)</button>
        </div>
      </div>}
      {visibleRows.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Calibrations on " + cmpFriendly(hist.viewDate) : "This Session"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Time</th><th>Probe</th><th>Method</th><th>Reading</th><th>Error</th><th>Action</th><th>By</th></tr></thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr key={i} style={r.outOfTol ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>{new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td style={{fontSize:12}}>{r.probeName}</td>
                  <td style={{fontSize:12}}>{r.method === "boiling" ? "🔥 Boiling" : "🧊 Ice"}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{r.reading}</td>
                  <td><span className={"cmp-badge " + (r.outOfTol ? "cmp-bad" : "cmp-ok")}>{r.errorC > 0 ? "+" : ""}{r.errorC}</span></td>
                  <td style={{fontSize:12}}>{r.action || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{r.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hist.isHistorical && !hist.loading && visibleRows.length === 0 && (
        <div className="cmp-empty">No calibrations recorded on {cmpFriendly(hist.viewDate)}.</div>
      )}
    </div>
  );
}

// ---- Fish Freezing (CCP-4) ----
function CmpFishFreezing({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [batches, setBatches] = useState([]);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [outFor, setOutFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "FishFreezingLog", "day");
  const visibleBatches = hist.isHistorical ? (() => {
    const byBatch = {};
    hist.histRows.slice().reverse().forEach(r => {
      const id = String(r.BatchId || "");
      if (!id) return;
      if (String(r.Event) === "intake") {
        byBatch[id] = {
          batchId: id, species: r.Species || "", supplier: r.Supplier || "",
          batchRef: r.BatchRef || "", freezer: r.Freezer || "",
          timeIn: r.TimeIn || r._ts, timeOut: "", coreTemp: "", pass: null,
          init: cmpInitials(r.StaffName || ""), incidentId: ""
        };
      } else if (String(r.Event) === "out" && byBatch[id]) {
        byBatch[id].timeOut = r.TimeOut || r._ts;
        byBatch[id].coreTemp = typeof r.CoreTemp === "number" ? r.CoreTemp : parseFloat(r.CoreTemp);
        byBatch[id].pass = String(r.Pass) === "Y";
        byBatch[id].init = cmpInitials(r.StaffName || "");
        byBatch[id].incidentId = r.CorrectiveActionId || "";
      }
    });
    return Object.values(byBatch).sort((a, b) => (b.timeIn || "").localeCompare(a.timeIn || ""));
  })() : batches;

  // Rehydrate batches from the last 7 days. Group intake/out by BatchId.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "FishFreezingLog", 7)
      .then(rows => {
        const byBatch = {};
        // Process oldest-first so intake lands before out
        rows.slice().reverse().forEach(r => {
          const id = String(r.BatchId || "");
          if (!id) return;
          if (String(r.Event) === "intake") {
            byBatch[id] = {
              batchId: id, species: r.Species || "", supplier: r.Supplier || "",
              batchRef: r.BatchRef || "", freezer: r.Freezer || "",
              timeIn: r.TimeIn || r._ts, timeOut: "", coreTemp: "", pass: null,
              init: cmpInitials(r.StaffName || ""), incidentId: ""
            };
          } else if (String(r.Event) === "out" && byBatch[id]) {
            byBatch[id].timeOut = r.TimeOut || r._ts;
            byBatch[id].coreTemp = typeof r.CoreTemp === "number" ? r.CoreTemp : parseFloat(r.CoreTemp);
            byBatch[id].pass = String(r.Pass) === "Y";
            byBatch[id].init = cmpInitials(r.StaffName || "");
            byBatch[id].incidentId = r.CorrectiveActionId || "";
          }
        });
        // Newest intake first in display
        const arr = Object.values(byBatch).sort((a, b) => (b.timeIn || "").localeCompare(a.timeIn || ""));
        setBatches(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitIntake = (vals) => {
    setIntakeOpen(false);
    pinPrompt({
      prompt: "Sign off fish intake (CCP-4)",
      desc: "Confirm with your PIN to start this batch.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "fish-freezing-intake", {
          species: vals.species, supplier: vals.supplier, batchRef: vals.batchRef, freezer: vals.freezer
        }, staff.pin).then(d => {
          setBatches(arr => [{
            batchId: d && d.batchId ? d.batchId : "(pending)",
            species: vals.species, supplier: vals.supplier, batchRef: vals.batchRef, freezer: vals.freezer,
            timeIn: d && d.timeIn ? d.timeIn : new Date().toISOString(),
            timeOut: "", coreTemp: "", pass: null, init: staff.initials, incidentId: ""
          }, ...arr]);
          cmpToast("Intake logged · " + (d && d.batchId ? d.batchId : ""), "success");
        }).catch(e => cmpToast("Intake failed: " + e.message, "error"));
      },
      onCancel: () => setIntakeOpen(true)
    });
  };

  const submitOut = (vals) => {
    const batchId = outFor;
    // Validate BEFORE closing the modal — losing the user's input on a
    // validation toast is hostile, especially the multi-field FAIL case.
    const coreTemp = parseFloat(vals.coreTemp);
    if (isNaN(coreTemp)) { cmpToast("Core temp must be numeric", "error"); return; }
    const pass = coreTemp <= -20;
    if (!pass && !String(vals.actionNotes || "").trim()) {
      cmpToast("Out-of-spec core temp needs a corrective action note", "error"); return;
    }
    setOutFor(null);
    pinPrompt({
      prompt: "Sign off freezing complete",
      desc: "Confirm with your PIN to close out " + batchId + ".",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "fish-freezing-out", {
          batchId: batchId, coreTemp: coreTemp, actionNotes: String(vals.actionNotes || "").trim()
        }, staff.pin).then(d => {
          setBatches(arr => arr.map(b => b.batchId === batchId ? Object.assign({}, b, {
            timeOut: new Date().toISOString(), coreTemp: coreTemp, pass: !!(d && d.pass),
            init: staff.initials, incidentId: d && d.correctiveActionId ? d.correctiveActionId : ""
          }) : b));
          cmpToast(pass ? "Freezing complete · " + coreTemp + "°C · PASS" : "⚠ Core " + coreTemp + "°C · corrective action raised", pass ? "success" : "warn");
        }).catch(e => cmpToast("Out failed: " + e.message, "error"));
      },
      onCancel: () => setOutFor(batchId)
    });
  };

  const open = visibleBatches.filter(b => !b.timeOut);
  const closed = visibleBatches.filter(b => !!b.timeOut);

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Fish Freezing — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Fish Freezing Log (CCP-4)</h3>
            <div className="cmp-sub">−20°C ≥ 24h before raw use. Time-out reading &gt; −20°C raises a corrective action.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => cmpOpenSop({id:"SOP-30",title:"SOP 30 — Freezing of Fish (parasite control)",version:"v1",updated:"2026-03-05"})}>📄 View Fish Freezing Policy</button>
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-primary" onClick={() => setIntakeOpen(true)}>+ Log Intake (PIN required)</button>}
          </div>
        </div>
      </div>
      {open.length > 0 && (
        <div className="cmp-card">
          <h3>Open Batches</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Batch</th><th>Species</th><th>Supplier · ref</th><th>Freezer</th><th>Time in</th><th>Action</th></tr></thead>
            <tbody>
              {open.map(b => (
                <tr key={b.batchId}>
                  <td style={{fontWeight:700,fontFamily:"monospace",fontSize:12}}>{b.batchId}</td>
                  <td>{b.species}</td>
                  <td style={{fontSize:12}}>{b.supplier} · {b.batchRef}</td>
                  <td>{b.freezer}</td>
                  <td style={{fontSize:12}}>{cmpFriendly(b.timeIn)} {new Date(b.timeIn).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td>{!hist.isHistorical ? <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => setOutFor(b.batchId)}>Close out · core °C</button> : <span style={{fontSize:11,color:"var(--cm-nori-60)"}}>open at end of day</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {closed.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Closed on " + cmpFriendly(hist.viewDate) : "Closed (this session)"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Batch</th><th>Species</th><th>Core °C</th><th>Result</th><th>By</th></tr></thead>
            <tbody>
              {closed.map(b => (
                <tr key={b.batchId} style={!b.pass ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700,fontFamily:"monospace",fontSize:12}}>{b.batchId}</td>
                  <td>{b.species}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{b.coreTemp}</td>
                  <td><span className={"cmp-badge " + (b.pass ? "cmp-ok" : "cmp-bad")}>{b.pass ? "PASS" : "FAIL"}</span></td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{b.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open.length === 0 && closed.length === 0 && !hist.loading && (
        <div className="cmp-empty">{hist.isHistorical ? "No fish-freezing batches recorded on " + cmpFriendly(hist.viewDate) + "." : "No batches in this session. Log an intake to start."}</div>
      )}
      {!hist.isHistorical && intakeOpen && (
        <CmpFormModal
          title="Log fish intake"
          desc="Capture species, supplier, batch ref, and which freezer. Time-in stamps automatically when you sign with your PIN."
          fields={[
            {key: "species", label: "Species", type: "text", placeholder: "Atlantic salmon / Tuna", required: true},
            {key: "supplier", label: "Supplier", type: "text", placeholder: "e.g. Sailbrand", required: true},
            {key: "batchRef", label: "Batch / invoice ref", type: "text", placeholder: "e.g. PBS-02312 · 4kg fillet", required: true},
            {key: "freezer", label: "Freezer", type: "text", placeholder: "e.g. F4", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitIntake}
          onCancel={() => setIntakeOpen(false)}
        />
      )}
      {!hist.isHistorical && outFor && (
        <CmpFormModal
          title={"Close out " + outFor}
          desc="Capture the core temperature reading. Anything warmer than −20°C will raise a corrective action."
          fields={[
            {key: "coreTemp", label: "Core temperature (°C)", type: "text", placeholder: "e.g. -20.4", required: true, hint: "Must be ≤ −20°C to pass."},
            {key: "actionNotes", label: "Corrective action (required if > −20°C)", type: "textarea", placeholder: "What did you do with the batch?"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitOut}
          onCancel={() => setOutFor(null)}
        />
      )}
    </div>
  );
}

// ---- Emergency Fridge ----
function CmpEmergencyFridge({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [incidents, setIncidents] = useState([]);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [checkFor, setCheckFor] = useState(null);
  const [disposalFor, setDisposalFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "EmergencyLog", "day");
  const visibleIncidents = hist.isHistorical ? (() => {
    const byId = {};
    hist.histRows.slice().reverse().forEach(r => {
      const id = String(r.IncidentId || "");
      if (!id) return;
      if (String(r.Event) === "raise") {
        byId[id] = {
          incidentId: id, unit: r.Unit || "", failureTime: r.FailureTime || "",
          initialTemp: r.InitialTemp, status: r.Status || "open",
          rows: [{event:"raise", ts: r._ts, temp: r.InitialTemp, notes: r.ActionNotes || "", init: cmpInitials(r.StaffName || "")}],
          correctiveActionId: r.CorrectiveActionId || "",
          disposalSigned: false
        };
      } else if (byId[id]) {
        byId[id].rows.push({
          event: r.Event, ts: r._ts,
          temp: r.CheckTemp || "", notes: r.ActionNotes || "",
          init: cmpInitials(r.StaffName || "")
        });
        if (String(r.Event) === "disposal") byId[id].disposalSigned = true;
        if (String(r.Event) === "close") byId[id].status = "resolved";
        if (String(r.Status) === "resolved") byId[id].status = "resolved";
      }
    });
    return Object.values(byId).sort((a,b) => (b.rows[0] && b.rows[0].ts || "").localeCompare(a.rows[0] && a.rows[0].ts || ""));
  })() : incidents;

  // Rehydrate from the last 14 days. Group all events by IncidentId.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "EmergencyLog", 14)
      .then(rows => {
        const byId = {};
        // Oldest-first so the timeline appends in order
        rows.slice().reverse().forEach(r => {
          const id = String(r.IncidentId || "");
          if (!id) return;
          if (String(r.Event) === "raise") {
            byId[id] = {
              incidentId: id, unit: r.Unit || "", failureTime: r.FailureTime || "",
              initialTemp: r.InitialTemp, status: r.Status || "open",
              rows: [{event:"raise", ts: r._ts, temp: r.InitialTemp, notes: r.ActionNotes || "", init: cmpInitials(r.StaffName || "")}],
              correctiveActionId: r.CorrectiveActionId || "",
              disposalSigned: false
            };
          } else if (byId[id]) {
            byId[id].rows.push({
              event: r.Event, ts: r._ts,
              temp: r.CheckTemp || "", notes: r.ActionNotes || "",
              init: cmpInitials(r.StaffName || "")
            });
            if (String(r.Event) === "disposal") byId[id].disposalSigned = true;
            if (String(r.Event) === "close") byId[id].status = "resolved";
            // Status column on later rows can also flip the incident
            if (String(r.Status) === "resolved") byId[id].status = "resolved";
          }
        });
        const arr = Object.values(byId).sort((a, b) =>
          (b.rows[0] && b.rows[0].ts || "").localeCompare(a.rows[0] && a.rows[0].ts || "")
        );
        setIncidents(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitRaise = (vals) => {
    setRaiseOpen(false);
    pinPrompt({
      prompt: "Raise emergency fridge incident",
      desc: "Your PIN starts the incident. 2-hourly monitoring required per FSMS §6.4.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-raise", {
          unit: vals.unit, failureTime: vals.failureTime,
          initialTemp: vals.initialTemp, actionNotes: vals.actionNotes
        }, staff.pin).then(d => {
          setIncidents(arr => [{
            incidentId: d && d.incidentId ? d.incidentId : "(pending)",
            unit: vals.unit, failureTime: vals.failureTime, initialTemp: vals.initialTemp,
            status: "open",
            rows: [{event:"raise", ts: new Date().toISOString(), temp: vals.initialTemp, notes: vals.actionNotes, init: staff.initials}],
            correctiveActionId: d && d.correctiveActionId ? d.correctiveActionId : ""
          }, ...arr]);
          cmpToast("Incident raised · " + (d && d.incidentId ? d.incidentId : "") + " · admin notified", "warn");
        }).catch(e => cmpToast("Raise failed: " + e.message, "error"));
      },
      onCancel: () => setRaiseOpen(true)
    });
  };

  const submitCheck = (vals) => {
    const id = checkFor;
    setCheckFor(null);
    pinPrompt({
      prompt: "Sign off temp check",
      desc: "Reading " + (vals.checkTemp || "—") + "°C on " + id,
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-check", {
          incidentId: id, checkTemp: vals.checkTemp, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            rows: [...x.rows, {event:"check", ts: new Date().toISOString(), temp: vals.checkTemp, notes: vals.actionNotes, init: staff.initials}]
          }) : x));
          cmpToast("Check logged on " + id, "success");
        }).catch(e => cmpToast("Check failed: " + e.message, "error"));
      },
      onCancel: () => setCheckFor(id)
    });
  };

  const submitDisposal = (vals) => {
    const id = disposalFor;
    setDisposalFor(null);
    pinPrompt({
      prompt: "Authorise stock disposal",
      desc: "4-hour rule disposal on " + id + ". Admin PIN only.",
      requiredRoles: ["Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-disposal", {
          incidentId: id, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            rows: [...x.rows, {event:"disposal", ts: new Date().toISOString(), temp: "", notes: vals.actionNotes, init: staff.initials}],
            disposalSigned: true
          }) : x));
          cmpToast("Stock disposal authorised by " + staff.name, "warn");
        }).catch(e => cmpToast("Disposal failed: " + e.message, "error"));
      },
      onCancel: () => setDisposalFor(id)
    });
  };

  const submitClose = (vals) => {
    const id = closeFor;
    setCloseFor(null);
    pinPrompt({
      prompt: "Close incident",
      desc: "Mark " + id + " resolved — fridge back in service.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-close", {
          incidentId: id, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            status: "resolved",
            rows: [...x.rows, {event:"close", ts: new Date().toISOString(), temp: "", notes: vals.actionNotes, init: staff.initials}]
          }) : x));
          cmpToast("Incident " + id + " closed", "success");
        }).catch(e => cmpToast("Close failed: " + e.message, "error"));
      },
      onCancel: () => setCloseFor(id)
    });
  };

  const open = visibleIncidents.filter(i => i.status === "open");
  const closed = visibleIncidents.filter(i => i.status !== "open");

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Emergency Fridge — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card cmp-coral">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Emergency Fridge</h3>
            <div className="cmp-sub">Incident-driven workflow. 2-hourly checks per FSMS §6.4. Over 4h = 4-hour-rule disposal (admin sign-off).</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-danger" onClick={() => setRaiseOpen(true)}>+ Raise incident</button>}
          </div>
        </div>
        <div className="cmp-card" style={{background:"var(--cm-sun-soft)",border:"1px solid var(--cm-amber)",margin:"12px 0 0",padding:"10px 14px"}}>
          <div style={{fontSize:12,lineHeight:1.7,color:"var(--cm-nori-70)"}}>
            <strong>&lt; 2h above +8°C:</strong> move stock to a verified-working secondary fridge after checking its temp.<br/>
            <strong>2–4h above +8°C:</strong> high-risk stock may be sold but <strong>must not be moved or held to next day</strong>. Label all affected stock with the exact time temp rose &gt;+8°C.<br/>
            <strong>&gt; 4h above +8°C:</strong> all remaining stock <strong>discarded</strong>. Not returned to a working fridge or sold. Recorded in Waste / Corrective Action Log.<br/>
            If the unit cannot maintain ≤+5°C, clear it, mark "Do Not Use", out of service until repaired.
          </div>
        </div>
      </div>
      {open.length === 0 && closed.length === 0 && !hist.loading && (
        <div className="cmp-empty">{hist.isHistorical ? "No emergency-fridge incidents recorded on " + cmpFriendly(hist.viewDate) + "." : "No incidents in this session. Tap Raise above to start."}</div>
      )}
      {open.map(i => (
        <div key={i.incidentId} className="cmp-card cmp-coral">
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0}}>
              <h4 style={{fontSize:16,marginBottom:4}}>{i.incidentId} · {i.unit}</h4>
              <div className="cmp-sub" style={{marginBottom:6}}>Initial temp <strong>{i.initialTemp}°C</strong> · {i.rows.length} event(s){i.correctiveActionId ? " · CA " + i.correctiveActionId : ""}</div>
            </div>
            <span className="cmp-chip cmp-bad"><span className="cmp-dot" />open</span>
          </div>
          {i.rows.map((r, ri) => (
            <div key={ri} style={{padding:"8px 0",borderTop: ri > 0 ? "1px dashed var(--cm-nori-08)" : "none"}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div style={{fontWeight:700,fontSize:13}}>{r.event === "raise" ? "Discovery" : r.event === "check" ? "Temp check" : r.event === "disposal" ? "Stock disposal" : "Close"}</div>
                <div style={{fontSize:11,color:"var(--cm-nori-60)"}}>{cmpFriendly(r.ts)} {new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</div>
              </div>
              <div style={{fontSize:12,marginTop:4}}>{r.temp ? <strong>{r.temp}°C · </strong> : null}{r.notes}</div>
              <div style={{fontSize:11,marginTop:4}}>by <span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"1px 5px",borderRadius:3,fontWeight:800}}>{r.init}</span></div>
            </div>
          ))}
          {!hist.isHistorical && (
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
              <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => setCheckFor(i.incidentId)}>+ Log check</button>
              <button className="cmp-btn cmp-btn-danger cmp-btn-sm" onClick={() => setDisposalFor(i.incidentId)}>Stock disposal (Admin)</button>
              <button className="cmp-btn cmp-btn-wasabi cmp-btn-sm" onClick={() => setCloseFor(i.incidentId)}>Close incident</button>
            </div>
          )}
        </div>
      ))}
      {closed.length > 0 && (
        <>
          <h3 style={{fontSize:14,margin:"16px 0 8px",color:"var(--cm-nori-60)"}}>Closed (this session)</h3>
          {closed.map(i => (
            <div key={i.incidentId} className="cmp-card cmp-green">
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div>
                  <strong>{i.incidentId} · {i.unit}</strong>
                  <div style={{fontSize:12,color:"var(--cm-nori-70)",marginTop:3}}>{i.rows.length} events · resolved</div>
                </div>
                <span className="cmp-chip cmp-ok"><span className="cmp-dot" />resolved</span>
              </div>
            </div>
          ))}
        </>
      )}
      {raiseOpen && (
        <CmpFormModal
          title="Raise emergency fridge incident"
          desc="Capture which unit, when failure was discovered, and the initial temp + initial action taken."
          fields={[
            {key: "unit", label: "Unit affected", type: "text", placeholder: "e.g. F2 Prep Fridge", required: true},
            {key: "failureTime", label: "Time failure discovered (HH:MM)", type: "time", required: true, default: new Date().toTimeString().slice(0,5)},
            {key: "initialTemp", label: "Initial temp (°C)", type: "text", placeholder: "e.g. 9.4", required: true},
            {key: "actionNotes", label: "Initial action taken", type: "textarea", placeholder: "Stock segregated to F3 · seal/airflow checked · compressor not running...", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitRaise}
          onCancel={() => setRaiseOpen(false)}
        />
      )}
      {checkFor && (
        <CmpFormModal
          title={"Log check on " + checkFor}
          desc="Capture the current temp reading and any action notes."
          fields={[
            {key: "checkTemp", label: "Current temp (°C)", type: "text", placeholder: "e.g. 7.8", required: true},
            {key: "actionNotes", label: "Notes", type: "textarea", placeholder: "What you observed; any stock movement or labelling."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitCheck}
          onCancel={() => setCheckFor(null)}
        />
      )}
      {disposalFor && (
        <CmpFormModal
          title={"Stock disposal · " + disposalFor}
          desc="Admin PIN required. Capture what was discarded and the reason (4-hour rule)."
          fields={[
            {key: "actionNotes", label: "Disposal notes (required)", type: "textarea", placeholder: "Full freezer contents discarded (~4kg mixed fish). Waste log updated.", required: true}
          ]}
          submitLabel="Continue → Admin PIN"
          onSubmit={submitDisposal}
          onCancel={() => setDisposalFor(null)}
        />
      )}
      {closeFor && (
        <CmpFormModal
          title={"Close incident " + closeFor}
          desc="Confirm the unit is back in service. Notes capture the resolution."
          fields={[
            {key: "actionNotes", label: "Resolution notes", type: "textarea", placeholder: "Compressor repaired by contractor · running stable at 4°C · stock returned."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitClose}
          onCancel={() => setCloseFor(null)}
        />
      )}
    </div>
  );
}

// ---- HACCP Daily ----
const CMP_HACCP_OPENING = [
  "Hot/cold water available",
  "Fridges & display ≤ 5°C / freezers ≤ −18°C — readings recorded",
  "Probe + pH calibrated/working",
  "Personal hygiene OK (uniform, hair, jewellery, illness)",
  "Hand wash basin stocked (soap, paper, hot water)",
  "Sanitiser at correct dilution + contact time",
  "Pest evidence check around prep area",
  "Allergen kit available (separate utensils, signage)"
];
const CMP_HACCP_CLOSING = [
  "All food date-labelled & rotated (FIFO)",
  "All surfaces sanitised, contact time observed",
  "Bins emptied, lids closed, area tidy",
  "Fridges & freezers final reading recorded",
  "Hand wash basin restocked",
  "All probes returned to charger / rack"
];
const CMP_HACCP_FRIDGES = [
  {id: "F1", name: "Walk-in fridge",  target: "≤+5°C"},
  {id: "F2", name: "Prep fridge",     target: "≤+5°C"},
  {id: "F3", name: "Display fridge",  target: "≤+5°C"},
  {id: "F4", name: "Fish freezer",    target: "≤−20°C"},
  {id: "F5", name: "Freezer 2",       target: "≤−18°C"}
];
function CmpHaccpDaily({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [opening, setOpening] = useState({});
  const [closing, setClosing] = useState({});
  const [fridges, setFridges] = useState({});
  const [deliveries, setDeliveries] = useState([]);
  const [cooks, setCooks] = useState([]);
  const [phBatches, setPhBatches] = useState([]);
  const [fridgeForm, setFridgeForm] = useState(null); // {fridgeId, slot, fridgeLabel}
  const [deliveryForm, setDeliveryForm] = useState(false);
  const [cookForm, setCookForm] = useState(false);
  const [phForm, setPhForm] = useState(false);
  const [chefSig, setChefSig] = useState(null);
  // viewDate drives the entire HACCP rehydrate + write pipeline. Today
  // = editable; any past date = read-only historical view (calendar
  // picker). HACCP has its own reader (cmpFetchHaccpDay) — distinct
  // from useCmpHistoryView since it returns a structured sheet object,
  // not a flat row array.
  const [viewDate, setViewDateRaw] = useState(() => cmpTodayIso());
  const isHistorical = viewDate !== cmpTodayIso();
  const setViewDate = (d) => setViewDateRaw(d || cmpTodayIso());
  const today = viewDate;

  // Rehydrate the day's HACCP state from the audit trail. State Reconstruction
  // memory: a "submitted/locked" UI must hydrate every form field from the
  // server response, not just paint a banner. Refresh = paint exactly what
  // the sheet has. Historical view picks a past date via the calendar.
  useEffect(() => {
    // Reset state when switching dates so a stale today's view never
    // bleeds into a historical view (or vice versa). Also close any
    // in-flight modal so a half-typed delivery/cook/pH/fridge form
    // can't get submitted against the wrong date.
    setOpening({}); setClosing({}); setFridges({});
    setDeliveries([]); setCooks([]); setPhBatches([]); setChefSig(null);
    setFridgeForm(null); setDeliveryForm(false); setCookForm(false); setPhForm(false);
    cmpFetchHaccpDay(backend, site.key, today)
      .then(d => {
        if (!d || !d.sheet) return;
        const sh = d.sheet;
        // Opening / closing checks
        const openObj = {};
        Object.keys(sh.openingChecks || {}).forEach(idx => {
          const stamp = sh.openingChecks[idx];
          if (stamp) openObj[idx] = {init: cmpInitials(stamp.by || ""), ts: stamp.at || ""};
        });
        setOpening(openObj);
        const closeObj = {};
        Object.keys(sh.closingChecks || {}).forEach(idx => {
          const stamp = sh.closingChecks[idx];
          if (stamp) closeObj[idx] = {init: cmpInitials(stamp.by || ""), ts: stamp.at || ""};
        });
        setClosing(closeObj);
        // Per-fridge AM/PM
        const perFridge = sh.storageTempsPerFridge || {};
        const fridgeObj = {};
        Object.keys(perFridge).forEach(fId => {
          fridgeObj[fId] = {};
          ["am","pm"].forEach(slot => {
            const v = perFridge[fId][slot];
            if (v) fridgeObj[fId][slot] = {value: v.temp, init: cmpInitials((v.initials && v.initials.by) || "")};
          });
        });
        setFridges(fridgeObj);
        // CCP-1 deliveries
        setDeliveries((sh.deliveries || []).map(dItem => ({
          time: dItem.time || "",
          supplier: dItem.supplier || "",
          invoice: dItem.invoice || "",
          item: dItem.item || "",
          temp: dItem.temp || "",
          pkg: dItem.pkg || "S",
          veh: dItem.veh || "S",
          init: cmpInitials((dItem.initials && dItem.initials.by) || "")
        })));
        // CCP-5 cooking
        setCooks((sh.cookingEvents || []).map(c => ({
          item: c.item || "",
          first: c.first,
          last: c.last,
          init: cmpInitials((c.initials && c.initials.by) || "")
        })));
        // CCP-2 pH batches — backend stores under ricePh keyed by field
        const phRows = [];
        Object.keys(sh.ricePh || {}).forEach(k => {
          const entry = sh.ricePh[k];
          if (!entry || typeof entry === "string") return;
          phRows.push({
            batchNo: entry.batchNo || phRows.length + 1,
            ph: entry.ph,
            action: entry.action || "",
            init: cmpInitials((entry.initials && entry.initials.by) || ""),
            ts: (entry.initials && entry.initials.at) || ""
          });
        });
        phRows.sort((a, b) => (a.batchNo || 0) - (b.batchNo || 0));
        setPhBatches(phRows);
        // Chef sign-off
        if (d.signOff && d.signOff.at) {
          setChefSig({init: cmpInitials(d.signOff.name || ""), name: d.signOff.name || "", ts: d.signOff.at});
        }
      })
      .catch(() => {/* silent — form starts blank */});
  }, [backend, site.key, today]);

  // Opening/closing ticks map to handleHaccpEvent's section="opening"|"closing"
  // contract: {section, field, value:true}. Backend keys state.openingChecks /
  // state.closingChecks by `field` (the tick index as a string).
  const tickItem = (kind, idx, label) => {
    if (isHistorical) return;
    pinPrompt({
      prompt: "Initial this check",
      desc: kind + " · " + label,
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: kind, field: String(idx), value: true,
          extra: {checkLabel: label}, date: today
        }, staff.pin).then(() => {
          const setter = kind === "opening" ? setOpening : setClosing;
          setter(s => Object.assign({}, s, {[idx]: {init: staff.initials, ts: new Date().toISOString()}}));
          cmpToast("✓ " + label, "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  // Per-fridge AM/PM readings via FormModal (Phase 2 v1.2, 2026-04-29).
  // Backend `storageTemp` section accepts `field:"<fridgeId>_<slot>"` and
  // writes to `state.storageTempsPerFridge[fridgeId][slot]` so 5 fridges
  // no longer overwrite each other. window.prompt replaced with the
  // styled CmpFormModal (tablet-friendly).
  const submitFridge = (vals) => {
    const f = fridgeForm;
    setFridgeForm(null);
    if (isHistorical) return;
    const num = parseFloat(vals.temp);
    if (isNaN(num)) { cmpToast("Reading must be numeric", "error"); return; }
    pinPrompt({
      prompt: "Log fridge reading",
      desc: f.fridgeLabel + " · " + f.slot.toUpperCase() + " · " + num + "°C",
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "storageTemp",
          field: f.fridgeId + "_" + f.slot,
          value: {temp: num, equipment: f.fridgeId, equipmentName: f.fridgeLabel},
          date: today
        }, staff.pin).then(() => {
          setFridges(s => Object.assign({}, s, {
            [f.fridgeId]: Object.assign({}, s[f.fridgeId] || {}, {[f.slot]: {value: num, init: staff.initials}})
          }));
          cmpToast("Logged " + f.fridgeLabel + " " + f.slot.toUpperCase() + ": " + num + "°C", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setFridgeForm(f)
    });
  };
  const logFridge = (fridgeId, slot, fridgeLabel) => { if (isHistorical) return; setFridgeForm({fridgeId, slot, fridgeLabel}); };

  // CCP-1 Goods Receipt — chilled ≤8°C, frozen ≤−15°C, fish 0 to +3°C.
  const submitDelivery = (vals) => {
    setDeliveryForm(false);
    if (isHistorical) return;
    pinPrompt({
      prompt: "Log delivery",
      desc: vals.supplier + " · " + vals.item + " · " + vals.temp + "°C",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "delivery",
          field: "add",
          value: {
            time: vals.time, supplier: vals.supplier, invoice: vals.invoice,
            item: vals.item, cf: vals.cf, temp: vals.temp,
            method: vals.method || "Hard probe",
            pkg: vals.pkg || "S", veh: vals.veh || "S"
          },
          date: today
        }, staff.pin).then(() => {
          setDeliveries(arr => [...arr, {
            time: vals.time, supplier: vals.supplier, invoice: vals.invoice,
            item: vals.item, cf: vals.cf, temp: vals.temp,
            pkg: vals.pkg || "S", veh: vals.veh || "S", init: staff.initials
          }]);
          cmpToast("Delivery logged · " + vals.supplier, "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setDeliveryForm(true)
    });
  };

  // CCP-5 Cook batch — first + last probe ≥75°C.
  const submitCook = (vals) => {
    setCookForm(false);
    if (isHistorical) return;
    const first = parseFloat(vals.first);
    const last = parseFloat(vals.last);
    if (isNaN(first) || isNaN(last)) { cmpToast("Both probes must be numeric", "error"); return; }
    pinPrompt({
      prompt: "Log cook batch",
      desc: vals.item + " · first " + first + "°C / last " + last + "°C",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "cooking",
          field: "add",
          value: {item: vals.item, first: first, last: last},
          date: today
        }, staff.pin).then(() => {
          setCooks(arr => [...arr, {item: vals.item, first: first, last: last, init: staff.initials}]);
          if (first < 75 || last < 75) cmpToast("⚠ Below 75°C — re-cook + re-probe before service", "warn");
          else cmpToast("Cook batch logged", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setCookForm(true)
    });
  };

  // CCP-2 Sushi rice acidification — pH ≤ 4.3.
  const submitPh = (vals) => {
    setPhForm(false);
    if (isHistorical) return;
    const num = parseFloat(vals.ph);
    if (isNaN(num)) { cmpToast("pH must be numeric", "error"); return; }
    const outOfRange = num >= 4.3;
    if (outOfRange && !String(vals.action || "").trim()) {
      cmpToast("pH ≥ 4.3 needs a corrective action note", "error");
      setPhForm(true);
      return;
    }
    pinPrompt({
      prompt: "Log rice batch pH",
      desc: "Batch " + (phBatches.length + 1) + " · pH " + num,
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        const batchNo = phBatches.length + 1;
        cmpHaccpEvent(backend, site.key, {
          section: "ricePh",
          field: "batch_" + batchNo,
          value: {batchNo: batchNo, ph: num, action: vals.action || ""},
          date: today
        }, staff.pin).then(() => {
          setPhBatches(arr => [...arr, {batchNo: batchNo, ph: num, action: vals.action || "", init: staff.initials, ts: new Date().toISOString()}]);
          if (outOfRange) cmpToast("pH " + num + " · corrective action raised", "warn");
          else cmpToast("Batch " + batchNo + " · pH " + num + " ✓", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setPhForm(true)
    });
  };

  const signChef = () => {
    if (isHistorical) return;
    pinPrompt({
      prompt: "Chef sign-off (close day)",
      desc: "Locks all HACCP entries for today. BOH or Admin PIN.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpSign(backend, site.key, {date: today}, staff.pin).then(() => {
          setChefSig({init: staff.initials, name: staff.name, ts: new Date().toISOString()});
          cmpToast("HACCP day locked — " + staff.name + " signed off", "success");
        }).catch(e => cmpToast("Sign failed: " + e.message, "error"));
      }
    });
  };

  const renderTicks = (kind, list, state) => (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {list.map((label, i) => {
        const tick = state[i];
        return (
          <div key={i} className="cmp-card" style={{padding:"10px 12px",margin:0,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
            <div style={{flex:1,fontSize:13}}>{tick ? "✓ " : "○ "}{label}</div>
            {tick ? (
              <span style={{fontSize:11,background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontFamily:"gelica,serif",fontWeight:800}}>{tick.init}</span>
            ) : (
              <button className="cmp-btn cmp-btn-primary cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => tickItem(kind, i, label)}>Tick (PIN)</button>
            )}
          </div>
        );
      })}
    </div>
  );

  // Build a "signedBy" object the historical banner understands so it
  // shows the chef who closed the picked day, not the live signer.
  const histSignedBy = isHistorical && chefSig
    ? {name: chefSig.name, ts: chefSig.ts}
    : null;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={viewDate} onClear={() => setViewDate(null)} backLabel="← Back to today" />
      <CmpHistoricalSignerBanner date={viewDate} formLabel="HACCP — historical view" signedBy={histSignedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Daily HACCP</h3>
          <CmpCalendarButton onPick={setViewDate} viewingDate={viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Opening checks · CCP-6 fridge readings (AM + PM) · closing checks · chef sign-off locks the day.</div>
      </div>
      {chefSig && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Day closed</h4>
          <div style={{fontSize:13}}>Signed by <strong>{chefSig.name}</strong> at {new Date(chefSig.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}. Entries locked.</div>
        </div>
      )}
      <div className="cmp-card">
        <h4>Opening checks</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>{Object.keys(opening).length}/{CMP_HACCP_OPENING.length} complete</div>
        {renderTicks("opening", CMP_HACCP_OPENING, opening)}
      </div>
      <div className="cmp-card">
        <h4>CCP-6 · Chilled storage AM + PM</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Fridge ≤ +5°C, freezer ≤ −18°C, fish freezer ≤ −20°C.</div>
        <table className="cmp-row-table">
          <thead><tr><th>Equipment</th><th>Target</th><th>AM</th><th>PM</th></tr></thead>
          <tbody>
            {CMP_HACCP_FRIDGES.map(f => {
              const entry = fridges[f.id] || {};
              const renderSlot = (slot) => {
                const v = entry[slot];
                if (v) return <div style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{v.value}°C <span style={{fontSize:10,fontFamily:"gelica,serif",fontWeight:800,color:"var(--cm-nori-60)",marginLeft:4}}>{v.init}</span></div>;
                return <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => logFridge(f.id, slot, f.name)}>Log {slot.toUpperCase()}</button>;
              };
              return (
                <tr key={f.id}>
                  <td style={{fontWeight:700}}>{f.name}</td>
                  <td style={{fontSize:12,color:"var(--cm-nori-60)"}}>{f.target}</td>
                  <td>{renderSlot("am")}</td>
                  <td>{renderSlot("pm")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="cmp-card">
        <h4>CCP-1 · Goods Receipt</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Chilled ≤+8°C · Frozen ≤−15°C · Fish 0 to +3°C. Probe one chilled + one frozen item per supplier per delivery.</div>
        {deliveries.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Time</th><th>Supplier</th><th>Item</th><th>°C</th><th>Pkg</th><th>Veh</th><th>By</th></tr></thead>
            <tbody>
              {deliveries.map((d, i) => (
                <tr key={i}>
                  <td style={{fontFamily:"monospace",fontSize:11,fontWeight:700}}>{d.time}</td>
                  <td style={{fontWeight:700}}>{d.supplier}</td>
                  <td style={{fontSize:12}}>{d.item}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{d.temp}</td>
                  <td><span className={"cmp-badge " + (d.pkg === "S" ? "cmp-ok" : "cmp-bad")}>{d.pkg}</span></td>
                  <td><span className={"cmp-badge " + (d.veh === "S" ? "cmp-ok" : "cmp-bad")}>{d.veh}</span></td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{d.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setDeliveryForm(true)}>+ Add delivery</button>
      </div>

      <div className="cmp-card">
        <h4>CCP-5 · Cooking (poultry / prawns)</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Core 75°C for 30s. Probe first AND last item every batch.</div>
        {cooks.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Item</th><th>First</th><th>Last</th><th>By</th></tr></thead>
            <tbody>
              {cooks.map((c, i) => (
                <tr key={i}>
                  <td style={{fontWeight:700}}>{c.item}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{c.first}°C {c.first >= 75 ? <span className="cmp-badge cmp-ok">OK</span> : <span className="cmp-badge cmp-bad">⚠</span>}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{c.last}°C {c.last >= 75 ? <span className="cmp-badge cmp-ok">OK</span> : <span className="cmp-badge cmp-bad">⚠</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{c.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setCookForm(true)}>+ Add cook batch</button>
      </div>

      <div className="cmp-card">
        <h4>CCP-2 · Sushi rice acidification (pH ≤ 4.3)</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Every batch logged individually. ≥4.3 auto-raises a corrective action.</div>
        {phBatches.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Batch</th><th>pH</th><th>Status</th><th>Logged</th><th>By</th></tr></thead>
            <tbody>
              {phBatches.map((b, i) => (
                <tr key={i} style={b.ph >= 4.3 ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>Batch {b.batchNo}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{b.ph}</td>
                  <td><span className={"cmp-badge " + (b.ph >= 4.3 ? "cmp-bad" : "cmp-ok")}>{b.ph >= 4.3 ? "⚠ Over 4.3" : "OK"}</span></td>
                  <td style={{fontSize:11,color:"var(--cm-nori-60)"}}>{new Date(b.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{b.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setPhForm(true)}>+ Log pH for new batch</button>
      </div>

      <div className="cmp-card">
        <h4>Closing checks</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>{Object.keys(closing).length}/{CMP_HACCP_CLOSING.length} complete</div>
        {renderTicks("closing", CMP_HACCP_CLOSING, closing)}
      </div>
      {!chefSig && !isHistorical && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <h3 style={{marginBottom:4}}>Chef sign-off</h3>
          <div className="cmp-sub">Locks the HACCP day. BOH or Admin PIN.</div>
          <button className="cmp-btn cmp-btn-primary" onClick={signChef}>Sign off &amp; lock day</button>
        </div>
      )}
      {fridgeForm && (
        <CmpFormModal
          title={"Log " + fridgeForm.fridgeLabel + " " + fridgeForm.slot.toUpperCase()}
          desc={"Target " + (fridgeForm.fridgeId === "F4" ? "≤ −20°C" : fridgeForm.fridgeId === "F5" ? "≤ −18°C" : "≤ +5°C") + ". Reading is captured against your PIN on the next step."}
          fields={[{key: "temp", label: "Temperature (°C)", type: "text", placeholder: "e.g. 3.4 or -20.1", required: true}]}
          submitLabel="Continue → PIN"
          onSubmit={submitFridge}
          onCancel={() => setFridgeForm(null)}
        />
      )}
      {deliveryForm && (
        <CmpFormModal
          title="Log delivery"
          desc="Capture the supplier, item, and temperature reading. Probe one chilled + one frozen item per supplier."
          fields={[
            {key: "time", label: "Time received (HH:MM)", type: "time", required: true, default: new Date().toTimeString().slice(0,5)},
            {key: "supplier", label: "Supplier", type: "text", placeholder: "e.g. Sailbrand", required: true},
            {key: "invoice", label: "Invoice / batch ref", type: "text", placeholder: "e.g. PBS-02281"},
            {key: "item", label: "Item", type: "text", placeholder: "e.g. Atlantic salmon fillet 6kg", required: true},
            {key: "cf", label: "Chilled / Frozen / Ambient", type: "select", options: ["Chilled","Frozen","Ambient"], default: "Chilled", required: true},
            {key: "temp", label: "Temperature (°C)", type: "text", placeholder: "e.g. 3.2", required: true},
            {key: "pkg", label: "Packaging", type: "select", options: [{value:"S",label:"Satisfactory"},{value:"U",label:"Unsatisfactory"}], default: "S"},
            {key: "veh", label: "Vehicle", type: "select", options: [{value:"S",label:"Satisfactory"},{value:"U",label:"Unsatisfactory"}], default: "S"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitDelivery}
          onCancel={() => setDeliveryForm(false)}
        />
      )}
      {cookForm && (
        <CmpFormModal
          title="Log cook batch"
          desc="Probe first AND last item of the batch. Both must hit ≥75°C for 30s."
          fields={[
            {key: "item", label: "Item", type: "text", placeholder: "e.g. Crispy chicken — fryer", required: true},
            {key: "first", label: "First probe (°C)", type: "text", placeholder: "e.g. 82", required: true},
            {key: "last", label: "Last probe (°C)", type: "text", placeholder: "e.g. 79", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitCook}
          onCancel={() => setCookForm(false)}
        />
      )}
      {phForm && (
        <CmpFormModal
          title="Log rice batch pH"
          desc="Target < 4.3. Anything ≥ 4.3 raises a corrective action — describe what you did."
          fields={[
            {key: "ph", label: "pH reading", type: "text", placeholder: "e.g. 3.9", required: true, hint: "Target: pH < 4.3"},
            {key: "action", label: "Corrective action (required if ≥ 4.3)", type: "textarea", placeholder: "Re-acidified, re-tested, batch released at pH 3.8…"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitPh}
          onCancel={() => setPhForm(false)}
        />
      )}
    </div>
  );
}

// ---- Declaration / FSMS Acceptance ----
// Wave 6.5 (2026-04-30): the per-section commitment list was lifted from
// the hardcoded CMP_DECLARATION_DOCS map into the admin-managed
// `declaration-commitments` surface. Doc title + intro are rare-change
// EHO copy and stay hardcoded here. The frontend regroups the flat
// surface items by (docType, sectionTitle) preserving items-array order.
const CMP_DECLARATION_VERSION = "v1.0";
const CMP_DECLARATION_HEADERS = {
  BOH: {
    title: "Declaration of Competency & Food Safety — BOH",
    intro: "As a Back of House team member at Rolled, I confirm I have been trained in the procedures below and that I will uphold these standards as a legal requirement for food safety at Rolled."
  },
  FOH: {
    title: "Declaration of Competency & Food Safety — FOH",
    intro: "As a Front of House team member at Rolled, I confirm I have been trained in the procedures below and that I will uphold these standards as a legal requirement for food safety at Rolled."
  }
};

// Rebuilds the {sections:[{title, commitments:[{label,detail}]}]} tree
// from the flat surface items. Section order = first-appearance order.
function cmpBuildDeclarationDoc(items, docType) {
  const header = CMP_DECLARATION_HEADERS[docType] || {title: docType, intro: ""};
  const sectionsByTitle = {};
  const sectionOrder = [];
  (items || []).forEach(it => {
    if (!it || it.docType !== docType) return;
    const title = String(it.sectionTitle || "Commitments");
    if (!sectionsByTitle[title]) {
      sectionsByTitle[title] = {title, commitments: []};
      sectionOrder.push(title);
    }
    sectionsByTitle[title].commitments.push({
      id: it.id, label: it.label || "", detail: it.detail || ""
    });
  });
  return {
    title: header.title, intro: header.intro,
    sections: sectionOrder.map(t => sectionsByTitle[t])
  };
}

function CmpDeclaration({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [docType, setDocType] = useState("BOH");
  const [config, setConfig] = useState(null);
  const [ack, setAck] = useState({});
  const [signed, setSigned] = useState(null);
  const [historyByPinPrefix, setHistoryByPinPrefix] = useState({});
  const hist = useCmpHistoryView(backend, site.key, "DeclarationLog", "day");

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load declaration: " + e.message, "error"));
  }, [backend, site.key]);

  const commitmentItems = useMemo(
    () => config ? cmpResolveSurfaceItems(config, "declaration-commitments", site.key) : [],
    [config, site.key]
  );
  const doc = useMemo(() => cmpBuildDeclarationDoc(commitmentItems, docType), [commitmentItems, docType]);

  // Filter historical rows to staff-sign + matching docType so the
  // banner reflects "who signed this declaration on the picked day".
  const histDeclSign = hist.isHistorical
    ? hist.histRows.filter(r => String(r.Event) === "staff-sign" && String(r.DocumentType) === docType)
    : [];
  const totalItems = doc.sections.reduce((n, s) => n + s.commitments.length, 0);
  const ackedCount = Object.values(ack).filter(Boolean).length;
  const allAcked = totalItems > 0 && ackedCount === totalItems;

  // Rehydrate the latest staff-sign per PIN-prefix for this docType. Used
  // so a returning user sees "Already signed by you on [date]" rather than
  // a blank ack list. Frontend can't see the full PIN, only the prefix —
  // the user's identity is confirmed when they next PIN-gate any action.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "DeclarationLog", 365)
      .then(rows => {
        const byPin = {};
        rows.forEach(r => {
          if (String(r.Event) !== "staff-sign") return;
          if (String(r.DocumentType) !== docType) return;
          const pp = String(r.StaffPin || "");
          if (!pp || byPin[pp]) return;
          byPin[pp] = {
            name: r.StaffName || "", ts: r._ts, nextDueAt: r.NextDueAt || ""
          };
        });
        setHistoryByPinPrefix(byPin);
      })
      .catch(() => {});
  }, [backend, site.key, docType]);

  const sign = () => {
    if (!allAcked) { cmpToast("Tick every commitment before signing", "error"); return; }
    pinPrompt({
      prompt: "Sign declaration",
      desc: "Your PIN is your signature. Annual refresh required.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "declaration-sign", {
          documentType: docType, declarationVersion: CMP_DECLARATION_VERSION, fsmsVersion: ""
        }, staff.pin).then(d => {
          setSigned({name: staff.name, init: staff.initials, ts: new Date().toISOString(), nextDueAt: d && d.nextDueAt});
          setAck({});
          cmpToast("Signed by " + staff.name + " — pending manager countersign", "success");
        }).catch(e => cmpToast("Sign failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Declaration — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>{doc.title}</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">{doc.intro}</div>
        <div className="cmp-tabs" style={{marginTop:10,maxWidth:300}}>
          {["BOH","FOH"].map(t => (
            <button key={t} className={docType === t ? "cmp-active" : ""} onClick={() => { setDocType(t); setAck({}); setSigned(null); }}>{t}</button>
          ))}
        </div>
      </div>
      {hist.isHistorical && (
        <div className="cmp-card">
          <h3>{docType} signs on {cmpFriendly(hist.viewDate)}</h3>
          {histDeclSign.length === 0 && !hist.loading && <div className="cmp-empty">No {docType} declarations signed on this day.</div>}
          {histDeclSign.map((r, i) => (
            <div key={i} className="cmp-doc-row">
              <div className="cmp-doc-icon">✍️</div>
              <div className="cmp-doc-body">
                <div className="cmp-doc-name">{r.StaffName}</div>
                <div className="cmp-doc-meta">{new Date(r._ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} · v{r.DeclarationVersion || "—"} · next due {cmpFriendly(r.NextDueAt)}</div>
              </div>
              <div className="cmp-doc-right"><span className="cmp-chip cmp-ok"><span className="cmp-dot" />signed</span></div>
            </div>
          ))}
        </div>
      )}
      {!hist.isHistorical && signed && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Signed by {signed.name}</h4>
          <div style={{fontSize:13}}>at {new Date(signed.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}{signed.nextDueAt ? " · next due " + cmpFriendly(signed.nextDueAt) : ""}. Manager countersign in admin Compliance Overview.</div>
        </div>
      )}
      {!hist.isHistorical && <div className="cmp-card">
        {ackedCount > 0 && ackedCount < totalItems && (
          <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",margin:"0 0 10px",padding:"8px 12px"}}>
            <div style={{fontSize:12}}>⏳ <strong>{ackedCount}/{totalItems} ticked.</strong> Ticks live on this device until you sign with your PIN.</div>
          </div>
        )}
        {doc.sections.map((sec, si) => (
          <div key={si} style={{marginBottom:14}}>
            <h5 style={{fontSize:11,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--cm-nori-60)",marginBottom:8,fontWeight:800}}>{sec.title}</h5>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {sec.commitments.map((c, i) => {
                const k = si + "/" + i;
                const ticked = !!ack[k];
                return (
                  <div key={i} onClick={() => { if (!signed) setAck(a => Object.assign({}, a, {[k]: !a[k]})); }}
                    style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",border:"1px solid " + (ticked ? "var(--cm-green)" : "var(--cm-nori-08)"),borderRadius:10,cursor:signed?"default":"pointer",background: ticked ? "var(--cm-green-soft)" : "var(--cm-rice)"}}>
                    <div style={{width:24,height:24,borderRadius:6,border:"1.5px solid " + (ticked ? "var(--cm-green)" : "var(--cm-nori-30)"),background: ticked ? "var(--cm-green)" : "transparent",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,flexShrink:0}}>{ticked ? "✓" : ""}</div>
                    <div style={{flex:1,fontSize:13,lineHeight:1.4}}><strong>{c.label}:</strong> {c.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>}
      {!hist.isHistorical && !signed && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <div className="cmp-sub" style={{marginBottom:12}}>{ackedCount}/{totalItems} ticked. Your PIN signs the {docType} declaration · annual refresh.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
            <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => cmpOpenPdf({title:CMP_FSMS_SUMMARY.title,meta:CMP_FSMS_SUMMARY.meta,body:CMP_FSMS_SUMMARY.body})}>📕 Open the FSMS to read it now</button>
            <button className="cmp-btn cmp-btn-primary" onClick={sign} disabled={!allAcked}>{allAcked ? "Sign with my PIN" : "Tick every item to enable"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Corrective Actions ----
function CmpCorrectiveActions({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");
  const [formOpen, setFormOpen] = useState(false);
  const [resolveFor, setResolveFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "CorrectiveActions", "day");
  const visibleItems = hist.isHistorical
    ? hist.histRows.map(r => ({
        id: r.IncidentId || "(pending)",
        source: r.Source || "Ad hoc raise",
        site: site.key, ts: r._ts,
        desc: r.Description || "",
        action: r.Action || "",
        flagged: r.RaisedByName || "",
        status: r.Status || "open",
        resolvedBy: r.ResolvedByName || "",
        resolvedAt: r.ResolvedAt || "",
        resolutionAction: r.ResolutionAction || ""
      }))
    : items;

  // Rehydrate the last 14 days of corrective actions, every status.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "CorrectiveActions", 14)
      .then(rows => {
        const arr = rows.map(r => ({
          id: r.IncidentId || "(pending)",
          source: r.Source || "Ad hoc raise",
          site: site.key, ts: r._ts,
          desc: r.Description || "",
          action: r.Action || "",
          flagged: r.RaisedByName || "",
          status: r.Status || "open",
          resolvedBy: r.ResolvedByName || "",
          resolvedAt: r.ResolvedAt || "",
          resolutionAction: r.ResolutionAction || ""
        }));
        setItems(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitRaise = (vals) => {
    setFormOpen(false);
    pinPrompt({
      prompt: "Sign off corrective action",
      desc: "Confirm with your PIN to log this concern.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-raise", {
          description: vals.description, action: vals.action
        }, s.pin).then(d => {
          setItems(arr => [{
            id: d && d.incidentId ? d.incidentId : "(pending)",
            source: "Ad hoc raise", site: site.key, ts: new Date().toISOString(),
            desc: vals.description, action: vals.action,
            flagged: s.name, status: "open"
          }, ...arr]);
          cmpToast("Corrective action raised", "warn");
        }).catch(e => cmpToast("Raise failed: " + e.message, "error"));
      },
      onCancel: () => setFormOpen(true)
    });
  };

  const submitResolve = (vals) => {
    const id = resolveFor;
    setResolveFor(null);
    pinPrompt({
      prompt: "Sign off resolution",
      desc: "Confirm with your PIN to close " + id + ".",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-resolve", {
          incidentId: id, resolutionAction: vals.resolution
        }, s.pin).then(() => {
          setItems(arr => arr.map(x => x.id === id ? Object.assign({}, x, {
            status: "resolved", resolvedBy: s.name, resolvedAt: new Date().toISOString(),
            resolutionAction: vals.resolution
          }) : x));
          cmpToast("Action resolved by " + s.name, "success");
        }).catch(e => cmpToast("Resolve failed: " + e.message, "error"));
      },
      onCancel: () => setResolveFor(id)
    });
  };

  const filtered = visibleItems.filter(i => tab === "all" || i.status === tab);

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Corrective Actions — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Corrective Actions</h3>
            <div className="cmp-sub">Raise a concern, sign with your PIN. Resolved actions stay on file.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-danger" onClick={() => setFormOpen(true)}>+ Raise concern</button>}
          </div>
        </div>
        <div className="cmp-tabs" style={{marginTop:12}}>
          {["open","resolved","all"].map(t => (
            <button key={t} className={tab === t ? "cmp-active" : ""} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)} ({visibleItems.filter(i => t === "all" || i.status === t).length})
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 && !hist.loading && (
        <div className="cmp-empty">
          {hist.isHistorical
            ? "No corrective actions matching this filter on " + cmpFriendly(hist.viewDate) + "."
            : (tab === "open" ? "No open actions in this session." : tab === "resolved" ? "Nothing resolved yet in this session." : "Nothing logged yet in this session.")}
        </div>
      )}
      {filtered.map(i => (
        <div key={i.id} className={"cmp-ca-row cmp-" + i.status}>
          <div className={"cmp-pill cmp-" + i.status}>{i.status === "open" ? "!" : "✓"}</div>
          <div className="cmp-body">
            <div className="cmp-desc">{i.desc}</div>
            <div className="cmp-meta">
              <strong style={{color:"var(--cm-nori)"}}>{i.id}</strong> · {i.source} · {cmpFriendly(i.ts)} · flagged by {i.flagged}
              {i.resolvedBy && <span> · <span style={{color:"var(--cm-green)"}}>resolved by {i.resolvedBy}</span></span>}
            </div>
            <div style={{fontSize:12,marginTop:8,lineHeight:1.45}}>{i.action}</div>
            {i.resolutionAction && (
              <div style={{fontSize:12,marginTop:8,lineHeight:1.45,padding:"8px 10px",background:"var(--cm-green-soft)",borderRadius:6,borderLeft:"3px solid var(--cm-green)"}}>
                <strong style={{color:"var(--cm-green)"}}>Resolution:</strong> {i.resolutionAction}
              </div>
            )}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
            <span className={"cmp-chip " + (i.status === "open" ? "cmp-bad" : "cmp-ok")}><span className="cmp-dot" />{i.status}</span>
            {!hist.isHistorical && i.status === "open" && <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setResolveFor(i.id)}>Mark resolved</button>}
          </div>
        </div>
      ))}
      {!hist.isHistorical && formOpen && (
        <CmpFormModal
          title="Raise corrective action"
          desc="Describe the issue and the action you're taking. You'll confirm with your PIN on the next step."
          fields={[
            {key: "description", label: "Describe the issue", type: "textarea", placeholder: "What did you spot? Where? When?", required: true},
            {key: "action", label: "Action you're taking", type: "textarea", placeholder: "What have you done / will do to fix it?", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitRaise}
          onCancel={() => setFormOpen(false)}
        />
      )}
      {!hist.isHistorical && resolveFor && (
        <CmpFormModal
          title="Resolve corrective action"
          desc={"Closing " + resolveFor + ". Capture the corrective action taken before signing — required."}
          fields={[
            {key: "resolution", label: "Corrective action taken", type: "textarea", placeholder: "What was done to close this issue?", required: true, hint: "Logged on the row and on the audit trail with your PIN signature."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitResolve}
          onCancel={() => setResolveFor(null)}
        />
      )}
    </div>
  );
}

// ---- ComplianceModule shell ----
const CMP_VALID_VIEWS = ["home","cleaning","pest","pestmonday","corrective","haccp","probe","fish","emergency","declaration",
  "hub-sops","hub-fsms","hub-risk","hub-ssow","hub-suppliers","hub-legal","hub-reference","hub-certs"];

// Lane AK read-source dot — shows ONLY when the compliance read-flip gate is on
// (COMPLIANCE_READ_HOSTS lists this host, or ?compread=on). Green = reading the
// food-safety record from Supabase; coral = fell back to the Google Sheet.
// Invisible in the shipped OFF state, so it changes nothing for staff today.
function CmpReadSourceDot() {
  const [src, setSrc] = useState(() => complianceReader.getComplianceSource());
  useEffect(() => {
    setSrc(complianceReader.getComplianceSource());
    return complianceReader.subscribeComplianceSource(setSrc);
  }, []);
  if ((!complianceReader.complianceReadEnabled() && !complianceReader.haccpReadEnabled()) || !src) return null;
  const supa = src === "supabase";
  return (
    <div style={{fontSize:"0.68rem",fontWeight:600,margin:"0 auto 8px",display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:supa?"#6FAF8F":"#FF7A5C"}}>
      <span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>
      {supa ? "Live — reading compliance from Supabase" : "⚠ Fallback — reading compliance from Google Sheet"}
    </div>
  );
}

function ComplianceModule({site, backend, requestPin, staffByPin}) {
  const [view, setView] = useState("home");
  const sundayTone = cmpSundayPillTone();
  // Live home-screen counts. Read on-mount from today's HACCP sheet
  // (existing public GET endpoint) so the status strip reflects real
  // state. Other counts (cleaning today, open incidents) are wired in
  // v1.3 once getComplianceFormHistory ships; for now we show what's
  // cheaply queryable.
  const [todayCounts, setTodayCounts] = useState({haccpDone: 0, haccpTotal: 0, cleaningCellsToday: 0, pestDoneToday: false, openIncidents: 0, openCorrective: 0});
  const [probesDue, setProbesDue] = useState([]);
  useEffect(() => {
    if (view !== "home") return;
    const today = cmpTodayIso();
    // HACCP today (live progress strip). Routed through cmpFetchHaccpDay so it
    // inherits the Lane AK read-flip gate — otherwise the home dashboard would
    // read the Sheet while the forms read Supabase (split-brain).
    cmpFetchHaccpDay(backend, site.key, today)
      .catch(() => null)
      .then(d => {
        if (!d || !d.sheet) return;
        const sh = d.sheet;
        const opening = Object.keys(sh.openingChecks || {}).length;
        const closing = Object.keys(sh.closingChecks || {}).length;
        const fridges = sh.storageTempsPerFridge || {};
        const fridgeReadings = Object.keys(fridges).reduce((n, fId) => n + Object.keys(fridges[fId] || {}).length, 0);
        const haccpDone = opening + closing + fridgeReadings;
        const haccpTotal = 8 + 6 + 10;
        setTodayCounts(c => Object.assign({}, c, {haccpDone, haccpTotal}));
      }).catch(() => {});
    // Pest done today + open corrective count + per-probe last-calibration
    Promise.all([
      cmpFetchFormToday(backend, site.key, "PestLog", 1).catch(() => []),
      cmpFetchFormToday(backend, site.key, "CorrectiveActions", 14).catch(() => []),
      cmpFetchFormToday(backend, site.key, "ProbeLog", 30).catch(() => []),
      cmpFetchFormToday(backend, site.key, "EmergencyLog", 14).catch(() => []),
      cmpFetchFormToday(backend, site.key, "CleaningLog", 7).catch(() => [])
    ]).then(([pestRows, caRows, probeRows, emRows, cleaningRows]) => {
      const pestDoneToday = pestRows.some(r => String(r.Date) === today && String(r.Event) === "daily-check");
      const openCorrective = caRows.filter(r => String(r.Status) === "open").length;
      // Open emergency incidents — group by IncidentId, latest status row wins
      const incStatus = {};
      emRows.slice().reverse().forEach(r => {
        const id = String(r.IncidentId || "");
        if (!id) return;
        if (String(r.Event) === "close" || String(r.Status) === "resolved") incStatus[id] = "resolved";
        else if (incStatus[id] !== "resolved") incStatus[id] = "open";
      });
      const openIncidents = Object.values(incStatus).filter(v => v === "open").length;
      const cleaningCellsToday = cleaningRows.filter(r => String(r.DayDate) === today && String(r.State) === "done").length;
      setTodayCounts(c => Object.assign({}, c, {pestDoneToday, openCorrective, openIncidents, cleaningCellsToday}));
      // Per-probe last calibration → probes-due banner
      const lastByProbe = {};
      probeRows.forEach(r => {
        const pId = String(r.ProbeId || "");
        if (!pId || lastByProbe[pId]) return;
        lastByProbe[pId] = r._ts;
      });
      const due = CMP_PROBES
        .map(p => {
          const ts = lastByProbe[p.id];
          const daysAgo = ts ? Math.floor((Date.now() - new Date(ts).getTime()) / (1000*60*60*24)) : null;
          return {id: p.id, name: p.name, daysAgo};
        })
        .filter(p => p.daysAgo === null || p.daysAgo >= 7);
      setProbesDue(due);
    });
  }, [view, backend, site.key]);

  useEffect(() => {
    try {
      const v = sessionStorage.getItem("cmpView");
      if (v && CMP_VALID_VIEWS.indexOf(v) !== -1) setView(v);
    } catch(_) {}
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem("cmpView", view); } catch(_) {}
    window.scrollTo({top: 0, left: 0, behavior: "auto"});
  }, [view]);

  let body;
  if (view === "cleaning")        body = <CmpCleaningSchedule backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "pest")       body = <CmpPestDaily       backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "pestmonday") body = <CmpPestMondaySweep backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "corrective") body = <CmpCorrectiveActions backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "haccp")      body = <CmpHaccpDaily      backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "probe")      body = <CmpProbeCalibration backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "fish")       body = <CmpFishFreezing    backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "emergency")  body = <CmpEmergencyFridge backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "declaration")body = <CmpDeclaration     backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "hub-sops")     body = <CmpHubSops backend={backend} site={site} />;
  else if (view === "hub-fsms")     body = <CmpHubFsms />;
  else if (view === "hub-risk")     body = <CmpHubRisk />;
  else if (view === "hub-ssow")     body = <CmpHubSsow />;
  else if (view === "hub-suppliers")body = <CmpHubSuppliers backend={backend} site={site} />;
  else if (view === "hub-legal")    body = <CmpHubLegal     backend={backend} site={site} />;
  else if (view === "hub-reference")body = <CmpHubReference />;
  else if (view === "hub-certs")    body = <CmpHubCerts />;
  else                            body = <CmpHome nav={setView} sundayTone={sundayTone} todayCounts={todayCounts} probesDue={probesDue} />;

  return (
    <div className="cmp-root">
      <CmpReadSourceDot />
      {view !== "home" && (
        <div className="cmp-wrap" style={{paddingTop:0,paddingBottom:0,marginBottom:8}}>
          <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setView("home")}>← Compliance home</button>
        </div>
      )}
      {body}
      <CmpToastLayer />
      <CmpPdfLayer />
    </div>
  );
}


// ===========================================
// PLACEHOLDER
// ===========================================
// ===========================================
// PREVIOUS WEEK INSIGHTS — read-only EOD view (Phase A)
// ===========================================
function PreviousWeekInsights({site, backend, availableSites}){
  const ymdLocal = (d) => d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
  const today = useMemo(()=>{ const d=new Date(); d.setHours(0,0,0,0); return d; },[]);
  const daysAgo = useCallback((n) => { const d = new Date(today); d.setDate(d.getDate()-n); return d; }, [today]);
  const PREV_7 = useMemo(()=>[1,2,3,4,5,6,7].map(daysAgo), [daysAgo]);

  // Site toggle — when availableSites has 2+ entries, a pill segment switcher
  // appears between the day banner and the date strip. Default is the home
  // site (`site` prop). Single-site frontends pass nothing and skip the toggle.
  const sitesList = (availableSites && availableSites.length) ? availableSites : [site];
  const showSiteToggle = sitesList.length > 1;
  const [currentSiteKey, setCurrentSiteKey] = useState(()=>site.key);
  const currentSite = sitesList.find(s => s.key === currentSiteKey) || sitesList[0];

  const [selectedDate, setSelectedDate] = useState(()=>ymdLocal(daysAgo(1)));
  // Cache of {[siteKey]: {data, error}} for the SELECTED date. Sites are
  // fetched in parallel on date change so toggling between Trafford/Arndale
  // is instant — no loading spinner just to switch sites once data lands.
  const [dataBySite, setDataBySite] = useState({});

  // Phase A — hardcoded per-site thresholds. Phase B replaces these with
  // an admin-managed Site Targets fetch on mount.
  const THRESH = (function(){
    const k = String(currentSite.key||'').toLowerCase();
    if (k==='arndale')    return {tgtgRange:[8,14],  wasteWarn:20, wasteBad:30, soldEarly:"15:30", soldMid:"17:30"};
    if (k==='meadowhall') return {tgtgRange:[10,18], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"};
    if (k==='kitchen')    return {tgtgRange:[10,20], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"};
    return                       {tgtgRange:[15,25], wasteWarn:15, wasteBad:25, soldEarly:"15:00", soldMid:"17:00"}; // tc
  })();

  // Stable string of site keys for dep array — re-creating availableSites on
  // each render would otherwise re-fire the effect on every parent re-render.
  const sitesListKey = sitesList.map(s=>s.key).join(",");

  useEffect(()=>{
    let cancelled = false;
    // Reset cache for the new date so stale data from a previous date doesn't
    // flash before the parallel fetch lands. EOD + Total Made fetch in parallel
    // per site — Total Made comes from Adam's Quantities sheet (refreshed daily
    // at 3-4am) and is independent of whether an EOD report was submitted.
    setDataBySite({});
    Promise.all(sitesList.map(s => {
      // Gated read: when EOD_INSIGHTS_READ_HOSTS lists this host (or ?eodinsightsread=on),
      // read the day from Supabase; otherwise the unchanged Google feed. The Supabase
      // reader falls back to this SAME feed on any failure, so the worst case is exactly
      // today's behaviour.
      const sheetUrl = backend + "?action=eodinsights&site=" + encodeURIComponent(s.key) + "&date=" + encodeURIComponent(selectedDate);
      const eodReq = (eodInsights.eodInsightsReadEnabled()
          ? eodInsights.fetchEodInsightsDay(s.key, selectedDate, { sheetUrl })
          : fetch(sheetUrl).then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); }))
        .then(d => ({data: d || null, error: null}))
        .catch(e => ({data: null, error: e.message || "Failed to load"}));
      // Lane BG: gate the Total Made read behind the SAME FORECAST_READ_HOSTS (+ 1122's
      // exact-day/fresh guard); the Supabase reader falls back to this SAME Google feed
      // on any miss/failure, so the OFF path (googleMade) is today's behaviour exactly.
      const madeUrl = backend + "?action=totalmade&site=" + encodeURIComponent(s.key) + "&date=" + encodeURIComponent(selectedDate);
      const googleMade = () => fetch(madeUrl)
        .then(r => r.ok ? r.json() : null)
        .then(d => (d && typeof d.totalMade === "number") ? d.totalMade : null)
        .catch(()=>null);
      const madeReq = forecastReader.forecastReadEnabled()
        ? forecastReader.kioskTotalMade(s.key, selectedDate, { googleFetch: googleMade })
        : googleMade();
      return Promise.all([eodReq, madeReq]).then(([eod, totalMade]) => ({key: s.key, data: eod.data, error: eod.error, totalMade}));
    })).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => { map[r.key] = {data: r.data, error: r.error, totalMade: r.totalMade}; });
      setDataBySite(map);
    });
    return () => { cancelled = true; };
  }, [selectedDate, backend, sitesListKey]);

  const slot = dataBySite[currentSite.key];
  const loading = !slot;
  const data = slot ? slot.data : null;
  const error = slot ? slot.error : null;
  const totalMade = slot ? slot.totalMade : null;

  const fmtLong = (d) => d.toLocaleDateString("en-GB", {weekday:"long", day:"numeric", month:"long"});
  const fmtDow  = (d) => d.toLocaleDateString("en-GB", {weekday:"short"});
  const fmtMon  = (d) => d.toLocaleDateString("en-GB", {month:"short"});
  const isYesterday = selectedDate === ymdLocal(daysAgo(1));

  const timeSev = (time) => {
    if (!time) return "low";
    const [h,m] = time.split(":").map(Number);
    const mins = h*60 + (m||0);
    const [eh,em] = THRESH.soldEarly.split(":").map(Number);
    const [mh,mm] = THRESH.soldMid.split(":").map(Number);
    if (mins < eh*60+em) return "early";
    if (mins < mh*60+mm) return "mid";
    return "late";
  };
  const qtySev = (qty) => qty >= 5 ? "high" : qty >= 3 ? "med" : "low";
  const sevColor = (sev) => sev==="high"||sev==="early"||sev==="bad" ? B.coral : sev==="med"||sev==="mid"||sev==="warn" ? B.sunshine : B.wasabi;
  const tgtgSev = (bags) => bags >= THRESH.tgtgRange[0] && bags <= THRESH.tgtgRange[1] ? "good" : bags < THRESH.tgtgRange[0] ? "warn" : "bad";

  const wrap = {maxWidth:760, margin:"0 auto", padding:"16px 18px 32px"};
  const card = {background:"rgba(212,251,111,0.04)", border:"1px solid rgba(212,251,111,0.1)", borderRadius:14, padding:"14px 16px", marginBottom:14};
  const labelStyle = {fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.45, marginBottom:6};
  const numStyle = {fontFamily:"Manrope,sans-serif", fontWeight:800, fontSize:"1.95rem", lineHeight:1.05, color:B.kewpie, letterSpacing:"-0.01em"};

  if (loading) return (
    <div style={wrap}>
      <div style={{...card, textAlign:"center", padding:"40px 20px"}}>
        <div style={{width:28, height:28, border:"3px solid rgba(212,251,111,0.15)", borderTopColor:B.wasabi, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto"}}/>
        <div style={{fontSize:"0.8rem", color:B.kewpie, opacity:0.5, marginTop:12}}>Loading...</div>
      </div>
    </div>
  );

  const r = data && (data.totalWaste !== null || data.tgtgSold !== null || (data.soldOut && data.soldOut.length) || (data.wastage && data.wastage.length)) ? data : null;
  const total = r && r.wastage ? r.wastage.reduce((s,row)=>s+(row[1]||0),0) : 0;
  const wastePct = r && r.forecastTotal ? Math.round(((r.totalWaste||0)/r.forecastTotal)*100) + "%" : null;

  return (
    <div style={wrap}>
      {/* Day banner */}
      <div style={{marginBottom:14}}>
        <div style={{fontFamily:"gelica,serif", fontSize:"1.5rem", fontWeight:700, color:B.kewpie, letterSpacing:"-0.01em", lineHeight:1.15}}>
          {fmtLong(new Date(selectedDate + "T00:00:00"))}
          {isYesterday && <span style={{display:"inline-block", marginLeft:10, fontFamily:"Manrope,sans-serif", fontSize:"0.65rem", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", padding:"3px 8px", borderRadius:5, background:"rgba(212,251,111,0.1)", color:B.wasabi, border:"1px solid rgba(212,251,111,0.2)", verticalAlign:"middle"}}>Yesterday</span>}
        </div>
        {r && r.submittedBy && <div style={{fontSize:"0.75rem", color:B.kewpie, opacity:0.5, marginTop:3}}>Submitted by {r.submittedBy[0]} ({r.submittedBy[1]}) at {r.submittedBy[2]} · {currentSite.name}</div>}
      </div>

      {/* Site toggle (only shown when 2+ sites configured) */}
      {showSiteToggle && (
        <div style={{display:"inline-flex", border:"1px solid rgba(212,251,111,0.15)", borderRadius:10, padding:4, marginBottom:14, gap:3, background:"rgba(254,250,240,0.04)"}}>
          {sitesList.map(s => {
            const active = s.key === currentSite.key;
            return (
              <button key={s.key} onClick={()=>setCurrentSiteKey(s.key)}
                style={{border:"none", background: active ? B.wasabi : "none", cursor:"pointer", fontFamily:"Manrope,sans-serif", fontSize:"0.82rem", fontWeight: active ? 700 : 600, color: active ? B.nori : B.kewpie, padding:"8px 18px", borderRadius:7, transition:"all 0.15s", minWidth:96, opacity: active ? 1 : 0.55}}>
                {s.name}
              </button>
            );
          })}
        </div>
      )}

      {/* 7-day picker strip */}
      <div style={{display:"flex", gap:5, overflowX:"auto", padding:4, background:"rgba(254,250,240,0.04)", border:"1px solid rgba(212,251,111,0.1)", borderRadius:10, marginBottom:16, WebkitOverflowScrolling:"touch"}}>
        {PREV_7.map(d => {
          const k = ymdLocal(d);
          const active = k === selectedDate;
          return (
            <button key={k} onClick={()=>setSelectedDate(k)} style={{flex:"0 0 auto", border:"1px solid transparent", background: active ? B.wasabi : "none", cursor:"pointer", fontFamily:"Manrope,sans-serif", padding:"8px 12px", borderRadius:7, color: active ? B.nori : B.kewpie, textAlign:"center", minWidth:62, transition:"background 0.15s"}}>
              <span style={{display:"block", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", opacity: active ? 0.7 : 0.5, color: active ? B.nori : B.kewpie}}>{fmtDow(d)}</span>
              <span style={{display:"block", fontFamily:"Manrope,sans-serif", fontSize:"1.05rem", fontWeight:700, lineHeight:1.1, marginTop:2, color: active ? B.nori : B.kewpie, fontVariantNumeric:"tabular-nums"}}>{d.getDate()}</span>
              <span style={{display:"block", fontSize:"0.6rem", fontWeight:600, opacity: active ? 0.7 : 0.5, textTransform:"uppercase", letterSpacing:"0.04em", marginTop:1, color: active ? B.nori : B.kewpie}}>{fmtMon(d)}</span>
            </button>
          );
        })}
      </div>

      {/* Total Made — Adam's headline from the per-site Quantities sheet
          (row 34 keyed by date in row 2; refreshed by his 3-4am script).
          Sits independently of the EOD report so it still shows on days
          with no submitted report. */}
      {totalMade !== null && (
        <div style={{...card, padding:"18px 18px 16px", marginBottom:14}}>
          <div style={labelStyle}>Total Made</div>
          <div style={{...numStyle, color:B.wasabi, fontSize:"2.6rem"}}>{totalMade}</div>
          <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}>
            Portions made on {currentSite.name} this day
          </div>
        </div>
      )}

      {error && <div style={{...card, color:B.coral, fontSize:"0.85rem"}}>Couldn't load: {error}</div>}

      {!r && !error && (
        <div style={{...card, textAlign:"center", padding:"36px 20px", fontStyle:"italic", color:B.kewpie, opacity:0.45, fontSize:"0.85rem"}}>
          No EOD report submitted for this day.
        </div>
      )}

      {r && (
        <>
          {/* Hero stats */}
          <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginBottom:14}}>
            <div style={card}>
              <div style={labelStyle}>Total Wasted</div>
              <div style={{...numStyle, color: sevColor((r.totalWaste||0) >= THRESH.wasteBad ? "bad" : (r.totalWaste||0) >= THRESH.wasteWarn ? "warn" : "good")}}>{r.totalWaste||0}</div>
              {wastePct && <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}><strong style={{color:B.kewpie, fontWeight:700}}>{wastePct}</strong> of the day's {r.forecastTotal} prep</div>}
            </div>
            <div style={card}>
              <div style={labelStyle}>TGTG Bags Sold</div>
              <div style={{...numStyle, color: sevColor(tgtgSev(r.tgtgSold||0))}}>{r.tgtgSold||0}</div>
              <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}>
                <strong style={{color:B.kewpie, fontWeight:700}}>{(r.tgtgSold||0)*3} rolls</strong> equivalent (1 bag = 3 rolls)
                <span style={{display:"block", marginTop:2, fontSize:"0.68rem", fontStyle:"italic"}}>(optimal range = {THRESH.tgtgRange[0]} to {THRESH.tgtgRange[1]})</span>
              </div>
            </div>
            <div style={card}>
              <div style={labelStyle}>Rolls Sold Out</div>
              <div style={{...numStyle, color: sevColor((r.soldOutCount||0) >= 6 ? "bad" : (r.soldOutCount||0) >= 3 ? "warn" : "good")}}>{r.soldOutCount||0}</div>
              <div style={{fontSize:"0.72rem", color:B.kewpie, opacity:0.5, marginTop:6, lineHeight:1.4}}><strong style={{color:B.kewpie, fontWeight:700}}>{r.soldOutCount||0}</strong> of 14 SKUs ran out before close</div>
            </div>
          </div>

          {/* Sell Out Times panel */}
          <div style={card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"0 0 10px", marginBottom:10, borderBottom:"1px solid rgba(212,251,111,0.1)"}}>
              <div>
                <div style={{fontFamily:"gelica,serif", fontSize:"1rem", fontWeight:700, color:B.kewpie}}>Sell Out Times</div>
                <div style={{fontSize:"0.7rem", color:B.kewpie, opacity:0.45, marginTop:2}}>Earliest first — make more today</div>
              </div>
              <span style={{fontSize:"0.72rem", fontWeight:700, color:B.kewpie, opacity:0.6, whiteSpace:"nowrap"}}>{r.soldOut.length} roll{r.soldOut.length===1?"":"s"}</span>
            </div>
            {r.soldOut.length === 0
              ? <div style={{textAlign:"center", padding:"18px 14px", color:B.kewpie, opacity:0.45, fontSize:"0.78rem", fontStyle:"italic"}}>Nothing sold out.</div>
              : <table style={{width:"100%", borderCollapse:"collapse", fontFamily:"Manrope,sans-serif"}}>
                  <thead><tr>
                    <th style={{textAlign:"left", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Roll</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Sold out</th>
                  </tr></thead>
                  <tbody>{r.soldOut.map(([sku,t],i)=>(
                    <tr key={sku+i}>
                      <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.85rem", fontWeight:600, color:B.kewpie}}>{sku}</td>
                      <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontFamily:"Manrope,sans-serif", fontWeight:700, fontSize:"0.95rem", textAlign:"right", color:sevColor(timeSev(t)), fontVariantNumeric:"tabular-nums"}}>{t}</td>
                    </tr>
                  ))}</tbody>
                </table>
            }
          </div>

          {/* Wastage panel */}
          <div style={card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"0 0 10px", marginBottom:10, borderBottom:"1px solid rgba(212,251,111,0.1)"}}>
              <div>
                <div style={{fontFamily:"gelica,serif", fontSize:"1rem", fontWeight:700, color:B.kewpie}}>Wastage</div>
                <div style={{fontSize:"0.7rem", color:B.kewpie, opacity:0.45, marginTop:2}}>Highest first — prep less today</div>
              </div>
              <span style={{fontSize:"0.72rem", fontWeight:700, color:B.kewpie, opacity:0.6, whiteSpace:"nowrap"}}>{total} portion{total===1?"":"s"}</span>
            </div>
            {(!r.wastage || r.wastage.length === 0)
              ? <div style={{textAlign:"center", padding:"18px 14px", color:B.kewpie, opacity:0.45, fontSize:"0.78rem", fontStyle:"italic"}}>Zero wastage — clean sweep.</div>
              : <table style={{width:"100%", borderCollapse:"collapse", fontFamily:"Manrope,sans-serif"}}>
                  <thead><tr>
                    <th style={{textAlign:"left", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Roll</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Wasted</th>
                    <th style={{textAlign:"right", fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", color:B.kewpie, opacity:0.3, padding:"6px 10px 8px", borderBottom:"1px solid rgba(212,251,111,0.1)"}}>Share</th>
                  </tr></thead>
                  <tbody>{r.wastage.map(([sku,q],i)=>{
                    const sev = qtySev(q);
                    const share = total ? Math.round((q/total)*100) + "%" : "—";
                    return (
                      <tr key={sku+i}>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.85rem", fontWeight:600, color:B.kewpie}}>{sku}</td>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontFamily:"Manrope,sans-serif", fontWeight:700, fontSize:"0.95rem", textAlign:"right", color:sevColor(sev), fontVariantNumeric:"tabular-nums"}}>{q}</td>
                        <td style={{padding:"10px", borderBottom:"1px solid rgba(254,250,240,0.06)", fontSize:"0.72rem", textAlign:"right", color:B.kewpie, opacity:0.5, fontWeight:500, fontVariantNumeric:"tabular-nums"}}>{share}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
            }
          </div>
        </>
      )}
    </div>
  );
}

function Placeholder({label,icon,msg}){
  return (<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"50vh",textAlign:"center",padding:40}}>
    <div style={{fontSize:"3rem",marginBottom:12}}>{icon}</div>
    <div style={{fontFamily:"gelica,serif",fontSize:"1.4rem",color:B.wasabi,marginBottom:6}}>{label}</div>
    <div style={{color:B.kewpie,opacity:0.4,fontSize:"0.85rem",maxWidth:300,lineHeight:1.5}}>{msg}</div>
  </div>);
}

// ===========================================
// STYLES
// ===========================================
const S = {
  // Login
  loginRoot:{minHeight:"100vh",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif",position:"relative",animation:"viewIn 0.18s ease both"},
  strip:{position:"absolute",top:0,left:0,right:0,background:"rgba(0,0,0,0.2)",padding:"10px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8},
  stripDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi},stripTxt:{fontWeight:700,fontSize:"0.8rem",color:B.kewpie,letterSpacing:"0.08em",textTransform:"uppercase"},
  loginBody:{display:"flex",flexDirection:"column",alignItems:"center",width:"100%",maxWidth:340},
  dots:{display:"flex",gap:20,marginBottom:10},
  dot:{width:20,height:20,borderRadius:"50%",border:"2.5px solid rgba(212,251,111,0.3)",borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none",transition:"all 0.15s ease"},
  dotFilled:{borderColor:B.wasabi,background:B.wasabi,boxShadow:"0 0 14px rgba(212,251,111,0.35)"},
  dotEmpty:{borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none"},
  pinErr:{color:B.coral,fontSize:"0.82rem",fontWeight:600,height:20,marginBottom:8},pinHint:{color:B.kewpie,opacity:0.45,fontSize:"0.82rem",height:20,marginBottom:8},
  pad:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,width:"100%",maxWidth:280},
  key:{aspectRatio:"1.35",borderRadius:14,border:"none",background:"rgba(212,251,111,0.1)",color:B.wasabi,fontSize:"1.6rem",fontFamily:"Manrope,sans-serif",fontWeight:700,cursor:"pointer",transition:"all 0.1s ease",display:"flex",alignItems:"center",justifyContent:"center"},
  loginFoot:{color:B.kewpie,opacity:0.25,fontSize:"0.75rem",textAlign:"center",paddingBottom:16},
  flash:{position:"fixed",top:0,left:0,right:0,bottom:0,width:"100%",height:"100%",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:100},
  greet:{fontFamily:"gelica,serif",fontSize:"2.8rem",fontWeight:700,color:B.wasabi,animation:"greetIn 1s ease forwards",textAlign:"center",padding:"0 1rem",width:"100%",maxWidth:600},

  // Home
  homeRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif",padding:"0 2rem 1rem",display:"flex",flexDirection:"column",animation:"viewIn 0.2s ease both"},
  topBar:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  topL:{display:"flex",alignItems:"center",gap:14},
  hSite:{background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,borderRadius:999,padding:"4px 14px",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase"},
  topR:{display:"flex",alignItems:"center",gap:12},chip:{display:"flex",alignItems:"center",gap:8,background:"rgba(254,250,240,0.07)",borderRadius:999,padding:"4px 14px 4px 4px"},
  chipAv:{width:28,height:28,borderRadius:"50%",background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:"0.75rem"},
  chipName:{fontSize:"0.82rem",fontWeight:600,color:B.kewpie},
  logoutBtn:{background:"none",border:"1px solid rgba(254,250,240,0.15)",borderRadius:999,padding:"5px 14px",fontSize:"0.75rem",fontWeight:600,color:B.kewpie,opacity:0.5,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  greetBox:{padding:"2rem 0 1.5rem"},greetHi:{fontFamily:"gelica,serif",fontSize:"2rem",fontWeight:700,color:B.kewpie,lineHeight:1.2},greetD:{fontSize:"0.88rem",color:B.kewpie,opacity:0.35,marginTop:4},
  // Tile grid — tablet-first, 3 cols capped at 960px, 4:3 tiles with vertically centred content
  grid:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,width:"100%",maxWidth:960,margin:"0 auto",flex:"0 0 auto"},
  card:{background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:16,padding:"1.4rem 1.4rem 1.3rem",textAlign:"left",cursor:"pointer",transition:"all 0.12s ease",display:"flex",flexDirection:"column",justifyContent:"center",fontFamily:"Manrope,sans-serif",animation:"fadeUp 0.35s ease both",position:"relative",aspectRatio:"4/3"},
  cardOff:{opacity:0.25,cursor:"default"},cTop:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12},cIcon:{fontSize:"1.75rem"},
  cSoon:{background:"rgba(254,250,240,0.08)",borderRadius:999,padding:"2px 6px",fontSize:"0.6rem",fontWeight:700,color:B.kewpie,opacity:0.5,textTransform:"uppercase"},
  cLabel:{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi,marginBottom:4,lineHeight:1.2},cDesc:{fontSize:"0.72rem",color:B.kewpie,opacity:0.45,lineHeight:1.4},
  hFoot:{textAlign:"center",color:B.kewpie,opacity:0.15,fontSize:"0.7rem",marginTop:"auto",paddingTop:16},

  // Shell
  shellRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif"},
  shellBar:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  shellBack:{background:"none",border:"none",color:B.wasabi,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  shellSiteFull:{fontSize:"0.72rem",fontWeight:700,color:B.wasabi,opacity:0.5,letterSpacing:"0.04em",textTransform:"uppercase"},
  // Sidebar (collapsible)
  sidebar:{width:200,borderRight:"1px solid rgba(212,251,111,0.08)",padding:"12px 8px",flexShrink:0,transition:"width 0.22s ease, padding 0.22s ease",overflow:"hidden"},
  sidebarCollapsed:{width:56,padding:"12px 6px"},
  sideToggle:{display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:32,borderRadius:8,border:"none",background:"none",color:B.kewpie,opacity:0.35,fontSize:"0.7rem",cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:10,transition:"opacity 0.15s ease"},
  sideItem:{display:"flex",alignItems:"center",width:"100%",padding:"8px 10px",borderRadius:10,border:"none",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.78rem",fontWeight:500,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",marginBottom:2,transition:"all 0.15s ease",whiteSpace:"nowrap"},
  sideItemCollapsed:{padding:"10px 0",justifyContent:"center"},
  sideItemActive:{background:"rgba(212,251,111,0.1)",color:B.wasabi,opacity:1,fontWeight:700},

  // Shared module styles
  content:{padding:"0 20px 24px",maxWidth:640,width:"100%",margin:"0 auto"},
  sectionCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"14px 16px",marginBottom:16},
  secLabel:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:2},secDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,lineHeight:1.5},
  secTitle:{fontSize:"0.7rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8,marginTop:8,display:"flex",alignItems:"center"},
  fieldLabel:{fontSize:"0.7rem",color:B.kewpie,opacity:0.4,marginBottom:3,display:"block"},
  input:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",outline:"none",boxSizing:"border-box"},
  textarea:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.85rem",outline:"none",resize:"none",minHeight:70,lineHeight:1.5,boxSizing:"border-box"},
  submitBtn:{width:"100%",padding:14,background:B.wasabi,color:B.nori,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:700,border:"none",borderRadius:14,cursor:"pointer",letterSpacing:"0.02em"},
  infoBanner:{padding:"10px 14px",borderRadius:10,border:"1px solid",marginBottom:12,fontSize:"0.78rem",lineHeight:1.5},

  // Rows
  row:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  soRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  itemName:{fontSize:"0.85rem",fontWeight:500,color:B.kewpie},
  ctrls:{display:"flex",alignItems:"center",gap:8},
  cBtn:{width:30,height:30,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.wasabi,fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif"},
  cVal:{fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.95rem",color:B.kewpie,opacity:0.4,minWidth:24,textAlign:"center"},
  markBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(212,251,111,0.25)",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  undoBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(255,122,92,0.2)",background:"rgba(255,122,92,0.06)",color:B.coral,fontSize:"0.78rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  restockBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  receivedBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  unclearedBox:{background:"rgba(212,251,111,0.03)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:12,padding:"10px 14px",marginBottom:8},

  // Stock categories
  catHeader:{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.08)",borderRadius:10,color:B.wasabi,fontSize:"0.82rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:2},

  // Modals — z-index must sit ABOVE the scanner's own .cc-modal (350) so the PIN modal
  // spawns on top of the scanner's Manual Lookup card, not behind it.
  overlay:{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",background:"rgba(0,0,0,0.6)",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",padding:16,boxSizing:"border-box",overflowY:"auto"},
  modal:{background:B.nori,border:"1px solid rgba(212,251,111,0.2)",borderRadius:18,padding:20,maxWidth:380,width:"calc(100% - 32px)",maxHeight:"80vh",overflowY:"auto"},
  modalTitle:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:4},
  modalDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,marginBottom:14},
  modalInput:{width:"100%",padding:"14px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.15)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"1.2rem",textAlign:"center",letterSpacing:"8px",outline:"none",marginBottom:8,boxSizing:"border-box"},
  modalErr:{fontSize:"0.78rem",color:B.coral,textAlign:"center",marginBottom:8},
  modalBtns:{display:"flex",gap:8},
  modalCancel:{flex:1,padding:12,borderRadius:10,border:"1px solid rgba(254,250,240,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  modalOk:{flex:1,padding:12,borderRadius:10,border:"none",background:B.wasabi,color:B.nori,fontSize:"0.85rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},

  // Loading
  loadingBox:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px"},
  spinner:{width:32,height:32,border:"3px solid rgba(212,251,111,0.15)",borderTopColor:B.wasabi,borderRadius:"50%",animation:"spin 0.8s linear infinite"},
  emptyState:{textAlign:"center",padding:"60px 20px",fontSize:"0.85rem",color:B.kewpie,opacity:0.4},

  // Kitchen-specific additions
  heroCard:{textAlign:"center",padding:"28px 20px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:14,marginBottom:12,position:"relative"},
  siteTab:{flex:1,padding:"10px 16px",borderRadius:10,border:"1px solid rgba(212,251,111,0.12)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.82rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif",transition:"all 0.15s ease",textAlign:"center"},
  siteTabActive:{background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.3)",color:B.wasabi,opacity:1},
  siteSectionHeader:{fontFamily:"gelica,serif",fontSize:"1.3rem",fontWeight:700,color:B.wasabi,padding:"18px 14px 8px",borderTop:"2px solid rgba(212,251,111,0.15)",marginTop:10,marginBottom:4,letterSpacing:"0.01em",display:"flex",alignItems:"center",justifyContent:"center",gap:10,flexWrap:"wrap",rowGap:6,textAlign:"center"},
  siteSectionDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi,boxShadow:"0 0 10px rgba(212,251,111,0.5)"},
  siteSectionSub:{fontSize:"0.72rem",color:B.kewpie,opacity:0.4,padding:"0 14px 14px",marginTop:-2},
  scaleRow:{display:"flex",justifyContent:"space-between",marginTop:6,padding:"0 2px",fontSize:"0.62rem",color:B.kewpie,opacity:0.35,fontFamily:"Manrope,sans-serif",fontWeight:600,letterSpacing:"0.04em"},
  scaleTick:{flex:1,textAlign:"center"},
  scaleTickEdge:{flex:"0 0 auto"},
  overrideHint:{fontSize:"0.7rem",color:B.kewpie,opacity:0.35,marginTop:8,textAlign:"center",lineHeight:1.5},
  skuColHeader:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 12px 10px",fontSize:"0.62rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",borderBottom:"1px solid rgba(212,251,111,0.08)",marginBottom:6},
  skuColHeaderLeft:{flex:1},
  skuColHeaderRolls:{width:140,textAlign:"center"},
  skuColHeaderPortions:{width:60,textAlign:"right"},
  skuRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease",gap:8},
  skuRowLeft:{flex:1,minWidth:0},
  skuPortionsVal:{width:60,textAlign:"right",fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.9rem",color:B.kewpie,opacity:0.55},
  flagsAppliedCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"12px 16px 14px",marginBottom:16,position:"relative"},
  flagsAppliedBadge:{position:"absolute",top:-8,left:14,background:B.nori,padding:"0 8px",fontSize:"0.6rem",fontWeight:800,color:B.wasabi,letterSpacing:"0.1em",textTransform:"uppercase",border:"1px solid rgba(212,251,111,0.15)",borderRadius:8,lineHeight:"16px"},
  dateTrigger:{display:"inline-flex",alignItems:"center",gap:10,padding:"10px 18px",borderRadius:14,border:"1px solid rgba(212,251,111,0.18)",background:"rgba(212,251,111,0.05)",color:B.kewpie,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",transition:"all 0.15s ease"},
  dateTriggerBadge:{fontSize:"0.6rem",fontWeight:800,color:B.wasabi,background:"rgba(212,251,111,0.12)",borderRadius:999,padding:"3px 10px",marginLeft:8,letterSpacing:"0.1em"},
  todayJumpBtn:{background:"none",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,fontSize:"0.7rem",fontWeight:700,padding:"5px 14px",borderRadius:999,cursor:"pointer",fontFamily:"Manrope,sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"},
  calWeekday:{fontSize:"0.68rem",fontWeight:700,color:B.kewpie,opacity:0.4,textAlign:"center",letterSpacing:"0.05em",textTransform:"uppercase",padding:"4px 0"},
  calDay:{aspectRatio:"1",borderRadius:10,border:"1px solid transparent",background:"none",color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.1s ease"},
  calDayToday:{background:"rgba(212,251,111,0.08)",border:"1px solid rgba(212,251,111,0.25)",fontWeight:700,color:B.wasabi},
  calDaySelected:{background:B.wasabi,color:B.nori,fontWeight:800,border:"1px solid "+B.wasabi,boxShadow:"0 0 18px rgba(212,251,111,0.4)"},
  srCard:{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.08)",borderRadius:12,marginBottom:6,cursor:"pointer",transition:"all 0.12s ease",flexWrap:"wrap",overflow:"hidden"},
  srCardSelected:{background:"rgba(212,251,111,0.1)",border:"1px solid rgba(212,251,111,0.35)",boxShadow:"0 0 12px rgba(212,251,111,0.08)"},
  srCardCollected:{background:"rgba(111,175,143,0.06)",border:"1px solid rgba(111,175,143,0.2)",opacity:0.85,cursor:"default"},
  srCardOos:{background:"rgba(255,122,92,0.06)",border:"1px solid rgba(255,122,92,0.25)",opacity:0.85,cursor:"default"},
  srCheckbox:{width:22,height:22,borderRadius:6,border:"1.5px solid rgba(212,251,111,0.3)",background:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.12s ease"},
  srCheckboxChecked:{background:B.wasabi,borderColor:B.wasabi},
  srCheckMark:{color:B.nori,fontSize:"0.8rem",fontWeight:900,lineHeight:1},
  srStatusBadge:{display:"inline-flex",alignItems:"center",gap:4,fontSize:"0.62rem",fontWeight:800,padding:"3px 8px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",whiteSpace:"nowrap"},
  srBulkBar:{position:"sticky",bottom:10,background:B.nori,border:"1px solid rgba(212,251,111,0.25)",borderRadius:14,padding:"10px 12px",marginTop:12,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",boxShadow:"0 6px 20px rgba(0,0,0,0.35)"},
  srBulkCount:{fontSize:"0.78rem",fontWeight:700,color:B.kewpie,flex:"1 1 auto",minWidth:120},
  srBulkCollectBtn:{padding:"10px 14px",borderRadius:10,border:"none",background:B.wasabi,color:B.nori,fontSize:"0.82rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  srBulkOosBtn:{padding:"10px 14px",borderRadius:10,border:"1px solid rgba(255,122,92,0.4)",background:"rgba(255,122,92,0.1)",color:B.coral,fontSize:"0.82rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  srBulkCancelBtn:{padding:"10px 14px",borderRadius:10,border:"1px solid rgba(254,250,240,0.15)",background:"none",color:B.kewpie,opacity:0.6,fontSize:"0.78rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  srSkeletonCard:{height:54,background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:12,marginBottom:6,opacity:0.5},
  pendingWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,transition:"box-shadow 0.45s ease",animation:"pendingPulse 3.2s ease-in-out infinite"},
  confirmedWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,boxShadow:"0 0 0 1px rgba(111,175,143,0.22)",transition:"box-shadow 0.45s ease"},
  pendingPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.25)",color:B.coral,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  pendingDot:{width:6,height:6,borderRadius:"50%",background:B.coral,boxShadow:"0 0 6px rgba(255,122,92,0.7)"},
  confirmedPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(111,175,143,0.1)",border:"1px solid rgba(111,175,143,0.3)",color:B.eucalyptus,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  navBtn:{width:36,height:36,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"},
  pill:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.kewpie,opacity:0.6,display:"inline-flex",alignItems:"center",gap:4},
  pillDot:{width:4,height:4,borderRadius:"50%",background:B.wasabi},
  pillWarn:{background:"rgba(255,179,71,0.06)"},
  flag:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.wasabi},
  calcRow:{display:"flex",justifyContent:"space-between",alignItems:"center"},
  statusBadge:{display:"inline-block"},
};

export { App as BohApp };